// frontend/src/components/FloatingVideo.js
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import Draggable from 'react-draggable';
import useVideoStore from '../store/useVideoStore';
import useChannelsStore from '../store/channels';
import useAuthStore from '../store/auth';
import mpegts from 'mpegts.js';
import Hls from 'hls.js';
// DISPATCHARR_CATCHUP_HLS_WEB_SHAKA_PLAYER
import * as shakaModule from 'shaka-player/dist/shaka-player.compiled.js';
const shakaLib = shakaModule.Player ? shakaModule : (shakaModule.default || shakaModule);
import { CloseButton, Flex, Loader, Text, Box } from '@mantine/core';
import {
  RotateCcw,
  RotateCw,
  // DISPATCHARR_LIVE_DESKTOP_BADGE_NATIVE_BAR_ICONS
  Rewind as RewindGlyph,
  Cast as CastGlyph,
  Play as PlayGlyph,
} from 'lucide-react';
import {
  applyConstraints,
  calculateNewDimensions,
  getClientCoordinates,
  getLivePlayerErrorMessage,
  getVODPlayerErrorMessage,
  getPlayerPrefs,
  savePlayerPrefs,
} from '../utils/components/FloatingVideoUtils.js';
import TimeshiftSeekBar from './TimeshiftSeekBar.jsx';
import {
  appendAdaptiveOutputProfile,
  directMseCodecsAreSupported,
  measureAdaptiveOutputProfile,
  getLastAdaptiveOutputProfile,
  withAdaptiveLiveOutputProfile,
  withAdaptiveLiveTranscodeFallback,
  setServerDirectAllowed,
} from '../utils/adaptiveOutput.js';
import {
  ArchiveCastController,
  archiveChannelIdFromDirectUrl,
} from '../utils/archiveCastController.js';
import {
  buildLiveCastTransportUrls,
  runLiveCastHandoff,
} from '../utils/liveCastHandoff.js';

// Native <video src> cannot send Authorization headers. Append ?token= at playback
// time (not when building the URL in cards/modals) so the JWT is fresh. hls.js
// paths authenticate via xhrSetup instead and do not need this helper.
const withRecordingAuthToken = (url) => {
  if (!url || typeof url !== 'string') return url;
  if (!url.includes('/api/channels/recordings/')) return url;
  const token = useAuthStore.getState().accessToken;
  if (!token) return url;
  const [base, query = ''] = url.split('?');
  const params = new URLSearchParams(query);
  params.set('token', token);
  return `${base}?${params.toString()}`;
};

// Live bandwidth selection remains in frontend/src/utils/adaptiveOutput.js.
// Archive Web playback is direct and deliberately performs no speed test.

const ResizeHandles = ({ startResize }) => {
  const HANDLE_SIZE = 18;
  const HANDLE_OFFSET = 0;

  const resizeHandleBaseStyle = {
    position: 'absolute',
    width: HANDLE_SIZE,
    height: HANDLE_SIZE,
    backgroundColor: 'transparent',
    borderRadius: 6,
    zIndex: 8,
    touchAction: 'none',
  };

  const resizeHandles = [
    {
      id: 'bottom-right',
      cursor: 'nwse-resize',
      xDir: 1,
      yDir: 1,
      isLeft: false,
      isTop: false,
      style: {
        bottom: HANDLE_OFFSET,
        right: HANDLE_OFFSET,
        borderBottom: '2px solid rgba(255, 255, 255, 0.9)',
        borderRight: '2px solid rgba(255, 255, 255, 0.9)',
        borderRadius: '0 0 6px 0',
      },
    },
    {
      id: 'bottom-left',
      cursor: 'nesw-resize',
      xDir: -1,
      yDir: 1,
      isLeft: true,
      isTop: false,
      style: {
        bottom: HANDLE_OFFSET,
        left: HANDLE_OFFSET,
        borderBottom: '2px solid rgba(255, 255, 255, 0.9)',
        borderLeft: '2px solid rgba(255, 255, 255, 0.9)',
        borderRadius: '0 0 0 6px',
      },
    },
    {
      id: 'top-right',
      cursor: 'nesw-resize',
      xDir: 1,
      yDir: -1,
      isLeft: false,
      isTop: true,
      style: {
        top: HANDLE_OFFSET,
        right: HANDLE_OFFSET,
        borderTop: '2px solid rgba(255, 255, 255, 0.9)',
        borderRight: '2px solid rgba(255, 255, 255, 0.9)',
        borderRadius: '0 6px 0 0',
      },
    },
    {
      id: 'top-left',
      cursor: 'nwse-resize',
      xDir: -1,
      yDir: -1,
      isLeft: true,
      isTop: true,
      style: {
        top: HANDLE_OFFSET,
        left: HANDLE_OFFSET,
        borderTop: '2px solid rgba(255, 255, 255, 0.9)',
        borderLeft: '2px solid rgba(255, 255, 255, 0.9)',
        borderRadius: '6px 0 0 0',
      },
    },
  ];

  return (
    <>
      {/* Resize handles */}
      {resizeHandles.map((handle) => (
        <Box
          key={handle.id}
          className="floating-video-no-drag"
          onMouseDown={(event) => startResize(event, handle)}
          onTouchStart={(event) => startResize(event, handle)}
          style={{
            ...resizeHandleBaseStyle,
            ...handle.style,
            cursor: handle.cursor,
          }}
        />
      ))}
    </>
  );
};

// DISPATCHARR_CHROMECAST_R8_8_28
const DISPATCHARR_CAST_SDK_URL =
  'https://www.gstatic.com/cv/js/sender/v1/cast_sender.js?loadCastFramework=1';
let dispatcharrCastSdkPromise = null;

// DISPATCHARR_CHROMECAST_PROFILE_SWITCH_R8_8_35
// Module scope survives route/FloatingVideo remounts in the SPA. The active
// CAF session remains the source of truth and can also reconstruct this state.
// DISPATCHARR_LIVE_CAST_RECOVERY_COOLDOWN_MODULE_LEVEL: module-level (not
// useRef) so it survives the component remount that follows a
// completed Cast recovery -- the same reason dispatcharrCastRuntime
// itself lives out here instead of inside the component.
let castRecoveryCooldownUntil = 0;

const dispatcharrCastRuntime = {
  active: false,
  kind: null,
  sourceUrl: null,
  resumeUrl: null,
  mediaUrl: null,
  bootstrapUrl: null,
  adaptiveProfile: null,
  deviceName: '',
  renewUrl: null,
  stopUrl: null,
  castSessionId: null,
  leaseTimer: null,
  bootstrapController: null,
  archivePosition: 0,
  archiveDuration: 0,
  archiveBasePosition: 0,
  archiveGeneration: null,
  archiveController: null,
  // DISPATCHARR_CAST_REMOTE_REWIND_R135: cast_session of the Live THIS tab
  // loaded on the TV (or took back from its own parked lease). Adopting a
  // cast another tab started never sets it.
  ownLiveCastSession: null,
};

// DISPATCHARR_CAST_INTENTIONAL_HANDBACK_R8_8_45
// CAF endCurrentSession() is asynchronous from the sender's point of view.
// During an intentional Stop-Cast -> Web handback, session callbacks may still
// expose the old Dispatcharr media briefly. Keep a module-scope fence so a
// route/FloatingVideo remount cannot re-green the Cast button or tear down the
// newly restarted local player when the final SESSION_ENDED callback arrives.
let dispatcharrCastIntentionalHandbackUntil = 0;

const beginDispatcharrCastIntentionalHandback = () => {
  dispatcharrCastIntentionalHandbackUntil = Date.now() + 8000;
};

const isDispatcharrCastIntentionalHandback = () =>
  Date.now() < dispatcharrCastIntentionalHandbackUntil;

const finishDispatcharrCastIntentionalHandback = () => {
  dispatcharrCastIntentionalHandbackUntil = 0;
};

const clearDispatcharrCastRuntime = () => {
  if (dispatcharrCastRuntime.leaseTimer != null && typeof window !== 'undefined') {
    window.clearInterval(dispatcharrCastRuntime.leaseTimer);
  }
  try {
    dispatcharrCastRuntime.bootstrapController?.abort?.();
  } catch {}
  try {
    dispatcharrCastRuntime.archiveController?.dispose?.();
  } catch {}
  Object.assign(dispatcharrCastRuntime, {
    active: false,
    kind: null,
    sourceUrl: null,
    resumeUrl: null,
    mediaUrl: null,
    bootstrapUrl: null,
    adaptiveProfile: null,
    deviceName: '',
    renewUrl: null,
    stopUrl: null,
    castSessionId: null,
    leaseTimer: null,
    bootstrapController: null,
    archivePosition: 0,
    archiveDuration: 0,
    archiveBasePosition: 0,
    archiveGeneration: null,
    archiveController: null,
    ownLiveCastSession: null,
  });
};
// DISPATCHARR_CHROMECAST_SMOOTH_R8_8_30
const DISPATCHARR_CAST_PREWARM_SEGMENTS = 3;
// DISPATCHARR_CAST_RECEIVER_HOLD_BACK_R20: segments the Live playlist
// must hold before loadMedia, so the receiver's join point (cast_hls
// advertises an inflated TARGETDURATION; since R103 the distance is a
// tools-JSON setting, cast_live.receiver_join_seconds, live value 6s)
// lands on media that already exists.
// DISPATCHARR_CAST_TEE_MATURITY_R107: R104 lowered this from 10 to 7 and
// created the frame-by-frame start: every stuttering cast (console trace
// prewarm=8) had joined a tee only ~7-8s old, still ramping (worst on 4K
// HEVC where decode+scale warm up), while every smooth cast found a mature
// tee (prewarm=12). 12 costs ~5s more before the TV shows a picture and
// does NOT add steady-state lag -- the join distance is measured from the
// live edge at load time, which moves with the wall clock.
const DISPATCHARR_CAST_RECEIVER_READY_SEGMENTS = 12;
// DISPATCHARR_CAST_LIVE_EDGE_CATCHUP_R104: distance from the live edge the
// one-shot sender catch-up seek aims at once the receiver plays. Must stay
// >= 3 advertised target durations (TARGETDURATION:2 with the 6s join) so
// the receiver can rebuffer from the target without stalling.
const DISPATCHARR_CAST_LIVE_EDGE_HOLDBACK_SECONDS = 7;
const DISPATCHARR_CAST_RESUME_LEASE_GRACE_MS = 2500;
// DISPATCHARR_ARCHIVE_CAST_PROGRAMME_END_R133: a Web seek never lands in the
// programme's last seconds (no producer can start inside the provider's last
// GOP, and the TV would finish before the seek even settled), and the TV
// reaching the end shows this notice instead of the receiver-gone label.
const ARCHIVE_CAST_END_GUARD_SECONDS = 5;
const ARCHIVE_CAST_FINISHED_NOTICE = 'Programme finished on the TV.';
// DISPATCHARR_LIVE_TIMESHIFT_CAST_CATCHUP_TO_LIVE_R135: a time-shifted Live
// cast whose TV gets this close to the aired edge (remote fast-forward, or the
// end of what it was given) continues with Live on the TV, like a DVR.
// The CAF receiver's Shaka fetches about 11 s ahead (1 s segments, no
// bufferingGoal override), so 15 s keeps it off segments that have not aired.
const LIVE_TIMESHIFT_CAST_LIVE_EDGE_SECONDS = 15;
// The edge rule arms only once the TV has been at least this far behind.
const LIVE_TIMESHIFT_CAST_EDGE_ARM_SECONDS = 20;
// Refresh the live-rewind receiver snapshot this long before its listed end,
// and never sooner than a minute after the last receiver LOAD. 150 s: a tab
// hidden for 5+ min runs its timers about once a minute, so the window must
// hold two of those wake-ups (the 10 min stretch leaves ~7.5 min between
// refreshes).
const LIVE_TIMESHIFT_CAST_SNAPSHOT_REFRESH_LEAD_SECONDS = 150;
const LIVE_TIMESHIFT_CAST_REFRESH_MIN_INTERVAL_MS = 60000;
// DISPATCHARR_LIVE_REWIND_PROGRAMME_TIME_R135: local 24 h "HH:MM:SS", built by
// hand (Intl with bg-BG may append " ч.").
const formatWallClock = (ms) => {
  const at = new Date(ms);
  return [at.getHours(), at.getMinutes(), at.getSeconds()]
    .map((part) => String(part).padStart(2, '0'))
    .join(':');
};
// DISPATCHARR_CAST_REMOTE_REWIND_R135: a TV-remote rewind on a Live cast is the
// receiver's own position dropping within one live media session (CAF turns
// the remote's rewind key into SEEK -10 s inside the ~21 s live window).
const CAST_REMOTE_REWIND_MIN_DROP_SECONDS = 5; // one press = -10 s; estimate noise < 1 s
const CAST_REMOTE_REWIND_CONFIRM_MS = 700; // presses ~400 ms apart coalesce
const CAST_REMOTE_REWIND_SETTLE_MAX_MS = 1500; // caps a held key
const CAST_REMOTE_REWIND_ARM_PLAYING_MS = 1000; // join adjustments sit around the first PLAYING
const CAST_REMOTE_REWIND_MIN_BEHIND_EDGE_SECONDS = 8; // normal parking: 3-7 s behind the seekable end
const CAST_REMOTE_REWIND_EXTRA_SECONDS = 5; // ~3 s Shaka live delay + ~2 s tee latency

// DISPATCHARR_LIVE_PAUSE_CHOICE_R136: a paused Live offers "Continue Live"
// (default: Space, the play button, a native play) or "Resume from <time>"
// (explicit click). The paused moment is a wall-clock estimate of the frame
// that was on screen, biased ~1 s early (re-see, never skip).
// R137: these two are only the first guess for a channel (see the calibration
// block below). Measured on five channels, the real value is 0.6-3.3 s for
// the MPEG-TS player, so 3.5 never skips; the fMP4 one is unmeasured.
const LIVE_PAUSE_LOCAL_TS_LAG_SECONDS = 3.5; // + buffer lead
const LIVE_PAUSE_LOCAL_HLS_LAG_SECONDS = 4; // + buffer lead; fMP4/HLS segmenter + playlist
const LIVE_PAUSE_WINDOW_SLACK_SECONDS = 15; // a frame this close before the window start lands on it
const CAST_LIVE_PAUSE_TV_BUFFER_MARGIN_SECONDS = 6; // in-place resume needs the frame this far inside the TV window
const CAST_LIVE_COMMAND_TIMEOUT_MS = 4000; // Media.pause/play/seek ack
const CAST_LIVE_STATE_WAIT_MS = 4000; // receiver reaching the wanted state after the ack
// DISPATCHARR_ARCHIVE_PTS_ALIGN_R137: the closest to Live a rewind may start
// (the floor of startLiveProviderTimeshift; a paused moment can be resumed
// exactly once it is this old). The archive itself is only a second or two
// behind Live -- this is the room playback needs behind it. The backend names
// it per channel on the live-reopen gate: 10 s where it follows the
// provider's recording without restarts, otherwise the 30 s that applied
// everywhere until R137. A rewind that plays on a TV keeps 30 s: the Cast
// receiver reads ~11 s ahead and has edge rules of its own
// (LIVE_TIMESHIFT_CAST_LIVE_EDGE_SECONDS and its neighbours).
const LIVE_REWIND_FLOOR_DEFAULT_SECONDS = 30;
const LIVE_REWIND_CAST_FLOOR_SECONDS = 30;
// How long an answer of the gate is taken as it is. Whatever shows a floor (a
// pause's "Resume from", the Rewind picker) asks for it, and the commit that
// follows asks again; within this time both get the one answer, so what was
// shown is what is started.
const LIVE_REWIND_FLOOR_FRESH_MS = 5000;
const liveRewindFloorFromGate = (response) => {
  const raw = response?.headers?.get?.('X-Dispatcharr-Live-Rewind-Floor');
  const value = Number(raw);
  return raw != null &&
    raw !== '' &&
    Number.isFinite(value) &&
    value >= 5 &&
    value <= LIVE_REWIND_FLOOR_DEFAULT_SECONDS
    ? Math.ceil(value)
    : LIVE_REWIND_FLOOR_DEFAULT_SECONDS;
};
// DISPATCHARR_ARCHIVE_PTS_ALIGN_R137: the paused-frame estimate, calibrated.
// "Resume from" starts the archive at (pause time - buffer lead - a constant).
// The constant is how late this player shows Live compared with the archive's
// own clock, and that belongs to the channel: its Live may come from another
// provider than its archive, and every head-end delays every channel
// differently. Now that an archive start is frame-exact, that constant is the
// whole error -- so it is measured instead of guessed. The picture that was on
// screen at the pause is looked for in the first seconds of the resumed
// archive (32x18 luma pattern, levels removed); where it turns up says what
// the constant should have been. The next pause of that channel in this
// browser uses the LARGEST of the last few such readings plus a margin: a
// start a little early shows a moment again, a start a little late skips
// one, so every doubt is settled towards early. Nothing is corrected
// mid-play, a picture that cannot be read or recognised (flat, frozen,
// repeated) teaches nothing, and only starts the backend made on the exact
// instant are learned from (it marks their fragments, X-HLS-Aligned).
const LIVE_PAUSE_LAG_STORE_KEY = 'dispatcharrLivePauseLag';
const LIVE_PAUSE_LAG_STORE_MAX = 400; // channels remembered
const LIVE_PAUSE_LAG_READINGS = 5; // readings kept per channel and player
const LIVE_PAUSE_LAG_MARGIN_SECONDS = 0.25; // land this much before the paused frame
// The fMP4 Live player's buffer grows a whole 1 s segment at a time, so its
// lead -- and with it a reading -- is anywhere within a second. The largest
// reading is the top of that second only once the readings span it; what
// they have not spanned yet is kept in hand: a whole second until three
// readings lie within a second of the largest (a lower one is from another
// cycle -- another Live source, another session -- and spans nothing).
const LIVE_PAUSE_LAG_HLS_CYCLE_SECONDS = 1;
const LIVE_PAUSE_LAG_HLS_SETTLED_READINGS = 3;
const LIVE_PAUSE_LAG_MIN_SECONDS = -3; // a Live that is ahead of the archive's clock
const LIVE_PAUSE_LAG_MAX_SECONDS = 8;
// A channel whose picture was in none of the stretches, whatever constant was
// tried, is left alone for this long and then looked at again.
const LIVE_PAUSE_LAG_RETRY_MS = 7 * 24 * 3600 * 1000;
const LIVE_PAUSE_MATCH_WINDOW_SECONDS = 7; // resumed media searched for the paused frame
const LIVE_PAUSE_MATCH_TIMEOUT_MS = 25000;
const LIVE_PAUSE_MATCH_COST = 0.25; // normalised pattern distance: same frame ~0.05, another ~0.5+
const LIVE_PAUSE_MATCH_RATIO = 1.5; // the runner-up (another moment) must be this much worse
// A slow picture: the frames around the paused one look the same, so none of
// them stands out. When that stretch is short and begins inside what was
// searched, the paused frame is one of its frames, and the first of them is
// taken -- the early end of the doubt. (Replayed over 25 000 recorded
// searches on five channels: one search in ten more teaches something, none
// of those wrongly, none when the paused frame was not there at all.)
const LIVE_PAUSE_MATCH_SLOW_COST = 0.1; // only the same picture; a similar one is further off
const LIVE_PAUSE_MATCH_SLOW_SPAN_SECONDS = 0.4; // longer than this says too little
const LIVE_PAUSE_MATCH_SLOW_LEAD_SECONDS = 0.2; // searched frames before it that do differ
let livePauseSignatureContext = null;
const livePauseFrameSignature = (video) => {
  try {
    if (!video || video.readyState < 2 || !video.videoWidth) return null;
    if (!livePauseSignatureContext) {
      const canvas = document.createElement('canvas');
      canvas.width = 192;
      canvas.height = 108;
      const context = canvas.getContext('2d', { willReadFrequently: true });
      if (!context) return null;
      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = 'high';
      livePauseSignatureContext = context;
    }
    const context = livePauseSignatureContext;
    context.drawImage(video, 0, 0, 192, 108);
    const data = context.getImageData(0, 0, 192, 108).data;
    const cells = new Float32Array(576);
    for (let y = 0; y < 108; y += 1) {
      const row = ((y / 6) | 0) * 32;
      let offset = y * 192 * 4;
      for (let x = 0; x < 192; x += 1, offset += 4) {
        cells[row + ((x / 6) | 0)] +=
          data[offset] * 77 + data[offset + 1] * 150 + data[offset + 2] * 29;
      }
    }
    let mean = 0;
    for (let i = 0; i < 576; i += 1) {
      cells[i] /= 36 * 256;
      mean += cells[i];
    }
    mean /= 576;
    let variance = 0;
    for (let i = 0; i < 576; i += 1) variance += (cells[i] - mean) * (cells[i] - mean);
    const deviation = Math.sqrt(variance / 576);
    // a flat picture (black, a fade, a plain card) cannot be recognised
    if (!(deviation >= 10)) return null;
    for (let i = 0; i < 576; i += 1) cells[i] = (cells[i] - mean) / deviation;
    return cells;
  } catch {
    // tainted or unsupported source: no calibration on this browser
    return null;
  }
};
const livePauseSignatureDistance = (a, b) => {
  let sum = 0;
  for (let i = 0; i < 576; i += 1) sum += Math.abs(a[i] - b[i]);
  return sum / 576;
};
// Which frame of a search shows the paused picture. rows: [media time,
// distance to the paused picture] of every frame the resumed archive
// presented, in order. row is -1 when none can be named; exact says the
// frame stood out on its own (otherwise it is the first of a slow stretch).
const livePauseMatch = (rows) => {
  let best = 0;
  for (let i = 1; i < rows.length; i += 1) if (rows[i][1] < rows[best][1]) best = i;
  let runnerUp = Infinity;
  for (let i = 0; i < rows.length; i += 1) {
    if (Math.abs(rows[i][0] - rows[best][0]) > 0.14 && rows[i][1] < runnerUp) runnerUp = rows[i][1];
  }
  const cost = rows[best][1];
  const match = { best, cost, runnerUp, row: -1, exact: false, alike: 0 };
  if (cost <= LIVE_PAUSE_MATCH_COST && runnerUp / Math.max(cost, 0.01) >= LIVE_PAUSE_MATCH_RATIO) {
    match.row = best;
    match.exact = true;
    return match;
  }
  if (cost > LIVE_PAUSE_MATCH_SLOW_COST) return match;
  const limit = LIVE_PAUSE_MATCH_RATIO * Math.max(cost, 0.01);
  let first = best;
  let from = rows[best][0];
  let to = rows[best][0];
  for (let i = 0; i < rows.length; i += 1) {
    if (rows[i][1] < limit) {
      if (rows[i][0] < from) {
        from = rows[i][0];
        first = i;
      }
      if (rows[i][0] > to) to = rows[i][0];
    }
  }
  if (
    to - from <= LIVE_PAUSE_MATCH_SLOW_SPAN_SECONDS &&
    from - rows[0][0] >= LIVE_PAUSE_MATCH_SLOW_LEAD_SECONDS
  ) {
    match.row = first;
    match.alike = to - from;
  }
  return match;
};
const readLivePauseLagStore = () => {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(LIVE_PAUSE_LAG_STORE_KEY) || 'null');
    return parsed && typeof parsed === 'object' && parsed.items && typeof parsed.items === 'object'
      ? parsed
      : { items: {} };
  } catch {
    return { items: {} };
  }
};
// An entry is { r: readings (newest last), s: an "earlier, please" value set
// when the paused frame was missed, b: how often in a row, g: when that was
// given up (every accepted constant tried), t: last write }.
const livePauseLagReadings = (entry) =>
  (Array.isArray(entry?.r) ? entry.r : [])
    .map(Number)
    .filter(
      (value) =>
        Number.isFinite(value) &&
        value >= LIVE_PAUSE_LAG_MIN_SECONDS &&
        value <= LIVE_PAUSE_LAG_MAX_SECONDS
    );
// What is known of a channel: { value (without the margin), readings } or null.
const readLivePauseLag = (key) => {
  if (!key) return null;
  const entry = readLivePauseLagStore().items[key];
  if (!entry || typeof entry !== 'object') return null;
  const readings = livePauseLagReadings(entry);
  const earlier = Number(entry.s);
  const candidates = readings.slice();
  if (entry.s != null && Number.isFinite(earlier)) {
    candidates.push(
      Math.min(LIVE_PAUSE_LAG_MAX_SECONDS, Math.max(LIVE_PAUSE_LAG_MIN_SECONDS, earlier))
    );
  }
  if (!candidates.length) return null;
  // how much of the top second the readings have shown so far
  const top = readings.length ? Math.max(...readings) : 0;
  const near = readings.filter((value) => value >= top - LIVE_PAUSE_LAG_HLS_CYCLE_SECONDS);
  return {
    value: Math.max(...candidates),
    readings: readings.length,
    spread:
      near.length >= LIVE_PAUSE_LAG_HLS_SETTLED_READINGS ? top - Math.min(...near) : 0,
  };
};
// measured: what the constant turned out to be. absent + base: the paused
// frame was not in the resumed stretch although that started at `base`
// (the constant used, without its margin) -- the start was probably late;
// guess is this player's first guess.
const noteLivePauseLag = (
  key,
  { measured = null, absent = false, base = null, guess = null } = {}
) => {
  if (!key) return null;
  try {
    const store = readLivePauseLagStore();
    const previous = store.items[key] && typeof store.items[key] === 'object' ? store.items[key] : null;
    const readings = livePauseLagReadings(previous);
    let entry = null;
    if (measured != null && Number.isFinite(measured)) {
      const value =
        Math.round(
          Math.min(LIVE_PAUSE_LAG_MAX_SECONDS, Math.max(LIVE_PAUSE_LAG_MIN_SECONDS, measured)) * 1000
        ) / 1000;
      entry = { r: readings.concat([value]).slice(-LIVE_PAUSE_LAG_READINGS), s: null, b: 0, t: Date.now() };
    } else if (absent && Number.isFinite(base)) {
      const now = Date.now();
      const gaveUpAt = Number(previous?.g) || 0;
      if (gaveUpAt > 0 && now - gaveUpAt < LIVE_PAUSE_LAG_RETRY_MS) return previous;
      if (base >= LIVE_PAUSE_LAG_MAX_SECONDS - 0.001) {
        // Every constant this code accepts has been tried and the picture
        // was in none of those stretches: on this channel it cannot be
        // recognised. Back to the first guess, and no more of this for a
        // while (not for good: a channel can change).
        entry = { r: [], s: null, b: 0, g: now, t: now };
      } else {
        // A second earlier next time -- and never below the first guess: a
        // constant learned earlier that now misses is no better than that.
        // Missing costs the viewer a moment and starting early only shows
        // one again, so this goes on until the frame is found (a reading
        // then takes over) or the largest accepted constant has missed too.
        const least = Number.isFinite(guess) ? guess : base;
        entry = {
          r: readings,
          s:
            Math.round(
              Math.min(LIVE_PAUSE_LAG_MAX_SECONDS, Math.max(base + 1, least)) * 1000
            ) / 1000,
          b: (Number(previous?.b) || 0) + 1,
          t: now,
        };
      }
    }
    if (!entry) return previous;
    store.items[key] = entry;
    const keys = Object.keys(store.items);
    if (keys.length > LIVE_PAUSE_LAG_STORE_MAX) {
      keys
        .sort((a, b) => (Number(store.items[a]?.t) || 0) - (Number(store.items[b]?.t) || 0))
        .slice(0, keys.length - LIVE_PAUSE_LAG_STORE_MAX)
        .forEach((stale) => delete store.items[stale]);
    }
    window.localStorage.setItem(LIVE_PAUSE_LAG_STORE_KEY, JSON.stringify(store));
    return entry;
  } catch {
    return null;
  }
};
const liveBufferAheadSeconds = (video) => {
  try {
    const ct = Number(video?.currentTime) || 0;
    const ranges = video?.buffered;
    for (let i = 0; ranges && i < ranges.length; i += 1) {
      if (ranges.start(i) - 0.25 <= ct && ct <= ranges.end(i) + 0.25) {
        return Math.min(30, Math.max(0, ranges.end(i) - ct));
      }
    }
  } catch {
    // emptied or detached
  }
  return null;
};

const loadDispatcharrCastSdk = () => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return Promise.resolve(false);
  }
  if (window.cast?.framework && window.chrome?.cast?.media) {
    return Promise.resolve(true);
  }
  if (dispatcharrCastSdkPromise) return dispatcharrCastSdkPromise;

  dispatcharrCastSdkPromise = new Promise((resolve) => {
    let settled = false;
    const finish = (available) => {
      if (settled) return;
      settled = true;
      resolve(Boolean(available));
    };

    const previousCallback = window.__onGCastApiAvailable;
    window.__onGCastApiAvailable = (isAvailable, errorInfo) => {
      try {
        if (typeof previousCallback === 'function') {
          previousCallback(isAvailable, errorInfo);
        }
      } catch {
        // A third-party callback must not break Dispatcharr Cast discovery.
      }
      finish(
        Boolean(
          isAvailable &&
            window.cast?.framework &&
            window.chrome?.cast?.media
        )
      );
    };

    let script = document.querySelector(
      'script[data-dispatcharr-google-cast="1"]'
    );
    if (!script) {
      script = document.createElement('script');
      script.src = DISPATCHARR_CAST_SDK_URL;
      script.async = true;
      script.dataset.dispatcharrGoogleCast = '1';
      script.onerror = () => finish(false);
      document.head.appendChild(script);
    }

    window.setTimeout(() => {
      finish(Boolean(window.cast?.framework && window.chrome?.cast?.media));
    }, 8000);
  });

  return dispatcharrCastSdkPromise;
};

export default function FloatingVideo() {
  const isVisible = useVideoStore((s) => s.isVisible);
  const streamUrl = useVideoStore((s) => s.streamUrl);
  const contentType = useVideoStore((s) => s.contentType);
  // DISPATCHARR_WEB_CATCHUP_R8_7_0
  const metadata = useVideoStore((s) => s.metadata);
  const hideVideo = useVideoStore((s) => s.hideVideo);
  // DISPATCHARR_LIVE_PROVIDER_TIMESHIFT_R4
  const showVideo = useVideoStore((s) => s.showVideo);
  // DISPATCHARR_LIVE_REWIND_ONLY_WITH_CATCHUP_R99
  const channelsById = useChannelsStore((s) => s.channels);
  const channelsByUUID = useChannelsStore((s) => s.channelsByUUID);
  const fetchChannels = useChannelsStore((s) => s.fetchChannels);
  const channelsFetchRequestedRef = useRef(false);

  // DISPATCHARR_TRANSPORT_SUSPEND_R8_8_27
  const [transportSuspendedKind, setTransportSuspendedKind] = useState(null);
  const transportSuspendedKindRef = useRef(null);
  const livePlaybackStartedRef = useRef(false);
  // DISPATCHARR_LIVE_PAUSE_CHOICE_R136: the paused-Live record (null while
  // playing): { where:'local'|'tv', streamUrl, pausedAtMs, frameInstantMs,
  // approx, by, busy:null|'resume'|'stopping', key, owner, tHeld,
  // tvBufferUntilMs } (last four: 'tv' only). Written on transitions only (R122).
  const [livePause, setLivePauseState] = useState(null);
  const livePauseRef = useRef(null);
  const livePauseSampleRef = useRef(null); // { at, ahead, hls, sig } of the last Live 'pause' event
  const livePauseResumeTimerRef = useRef(null); // R136b: "Resume from" waiting for the archive
  const livePauseCalibrationRef = useRef(null); // R137: the running paused-frame search, { cancel }
  // R137: fragments of the archive being played that the backend marked as
  // coming from a producer started on the exact instant: { url, indexes }.
  const archiveAlignedFragmentsRef = useRef({ url: null, indexes: new Set() });
  const setLivePause = (next) => {
    const value = (typeof next === 'function' ? next(livePauseRef.current) : next) || null;
    livePauseRef.current = value;
    setLivePauseState(value);
  };
  const archivePlaybackStartedRef = useRef(false);
  const archiveLeaseSuspendedRef = useRef(false);
  const archiveSuspendedPositionRef = useRef(0);
  // DISPATCHARR_ARCHIVE_RECOVERY_CAST_HANDBACK_REUSE
  const archiveLocalRuntimeUrlRef = useRef(null);
  // DISPATCHARR_ARCHIVE_R2_SEEK_HANDBACK_FIXES
  const archiveNativeSeekInProgressRef = useRef(false);
  // DISPATCHARR_ARCHIVE_SEEK_WARMUP_R75
  const archiveWarmAtRef = useRef(0);
  const archiveWarmPositionRef = useRef(0);
  const archiveSeekCommittingRef = useRef(false);
  // DISPATCHARR_LIVE_REWIND_SEEK_FENCE_R77
  const archiveNativeSeekCommitTimerRef = useRef(null);
  // DISPATCHARR_LIVE_REWIND_SINGLE_SEEK_OWNER_R78
  const archiveCommittedPositionRef = useRef(Number.NEGATIVE_INFINITY);
  // DISPATCHARR_LIVE_REWIND_INSTANT_SEEK_R80
  const archiveLastCommitAtRef = useRef(0);
  // Floating mm:ss readout for the rewind picker; positioned fixed to the
  // viewport so it needs no containing-block maths.
  const rewindHoverTipRef = useRef(null);
  // DISPATCHARR_CAST_SEEK_HOVER_CLOCK_R11
  const castSeekHoverTipRef = useRef(null);
  // DISPATCHARR_NATIVE_RANGE_TIP_R133: the Cast seek slider and the Rewind
  // picker slider, and the pointer over each in PointerEvent coordinates
  // (x/y null while away; pressed between mousedown and mouseup).
  const castSeekInputRef = useRef(null);
  const castSeekPointerRef = useRef({ x: null, y: null, pressed: false, pressValue: null });
  const rewindPickerInputRef = useRef(null);
  const rewindPickerPointerRef = useRef({ x: null, y: null, pressed: false });
  // DISPATCHARR_CAST_LIVE_REWIND_R17: Rewind requested while Live is Cast.
  const pendingCastTimeshiftRef = useRef(null);
  // Shaka instance driving live-timeshift/archive HLS, so the initial
  // seek can consult the manifest window instead of video.seekable.
  const shakaTimeshiftPlayerRef = useRef(null);
  const archiveNativeSeekPauseTimerRef = useRef(null);
  // DISPATCHARR_ARCHIVE_PAUSE_LINGER_R128: desktop archive pause keeps the
  // player loaded for a short linger (overlay shown at once); the real suspend
  // (kill ffmpeg + /suspend) only happens if play does not come back in time.
  // Must stay well under the backend's ~30 s idle producer reap.
  const ARCHIVE_PAUSE_LINGER_MS = 8000;
  const archivePauseLingerTimerRef = useRef(null);
  const archivePlayerRetainedRef = useRef(false);
  const [archiveSoftPaused, setArchiveSoftPaused] = useState(false);
  // DISPATCHARR_CHROMECAST_R8_8_28
  const [castAvailable, setCastAvailable] = useState(false);
  const [castSdkReady, setCastSdkReady] = useState(false);
  const [castActive, setCastActive] = useState(() => Boolean(dispatcharrCastRuntime.active));
  const [castDeviceName, setCastDeviceName] = useState(() => dispatcharrCastRuntime.deviceName || '');
  const [castError, setCastError] = useState(null);
  // DISPATCHARR_CAST_SEAMLESS_HANDBACK_ARCHIVE_CONTROLS_R8_8_46
  const [castHover, setCastHover] = useState(false);
  // DISPATCHARR_ARCHIVE_RECOVERY_CAST_PLAYER_HOVER
  const [castPlayerHover, setCastPlayerHover] = useState(false);
  // DISPATCHARR_LIVE_REWIND_NATIVE_MATCH_AND_IDLE_HOVER
  const castPlayerIdleTimerRef = useRef(null);
  const [castArchivePositionSeconds, setCastArchivePositionSeconds] = useState(0);
  // DISPATCHARR_CAST_CONTINUE_LIVE_INDICATOR: show a clear "Returning to Live"
  // state on the cast overlay during the several-second Continue-Live handoff
  // so the wait is not mistaken for a stall and nobody presses Stop Casting.
  const [castReturningToLive, setCastReturningToLive] = useState(false);
  // DISPATCHARR_CAST_REMOTE_REWIND_R135: "Rewinding on <TV>..." while a Rewind
  // on Cast is being set up (null | 'web' | 'tv-remote'); timers read the
  // Continue Live indicator through a ref.
  const [castRewindingOnTv, setCastRewindingOnTv] = useState(null);
  useEffect(() => {
    castReturningToLiveRef.current = castReturningToLive;
  }, [castReturningToLive]);
  useEffect(() => {
    if (!castActive) setCastRewindingOnTv(null);
  }, [castActive]);
  // DISPATCHARR_LIVE_PAUSE_CHOICE_R136: Live-cast command in flight
  // (null|'pause'|'live'|'resume') and this tab's last pause request.
  const [castLiveCommand, setCastLiveCommand] = useState(null);
  const castLivePressRef = useRef(null);
  // R136b: a "Resume from" that waits for the archive belongs to its pause
  // record; any change of that record (cleared, replaced, started) drops it.
  useEffect(() => {
    if (livePause?.busy === 'waiting') return undefined;
    if (livePauseResumeTimerRef.current != null) {
      window.clearTimeout(livePauseResumeTimerRef.current);
      livePauseResumeTimerRef.current = null;
    }
    return undefined;
  }, [livePause]);
  useEffect(
    () => () => {
      if (livePauseResumeTimerRef.current != null) {
        window.clearTimeout(livePauseResumeTimerRef.current);
        livePauseResumeTimerRef.current = null;
      }
      livePauseCalibrationRef.current?.cancel?.(); // R137
    },
    []
  );
  // R136 review: the TV plays THIS tab's Live (a tab joined to another
  // channel's cast must not draw a dead pause/play bar). Transitions only.
  const [castLiveReadable, setCastLiveReadableState] = useState(false);
  const castLiveReadableRef = useRef(false);
  const setCastLiveReadable = (value) => {
    const next = Boolean(value);
    if (castLiveReadableRef.current === next) return;
    castLiveReadableRef.current = next;
    setCastLiveReadableState(next);
  };
  const [castArchiveDurationSeconds, setCastArchiveDurationSeconds] = useState(0);
  const [castArchivePaused, setCastArchivePaused] = useState(false);
  // DISPATCHARR_CAST_LIVE_REWIND_UI_R18: bumped when a Cast switches to
  // catch-up without castActive ever changing (Rewind on Cast), so the
  // transport poller below re-evaluates its kind === 'archive' guard.
  const [castTransportEpoch, setCastTransportEpoch] = useState(0);
  // DISPATCHARR_CAST_BADGE_MEASURED_R8: stop guessing the pill width.
  const castButtonRef = useRef(null);
  const [castButtonWidth, setCastButtonWidth] = useState(0);
  const castArchiveScrubbingRef = useRef(false);
  // DISPATCHARR_ARCHIVE_CAST_REMUX_R8_19_0: one small observer translates the
  // receiver-relative clock into an absolute programme position. All producer
  // seek serialization lives outside React in ArchiveCastController.
  const castArchiveRemoteSeekObserverRef = useRef({
    lastPosition: null,
    lastAt: 0,
    suppressUntil: 0,
    receiverLoadInProgress: false,
  });
  // DISPATCHARR_CAST_FAILSAFE_ARCHIVE_RESEEK_R8_8_47
  const castFailureRecoveryRef = useRef(false);
  // DISPATCHARR_ARCHIVE_REMOTE_POST_SEEK_IDLE_GRACE_R8_14_16
  const castArchiveSeekHealthRef = useRef({
    inProgress: false,
    startedAt: 0,
    completedAt: 0,
    target: 0,
  });
  const castHealthRef = useRef({
    state: '',
    bufferingSince: 0,
    lastPosition: 0,
    lastProgressAt: Date.now(),
  });
  // castHoverRequired now derives from coarsePointer (declared with the
  // other player state below) so badges and controls agree on one answer.
  // DISPATCHARR_LIVE_REWIND_MOUNT_REVERT_TOGGLE_TIMING_POSITION: mount-triggered
  // auto-show removed above -- badges now only ever appear from a real
  // interaction (hover/click on desktop, tap on touch), never on load.
  const castActiveRef = useRef(Boolean(dispatcharrCastRuntime.active));
  // DISPATCHARR_UNIFIED_CAST_LEASE_NO_WEB_HEARTBEATS_R8_8_54
  const dispatcharrPageExitHandledRef = useRef(false);
  // DISPATCHARR_CAST_X_CLOSE_NO_HANDBACK_R8_8_53
  const dispatcharrPlayerClosingRef = useRef(false);
  const castHandoffInProgressRef = useRef(false);
  // DISPATCHARR_CAST_PICKER_REENTRY_R124: true only while CastContext
  // .requestSession() (the browser's device picker) is pending. A second
  // Cast-icon press during that window must be a no-op: the Cast SDK rejects a
  // competing requestSession locally (chrome.cast.ErrorCode.INVALID_PARAMETER,
  // "Already requesting session") and the browser would reject a competing
  // PresentationRequest.start() with PREVIOUS_START_IN_PROGRESS while the first
  // one stays pending -- nothing the page does closes or replaces the picker.
  const castPickerPendingRef = useRef(false);
  // DISPATCHARR_CAST_PICKER_TRAP_R126: the picker is Chrome's own Global Media
  // Controls bubble (MediaDialogView) anchored to the toolbar's media button.
  // In stage fullscreen (Chrome <= 154, macOS) it is drawn over the video and a
  // click on the page does not dismiss it, while the click still reaches the
  // <video> and R121 click-to-pause suspends Live. Chrome 155+ (stable from
  // 2026-10-06, crrev.com/c/8383376) drops HTML fullscreen itself when the
  // picker opens and refuses requestFullscreen until it closes. We leave
  // fullscreen up front, so the picker always opens in windowed mode, where an
  // outside click closes it, and remember when it opened so a quick cancel can
  // restore fullscreen while the Cast click's activation (5 s) is still fresh.
  // The bubble's CANCEL rejection trails the click that dismissed it, so the
  // "do not pause" guard outlives the pending flag by a beat.
  const castPickerOpenedAtRef = useRef(0);
  const castPickerSettledAtRef = useRef(0);
  const castPickerGuardActive = () =>
    castPickerPendingRef.current ||
    Date.now() - castPickerSettledAtRef.current < 500;
  const castContextRef = useRef(null);
  const castRemotePlayerRef = useRef(null);
  const castRemoteControllerRef = useRef(null);
  const liveAdaptiveStreamUrlRef = useRef(null);
  // DISPATCHARR_LIVE_FMP4_R3
  const liveWebFmp4SessionRef = useRef(null);
  const liveWebFmp4StartIdRef = useRef(0);
  // DISPATCHARR_LIVE_PROVIDER_TIMESHIFT_R4
  const LIVE_PROVIDER_TIMESHIFT_MAX_SECONDS = 6 * 60 * 60;
  const [liveTimeshiftDraftSeconds, setLiveTimeshiftDraftSeconds] = useState(0);
  // DISPATCHARR_LIVE_REWIND_BUTTON_OPENS_PICKER
  const [showLiveRewindPicker, setShowLiveRewindPicker] = useState(false);
  // DISPATCHARR_LIVE_REWIND_PROGRAMME_TIME_R135: EPG programmes of the Live
  // channel across the rewind window ({ channelUuid, list }), and the picker
  // scale frozen while the pointer is on it.
  const [livePickerProgrammes, setLivePickerProgrammes] = useState(null);
  const livePickerProgrammesCacheRef = useRef(null);
  const rewindPickerFrozenMinRef = useRef(null);
  const [, refreshLiveTimeshiftWindow] = useState(0);
  const liveTimeshiftStartingRef = useRef(false);
  // DISPATCHARR_LIVE_PROVIDER_TIMESHIFT_SEEK_FULLSCREEN_R5
  const liveTimeshiftInitialPositionConsumedUrlRef = useRef(null);
  const liveTimeshiftScrubbingRef = useRef(false);
  // DISPATCHARR_LIVE_PROVIDER_TIMESHIFT_UI_EDGE_R5_1
  const liveTimeshiftReturningToLiveRef = useRef(false);
  // DISPATCHARR_LIVE_PROVIDER_TIMESHIFT_POSITION_REOPEN_R5_2
  const liveTimeshiftInitialSeekSettledUrlRef = useRef(null);
  // DISPATCHARR_LIVE_PROVIDER_TIMESHIFT_CAST_HANDBACK_R5_3
  const liveTimeshiftExpectedPositionRef = useRef(null);
  const [liveTimeshiftPlaybackSeconds, setLiveTimeshiftPlaybackSeconds] = useState(0);
  // DISPATCHARR_LIVE_FMP4_R3_STARTUP_RACE_HOTFIX
  const liveWebFmp4TransitionRef = useRef(false);
  const castAdaptiveProfileRef = useRef(null);
  // DISPATCHARR_CHROMECAST_HLS_R8_8_29
  const castLeaseTimerRef = useRef(null);
  // DISPATCHARR_CAST_CONTINUE_LIVE_KEEP_OLD_LEASE_R14
  const castDeferredLeaseStopUrlRef = useRef(null);
  // DISPATCHARR_CAST_LIVE_WARM_DURING_REWIND_R21
  const castParkedLiveLeaseRef = useRef(null);
  // DISPATCHARR_CAST_TRANSITION_OPS_R134: every user-visible Cast transition
  // (Cast start, Rewind on Cast, Continue Live, Stop/X, Web handback) is one
  // "op". A newer op supersedes the older one; see beginCastOp().
  const castOpSeqRef = useRef(0);
  const castOpRef = useRef(null);
  // Async Cast flows outlive the render that started them; this always holds
  // the CURRENT content and the functions of the latest render.
  const castLatestRef = useRef(null);
  // DISPATCHARR_LIVE_TIMESHIFT_CAST_CATCHUP_TO_LIVE_R135
  const liveTimeshiftCastToLiveAtRef = useRef(0);
  const liveTimeshiftCastRef = useRef({
    lastLoadAt: 0,
    edgeArmed: false,
    refreshing: false,
    pendingSeek: null, // a Web seek made during a refresh, committed after it
    refreshFailedAt: 0,
  });
  // DISPATCHARR_CAST_REMOTE_REWIND_R135
  const castLiveRemoteRewindRef = useRef({ backwardSeenAt: 0 });
  const castReturningToLiveRef = useRef(false);
  // DISPATCHARR_LIVE_IOS_SPURIOUS_PAUSE_R22
  const liveDiagCountRef = useRef(0);
  // DISPATCHARR_LIVE_ADAPTIVE_BAND_R64
  const liveSyncBandRef = useRef(null);
  const liveBandWarnedRef = useRef(false);
  const liveBandDecayTimerRef = useRef(null);
  const liveStartedAtRef = useRef(0);
  const liveAutoResumeCountRef = useRef(0);
  // DISPATCHARR_LIVE_IOS_AUTORESUME_BUDGET_R39
  const liveAutoResumeResetTimerRef = useRef(null);
  const castRenewUrlRef = useRef(null);
  const castStopUrlRef = useRef(null);
  const archiveCastControllerRef = useRef(
    dispatcharrCastRuntime.archiveController || null
  );
  const archiveCompatibilityControllerRef = useRef(null);
  const archiveCompatibilityStartingRef = useRef(false);
  const archiveCompatibilityPlaybackRef = useRef(false);
  // DISPATCHARR_CHROMECAST_SMOOTH_R8_8_30
  const castResumeUrlRef = useRef(null);
  // DISPATCHARR_FULLSCREEN_PAUSE_CLASSIFIER_R8_8_27
  const liveFullscreenActiveRef = useRef(false);
  const liveFullscreenPauseDecisionTimerRef = useRef(null);
  const archiveFullscreenActiveRef = useRef(false);
  const archiveFullscreenPauseDecisionTimerRef = useRef(null);

  // DISPATCHARR_WEB_CATCHUP_V2_R8_7_0
  const [archiveOffsetSeconds, setArchiveOffsetSeconds] = useState(0);
  const [archivePositionSeconds, setArchivePositionSeconds] = useState(0);
  const [archivePaused, setArchivePaused] = useState(false);
  const [archiveMuted, setArchiveMuted] = useState(false);
  // DISPATCHARR_LIVE_DESKTOP_CUSTOM_CONTROLS_R120
  const [liveMuted, setLiveMuted] = useState(false);
  // DISPATCHARR_LIVE_DESKTOP_VOLUME_R121: the native volume slider went away
  // with the native controls; our bar carries its own.
  const [liveVolume, setLiveVolume] = useState(1);
  // DISPATCHARR_LIVE_DESKTOP_CLICK_DBLCLICK_R121
  const liveClickTimerRef = useRef(null);
  const archiveScrubbingRef = useRef(false);
  const archiveSegmentStartMediaTimeRef = useRef(null);
  const archiveDurationSeconds =
    contentType === 'catchup' || contentType === 'catchup-hls'
      ? Math.max(1, Math.round(Number(metadata?.durationMinutes || 0) * 60))
      : 0;

  const formatArchiveClock = (value) => {
    const seconds = Math.max(0, Math.floor(Number(value) || 0));
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    if (h > 0) {
      return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    }
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  const buildArchiveStopUrl = (url = streamUrl) => {
    if (!url || typeof url !== 'string') return null;

    try {
      const parsed = new URL(url, window.location.origin);
      if (contentType === 'catchup-hls') {
        if (!parsed.pathname.includes('/proxy/catchup-hls/')) return null;
        parsed.pathname = parsed.pathname.replace(/\/index\.m3u8$/, '/stop');
        parsed.searchParams.delete('adaptive_output_profile');
        return parsed.toString();
      }
      if (contentType === 'catchup') {
        if (!archiveChannelIdFromDirectUrl(parsed.toString())) {
          return null;
        }
        parsed.pathname = `${parsed.pathname}/stop`;
        const sessionId = parsed.searchParams.get('session_id');
        parsed.search = '';
        if (sessionId) parsed.searchParams.set('session_id', sessionId);
        return parsed.toString();
      }
    } catch {
      return null;
    }
    return null;
  };

  const requestArchiveStop = (url = streamUrl, { beacon = false } = {}) => {
    if (
      (contentType !== 'catchup' && contentType !== 'catchup-hls') ||
      !url
    ) {
      return;
    }

    const compatibilityController = archiveCompatibilityControllerRef.current;
    if (compatibilityController?.active) {
      archiveCompatibilityControllerRef.current = null;
      void compatibilityController.stop({ keepalive: beacon }).catch(() => {});
    }

    const stopUrl = buildArchiveStopUrl(url);
    if (!stopUrl) return;

    if (
      beacon &&
      contentType === 'catchup-hls' &&
      typeof navigator !== 'undefined' &&
      typeof navigator.sendBeacon === 'function'
    ) {
      try {
        if (navigator.sendBeacon(stopUrl, '')) return;
      } catch {
        // Fall through to keepalive fetch.
      }
    }

    fetch(stopUrl, {
      method: 'POST',
      cache: 'no-store',
      credentials: 'same-origin',
      keepalive: true,
    }).catch(() => {});
  };

  const requestArchivePing = (url = streamUrl) => {
    if (
      (contentType !== 'catchup' && contentType !== 'catchup-hls') ||
      !url
    ) {
      return;
    }

    let pingUrl = null;
    try {
      if (contentType === 'catchup-hls') {
        const parsed = new URL(url, window.location.origin);
        if (!parsed.pathname.includes('/proxy/catchup-hls/')) return;
        parsed.pathname = parsed.pathname.replace(/\/index\.m3u8$/, '/ping');
        parsed.searchParams.delete('adaptive_output_profile');
        pingUrl = parsed.toString();
      } else {
        const parsed = new URL(url, window.location.origin);
        if (!archiveChannelIdFromDirectUrl(parsed.toString())) {
          return;
        }
        parsed.pathname = `${parsed.pathname}/ping`;
        const sessionId = parsed.searchParams.get('session_id');
        parsed.search = '';
        if (sessionId) parsed.searchParams.set('session_id', sessionId);
        pingUrl = parsed.toString();
      }
    } catch {
      return;
    }

    fetch(pingUrl, {
      method: 'POST',
      cache: 'no-store',
      credentials: 'same-origin',
      keepalive: true,
    }).catch(() => {});
  };


  const buildArchiveSessionControlUrl = (url, action) => {
    if (!url || contentType !== 'catchup-hls') return null;
    try {
      const parsed = new URL(url, window.location.origin);
      if (!parsed.pathname.includes('/proxy/catchup-hls/')) return null;
      parsed.pathname = parsed.pathname.replace(
        /\/index\.m3u8$/,
        `/${action}`
      );
      parsed.searchParams.delete('adaptive_output_profile');
      return parsed.toString();
    } catch {
      return null;
    }
  };

  const buildArchiveCastLeaseRenewUrl = (url) => {
    const pingUrl = buildArchiveSessionControlUrl(url, 'ping');
    if (!pingUrl) return null;
    const renewUrl = new URL(pingUrl, window.location.origin);
    renewUrl.searchParams.set('cast_lease', '1');
    return renewUrl.toString();
  };

  // DISPATCHARR_ARCHIVE_CAST_SEEK_LEASE_RACE_R8_8_55
  const buildArchiveCastLeaseReleaseUrl = (url) => {
    const pingUrl = buildArchiveSessionControlUrl(url, 'ping');
    if (!pingUrl) return null;
    const releaseUrl = new URL(pingUrl, window.location.origin);
    releaseUrl.searchParams.set('cast_lease', 'release');
    return releaseUrl.toString();
  };

  const requestArchiveSuspend = (url = streamUrl) => {
    const controlUrl = buildArchiveSessionControlUrl(url, 'suspend');
    if (!controlUrl) return Promise.resolve(false);
    return fetch(controlUrl, {
      method: 'POST',
      cache: 'no-store',
      credentials: 'same-origin',
      keepalive: true,
    })
      .then((response) => response.ok)
      .catch(() => false);
  };

  const requestArchiveResume = (url = streamUrl, position = 0) => {
    const controlUrl = buildArchiveSessionControlUrl(url, 'resume');
    if (!controlUrl) return Promise.resolve(false);
    const resumeUrl = new URL(controlUrl, window.location.origin);

    // DISPATCHARR_ARCHIVE_RESUME_PROFILE_R8_8_27
    // Resume must restart FFmpeg with the SAME adaptive profile used by
    // the browser's HLS segment requests.
    try {
      const sourceUrl = new URL(url, window.location.origin);
      const adaptiveProfile =
        sourceUrl.searchParams.get('adaptive_output_profile');
      if (adaptiveProfile) {
        resumeUrl.searchParams.set(
          'adaptive_output_profile',
          adaptiveProfile
        );
      }
    } catch {
      // Keep backend fallback if the original URL is malformed.
    }

    resumeUrl.searchParams.set(
      'position',
      String(Math.max(0, Number(position) || 0))
    );
    return fetch(resumeUrl.toString(), {
      method: 'POST',
      cache: 'no-store',
      credentials: 'same-origin',
      keepalive: true,
    })
      .then((response) => response.ok)
      .catch(() => false);
  };

  // DISPATCHARR_ARCHIVE_RESUME_REMINT_R116
  // A paused archive's backend session is a Redis record with a 10-minute
  // sliding idle TTL that nothing refreshes while suspended (no keepalive ping
  // exists for catchup-hls). After a longer pause /resume returns 401 and the
  // session cannot be revived -- previously a dead end ("Close and reopen").
  // Re-mint an IDENTICAL session (same channel + programme start + duration, so
  // the saved position still maps to the same instant) exactly like the guide's
  // Watch Archive does, and return its fresh playback URL. Plain archive only;
  // live-timeshift (dynamic edge) is left to its own path.
  const remintArchiveHlsSession = async (activeUrl) => {
    if (contentType !== 'catchup-hls' || metadata?.liveTimeshift) return null;
    const channelUuid =
      metadata?.channelUuid || archiveChannelIdFromDirectUrl(activeUrl);
    const start = metadata?.catchupStart;
    const duration = Math.max(1, Math.round(Number(metadata?.durationMinutes) || 0));
    if (!channelUuid || !start || duration < 1) return null;

    let adaptiveProfile = metadata?.adaptiveProfile || '2.5-3.5m';
    try {
      const fromUrl = new URL(activeUrl, window.location.origin).searchParams.get(
        'adaptive_output_profile'
      );
      if (fromUrl) adaptiveProfile = fromUrl;
    } catch {
      // keep the metadata/default profile
    }

    try {
      const token = useAuthStore.getState().accessToken;
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;
      const response = await fetch('/api/catchup/sessions/', {
        method: 'POST',
        cache: 'no-store',
        credentials: 'same-origin',
        headers,
        body: JSON.stringify({
          channel_uuid: channelUuid,
          start,
          duration,
        }),
      });
      let payload = {};
      try { payload = await response.json(); } catch { /* non-JSON body */ }
      if (!response.ok || !payload?.session_id) {
        console.debug(`[archive-remint] mint failed HTTP ${response.status}`);
        return null;
      }
      const url = new URL(
        `/proxy/catchup-hls/${encodeURIComponent(channelUuid)}/index.m3u8`,
        window.location.origin
      );
      url.searchParams.set('session_id', String(payload.session_id));
      url.searchParams.set('adaptive_output_profile', String(adaptiveProfile));
      // DISPATCHARR_SEEK_FENCE_EVERY_TRANSPORT_R86: this player owns seeks.
      url.searchParams.set('seek_owner', '1');
      console.info('[archive-remint] fresh session minted for resume');
      return url.toString();
    } catch (error) {
      console.debug('[archive-remint] mint error:', error);
      return null;
    }
  };

  // DISPATCHARR_ARCHIVE_SEEK_WARMUP_R75
  // A seek costs a provider connection and an encoder start, and until R73
  // that cost landed entirely after the user let go of the scrubber. The
  // positions the thumb passes through are already known while it moves, so
  // start producing at one of them: the committed seek then usually finds a
  // producer that is already running and serves from the spool.
  //
  // The anchor is the thumb itself. Starting earlier to cover a backward
  // correction was measured worse: the producer then has to encode its way to
  // the target first, and the committed seek waited 3.0s instead of 1.1s.
  const warmArchiveSeekTarget = (position, url = streamUrl) => {
    if (contentType !== 'catchup-hls' || !url) return;
    // DISPATCHARR_LIVE_REWIND_SINGLE_SEEK_OWNER_R78: only the hls.js/MMS
    // transport warms. On the native path the debounced commit is the one and
    // only place a position is sent; a warm-up there raced it with a stale
    // currentTime and the two placements killed each other's producer.
    if (videoRef.current?.dataset?.dispatcharrArchiveIosMms !== '1') return;
    // A committed seek owns the producer; warming during it would move the
    // producer back behind the position the player is about to play.
    if (archiveSeekCommittingRef.current) return;

    const target = Math.max(0, Number(position) || 0);
    if (!Number.isFinite(target)) return;

    const now = Date.now();
    if (now - archiveWarmAtRef.current < 700) return;
    if (Math.abs(target - archiveWarmPositionRef.current) < 5) return;

    archiveWarmAtRef.current = now;
    archiveWarmPositionRef.current = target;
    void requestArchiveResume(url, target);
  };

  // DISPATCHARR_ARCHIVE_REMOTE_SEAMLESS_PRODUCER_SEEK_R8_14_10
  const requestArchiveNativeRemoteSeek = (url, position) => {
    const controlUrl = buildArchiveSessionControlUrl(url, 'resume');
    if (!controlUrl) return Promise.resolve(false);
    const resumeUrl = new URL(controlUrl, window.location.origin);

    try {
      const sourceUrl = new URL(url, window.location.origin);
      const adaptiveProfile =
        sourceUrl.searchParams.get('adaptive_output_profile');
      if (adaptiveProfile) {
        resumeUrl.searchParams.set(
          'adaptive_output_profile',
          adaptiveProfile
        );
      }
    } catch {}

    resumeUrl.searchParams.set(
      'position',
      String(Math.max(0, Number(position) || 0))
    );
    resumeUrl.searchParams.set('cast_native_seek', '1');

    return fetch(resumeUrl.toString(), {
      method: 'POST',
      cache: 'no-store',
      credentials: 'same-origin',
      keepalive: true,
    })
      .then((response) => response.ok)
      .catch(() => false);
  };

  const commitArchiveSeek = (value) => {
    const maxOffset = Math.max(0, archiveDurationSeconds - 1);
    const nextOffset = Math.max(
      0,
      Math.min(maxOffset, Math.floor(Number(value) || 0))
    );
    archiveScrubbingRef.current = false;
    archiveSegmentStartMediaTimeRef.current = null;
    const compatibilityController = archiveCompatibilityControllerRef.current;
    if (compatibilityController?.active) {
      setArchivePositionSeconds(nextOffset);
      setArchivePaused(false);
      setIsLoading(true);
      compatibilityController.scheduleSeek(nextOffset, async (generation) => {
        safeDestroyPlayer();
        initializeVODPlayer(compatibilityController.mediaUrl(generation), {
          archiveCompatibility: archiveCompatibilityPlaybackRef.current,
        });
      });
      return;
    }
    safeDestroyPlayer();
    setArchiveOffsetSeconds(nextOffset);
    setArchivePositionSeconds(nextOffset);
    setArchivePaused(false);
    setIsLoading(true);
  };

  const toggleArchivePlayback = () => {
    // DISPATCHARR_LIVE_PROVIDER_TIMESHIFT_PLAYBACK_FULLSCREEN_R5_6
    if (transportSuspendedKindRef.current === 'archive') {
      void resumeSuspendedTransport();
      return;
    }
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      try { playerRef.current?.startLoad?.(); } catch {}
      video.play().catch(() => {});
    } else {
      video.pause();
    }
  };

  const toggleArchiveMute = () => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    setArchiveMuted(video.muted);
  };

  // DISPATCHARR_LIVE_DESKTOP_CUSTOM_CONTROLS_R120: desktop Live now uses our own
  // control bar instead of the browser's native <video> controls, so that (a)
  // Space, the fullscreen button and the "Resume Live" overlay are all ours --
  // native controls put the fullscreen button in the video's shadow DOM, which
  // on Chrome stole Space (re-activating it EXITED fullscreen) and forced native
  // <video> fullscreen where our overlay cannot render (R119 could not override
  // it). Pause routes through the transport-suspend path like the on-screen
  // Pause, so "Resume Live" appears; resume restarts the Live stream.
  const toggleLivePlayback = () => {
    if (transportSuspendedKindRef.current === 'live') {
      void resumeSuspendedTransport();
      return;
    }
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      video.play().catch(() => {});
    } else {
      // DISPATCHARR_LIVE_BAR_PAUSE_IS_GESTURE_R121: the bar's buttons are
      // siblings of <video>, so the live pointerdown listener never sees them.
      // Record the gesture here, or the R19 handback guard/health check (and
      // R22 on an iPad with a trackpad) treat this deliberate Pause as
      // spurious and resume it.
      liveLastPointerAtRef.current = Date.now();
      video.pause();
    }
  };

  const toggleLiveMute = () => {
    const video = videoRef.current;
    if (!video) return;
    // DISPATCHARR_LIVE_DESKTOP_VOLUME_R121: unmuting a slider-at-zero player
    // must make sound, so bring the volume back to a sensible level too.
    if (video.muted || video.volume === 0) {
      video.muted = false;
      if (video.volume === 0) video.volume = 0.5;
    } else {
      video.muted = true;
    }
    setLiveMuted(video.muted);
    setLiveVolume(video.volume);
    try { savePlayerPrefs({ muted: video.muted }); } catch { /* ignore */ }
  };

  // DISPATCHARR_SCRUB_RANGE_SAFARI_VISIBLE: percentage of the track that
  // sits behind the handle, used to paint Safari's filled portion.
  const scrubFillPercent = (value, min, max) => {
    const lo = Number(min);
    const hi = Number(max);
    const current = Number(value);
    if (!Number.isFinite(lo) || !Number.isFinite(hi) || hi === lo) return '0%';
    const pct = ((current - lo) / (hi - lo)) * 100;
    if (!Number.isFinite(pct)) return '0%';
    return `${Math.min(100, Math.max(0, pct))}%`;
  };

  // DISPATCHARR_NATIVE_RANGE_HOVER_INSET_R132: a native <input type="range">
  // keeps the thumb CENTRE half a thumb away from either end and maps a
  // click the same way, so a hover readout spread over the full width
  // disagreed with the thumb and with the time a click seeks to (2 min near
  // the ends of a 220px bar over a 55 min programme). Insets from the border
  // box, measured 2026-10-06: Chromium 153/154 (Linux, macOS) 8px (16px
  // thumb), Firefox 157 10px (20px thumb, not moved by padding); Safari
  // draws the 13px thumb the SCRUB_RANGE_SAFARI_VISIBLE CSS below defines.
  const nativeRangeThumbInset = () => {
    if (document.documentElement.classList.contains('dispatcharr-webkit-scrub')) {
      return 6.5;
    }
    try {
      if (window.CSS?.supports?.('selector(::-moz-range-thumb)')) return 10;
    } catch {
      // no selector() support: not Gecko
    }
    return 8;
  };

  const nativeRangeRatioAtClientX = (input, clientX) => {
    const rect = input?.getBoundingClientRect?.();
    if (!rect || !(rect.width > 0)) return null;
    const inset = Math.min(nativeRangeThumbInset(), rect.width / 2);
    const travel = rect.width - inset * 2;
    if (travel <= 0) return 0;
    return Math.min(1, Math.max(0, (clientX - rect.left - inset) / travel));
  };

  const nativeRangeThumbCenterX = (input) => {
    const rect = input?.getBoundingClientRect?.();
    if (!rect || !(rect.width > 0)) return null;
    const min = Number(input.min) || 0;
    const max = Number(input.max);
    const inset = Math.min(nativeRangeThumbInset(), rect.width / 2);
    const ratio = max > min ? (Number(input.value) - min) / (max - min) : 0;
    return (
      rect.left + inset + Math.min(1, Math.max(0, ratio)) * (rect.width - inset * 2)
    );
  };

  // DISPATCHARR_NATIVE_RANGE_TIP_R133: what a press at this point does to a
  // native range, so a readout can show exactly that. null = the press only
  // grabs the thumb (value unchanged), undefined = not laid out.
  // - Chromium and WebKit: a press ON the thumb grabs it and the value stays
  //   (RangeInputType::HandleMouseDownEvent returns for the thumb); a drag
  //   then centres the thumb on the pointer, no grab offset. Blink hit-tests
  //   a 1x1px box at the point, so the grab zone starts 1px left of/above
  //   the thumb box (measured, Chromium 153).
  // - Firefox moves the thumb to the press point even on the thumb
  //   (HTMLInputElement::StartRangeThumbDrag): no grab zone.
  // - Elsewhere: round(ratio) on the step grid from min, at the FRACTIONAL
  //   pointer position (Blink floors it to 1/64px). MouseEvent clientX/Y are
  //   floored to whole px in all three engines, which read up to 1px low
  //   (0-11 s early on a 346px bar over 1 h): pass PointerEvent coordinates.
  const nativeRangePressValue = (input, clientX, clientY) => {
    const rect = input?.getBoundingClientRect?.();
    if (!rect || !(rect.width > 0) || clientX == null) return undefined;
    const min = Number(input.min) || 0;
    const max = Number(input.max);
    const step = Number(input.step) || 1;
    if (!(max > min)) return min;
    const inset = Math.min(nativeRangeThumbInset(), rect.width / 2);
    let gecko = false;
    try {
      gecko = Boolean(window.CSS?.supports?.('selector(::-moz-range-thumb)'));
    } catch {
      gecko = false;
    }
    if (!gecko) {
      // Blink hit-tests the point rounded to 1/64px (and maps the value from
      // it floored, below) -- both measured, Chromium 153.
      const hitX = Math.round(clientX * 64) / 64;
      const hitY = Math.round(clientY * 64) / 64;
      const thumbRatio = Math.min(
        1,
        Math.max(0, (Number(input.value) - min) / (max - min))
      );
      const thumbLeft =
        rect.left +
        Math.floor(thumbRatio * (rect.width - inset * 2) * 64) / 64;
      const thumbTop = rect.top + rect.height / 2 - inset;
      if (
        hitX > thumbLeft - 1 &&
        hitX < thumbLeft + inset * 2 &&
        hitY > thumbTop - 1 &&
        hitY < thumbTop + inset * 2
      ) {
        return null;
      }
    }
    const x = gecko ? clientX : Math.floor(clientX * 64) / 64;
    const ratio = nativeRangeRatioAtClientX(input, x);
    if (ratio == null) return undefined;
    const steps = gecko
      ? Math.round((ratio * (max - min)) / step)
      : nativeRangeDecimalSteps(ratio, (max - min) / step);
    const value = min + steps * step;
    return value > max ? value - step : value;
  };

  // DISPATCHARR_NATIVE_RANGE_TIP_R133: Chromium and WebKit take the ratio as
  // Decimal::FromDouble -- the double's SHORTEST decimal, i.e. String(ratio)
  // -- multiply it out exactly and round half up. A plain double product
  // rounds exact .5 ties the other way about half the time (one step off at
  // ~0.5% of pointer positions, measured), so multiply the digits instead.
  const nativeRangeDecimalSteps = (ratio, spanSteps) => {
    const decimal = (number) => {
      const m = /^(\d+)(?:\.(\d+))?(?:e([+-]\d+))?$/.exec(String(number));
      if (!m) return null;
      const scale = (m[2] || '').length - Number(m[3] || 0);
      return scale < 0 ? null : { digits: BigInt(m[1] + (m[2] || '')), scale };
    };
    const r = decimal(ratio);
    const s = decimal(spanSteps);
    if (!r || !s) return Math.round(ratio * spanSteps);
    const unit = BigInt(10) ** BigInt(r.scale + s.scale);
    const product = r.digits * s.digits;
    const up = (product % unit) * BigInt(2) >= unit ? BigInt(1) : BigInt(0);
    return Number(product / unit + up);
  };

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    // DISPATCHARR_SCRUB_RANGE_SAFARI_VISIBLE: gate the restyle on Safari
    // only. Chrome is WebKit-prefixed too, so an ungated
    // ::-webkit-slider-* rule would restyle the browsers that already
    // render correctly. Set before the stylesheet guard below, so a
    // remount still classes the document.
    const scrubUa =
      typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
    if (
      /Safari/i.test(scrubUa) &&
      !/Chrome|Chromium|CriOS|Edg|EdgiOS|OPR|FxiOS/i.test(scrubUa)
    ) {
      document.documentElement.classList.add('dispatcharr-webkit-scrub');
    }

    const styleId = 'dispatcharr-catchup-fullscreen-style';
    if (document.getElementById(styleId)) return undefined;

    const style = document.createElement('style');
    style.id = styleId;
    // DISPATCHARR_FULLSCREEN_STAGE_CSS_SPLIT_R121: one rule per fullscreen
    // pseudo-class. In a plain selector list ONE pseudo-class the engine does
    // not know invalidates the WHOLE rule: Firefox has no :-webkit-full-screen
    // and Safari < 16.4 has no :fullscreen, so the combined rule was dropped
    // there and the stage-fullscreened <video> kept its floating-window height.
    const fullscreenStageRules = (pseudo) => `
      .dispatcharr-catchup-fullscreen-stage${pseudo} {
        width: 100vw !important;
        height: 100vh !important;
        max-width: none !important;
        max-height: none !important;
        margin: 0 !important;
        padding: 0 !important;
        background: #000 !important;
        position: relative !important;
        overflow: hidden !important;
      }
      .dispatcharr-catchup-fullscreen-stage${pseudo} video {
        width: 100% !important;
        height: 100% !important;
        max-width: none !important;
        max-height: none !important;
        object-fit: contain !important;
        display: block !important;
        background: #000 !important;
      }
      /* DISPATCHARR_FULLSCREEN_CONTROL_SIZES_R122: our own controls kept their
         floating-window pixel sizes in stage fullscreen (~13px glyphs on a
         1680px-wide screen). Scale them with the screen the way Chrome scales
         its own fullscreen controls (measured, Chromium 153: 48px buttons at
         1280 wide, 64px at 1680). Every rule is fullscreen-only, so the
         floating player is untouched; !important beats the inline styles.
         opacity and pointer-events (the hover show/hide) are not touched. */
      .dispatcharr-catchup-fullscreen-stage${pseudo} {
        --dispatcharr-fs-hit: clamp(48px, 3.75vw, 64px);
        --dispatcharr-fs-glyph: clamp(26px, 1.9vw, 32px);
        --dispatcharr-fs-text: clamp(16px, 1.05vw, 18px);
      }
      .dispatcharr-catchup-fullscreen-stage${pseudo} .dispatcharr-fs-bar {
        gap: 12px !important;
        padding: 12px 20px max(18px, env(safe-area-inset-bottom)) !important;
      }
      .dispatcharr-catchup-fullscreen-stage${pseudo} .dispatcharr-fs-btn {
        min-width: var(--dispatcharr-fs-hit) !important;
        height: var(--dispatcharr-fs-hit) !important;
        padding: 0 8px !important;
        line-height: 1 !important;
      }
      .dispatcharr-catchup-fullscreen-stage${pseudo} .dispatcharr-fs-play {
        font-size: var(--dispatcharr-fs-glyph) !important;
      }
      .dispatcharr-catchup-fullscreen-stage${pseudo} .dispatcharr-fs-mute {
        font-size: calc(var(--dispatcharr-fs-glyph) * 0.92) !important;
      }
      .dispatcharr-catchup-fullscreen-stage${pseudo} .dispatcharr-fs-full {
        font-size: calc(var(--dispatcharr-fs-glyph) * 1.12) !important;
      }
      .dispatcharr-catchup-fullscreen-stage${pseudo} .dispatcharr-fs-close {
        font-size: calc(var(--dispatcharr-fs-glyph) * 0.92) !important;
      }
      .dispatcharr-catchup-fullscreen-stage${pseudo} .dispatcharr-fs-range {
        height: 32px !important;
      }
      .dispatcharr-catchup-fullscreen-stage${pseudo} .dispatcharr-fs-volume {
        width: clamp(140px, 10vw, 180px) !important;
      }
      .dispatcharr-catchup-fullscreen-stage${pseudo} .dispatcharr-fs-time {
        font-size: var(--dispatcharr-fs-text) !important;
        min-width: calc(var(--dispatcharr-fs-text) * 3.6) !important;
      }
      .dispatcharr-catchup-fullscreen-stage${pseudo} .dispatcharr-fs-tip {
        font-size: 15px !important;
        line-height: 1.3 !important;
        padding: 4px 8px !important;
      }
      .dispatcharr-catchup-fullscreen-stage${pseudo} .dispatcharr-fs-suspend {
        gap: 20px !important;
      }
      .dispatcharr-catchup-fullscreen-stage${pseudo} .dispatcharr-fs-suspend-title {
        font-size: clamp(20px, 1.5vw, 26px) !important;
        line-height: 1.3 !important;
      }
      .dispatcharr-catchup-fullscreen-stage${pseudo} .dispatcharr-fs-resume {
        font-size: clamp(18px, 1.3vw, 22px) !important;
        line-height: 1.25 !important;
        padding: 0.6em 1.4em !important;
        border-radius: 10px !important;
      }
      /* DISPATCHARR_LIVE_PAUSE_CHOICE_R136: the paused-Live choice block. */
      .dispatcharr-catchup-fullscreen-stage${pseudo} .dispatcharr-fs-suspend-sub {
        font-size: clamp(14px, 1.05vw, 18px) !important;
      }
      .dispatcharr-catchup-fullscreen-stage${pseudo} .dispatcharr-fs-suspend-row {
        gap: 14px !important;
        flex-wrap: wrap !important;
      }
      .dispatcharr-catchup-fullscreen-stage${pseudo} .dispatcharr-fs-suspend-wait {
        font-size: clamp(14px, 1.05vw, 18px) !important;
      }
      .dispatcharr-catchup-fullscreen-stage${pseudo} .dispatcharr-fs-suspend-hint {
        display: block !important;
        font-size: clamp(12px, 0.9vw, 15px) !important;
      }
      /* TimeshiftSeekBar (archive / rewound Live). Its hover readout sits
         above the track inside the bar's clipped box, hence the top room. */
      .dispatcharr-catchup-fullscreen-stage${pseudo} .dispatcharr-fs-tsbar {
        gap: 12px !important;
        padding: 32px 20px max(18px, env(safe-area-inset-bottom)) !important;
        --dispatcharr-seekbar-volume-open: 128px;
      }
      .dispatcharr-catchup-fullscreen-stage${pseudo} .dispatcharr-fs-track {
        height: 40px !important;
      }
      .dispatcharr-catchup-fullscreen-stage${pseudo} .dispatcharr-fs-rail {
        height: 10px !important;
      }
      .dispatcharr-catchup-fullscreen-stage${pseudo} .dispatcharr-fs-thumb {
        width: 22px !important;
        height: 22px !important;
        margin-left: -11px !important;
        margin-top: -11px !important;
      }
      .dispatcharr-catchup-fullscreen-stage${pseudo} .dispatcharr-fs-volbar {
        width: 120px !important;
        height: 28px !important;
      }
      .dispatcharr-catchup-fullscreen-stage${pseudo} .dispatcharr-fs-volrail {
        height: 7px !important;
      }
      .dispatcharr-catchup-fullscreen-stage${pseudo} .dispatcharr-fs-volknob {
        width: 16px !important;
        height: 16px !important;
        margin-left: -8px !important;
        margin-top: -8px !important;
      }
      /* Top-right badges (Cast, Rewind, Continue Live, cast error). zoom
         scales each badge's own inline top/right/height/padding/glyph
         together, so the JS-computed right offsets (Rewind and Continue Live
         clear Cast's width) still line up at the larger size. */
      .dispatcharr-catchup-fullscreen-stage${pseudo} .dispatcharr-fs-badge {
        zoom: 1.5;
        margin: 6px 6px 0 0 !important;
      }
      /* DISPATCHARR_FULLSCREEN_LOAD_ERROR_IN_STAGE_R124: in the floating
         player the load-error strip is a flow block under the <video>; in
         stage fullscreen the video is forced to 100% height and the stage
         clips overflow, so pin the strip to the stage's bottom edge and scale
         it like the R122 badges. z-index 33 keeps it over the video and the
         suspend overlay (30) but under the hover control bar (34) and the
         top-right badges (35+). */
      .dispatcharr-catchup-fullscreen-stage${pseudo} .dispatcharr-fs-error {
        position: absolute !important;
        left: 0 !important;
        right: 0 !important;
        bottom: 0 !important;
        z-index: 33 !important;
        zoom: 1.5;
      }
      /* DISPATCHARR_FULLSCREEN_NO_BACKDROP_BLUR_R122: Chromium still draws an
         element that has backdrop-filter while its opacity is 0, and on macOS a
         backdrop-filter pass anywhere in a frame disables the CoreAnimation
         video overlay for that whole frame. The idle Cast/Rewind badges made
         every stage-fullscreen frame a full Retina re-composite (3 render
         passes instead of 1), which native <video> fullscreen (before R121)
         never paid -- the extra load lined up with Chrome-only audio
         underflows/clicks at the fullscreen switch. */
      .dispatcharr-catchup-fullscreen-stage${pseudo} * {
        backdrop-filter: none !important;
        -webkit-backdrop-filter: none !important;
      }
    `;
    style.textContent =
      fullscreenStageRules(':fullscreen') +
      fullscreenStageRules(':-webkit-full-screen');
    style.textContent += `
      html.dispatcharr-catchup-ios-fullscreen-lock,
      body.dispatcharr-catchup-ios-fullscreen-lock {
        overflow: hidden !important;
        overscroll-behavior: none !important;
      }
      /* DISPATCHARR_IOS_FULLSCREEN_STAGE_ESCAPES_TRANSFORM_R83
         A transformed ancestor becomes the containing block for anything
         fixed inside it, so the stage was laid out inside the draggable
         player box instead of the viewport: the app's sidebar stayed on
         screen and our own bar was cut off at the edge. The player is
         dragged with a transform, so every ancestor is flattened while the
         stage is up, and put back when it comes down. */
      .dispatcharr-fullscreen-ancestor {
        transform: none !important;
        filter: none !important;
        perspective: none !important;
        backdrop-filter: none !important;
        will-change: auto !important;
        contain: none !important;
        overflow: visible !important;
      }
      .dispatcharr-catchup-ios-pseudo-fullscreen {
        position: fixed !important;
        inset: 0 !important;
        width: 100vw !important;
        width: 100dvw !important;
        height: 100vh !important;
        height: 100dvh !important;
        max-width: none !important;
        max-height: none !important;
        margin: 0 !important;
        padding: 0 !important;
        box-sizing: border-box !important;
        background: #000 !important;
        overflow: hidden !important;
        z-index: 2147483647 !important;
        transform: none !important;
      }
      .dispatcharr-catchup-ios-pseudo-fullscreen video {
        width: 100% !important;
        height: 100% !important;
        max-width: none !important;
        max-height: none !important;
        object-fit: contain !important;
        display: block !important;
        background: #000 !important;
      }
      .dispatcharr-catchup-ios-pseudo-fullscreen .floating-video-no-drag {
        padding-bottom: max(7px, env(safe-area-inset-bottom)) !important;
      }
      /* DISPATCHARR_SCRUB_RANGE_SAFARI_VISIBLE: Safari draws an unstyled
         range in a dark appearance that disappears against the player's
         near-black control gradient, and ignores accent-color here.
         Every rule below is gated on the Safari-only root class, so
         Chrome and Firefox keep their native rendering untouched. */
      html.dispatcharr-webkit-scrub .dispatcharr-scrub-range {
        -webkit-appearance: none;
        appearance: none;
        background: transparent;
        height: 16px;
      }
      html.dispatcharr-webkit-scrub .dispatcharr-scrub-range::-webkit-slider-runnable-track {
        height: 4px;
        border-radius: 2px;
        background: linear-gradient(
          to right,
          #4c8dff 0%,
          #4c8dff var(--dispatcharr-scrub-fill, 0%),
          rgba(255, 255, 255, 0.38) var(--dispatcharr-scrub-fill, 0%),
          rgba(255, 255, 255, 0.38) 100%
        );
      }
      html.dispatcharr-webkit-scrub .dispatcharr-scrub-range::-webkit-slider-thumb {
        -webkit-appearance: none;
        appearance: none;
        width: 13px;
        height: 13px;
        margin-top: -4.5px;
        border-radius: 50%;
        background: #fff;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.65);
        cursor: pointer;
      }
    `;
    document.head.appendChild(style);
    return () => style.remove();
  }, []);

  const isIPhoneCatchupBrowser = () => {
    if (typeof navigator === 'undefined') return false;
    return /iPhone|iPod/i.test(navigator.userAgent || '');
  };

  const clearArchivePseudoFullscreen = () => {
    // DISPATCHARR_IOS_REWIND_IN_FULLSCREEN_R82
    archiveOwnFullscreenRef.current = false;
    if (typeof document === 'undefined') return;
    document
      .querySelectorAll('.dispatcharr-catchup-ios-pseudo-fullscreen')
      .forEach((node) =>
        node.classList.remove('dispatcharr-catchup-ios-pseudo-fullscreen')
      );
    // DISPATCHARR_IOS_FULLSCREEN_STAGE_ESCAPES_TRANSFORM_R83
    document
      .querySelectorAll('.dispatcharr-fullscreen-ancestor')
      .forEach((node) =>
        node.classList.remove('dispatcharr-fullscreen-ancestor')
      );
    document.documentElement?.classList.remove(
      'dispatcharr-catchup-ios-fullscreen-lock'
    );
    document.body?.classList.remove('dispatcharr-catchup-ios-fullscreen-lock');
  };

  // DISPATCHARR_IOS_FULLSCREEN_STAGE_ESCAPES_TRANSFORM_R83
  const raiseOwnFullscreenStage = (stage) => {
    if (!stage || typeof document === 'undefined') return false;
    stage.classList.add('dispatcharr-catchup-fullscreen-stage');
    stage.classList.add('dispatcharr-catchup-ios-pseudo-fullscreen');
    // Walk to the body flattening transforms, or the stage stays trapped
    // inside the draggable player box.
    let ancestor = stage.parentElement;
    while (ancestor && ancestor !== document.body) {
      ancestor.classList.add('dispatcharr-fullscreen-ancestor');
      ancestor = ancestor.parentElement;
    }
    document.documentElement?.classList.add(
      'dispatcharr-catchup-ios-fullscreen-lock'
    );
    document.body?.classList.add('dispatcharr-catchup-ios-fullscreen-lock');
    archiveOwnFullscreenRef.current = true;
    return true;
  };

  // DISPATCHARR_IOS_REWIND_IN_FULLSCREEN_R84
  // An earlier attempt left Apple's player the moment it opened and put our
  // own stage up instead. iOS does not reliably honour webkitExitFullscreen
  // from inside webkitbeginfullscreen: the system player stayed up with our
  // stage classes already applied, and the page was left broken behind it.
  // Apple's fullscreen is its own again. Our stage is now something the
  // viewer chooses, from the button below.

  // DISPATCHARR_LIVE_PROVIDER_TIMESHIFT_DYNAMIC_EDGE_NATIVE_FULLSCREEN_R5_4
  // DISPATCHARR_LIVE_PROVIDER_TIMESHIFT_PLAYBACK_FULLSCREEN_R5_6
  const toggleArchiveFullscreen = () => {
    const video = videoRef.current;
    if (!video || typeof document === 'undefined') return;

    const stage = video.parentElement || videoContainerRef.current || video;
    if (!stage) return;
    stage.classList.add('dispatcharr-catchup-fullscreen-stage');

    // DISPATCHARR_IOS_REWIND_IN_FULLSCREEN_R81
    // Apple's player replaces our bar with its own, and its bar has no Rewind
    // - the one control this content exists for. Live and a rewound Live
    // therefore go fullscreen the only way that keeps our bar on screen: the
    // CSS stand-in, which fills the viewport and keeps every badge reachable.
    // Plain archives still use the system player, where Apple's scrubber is
    // the better control and our seek commits follow it anyway.
    const keepOwnBarInFullscreen = Boolean(
      contentType === 'live' ||
        (contentType === 'catchup-hls' && metadata?.liveTimeshift)
    );

    if (isIPhoneCatchupBrowser() && keepOwnBarInFullscreen) {
      // The stand-in: the stage fills the viewport and the page behind it is
      // locked, so our Rewind, Go Live and seek bar stay where they were.
      const entering = !stage.classList.contains(
        'dispatcharr-catchup-ios-pseudo-fullscreen'
      );
      if (video.webkitDisplayingFullscreen && video.webkitExitFullscreen) {
        // Apple's player may already be up if it was opened from its own
        // control; leave it, or the two would fight over the screen.
        try {
          video.webkitExitFullscreen();
        } catch {
          // Nothing to undo.
        }
      }
      clearArchivePseudoFullscreen();
      if (entering) {
        raiseOwnFullscreenStage(stage);
        showBadgesWithNativeControls();
      }
      return;
    }

    if (isIPhoneCatchupBrowser() && !keepOwnBarInFullscreen) {
      // DISPATCHARR_CATCHUP_IOS_NATIVE_FULLSCREEN_R5_12
      // iOS Safari implements no Element.requestFullscreen at all -- only
      // <video> can go fullscreen, through webkitEnterFullscreen. The CSS
      // stand-in below keeps our own overlays on screen but is not real
      // fullscreen: no system player, no rotation, no minimise. Prefer the
      // native call and keep the stand-in only for a runtime that lacks it.
      //
      // The trade-off is inherent to the platform: inside the iOS player our
      // own bar is replaced by Apple's controls.
      if (video.webkitDisplayingFullscreen && video.webkitExitFullscreen) {
        try {
          video.webkitExitFullscreen();
          return;
        } catch {
          // fall through to the stand-in
        }
      }
      if (typeof video.webkitEnterFullscreen === 'function') {
        clearArchivePseudoFullscreen();
        try {
          video.webkitEnterFullscreen();
          return;
        } catch {
          // fall through to the stand-in
        }
      }

      const entering = !stage.classList.contains(
        'dispatcharr-catchup-ios-pseudo-fullscreen'
      );
      clearArchivePseudoFullscreen();
      if (entering) raiseOwnFullscreenStage(stage);
      return;
    }

    const activeFullscreen =
      document.fullscreenElement || document.webkitFullscreenElement;
    if (activeFullscreen) {
      if (document.exitFullscreen) {
        document.exitFullscreen().catch?.(() => {});
        return;
      }
      if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
        return;
      }
      if (document.webkitCancelFullScreen) {
        document.webkitCancelFullScreen();
        return;
      }
    }

    // DISPATCHARR_LIVE_TIMESHIFT_STAGE_FULLSCREEN_FOR_BADGES
    // DISPATCHARR_ARCHIVE_FULLSCREEN_STAGE_R116: plain archive on DESKTOP drives
    // playback through the custom TimeshiftSeekBar (native <video> controls are
    // off when !coarsePointer). Native <video> fullscreen would hide that bar
    // AND the "Resume Archive" suspend overlay, leaving no way to control or
    // revive playback in fullscreen. Fullscreen the DOM stage instead, exactly
    // like live, so both stay inside the fullscreen subtree. Mobile/iOS (coarse
    // pointer) keeps Apple's native player + scrubber, which it expects.
    const providerDvrFullscreen = Boolean(
      contentType === 'live' ||
        (contentType === 'catchup-hls' && metadata?.liveTimeshift) ||
        (contentType === 'catchup-hls' && !coarsePointer)
    );
    if (providerDvrFullscreen) {
      // Fullscreen the DOM stage, not only <video>, so the Go Live/Cast
      // badges (live_timeshift) or the rewind-picker bar (plain Live) stay
      // inside the fullscreen subtree and remain reachable.
      if (stage.requestFullscreen) {
        stage.requestFullscreen().catch?.(() => {});
        return;
      }
      if (stage.webkitRequestFullscreen) {
        stage.webkitRequestFullscreen();
        return;
      }
    }

    // Ordinary Archive retains r5.4 native video fullscreen.
    if (video.requestFullscreen) {
      video.requestFullscreen().catch?.(() => {});
      return;
    }
    if (video.webkitRequestFullscreen) {
      video.webkitRequestFullscreen();
      return;
    }
    if (video.webkitEnterFullscreen) {
      video.webkitEnterFullscreen();
      return;
    }

    if (stage.requestFullscreen) {
      stage.requestFullscreen().catch?.(() => {});
    } else if (stage.webkitRequestFullscreen) {
      stage.webkitRequestFullscreen();
    }
  };

  useEffect(() => {
    return () => clearArchivePseudoFullscreen();
  }, []);

  useEffect(() => {
    if (contentType !== 'catchup') return;
    archiveScrubbingRef.current = false;
    archiveSegmentStartMediaTimeRef.current = null;
    setArchiveOffsetSeconds(0);
    setArchivePositionSeconds(0);
    setArchivePaused(false);
  }, [streamUrl, contentType]);

  // DISPATCHARR_WEB_ARCHIVE_DIRECT_R8_19_0: nginx carries the media bytes, so
  // keep the short Django session alive explicitly without involving FFmpeg.
  useEffect(() => {
    if (!isVisible || contentType !== 'catchup' || !streamUrl) return undefined;
    requestArchivePing(streamUrl);
    const timer = window.setInterval(() => requestArchivePing(streamUrl), 60000);
    return () => window.clearInterval(timer);
  }, [isVisible, streamUrl, contentType]);

  // DISPATCHARR_SPACEBAR_PLAY_PAUSE_R117: Space toggles play/pause for the web
  // player, like every other video player, in BOTH minimized and fullscreen.
  // A document-level listener covers both because desktop live and archive use
  // DOM/stage fullscreen (R116), where document keydown still fires. It routes
  // through the same transport logic as the on-screen buttons: a suspended
  // Archive/Live revives (Archive re-mints an expired session, R116); otherwise
  // it toggles the current player.
  useEffect(() => {
    if (!isVisible) return undefined;
    // DISPATCHARR_SPACEBAR_CROSS_BROWSER_R121: CAPTURE phase on document, so we
    // run before (a) the UA media-control listeners that sit on the <video>
    // itself -- Chromium's MediaControlsMediaEventListener (keydown/keyup; acts
    // on Space only while the event is not yet defaultPrevented) and WebKit's
    // MediaController (toggles and stopPropagation()s while the <video> itself
    // is fullscreen) -- and (b) React's root listeners. The paired keyup is
    // cancelled too, which also stops keyup-driven activation (pre-2022
    // Firefox). Only genuine text entry keeps Space; a focused button/link is
    // blurred (R119: the fullscreen button keeps focus after its click).
    // NB (verified in real headless Chromium, R121): while focus is INSIDE a
    // native UA media control, Chromium delivers NO key events to the page at
    // all -- which is why desktop Live must never have native controls.
    let spaceKeydownTaken = false;
    const isSpaceKey = (event) => event.key === ' ' || event.code === 'Space';
    const ownsSpace = (event) => {
      // Innermost target, through open shadow roots.
      const path =
        typeof event.composedPath === 'function' ? event.composedPath() : null;
      const target = (path && path[0]) || event.target;
      if (!target || target.nodeType !== 1) return false;
      if (target.isContentEditable) return true;
      const tag = target.tagName;
      if (tag === 'TEXTAREA' || tag === 'SELECT') return true;
      // A range (scrub/volume slider) has no Space behaviour of its own, and
      // Chrome/Firefox leave it focused after it was dragged.
      if (tag === 'INPUT') return String(target.type).toLowerCase() !== 'range';
      return false;
    };
    // The <video> ITSELF is fullscreen (a UA path: context menu, a leftover
    // native control): its own controls act on Space too, so keep the key away
    // from them and playback toggles exactly once.
    const videoOwnsFullscreen = () => {
      const video = videoRef.current;
      if (!video || typeof document === 'undefined') return false;
      return (
        (document.fullscreenElement || document.webkitFullscreenElement) ===
        video
      );
    };
    const onKeyUp = (event) => {
      if (!spaceKeydownTaken || !isSpaceKey(event)) return;
      spaceKeydownTaken = false;
      event.preventDefault();
      if (videoOwnsFullscreen()) event.stopPropagation();
    };
    const onKeyDown = (event) => {
      if (!isSpaceKey(event)) return;
      // A keydown we do not take must not inherit a stale flag from a Space
      // whose keyup never reached us (window switched while held): that flag
      // would cancel THIS key's keyup, which is where a focused
      // checkbox/radio/switch toggles.
      spaceKeydownTaken = false;
      // IME conversion (key "Process", code still "Space") is not ours.
      if (event.isComposing || event.keyCode === 229) return;
      if (event.altKey || event.ctrlKey || event.metaKey) return;
      if (ownsSpace(event)) return;
      // DISPATCHARR_CAST_PICKER_TRAP_R126: Space while Chrome's Cast picker is
      // open (or just closed) must not toggle playback; swallow it so it does
      // not scroll or activate a focused control either.
      if (castPickerGuardActive()) {
        event.preventDefault();
        spaceKeydownTaken = true;
        return;
      }
      // Cancel BEFORE the repeat check: page scroll and the focused control's
      // Space activation are suppressed for auto-repeat too.
      event.preventDefault();
      spaceKeydownTaken = true;
      if (videoOwnsFullscreen()) event.stopPropagation();
      if (event.repeat) return;
      const active =
        typeof document !== 'undefined' ? document.activeElement : null;
      if (
        active &&
        active !== document.body &&
        typeof active.blur === 'function' &&
        (active.tagName === 'BUTTON' ||
          active.tagName === 'A' ||
          active.getAttribute?.('role') === 'button')
      ) {
        try { active.blur(); } catch { /* ignore */ }
      }
      // DISPATCHARR_SPACE_TOGGLES_CAST_R133 + DISPATCHARR_LIVE_PAUSE_CHOICE_R136:
      // while a cast owns playback the local <video> is an empty, src-less
      // element: Space must never toggle it. A settled Live cast pauses the TV
      // or continues Live (R136 gave Live casts a pause UI and a defined
      // resume, retiring the R133a no-op); an Archive cast toggles the TV
      // (R133). During any Cast transition (an R134 op, a Cast start, the
      // Rewind / Continue Live switch, a Stop handback) Space does nothing.
      // A paused local Live continues Live -- "Resume from" is click-only.
      const latest = castLatestRef.current || {};
      if (
        castActiveRef.current ||
        dispatcharrCastRuntime.active ||
        castHandoffInProgressRef.current ||
        castOpInFlight(['start'])
      ) {
        if (
          castActiveRef.current &&
          dispatcharrCastRuntime.kind === 'live' &&
          latest.contentType === 'live'
        ) {
          latest.toggleLiveCastPlayback?.('space');
          return;
        }
        if (
          castActiveRef.current &&
          castRemoteControllerRef.current &&
          dispatcharrCastRuntime.kind === 'archive' &&
          !castOpInFlight()
        ) {
          toggleArchiveCastPlayback();
          return;
        }
        console.info('[live-pause-r136] Space ignored: a Cast transition owns playback');
        return;
      }
      if (transportSuspendedKindRef.current === 'live') {
        void (latest.continueLiveFromPause || continueLiveFromPause)('space');
        return;
      }
      if (transportSuspendedKindRef.current) {
        void resumeSuspendedTransport();
        return;
      }
      if (contentType === 'catchup-hls' || contentType === 'catchup') {
        toggleArchivePlayback();
        return;
      }
      const video = videoRef.current;
      if (!video) return;
      if (video.paused) {
        try { playerRef.current?.startLoad?.(); } catch { /* no hls wrapper */ }
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    document.addEventListener('keyup', onKeyUp, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.removeEventListener('keyup', onKeyUp, true);
    };
    // DISPATCHARR_SPACEBAR_FRESH_CLOSURE_R121: resumeSuspendedTransport and the
    // starters it calls read streamUrl/metadata from the render that created
    // this listener, so a live->live channel switch (or a new archive
    // programme) without closing the player left Space resuming the PREVIOUS
    // source. Re-subscribe whenever the source changes.
  }, [isVisible, contentType, streamUrl, metadata]);

  // DISPATCHARR_TRANSPORT_SUSPEND_R8_8_27
  useEffect(() => {
    transportSuspendedKindRef.current = null;
    setTransportSuspendedKind(null);
    livePlaybackStartedRef.current = false;
    archivePlaybackStartedRef.current = false;
    archiveLeaseSuspendedRef.current = false;
    archiveSuspendedPositionRef.current = 0;
    // DISPATCHARR_LIVE_PAUSE_CHOICE_R136: a pause belongs to its source.
    livePauseSampleRef.current = null;
    if (livePauseRef.current) setLivePause(null);

    // DISPATCHARR_ARCHIVE_RECOVERY_R8_8_48
    // Never inherit the previous programme's Cast seek position. The sole
    // exception is a same-source SPA remount while that exact Cast is active.
    const sameActiveArchiveCast = Boolean(
      dispatcharrCastRuntime.active &&
        dispatcharrCastRuntime.kind === 'archive' &&
        dispatcharrCastRuntime.sourceUrl === streamUrl
    );
    if (sameActiveArchiveCast) {
      setCastArchivePositionSeconds(
        Math.max(0, Number(dispatcharrCastRuntime.archivePosition) || 0)
      );
      setCastArchiveDurationSeconds(
        Math.max(0, Number(dispatcharrCastRuntime.archiveDuration) || 0)
      );
      return;
    }

    setCastArchivePositionSeconds(0);
    setCastArchiveDurationSeconds(0);
    setCastArchivePaused(false);
    castArchiveScrubbingRef.current = false;
    castArchiveSeekHealthRef.current = {
      inProgress: false,
      startedAt: 0,
      completedAt: 0,
      target: 0,
    };
    castHealthRef.current = {
      state: '',
      bufferingSince: 0,
      lastPosition: 0,
      lastProgressAt: Date.now(),
    };
    castArchiveRemoteSeekObserverRef.current = {
      lastPosition: null,
      lastAt: 0,
      suppressUntil: 0,
      receiverLoadInProgress: false,
      pending: false,
      debounceTimer: null,
      candidatePosition: null,
      armDirection: 0,
      armCount: 0,
      armUntil: 0,
      pausedByRemoteScrub: false,
      resumeAfterRemoteScrub: true,
      scrubAnchorPosition: null,
      virtualTargetPosition: null,
      lastRawRemotePosition: null,
      snapbackTimer: null,
      ignoreAnchorUntil: 0,
    };
  }, [isVisible, streamUrl, contentType]);

  // DISPATCHARR_CAST_ERROR_SESSION_SCOPED_R115: "Cast stopped on the device.
  // Playback was not resumed." (and every other cast-session message) belongs
  // to the session it happened in. It must keep showing on the dead player,
  // but the NEXT playback -- opened without a page refresh -- must not inherit
  // it. showVideo() ships a fresh metadata object on every open (even for the
  // same channel/streamUrl), so these deps fire exactly on new-playback and
  // close transitions, never at the moment the error itself is set (the
  // recovery path leaves the video store untouched).
  useEffect(() => {
    setCastError(null);
  }, [isVisible, streamUrl, contentType, metadata]);

  // DISPATCHARR_CAST_ERROR_AUTOHIDE_R124: the red cast badge is also a toast:
  // it expires on its own and a click on it dismisses it (the badge's own
  // onClick). Before this it could only be cleared by the next cast attempt or
  // a source change, and in stage fullscreen it baited "dismiss" clicks into
  // R121 click-to-pause. R115's session-scoped clearing above is untouched --
  // this effect only bounds how long any one message lingers.
  useEffect(() => {
    if (!castError) return undefined;
    // The two deliberate dead-player STATE labels (R109 receiver-gone, CAF
    // disconnect) must keep explaining the black player until the next
    // playback (R115) -- they stay click-dismissible but never auto-expire.
    if (
      castError === 'Cast stopped on the device. Playback was not resumed.' ||
      castError === ARCHIVE_CAST_FINISHED_NOTICE ||
      castError.startsWith('Chromecast disconnected.')
    ) {
      return undefined;
    }
    const timer = window.setTimeout(() => setCastError(null), 8000);
    return () => window.clearTimeout(timer);
  }, [castError]);

  // DISPATCHARR_ARCHIVE_RECOVERY_CAST_HANDBACK_REUSE
  useEffect(() => {
    archiveLocalRuntimeUrlRef.current = null;
    // DISPATCHARR_ARCHIVE_R2_SEEK_HANDBACK_FIXES
    archiveNativeSeekInProgressRef.current = false;
    if (archiveNativeSeekPauseTimerRef.current != null) {
      window.clearTimeout(archiveNativeSeekPauseTimerRef.current);
      archiveNativeSeekPauseTimerRef.current = null;
    }
  }, [streamUrl, contentType]);

  // DISPATCHARR_UNIFIED_CAST_LEASE_NO_WEB_HEARTBEATS_R8_8_54: catchup-hls Web uses playlist/segment activity; no periodic ping.

  const videoRef = useRef(null);
  const playerRef = useRef(null);
  const videoContainerRef = useRef(null);
  const resizeStateRef = useRef(null);
  const overlayTimeoutRef = useRef(null);
  const adaptiveOutputRequestRef = useRef(0);
  const liveInlineGuardUntilRef = useRef(0);
  const liveWasPlayingBeforeFullscreenRef = useRef(false);
  const liveLastPointerAtRef = useRef(0);
  // DISPATCHARR_CAST_HANDBACK_GUARD_R19
  const liveLastKeyAtRef = useRef(0);
  // DISPATCHARR_WEB_LIVE_TUNING_FROM_CONFIG_R29
  const liveSyncTuningRef = useRef(null);
  // DISPATCHARR_ARCHIVE_PTS_ALIGN_R137: what the live-reopen gate said, per
  // Live URL (a start that was superseded must not overwrite the note of the
  // Live that is playing); a Live without a note gets the default.
  const liveRewindFloorRef = useRef(new Map());
  // ... and, per Live URL, the question that is on its way or when the last
  // one was answered (see refreshLiveRewindFloor).
  const liveRewindFloorAskedRef = useRef(new Map());
  const liveUserPauseAtRef = useRef(0);
  const castLiveHandbackAtRef = useRef(0);
  const castLiveHandbackPauseResumedRef = useRef(false);
  const castLiveHandbackHealedRef = useRef(false);
  const liveFullscreenResumeUntilRef = useRef(0);
  const liveFullscreenResumeTimerRef = useRef(null);
  const archiveWasPlayingBeforeFullscreenRef = useRef(false);
  // DISPATCHARR_IOS_FULLSCREEN_EXIT_RESUME_R81
  const archiveFullscreenChangedAtRef = useRef(0);
  // DISPATCHARR_IOS_REWIND_IN_FULLSCREEN_R82
  const archiveOwnFullscreenRef = useRef(false);
  const archiveFullscreenResumeUntilRef = useRef(0);
  const archiveFullscreenResumeTimerRef = useRef(null);
  const aspectRatioRef = useRef(320 / 180);
  const dragPositionRef = useRef(null);
  const dragOffsetRef = useRef({ x: 0, y: 0 });
  const initialPositionRef = useRef(null);
  // Ref kept in sync with videoSize state for use inside event handlers
  // where closures over state would be stale.
  const videoSizeRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimeoutRef = useRef(null);

  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  // DISPATCHARR_ARCHIVE_IOS_TAP_TO_PLAY_R71
  const [archiveTapToPlay, setArchiveTapToPlay] = useState(false);
  const [showOverlay, setShowOverlay] = useState(false);
  const [showControls, setShowControls] = useState(false);
  // DISPATCHARR_CATCHUP_NATIVE_CONTROLS_ON_TOUCH_R5_13
  const [coarsePointer, setCoarsePointer] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return !window.matchMedia('(any-hover: hover)').matches;
  });
  const castHoverRequired = !coarsePointer;
  const [videoSize, setVideoSize] = useState(() => {
    const prefs = getPlayerPrefs();
    const saved = prefs.size;
    if (saved?.width >= 220 && saved?.height >= 124) {
      if (typeof window !== 'undefined') {
        // Cap to viewport minus a margin so the header is always reachable on
        // first render even if the saved size was set on a larger display.
        const maxW = window.innerWidth - 48; // VISIBLE_MARGIN
        const maxH = window.innerHeight - 83; // HEADER_HEIGHT(38) + VISIBLE_MARGIN(48) - 1 extra row
        if (saved.width > maxW || saved.height > maxH) {
          const scale = Math.min(maxW / saved.width, maxH / saved.height);
          return {
            width: Math.max(220, Math.round(saved.width * scale)),
            height: Math.max(124, Math.round(saved.height * scale)),
          };
        }
      }
      return saved;
    }
    return { width: 320, height: 180 };
  });
  const [isResizing, setIsResizing] = useState(false);
  const [dragPosition, setDragPosition] = useState(null);

  const MIN_WIDTH = 220;
  const MIN_HEIGHT = 124;
  const VISIBLE_MARGIN = 48; // keep part of the window visible when dragging
  const HEADER_HEIGHT = 38; // height of the close button header area
  const ERROR_HEIGHT = 45; // approximate height of error message area when displayed
  const MAX_LIVE_RECONNECT_ATTEMPTS = 5;
  const LIVE_RECONNECT_BASE_DELAY_MS = 1000;
  const LIVE_RECONNECT_MAX_DELAY_MS = 10000;

  // Safely destroy the mpegts player to prevent errors
  // DISPATCHARR_LIVE_IOS_SPURIOUS_PAUSE_R22: Chrome for iOS cannot be
  // inspected from a desktop, so the live player reports its own events by
  // fetching a static file with query parameters. They land in the nginx
  // access log, which is readable server-side. Capped per player start so a
  // stuck stream cannot flood the log.
  const isWebKitMobile = () => {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent || '';
    if (/iPhone|iPad|iPod/.test(ua)) return true;
    // iPadOS 13+ reports itself as Macintosh; touch points give it away.
    return /Macintosh/.test(ua) && (navigator.maxTouchPoints || 0) > 1;
  };

  // DISPATCHARR_LIVE_ADAPTIVE_BAND_R64: live-latency-chaser.ts holds a
  // reference to the player's config object and re-reads these two numbers on
  // every check, so writing them here changes the behaviour immediately - no
  // player restart and no reconnect.
  // The chaser reads the ENGINE's config object, not the one handed to
  // createPlayer(): PlayerEngineMainThread does Object.assign onto a fresh
  // default, so our object is only a template. Reach the real one.
  const liveChaserConfig = () => {
    const player = playerRef.current;
    if (!player) return null;
    return player._player_engine?._config || player._config || null;
  };

  const applyLiveBand = (why) => {
    const band = liveSyncBandRef.current;
    if (!band) return;
    const config = liveChaserConfig();
    if (!config) {
      if (!liveBandWarnedRef.current) {
        liveBandWarnedRef.current = true;
        console.warn(
          '[live-r64] adaptive band is inert: the player engine config was not ' +
            'reachable, so latency thresholds cannot be changed at runtime'
        );
      }
      return;
    }
    config.liveBufferLatencyMaxLatency = band.max;
    config.liveBufferLatencyMinRemain = band.target;
    console.info(
      `[live-r64] band ${band.target.toFixed(2)}s/${band.max.toFixed(2)}s ` +
        `(floor ${band.floorTarget}/${band.floorMax}, ceiling ${band.ceilMax.toFixed(2)}) - ${why}`
    );
  };

  const liveDiagBeacon = (event, extra = {}) => {
    try {
      // DISPATCHARR_LIVE_DIAG_OPT_IN_R59: these fetches ride the same HTTP/2
      // connection as the MPEG-TS stream, and `waiting`/`stalled` fire them
      // exactly when playback is already in trouble -- measured in Edge at up to
      // 5.11 s each, with the picture frozen until one completed. Opt in with
      //   localStorage.setItem('dispatcharrLiveDiag', '1')
      try {
        if (!window.localStorage?.getItem('dispatcharrLiveDiag')) return;
      } catch {
        return;
      }
      if (liveDiagCountRef.current >= 25) return;
      liveDiagCountRef.current += 1;
      const video = videoRef.current;
      const now = Date.now();
      const sinceStart = liveStartedAtRef.current
        ? now - liveStartedAtRef.current
        : -1;
      const lastGesture = Math.max(
        liveLastPointerAtRef.current || 0,
        liveLastKeyAtRef.current || 0
      );
      const params = new URLSearchParams({
        diag: 'live',
        ev: String(event),
        n: String(liveDiagCountRef.current),
        ms: String(sinceStart),
        t: video ? (Number(video.currentTime) || 0).toFixed(1) : 'x',
        paused: video ? String(video.paused) : 'x',
        rs: video ? String(video.readyState) : 'x',
        ns: video ? String(video.networkState) : 'x',
        muted: video ? String(video.muted) : 'x',
        hidden: typeof document !== 'undefined' ? String(document.hidden) : 'x',
        gest: String(lastGesture ? now - lastGesture : -1),
        ...extra,
      });
      void fetch(`/logo.png?${params.toString()}`, {
        method: 'GET',
        cache: 'no-store',
        keepalive: true,
        credentials: 'same-origin',
      }).catch(() => undefined);
    } catch {}
  };

  // DISPATCHARR_ARCHIVE_IOS_TAP_TO_PLAY_R71: this tap is a real gesture, so
  // iOS accepts play() here and starts buffering with sound.
  const handleArchiveTapToPlay = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    const video = videoRef.current;
    if (!video) return;
    setArchiveTapToPlay(false);
    setLoadError(null);
    // hls.js/MMS may have parked loading while nothing wanted data.
    try { playerRef.current?.startLoad?.(); } catch {}
    const attempt = video.play();
    attempt?.catch?.((error) => {
      console.debug('[archive-start] tap play rejected:', error);
      if (videoRef.current === video && error?.name === 'NotAllowedError') {
        setArchiveTapToPlay(true);
      }
    });
  };

  const safeDestroyPlayer = () => {
    adaptiveOutputRequestRef.current += 1;
    // DISPATCHARR_CAST_HANDBACK_GUARD_R19: name whoever tears down the Web
    // Live player a Stop Cast handback has just started.
    const handbackAgeMs = Date.now() - castLiveHandbackAtRef.current;
    if (castLiveHandbackAtRef.current && playerRef.current && handbackAgeMs < 20000) {
      console.warn(
        `[chromecast-r19] Web Live player destroyed ${handbackAgeMs}ms after Stop Cast handback`,
        new Error('r19 destroy trace').stack
      );
    }
    try {
      if (playerRef.current) {
        setIsLoading(false);
        setLoadError(null);

        if (videoRef.current) {
          videoRef.current.removeAttribute('src');
          videoRef.current.load();
        }

        try {
          playerRef.current.pause();
        } catch (e) {
          // Ignore pause errors
        }

        try {
          playerRef.current.destroy();
        } catch (error) {
          if (
            error.name !== 'AbortError' &&
            !error.message?.includes('aborted')
          ) {
            console.log('Error during player destruction:', error.message);
          }
        } finally {
          playerRef.current = null;
        }
      }
    } catch (error) {
      console.log('Error during player cleanup:', error);
      playerRef.current = null;
    }

    // Clear overlay timer
    if (overlayTimeoutRef.current) {
      clearTimeout(overlayTimeoutRef.current);
      overlayTimeoutRef.current = null;
    }

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  };

  // Start overlay auto-hide timer
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(any-hover: hover)');
    const onChange = (event) => setCoarsePointer(!event.matches);
    mq.addEventListener?.('change', onChange);

    // One real mouse event proves a hovering device exists no matter what
    // the media query claims; one touch event proves the finger.
    const onPointer = (event) => {
      if (event.pointerType === 'mouse') setCoarsePointer(false);
      else if (event.pointerType === 'touch') setCoarsePointer(true);
    };
    window.addEventListener('pointermove', onPointer, { passive: true });
    window.addEventListener('pointerdown', onPointer, { passive: true });

    return () => {
      mq.removeEventListener?.('change', onChange);
      window.removeEventListener('pointermove', onPointer);
      window.removeEventListener('pointerdown', onPointer);
    };
  }, []);

  // DISPATCHARR_IOS_BADGE_FOLLOWS_NATIVE_CONTROLS_R81
  // WebKit's own rules, from Source/WebCore/Modules/modern-media-controls:
  // AutoHideDelayMS is 4000, controls "fade while idle" ONLY while the video
  // is playing (controls-visibility-support.js gates it on !media.paused),
  // the timer is reset on pointerup rather than pointerdown, and a tap
  // outside the controls toggles them. Our Rewind and Go Live badges live in
  // our own layer, where none of that is observable, so they follow the same
  // rules here instead of a timer of their own - which is why they used to
  // drift out of step with Apple's bar, especially while paused.
  const NATIVE_CONTROLS_AUTO_HIDE_MS = 4000;

  const cancelBadgeIdleTimer = () => {
    if (castPlayerIdleTimerRef.current != null) {
      window.clearTimeout(castPlayerIdleTimerRef.current);
      castPlayerIdleTimerRef.current = null;
    }
  };

  const showBadgesWithNativeControls = ({ fade = true } = {}) => {
    setCastPlayerHover(true);
    setShowControls(true);
    cancelBadgeIdleTimer();
    // Paused controls never fade, so neither do the badges beside them.
    if (!fade || videoRef.current?.paused) return;
    castPlayerIdleTimerRef.current = window.setTimeout(() => {
      castPlayerIdleTimerRef.current = null;
      setCastPlayerHover(false);
    }, NATIVE_CONTROLS_AUTO_HIDE_MS);
  };

  const hideBadgesWithNativeControls = () => {
    cancelBadgeIdleTimer();
    setCastPlayerHover(false);
  };

  const startOverlayTimer = () => {
    if (overlayTimeoutRef.current) {
      clearTimeout(overlayTimeoutRef.current);
    }
    overlayTimeoutRef.current = setTimeout(() => {
      setShowOverlay(false);
    }, 4000); // Hide after 4 seconds
  };

  // Initialize VOD player (native HTML5 with enhanced controls)
  const initializeVODPlayer = (
    forcedStreamUrl = null,
    { archiveCompatibility = false, archiveResumePosition = null } = {}
  ) => {
    const playbackUrl = forcedStreamUrl || streamUrl;
    if (!videoRef.current || !playbackUrl) return;

    setIsLoading(true);
    setLoadError(null);
    setArchiveTapToPlay(false);
    setShowOverlay(false);
    setShowControls(false);

    console.log('Initializing VOD player for:', playbackUrl);

    const video = videoRef.current;

    // Enhanced video element configuration for VOD
    // DISPATCHARR_ARCHIVE_EAGER_PLAY_R8_8_7
    const archiveHlsPlayback = contentType === 'catchup-hls' || archiveCompatibility;
    // DISPATCHARR_LIVE_PROVIDER_TIMESHIFT_SEEK_FULLSCREEN_R5
    const liveTimeshiftHlsPlayback = Boolean(
      contentType === 'catchup-hls' && metadata?.liveTimeshift
    );
    // DISPATCHARR_LIVE_PROVIDER_TIMESHIFT_CAST_HANDBACK_R5_3
    // A normal provider-timeshift open uses the original rewind target from
    // metadata. A Cast -> Web handback carries the current programme position
    // in cast_start_position; that newer value must win over the old metadata.
    const liveTimeshiftNeedsInitialPosition = Boolean(
      liveTimeshiftHlsPlayback &&
        liveTimeshiftInitialPositionConsumedUrlRef.current !== playbackUrl
    );
    const liveTimeshiftHandbackPosition = (() => {
      if (!liveTimeshiftNeedsInitialPosition || !playbackUrl) return null;
      try {
        const raw = new URL(playbackUrl, window.location.origin).searchParams.get(
          'cast_start_position'
        );
        if (raw == null || raw === '') return null;
        const parsed = Number(raw);
        return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
      } catch {
        return null;
      }
    })();
    const metadataInitialPosition = Math.max(
      0,
      Number(metadata?.liveTimeshiftInitialPositionSeconds) || 0
    );
    // DISPATCHARR_ARCHIVE_PTS_ALIGN_R137: a live rewind rebuilt after a real
    // pause (longer than the linger) resumes where it was paused. Its URL --
    // and so the manifest's EXT-X-START -- still names the point the rewind
    // first started at, and Shaka's load-time placement went back there
    // after restoreArchivePosition had put the playhead right: paused at
    // 29:13, resumed at 29:07, with a second producer started at the old
    // point. R130 fixed exactly this for the plain archive; a live rewind
    // gets the same treatment here and in the master URL below.
    const liveTimeshiftResumePosition = (() => {
      const resumeAt = Number(archiveResumePosition);
      return liveTimeshiftHlsPlayback && Number.isFinite(resumeAt) && resumeAt > 0
        ? resumeAt
        : null;
    })();
    const requestedInitialPosition =
      liveTimeshiftResumePosition != null
        ? liveTimeshiftResumePosition
        : liveTimeshiftNeedsInitialPosition
          ? Math.max(
              0,
              liveTimeshiftHandbackPosition == null
                ? metadataInitialPosition
                : liveTimeshiftHandbackPosition
            )
          : 0;
    if (liveTimeshiftNeedsInitialPosition) {
      liveTimeshiftExpectedPositionRef.current = requestedInitialPosition;
      if (liveTimeshiftHandbackPosition != null) {
        console.info(
          `[live-r5.3] Web handback programme target=${requestedInitialPosition.toFixed(2)}s`
        );
      }
    }
    video.preload = archiveHlsPlayback ? 'auto' : 'metadata';
    video.crossOrigin = 'anonymous';

    // Restore saved volume
    const { volume: savedVolume, muted: savedMuted } = getPlayerPrefs();
    if (typeof savedVolume === 'number') video.volume = savedVolume;
    if (typeof savedMuted === 'boolean') video.muted = savedMuted;
    // DISPATCHARR_LIVE_PROVIDER_TIMESHIFT_SEEK_FULLSCREEN_R5
    // Archive still starts at the beginning. Provider-backed Live time-shift
    // starts at the user's selected programme-relative point. Apply it only
    // after a seekable range exists, then mark this exact HLS URL consumed so
    // pause/resume reinitialization cannot snap back to the original target.
    let hasSeekedToStart = false;
    const seekToStart = () => {
      if (hasSeekedToStart) return;
      try {
        let target = 0;
        // Chrome reports the full MediaSource window in video.seekable;
        // Safari reports roughly what is buffered, which made the guard
        // below defer long after the position was actually reachable.
        // Shaka knows the true window from the manifest, so trust that
        // whenever it is the one playing.
        let shakaRange = null;
        try {
          const shakaPlayer = shakaTimeshiftPlayerRef.current;
          const range = shakaPlayer ? shakaPlayer.seekRange() : null;
          if (
            range &&
            Number.isFinite(range.start) &&
            Number.isFinite(range.end) &&
            range.end > range.start
          ) {
            shakaRange = range;
          }
        } catch {
          shakaRange = null;
        }
        const nativeRange = video.seekable && video.seekable.length > 0;
        if (shakaRange || nativeRange) {
          const first = shakaRange
            ? shakaRange.start
            : video.seekable.start(0);
          const last = shakaRange
            ? shakaRange.end
            : video.seekable.end(video.seekable.length - 1);
          const requestedMediaTarget = first + requestedInitialPosition;
          // DISPATCHARR_LIVE_PROVIDER_TIMESHIFT_POSITION_REOPEN_R5_2
          if (
            liveTimeshiftHlsPlayback &&
            requestedInitialPosition > 0 &&
            Number.isFinite(last) &&
            last > first &&
            last + 0.25 < requestedMediaTarget
          ) {
            console.debug(
              `[live-r5.2] initial provider target pending: ` +
                `wanted=${requestedInitialPosition.toFixed(2)}s ` +
                `seekable=${Math.max(0, last - first).toFixed(2)}s`
            );
            return;
          }
          target = requestedMediaTarget;
          if (
            !liveTimeshiftHlsPlayback &&
            Number.isFinite(last) &&
            last > first
          ) {
            target = Math.min(target, Math.max(first, last - 0.05));
          }
        } else if (requestedInitialPosition > 0) {
          // Wait for loadeddata/canplay instead of consuming the target while
          // the browser still exposes an empty seekable range.
          return;
        }
        // DISPATCHARR_LIVE_PROVIDER_TIMESHIFT_POSITION_REOPEN_R5_2
        // DISPATCHARR_LIVE_PROVIDER_TIMESHIFT_CAST_HANDBACK_R5_3
        if (liveTimeshiftNeedsInitialPosition) {
          liveTimeshiftInitialSeekSettledUrlRef.current = null;
          liveTimeshiftExpectedPositionRef.current = requestedInitialPosition;
        }
        if (
          Number.isFinite(target) &&
          Math.abs((video.currentTime || 0) - target) > 0.25
        ) {
          video.currentTime = target;
        }
        hasSeekedToStart = true;
        if (liveTimeshiftHlsPlayback) {
          liveTimeshiftInitialPositionConsumedUrlRef.current = playbackUrl;
          setLiveTimeshiftPlaybackSeconds(
            Math.max(0, requestedInitialPosition)
          );
          // DISPATCHARR_LIVE_PROVIDER_TIMESHIFT_PLAYBACK_FULLSCREEN_R5_6
          const keepProviderTimeshiftPlaying = () => {
            if (
              videoRef.current === video &&
              contentType === 'catchup-hls' &&
              metadata?.liveTimeshift &&
              !video.ended
            ) {
              video.play().catch((error) => {
                console.debug('[live-r5.6] target play retry skipped:', error);
              });
            }
          };
          window.setTimeout(keepProviderTimeshiftPlaying, 0);
          window.setTimeout(keepProviderTimeshiftPlaying, 180);
        }
      } catch {
        // Keep the target pending for the next media readiness event.
      }
    };

    const handleLoadStart = () => setIsLoading(true);
    const handleLoadedMetadata = () => {
      seekToStart();
    };
    const handleLoadedData = () => {
      setIsLoading(false);
      // hls.js applies its `startPosition` after MEDIA_ATTACHED, which can
      // run later than `loadedmetadata`. Re-seek here as a safety net so a
      // hls.js live playlist doesn't snap to the live edge after our first
      // seek attempt happened against an empty seekable range.
      seekToStart();
    };
    const handleCanPlay = () => {
      setIsLoading(false);
      // Final fallback for the Safari native-HLS path where seekable.start(0)
      // is sometimes only valid by the time `canplay` fires.
      seekToStart();
      // Auto-play for VOD content
      video.play().catch((e) => {
        console.log('Auto-play prevented:', e);
        if (contentType === 'catchup-hls') {
          // Native iOS may require one direct Play gesture after the async
          // session + speed test. Keep the already-open player and controls
          // visible instead of making the user press Watch Archive again.
          if (e?.name === 'NotAllowedError') setArchiveTapToPlay(true);
          else setLoadError('Tap Play once to start archive.');
        } else {
          setLoadError('Auto-play was prevented. Click play to start.');
        }
      });
      // Show overlay briefly when video is ready, then auto-hide
      setShowOverlay(true);
      startOverlayTimer();
    };
    const handleError = (e) => {
      setIsLoading(false);

      setLoadError(getVODPlayerErrorMessage(e.target.error));
    };

    // Enhanced progress tracking for VOD
    const handleProgress = () => {
      // A deferred initial seek is waiting for the window to cover the
      // target; more data means it may be reachable now.
      seekToStart();
      // if (video.buffered.length > 0) {
      //   const bufferedEnd = video.buffered.end(video.buffered.length - 1);
      //   const duration = video.duration;
      //   if (duration > 0) {
      //     const bufferedPercent = (bufferedEnd / duration) * 100;
      //     // You could emit this to a store for UI feedback
      //   }
      // }
    };

    // Add event listeners
    video.addEventListener('loadstart', handleLoadStart);
    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('loadeddata', handleLoadedData);
    video.addEventListener('canplay', handleCanPlay);
    video.addEventListener('error', handleError);
    video.addEventListener('progress', handleProgress);

    // Safety net: Safari fires `progress` sparsely and every load event
    // above lands before the window is wide enough, so poll briefly
    // until the deferred target is applied, then stop.
    const seekRetryStartedAt = Date.now();
    const seekRetryTimer = window.setInterval(() => {
      if (hasSeekedToStart || Date.now() - seekRetryStartedAt > 30000) {
        window.clearInterval(seekRetryTimer);
        return;
      }
      seekToStart();
    }, 250);

    // HLS handling: in-progress recordings expose .m3u8 playlists (and ended
    // recordings whose MKV concat hasn't completed yet still serve via /hls/).
    // Native <video src="*.m3u8"> only works on Safari/iOS, and even there it
    // cannot follow a growing playlist with a useful seekable window.  Use
    // hls.js when supported so the user gets a full DVR/timeshift window
    // across all browsers.  The HLS playlist uses #EXT-X-PLAYLIST-TYPE=EVENT-
    // style growth (omit_endlist + hls_list_size 0), so hls.js will treat the
    // entire recorded duration as the seekable range while the recording is
    // still in progress and as a complete VOD once it ends.
    const isHls =
      typeof playbackUrl === 'string' &&
      (playbackUrl.includes('.m3u8') ||
        playbackUrl.includes('/hls/index') ||
        playbackUrl.includes('application/vnd.apple.mpegurl'));
    let hls = null;
    // DISPATCHARR_ARCHIVE_IOS_MMS_TRANSACTIONAL_SEEK_R8
    let archiveMmsSeekTimer = null;
    let archiveMmsSeekGeneration = 0;
    let vodPlayerDisposed = false;
    let archiveStartWatchTimer = null;

    const nativeHlsSupported = Boolean(
      video.canPlayType('application/vnd.apple.mpegurl')
    );
    const ua = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
    const iOSFamily =
      /iPhone|iPad|iPod/i.test(ua) ||
      (typeof navigator !== 'undefined' &&
        navigator.platform === 'MacIntel' &&
        Number(navigator.maxTouchPoints || 0) > 1);
    const desktopSafari =
      /Safari/i.test(ua) &&
      !/Chrome|Chromium|CriOS|Edg|EdgiOS|OPR|FxiOS/i.test(ua);
    // DISPATCHARR_ARCHIVE_IOS_MMS_HLSJS_R8
    // Plain Archive keeps the existing player UI and controls. On iOS, the
    // presence of ManagedMediaSource is the transport gate. Do not let the
    // stricter hls.js baseline codec probe route this experiment back to
    // native AVPlayer before hls.js gets a chance to attach MMS.
    const managedMediaSourceSupported =
      typeof window !== 'undefined' && 'ManagedMediaSource' in window;
    let hlsMseSupported = false;
    let hlsBaselineSupported = false;
    try {
      hlsMseSupported = Boolean(Hls.isMSESupported?.());
    } catch {
      hlsMseSupported = false;
    }
    try {
      hlsBaselineSupported = Boolean(Hls.isSupported());
    } catch {
      hlsBaselineSupported = false;
    }
    const useManagedMediaSourceArchive =
      archiveHlsPlayback &&
      !liveTimeshiftHlsPlayback &&
      iOSFamily &&
      managedMediaSourceSupported;

    // Persist the selected transport on the media element. Archive transport
    // pause/seek lifecycle handlers live in a separate React effect and cannot
    // safely close over this initializeVODPlayer-local variable.
    try {
      video.dataset.dispatcharrArchiveIosMms =
        useManagedMediaSourceArchive ? '1' : '0';
    } catch {
      // Best effort only; playback selection itself does not depend on dataset.
    }

    if (useManagedMediaSourceArchive) {
      try {
        video.disableRemotePlayback = true;
      } catch {
        // Best effort on WebKit variants without this property.
      }
    }

    // DISPATCHARR_LIVE_TIMESHIFT_SAFARI_FORCE_HLSJS
    const preferNativeArchiveHls =
      archiveHlsPlayback &&
      !liveTimeshiftHlsPlayback &&
      nativeHlsSupported &&
      (
        (desktopSafari && !iOSFamily) ||
        (iOSFamily && !useManagedMediaSourceArchive)
      );
    const hlsJsTransportSupported =
      useManagedMediaSourceArchive || hlsBaselineSupported;
    const useHlsJs =
      isHls &&
      hlsJsTransportSupported &&
      (!archiveHlsPlayback || !preferNativeArchiveHls);
    // DISPATCHARR_CATCHUP_HLS_WEB_SHAKA_PLAYER
    const useShakaForArchive =
      useHlsJs &&
      (archiveHlsPlayback || liveTimeshiftHlsPlayback) &&
      !useManagedMediaSourceArchive;


    // DISPATCHARR_CATCHUP_HLS_SHAKA_DISABLE_LEGACY_SEEK_TO_START: this
    // Shaka branch owns the initial seek itself (see the load() call
    // below) -- prevent the older, Hls.js-era seekToStart(), wired to
    // native video events unconditionally, from also firing and
    // fighting over video.currentTime.
    if (useShakaForArchive) {
      hasSeekedToStart = true;
    }

    if (useShakaForArchive) {
      (async () => {
        try {
          shakaLib.polyfill.installAll();
          if (!shakaLib.Player.isBrowserSupported()) {
            throw new Error('Shaka Player is not supported in this browser');
          }
          const player = new shakaLib.Player();
          await player.attach(video);
          shakaTimeshiftPlayerRef.current = player;

          // DISPATCHARR_CATCHUP_HLS_SHAKA_RETRY_PATIENCE: the
          // catchup-hls producer needs several seconds to connect
          // upstream and produce its first segment -- give Shaka's own
          // request retry budget enough patience to ride that out
          // instead of giving up mid-startup.
          // DISPATCHARR_CATCHUP_HLS_SHAKA_RETRY_FASTER_EARLY
          const shakaRetryParameters = {
            maxAttempts: 25,
            baseDelay: 300,
            backoffFactor: 1.2,
            fuzzFactor: 0.5,
            timeout: 20000,
          };
          // DISPATCHARR_CATCHUP_HLS_MASTER_START_AND_NO_CC: Shaka downloads a
          // sample segment (the middle of the playlist) purely to
          // sniff for CEA-608/708 captions this pipeline does not
          // carry, and that fetch restarts the catchup-hls producer
          // on a segment nothing is going to play. The master now
          // declares CLOSED-CAPTIONS=NONE; this is the same
          // suppression from the client side, so neither half can
          // silently regress alone.
          // DISPATCHARR_CATCHUP_HLS_SHAKA_SEQUENCE_MODE: the catchup-hls
          // producer restarts media timestamps from zero on every
          // run while the manifest places that run's first segment
          // at its true programme offset. With sequenceMode off,
          // Shaka recomputes an offset per segment from the parsed
          // media timestamp and applies it through abort() +
          // setTimestampOffset() on each append -- on Firefox that
          // lands the offset twice (measured: playhead 870, buffer
          // 1736, exactly twice segment 434's 868). Sequence mode
          // sets timestampOffset once from the segment reference and
          // lets the SourceBuffer rebase, which is the path this
          // stream shape was designed for and was Shaka's own HLS
          // default for years.
          player.configure({
            streaming: { retryParameters: shakaRetryParameters },
            manifest: {
              retryParameters: shakaRetryParameters,
              hls: {
                disableClosedCaptionsDetection: true,
                sequenceMode: true,
              },
            },
          });

          player.addEventListener('error', (event) => {
            const detail = event?.detail;
            // eslint-disable-next-line no-console
            console.error('Shaka playback error:', detail?.code, detail);
          });

          // DISPATCHARR_ARCHIVE_PTS_ALIGN_R137: the backend marks the
          // fragments of a producer that started on the exact instant
          // (X-HLS-Aligned). The paused-frame calibration learns from those
          // only: on any other start the position is whatever the provider
          // happened to send first, and a reading would be that error.
          try {
            const alignedFor = playbackUrl;
            archiveAlignedFragmentsRef.current = { url: alignedFor, indexes: new Set() };
            player.getNetworkingEngine()?.registerResponseFilter((_type, response) => {
              const headers = response?.headers;
              if (!headers || headers['x-hls-aligned'] !== '1') return;
              const index = Number(headers['x-hls-segment-index']);
              const noted = archiveAlignedFragmentsRef.current;
              if (Number.isFinite(index) && noted.url === alignedFor && noted.indexes.size < 4096) {
                noted.indexes.add(index);
              }
            });
          } catch {
            // without the filter nothing is marked and nothing is learned
          }

          let shakaLoadUrl = playbackUrl;
          if (playbackUrl) {
            try {
              const masterUrl = new URL(playbackUrl, window.location.origin);
              masterUrl.searchParams.set('cast_master', '1');
              // DISPATCHARR_ARCHIVE_RESUME_EXT_X_START_R130: a real archive
              // resume (after the pause linger) tells the backend where it
              // resumes, so the master AND the media playlist carry
              // #EXT-X-START:TIME-OFFSET=P,PRECISE=YES. The web playlist still
              // describes the full programme (no window: the timeline and seek
              // bar stay programme-relative; init.mp4 reads the value
              // read-only), and Shaka's load-time playhead placement now
              // targets P instead of 0 -- no 0:00 flash, no extra producer
              // placement at the programme start (R129's guard stays as a net).
              // R137: a live rewind as well -- its own URL names the point the
              // rewind first started at, which is where a resume used to go.
              const resumeAt = Number(archiveResumePosition);
              if (
                contentType === 'catchup-hls' &&
                Number.isFinite(resumeAt) &&
                resumeAt > 0
              ) {
                masterUrl.searchParams.set(
                  'cast_start_position',
                  resumeAt.toFixed(3)
                );
              }
              shakaLoadUrl = masterUrl.toString();
            } catch {
              shakaLoadUrl = playbackUrl;
            }
          }

          // DISPATCHARR_CATCHUP_HLS_SHAKA_SEEK_AFTER_LOAD: pass no
          // explicit start time to load() -- Shaka's own load-time
          // position resolution for EVENT-type (growing) playlists
          // fights toward the live head first regardless of what is
          // asked for there. Seek via video.currentTime once loaded
          // instead, a real native seek Shaka has no live-edge-
          // seeking special case for.
          if (videoRef.current !== video) return;
          await player.load(shakaLoadUrl);
          if (videoRef.current !== video) {
            try {
              player.destroy();
            } catch {
              // ignore
            }
            return;
          }

          // DISPATCHARR_CATCHUP_HLS_SHAKA_SKIP_REDUNDANT_SEEK: the master now
          // declares #EXT-X-START:TIME-OFFSET, so load() has already
          // placed the playhead. Re-assigning currentTime on top of
          // that is a second seek to where the player already is --
          // Firefox honours it, throws away the in-flight append and
          // re-runs init + first segment, then never starts. Only
          // seek when the playhead really is off target, which now
          // only happens if the manifest carried no start offset.
          // DISPATCHARR_CATCHUP_HLS_SHAKA_DEFER_SEEK_CHECK: read the settled
          // position only once the element actually carries it.
          // Shaka resolves the playhead from the manifest's
          // #EXT-X-START during load(), but propagates it onto the
          // media element only after that element has metadata --
          // reading it straight after load() returns 0 every time,
          // which made the previous drift test always seek.
          const describeBuffered = () => {
            try {
              const ranges = video.buffered;
              const parts = [];
              for (let i = 0; i < ranges.length; i += 1) {
                parts.push(
                  `${ranges.start(i).toFixed(2)}-${ranges.end(i).toFixed(2)}`
                );
              }
              return parts.join(' | ') || 'none';
            } catch {
              return 'unavailable';
            }
          };
          const settleInitialPosition = () => {
            if (videoRef.current !== video) return;
            const settledPosition = Number(video.currentTime) || 0;
            const positionDrift = Math.abs(
              settledPosition - requestedInitialPosition
            );
            // One segment: anywhere inside the target segment is the
            // same fetch, so a closer seek buys nothing, and seeking
            // anyway costs the in-flight append.
            if (positionDrift > 2) {
              // eslint-disable-next-line no-console
              console.info(
                '[catchup-hls-shaka] manifest start NOT honoured at',
                settledPosition,
                '-- seeking to',
                requestedInitialPosition
              );
              video.currentTime = requestedInitialPosition;
            } else {
              // eslint-disable-next-line no-console
              console.info(
                '[catchup-hls-shaka] manifest start honoured at',
                settledPosition,
                'target',
                requestedInitialPosition,
                '-- redundant seek skipped'
              );
            }
            window.setTimeout(() => {
              if (videoRef.current !== video) return;
              let seekRange = 'unavailable';
              try {
                const range = player.seekRange();
                seekRange =
                  `${range.start.toFixed(2)}-${range.end.toFixed(2)}`;
              } catch {
                // ignore
              }
              // eslint-disable-next-line no-console
              console.info(
                '[catchup-hls-shaka] playback state currentTime=' +
                  `${Number(video.currentTime).toFixed(2)} ` +
                  `readyState=${video.readyState} ` +
                  `paused=${video.paused} ` +
                  `duration=${Number(video.duration).toFixed(2)} ` +
                  `seekRange=${seekRange} ` +
                  `buffered=${describeBuffered()}`
              );
            }, 8000);
          };
          if (requestedInitialPosition > 0) {
            if (video.readyState >= 1) {
              settleInitialPosition();
            } else {
              video.addEventListener(
                'loadedmetadata',
                settleInitialPosition,
                { once: true }
              );
            }
          }

          playerRef.current = {
            startLoad: () => {
              try {
                player.retryStreaming?.();
              } catch {
                // ignore
              }
            },
            stopLoad: () => {},
            destroy: () => {
              video.removeEventListener('loadstart', handleLoadStart);
              video.removeEventListener('loadedmetadata', handleLoadedMetadata);
              video.removeEventListener('loadeddata', handleLoadedData);
              video.removeEventListener('canplay', handleCanPlay);
              video.removeEventListener('error', handleError);
              video.removeEventListener('progress', handleProgress);
              window.clearInterval(seekRetryTimer);
              try {
                player.destroy();
              } catch {
                // ignore
              }
              video.removeAttribute('src');
              video.load();
            },
          };

          if (archiveHlsPlayback) {
            video.play().catch(() => {});
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Shaka Player setup failed:', error);
          setLoadError(
            `Playback error: ${error?.message || 'Shaka Player failed to start'}`
          );
        }
      })();
    } else if (useHlsJs) {
      try {
        // DISPATCHARR_LIVE_TIMESHIFT_MASTER_PLAYLIST_CODECS: Hls.js
        // guesses codec info by probing the muxed init segment when a
        // bare media playlist carries no CODECS attribute -- wrong often
        // enough on Firefox specifically to fail outright. Point it at the
        // small master-playlist form instead, which carries an explicit,
        // correctly-formed CODECS attribute and auto-follows back to the
        // same media playlist.
        let hlsLoadUrl = playbackUrl;
        if (
          (liveTimeshiftHlsPlayback || useManagedMediaSourceArchive) &&
          playbackUrl
        ) {
          try {
            const masterUrl = new URL(playbackUrl, window.location.origin);
            masterUrl.searchParams.set('cast_master', '1');
            if (useManagedMediaSourceArchive) {
              masterUrl.searchParams.set('ios_mms', '1');
            }
            hlsLoadUrl = masterUrl.toString();
          } catch {
            hlsLoadUrl = playbackUrl;
          }
        }
        hls = new Hls({
          // DISPATCHARR_ARCHIVE_IOS_MMS_HLSJS_R8
          preferManagedMediaSource: useManagedMediaSourceArchive,
          // Open at the very beginning of the recording rather than the live
          // edge.  Without this, an in-progress recording would start at "now"
          // and hide everything already recorded.  hls.js applies this AFTER
          // MEDIA_ATTACHED, so the listener-driven `seekToStart()` above is
          // also kept as a safety net for the Safari native-HLS path and for
          // edge cases where this initial-position logic loses to the user's
          // first interaction.
          startPosition: requestedInitialPosition,
          // Allow seeking back to the start of the recording, regardless of
          // current playhead position.  Recordings can be hours long and the
          // user may want to scrub anywhere; we explicitly disable buffer
          // eviction by setting a very large back-buffer length.
          backBufferLength: 90 * 60, // 90 minutes
          maxBufferLength: useManagedMediaSourceArchive
            ? 8
            : (archiveHlsPlayback ? 20 : 60),
          maxMaxBufferLength: useManagedMediaSourceArchive
            ? 12
            : (archiveHlsPlayback ? 60 : 600),
          // Leave liveMaxLatencyDurationCount at the hls.js default (Infinity).
          // A finite value forces the playhead to the live edge during playback.
          enableWorker: true,
          // Finite archive HLS should start loading immediately.
          startFragPrefetch: archiveHlsPlayback,
          // DISPATCHARR_LIVE_PROVIDER_TIMESHIFT_WORKER_SAFETY_R5_7
          ...(liveTimeshiftHlsPlayback
            ? {
                // DISPATCHARR_LIVE_TIMESHIFT_HLSJS_TIGHTER_FRAG_RETRY: 20
                // retries with this backoff formula summed to a ~40-47s
                // worst case before Hls.js gave up on one fragment --
                // confirmed live (server logs) to be enough time for the
                // catchup-hls producer's own spool retention to prune the
                // exact segment a stuck client was still waiting on,
                // producing an infinite loop of 503s on a segment that
                // will never come back. 6 retries bounds the same
                // formula to ~6.25s.
                fragLoadingMaxRetry: 6,
                fragLoadingRetryDelay: 250,
                fragLoadingMaxRetryTimeout: 1500,
                manifestLoadingMaxRetry: 8,
                manifestLoadingRetryDelay: 250,
              }
            : {}),
          lowLatencyMode: false,
          // Inject the JWT into every playlist + segment XHR.  Read the token
          // from the auth store at request time rather than capturing the
          // closure value at hls.js init, so a refreshed access token mid-
          // playback is picked up on the next segment fetch.
          xhrSetup: (xhr) => {
            const token = useAuthStore.getState().accessToken;
            if (token) {
              xhr.setRequestHeader('Authorization', `Bearer ${token}`);
            }
          },
        });
        // DISPATCHARR_LIVE_TIMESHIFT_NONFATAL_FRAG_ERROR_RECOVERY: Hls.js
        // exhausting fragLoadingMaxRetry on the very first requested
        // fragment doesn't reliably escalate to a fatal error -- confirmed
        // live (a single 503, then nothing else, ever; no fatal log, no
        // recovery, playback stuck on "Loading video..." forever).
        let nonFatalFragRetryCount = 0;
        // DISPATCHARR_LIVE_TIMESHIFT_DEBOUNCE_FRAG_RECOVERY: hls.js's
        // own error bursts can fire far faster than the backend can spin
        // up a producer -- without a minimum spacing, repeated
        // hls.startLoad() calls were very likely interrupting the
        // producer-start sequence before it could ever finish (confirmed
        // live: multiple init.mp4 requests landing in the same second,
        // producer stop log reading "processes=0" -- it never launched).
        let lastNonFatalRecoveryAt = 0;
        hls.on(Hls.Events.ERROR, (_evt, data) => {
          if (data.fatal) {
            // eslint-disable-next-line no-console
            console.error('HLS fatal error:', data.type, data.details);
            if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
              try {
                hls.startLoad();
              } catch {
                // ignore
              }
            } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
              try {
                hls.recoverMediaError();
              } catch {
                // ignore
              }
            } else {
              setLoadError(`HLS playback error: ${data.details || data.type}`);
            }
            return;
          }
          if (
            liveTimeshiftHlsPlayback &&
            nonFatalFragRetryCount < 5 &&
            (data.details === Hls.ErrorDetails.FRAG_LOAD_ERROR ||
              data.details === Hls.ErrorDetails.FRAG_LOAD_TIMEOUT)
          ) {
            const nowMs = Date.now();
            if (nowMs - lastNonFatalRecoveryAt < 3000) {
              // Still inside the cooldown from the last recovery attempt --
              // let the backend keep working instead of interrupting it again.
              return;
            }
            lastNonFatalRecoveryAt = nowMs;
            nonFatalFragRetryCount += 1;
            console.warn(
              '[live-r5.7] non-fatal frag error during rewind startup, ' +
                `forcing reload (${nonFatalFragRetryCount}/5):`,
              data.details
            );
            try {
              hls.startLoad();
            } catch {
              // ignore
            }
            // DISPATCHARR_LIVE_TIMESHIFT_REPLAY_AFTER_FRAG_RECOVERY: the
            // two seekToStart() play attempts already spent themselves
            // before this recovery ever ran -- nothing else was going to
            // call play() again once the reload actually had data.
            window.setTimeout(() => {
              if (
                videoRef.current === video &&
                contentType === 'catchup-hls' &&
                metadata?.liveTimeshift &&
                !video.ended
              ) {
                video.play().catch((error) => {
                  console.debug(
                    '[live-r5.7] post-recovery play retry skipped:',
                    error
                  );
                });
              }
            }, 800);
          }
        });
        hls.attachMedia(video);
        hls.on(Hls.Events.MEDIA_ATTACHED, () => {
          hls.loadSource(hlsLoadUrl);
        });
        if (archiveHlsPlayback) {
          hls.on(Hls.Events.MANIFEST_PARSED, () => {
            // Eagerly request playback as soon as hls.js has a manifest. The
            // normal canplay handler remains the fallback for autoplay policy.
            // DISPATCHARR_ARCHIVE_IOS_TAP_TO_PLAY_R71: ManagedMediaSource only
            // feeds an element that wants data, so a refused play() can leave
            // nothing buffering and canplay never arrives. Ask for the one tap
            // here rather than waiting for a fallback that cannot fire.
            const archiveStart = video.play();
            archiveStart?.catch?.((error) => {
              if (vodPlayerDisposed || videoRef.current !== video) return;
              if (error?.name !== 'NotAllowedError') {
                console.debug('[archive-start] play() rejected:', error);
                return;
              }
              console.info(
                '[archive-start] browser blocked autoplay; waiting for one tap'
              );
              setArchiveTapToPlay(true);
            });
          });
        }
      } catch (error) {
        setIsLoading(false);
        setLoadError(`HLS initialization error: ${error.message}`);
        return;
      }
    } else if (isHls && nativeHlsSupported) {
      // Safari path: native HLS support, including seekable DVR windows.
      video.src = withRecordingAuthToken(playbackUrl);
      video.load();
      if (archiveHlsPlayback) {
        // DISPATCHARR_ARCHIVE_IOS_TAP_TO_PLAY_R71
        // iOS buffers an audible video only after play(), and honours play()
        // only inside a tap. The tap on Watch Archive is spent by the time we
        // get here (session + speed test + render ran in between), so iOS can
        // refuse. canplay then never fires, and "Loading video..." would sit
        // over the player for good, swallowing taps meant for its Play button.
        // A <video> that already played from a tap is exempt, which is why only
        // some opens got stuck: a freshly opened player, not a reused one.
        const archiveStart = video.play();
        archiveStart?.catch?.((error) => {
          if (vodPlayerDisposed || videoRef.current !== video) return;
          if (error?.name !== 'NotAllowedError') {
            console.debug('[archive-start] play() rejected:', error);
            return;
          }
          console.info('[archive-start] browser blocked autoplay; waiting for one tap');
          setArchiveTapToPlay(true);
        });
        archiveStartWatchTimer = window.setTimeout(() => {
          archiveStartWatchTimer = null;
          if (vodPlayerDisposed || videoRef.current !== video) return;
          if (video.currentTime > 0 || video.readyState >= 3) return;
          console.info(
            `[archive-start] no media after 10s: readyState=${video.readyState} ` +
              `networkState=${video.networkState} paused=${video.paused} ` +
              `error=${video.error?.code ?? '-'}`
          );
        }, 10000);
      }
    } else {
      // Plain progressive file (MKV/MP4): native HTML5.
      video.src = withRecordingAuthToken(playbackUrl);
      video.load();
    }

    // Store cleanup function
    playerRef.current = {
      // DISPATCHARR_LIVE_PROVIDER_TIMESHIFT_PLAYBACK_FULLSCREEN_R5_6
      startLoad: () => {
        try { hls?.startLoad?.(-1); } catch {}
      },
      stopLoad: () => {
        try { hls?.stopLoad?.(); } catch {}
      },
      // DISPATCHARR_ARCHIVE_IOS_MMS_TRANSACTIONAL_SEEK_R8
      queueArchiveMmsSeek: (position) => {
        if (!useManagedMediaSourceArchive || !hls) return false;

        const target = Math.max(
          0,
          Math.min(
            Number.isFinite(Number(video.duration)) && Number(video.duration) > 0
              ? Math.max(0, Number(video.duration) - 0.05)
              : Number.MAX_SAFE_INTEGER,
            Number(position) || 0
          )
        );

        archiveMmsSeekGeneration += 1;
        const generation = archiveMmsSeekGeneration;

        if (archiveMmsSeekTimer != null) {
          window.clearTimeout(archiveMmsSeekTimer);
          archiveMmsSeekTimer = null;
        }

        // Stop immediately so intermediate native scrub positions cannot
        // continue spawning fragment GETs while the thumb is moving.
        try { hls.stopLoad(); } catch {}

        archiveMmsSeekTimer = window.setTimeout(async () => {
          archiveMmsSeekTimer = null;
          if (
            generation !== archiveMmsSeekGeneration ||
            videoRef.current !== video
          ) {
            return;
          }

          // DISPATCHARR_ARCHIVE_SEEK_WARMUP_R75: from here the committed seek
          // owns the producer. The currentTime we set below fires `seeking`
          // again, and warming on that would drag the producer back behind
          // the position playback is about to resume from.
          archiveSeekCommittingRef.current = true;
          archiveWarmPositionRef.current = target;

          let resumeSourceUrl = playbackUrl;
          try {
            const resumeSource = new URL(
              playbackUrl,
              window.location.origin
            );
            // DISPATCHARR_ARCHIVE_IOS_MMS_SEEK_PREWARM_R8
            // Backend /resume becomes the single seek transaction owner and
            // waits for the target fragment plus one runway fragment.
            resumeSource.searchParams.set('ios_mms_seek', '1');
            resumeSourceUrl = resumeSource.toString();
          } catch {
            // Keep the ordinary resume URL as a fallback.
          }

          const resumeStartedAt = performance.now();
          const resumed = await requestArchiveResume(
            resumeSourceUrl,
            target
          );
          if (
            generation !== archiveMmsSeekGeneration ||
            videoRef.current !== video
          ) {
            return;
          }

          const resumeWaitMs = Math.max(
            0,
            Math.round(performance.now() - resumeStartedAt)
          );
          if (!resumed) {
            console.warn(
              '[archive-ios-mms-seek] backend resume/prewarm failed at ' +
                target.toFixed(3) + 's after ' + resumeWaitMs +
                'ms; continuing with hls.js fallback'
            );
          }

          try {
            if (
              !Number.isFinite(Number(video.currentTime)) ||
              Math.abs(Number(video.currentTime) - target) > 0.25
            ) {
              video.currentTime = target;
            }
          } catch {}

          try { hls.startLoad(target); } catch {}
          video.play().catch(() => {});
          // DISPATCHARR_ARCHIVE_SEEK_WARMUP_R75: the `seeking` events this
          // commit produces are ours, not the user's. Hand warming back once
          // they have passed.
          window.setTimeout(() => {
            archiveSeekCommittingRef.current = false;
          }, 1200);
        }, 150);

        return true;
      },
      destroy: () => {
        vodPlayerDisposed = true;
        if (archiveStartWatchTimer != null) {
          window.clearTimeout(archiveStartWatchTimer);
          archiveStartWatchTimer = null;
        }
        setArchiveTapToPlay(false);
        video.removeEventListener('loadstart', handleLoadStart);
        video.removeEventListener('loadedmetadata', handleLoadedMetadata);
        video.removeEventListener('loadeddata', handleLoadedData);
        video.removeEventListener('canplay', handleCanPlay);
        video.removeEventListener('error', handleError);
        video.removeEventListener('progress', handleProgress);
        window.clearInterval(seekRetryTimer);
        if (archiveMmsSeekTimer != null) {
          window.clearTimeout(archiveMmsSeekTimer);
          archiveMmsSeekTimer = null;
        }
        archiveMmsSeekGeneration += 1;
        if (hls) {
          try {
            hls.destroy();
          } catch {
            // ignore
          }
        }
        if (useManagedMediaSourceArchive) {
          try {
            video.disableRemotePlayback = false;
          } catch {
            // ignore
          }
        }
        try {
          delete video.dataset.dispatcharrArchiveIosMms;
          delete video.dataset.dispatcharrArchiveIosMmsSeekGraceUntil;
        } catch {
          // ignore
        }
        video.removeAttribute('src');
        video.load();
      },
    };
  };

  const startArchiveCompatibilityFallback = async (sourceUrl, reason) => {
    const channelId = archiveChannelIdFromDirectUrl(sourceUrl);
    let sessionId = null;
    try {
      sessionId = new URL(sourceUrl, window.location.origin).searchParams.get(
        'session_id'
      );
    } catch {}
    if (!channelId || !sessionId) {
      throw new Error('Archive compatibility session is unavailable');
    }

    const existing = archiveCompatibilityControllerRef.current;
    if (existing?.active) {
      await existing.stop();
    }
    const controller = new ArchiveCastController();
    archiveCompatibilityControllerRef.current = controller;
    archiveCompatibilityStartingRef.current = true;
    archiveCompatibilityPlaybackRef.current = false;
    console.warn(
      `[archive-compat] Direct MSE ${reason}; starting isolated HLS remux`
    );
    let payload;
    try {
      payload = await controller.start({
        channelId,
        sessionId,
        position: Math.max(0, Number(archivePositionSeconds) || 0),
        loadGeneration: async () => {},
      });
      if (archiveCompatibilityControllerRef.current !== controller) {
        await controller.stop();
        return;
      }
    } finally {
      archiveCompatibilityStartingRef.current = false;
    }
    archiveCompatibilityPlaybackRef.current = true;
    archiveSegmentStartMediaTimeRef.current = 0;
    setArchiveOffsetSeconds(Math.floor(controller.basePosition));
    setArchivePositionSeconds(controller.basePosition);
    setArchivePaused(false);
    initializeVODPlayer(controller.mediaUrl(payload), {
      archiveCompatibility: true,
    });
  };

  // DISPATCHARR_LIVE_FMP4_R3
  // Web Live and Chromecast consume the same adaptive fMP4 HLS files. The
  // ordinary /stream request is opened only long enough to start the existing
  // OutputProfile producer; once init.mp4 + the first m4s exist, the browser
  // consumes HLS and the renewable session lease owns producer lifetime.
  const buildLiveWebFmp4Target = async (preferredProfile = null) => {
    if (!streamUrl) throw new Error('Live source URL is unavailable');
    let channelId = null;
    try {
      const parsed = new URL(streamUrl, window.location.origin);
      const marker = '/proxy/ts/stream/';
      const at = parsed.pathname.indexOf(marker);
      if (at >= 0) {
        channelId = parsed.pathname.slice(at + marker.length).split('/')[0] || null;
      }
    } catch {}
    if (!channelId) throw new Error('Live channel id is unavailable');

    const measurement = preferredProfile
      ? { profile: preferredProfile }
      : await measureAdaptiveOutputProfile();
    const profile = String(measurement?.profile || '2.5-3.5m');
    const uuid =
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
    const webSession = `web-${uuid}`;
    const base =
      `/proxy/ts/cast-hls/${encodeURIComponent(channelId)}/` +
      `${encodeURIComponent(profile)}`;
    const withSession = (leaf) => {
      const url = new URL(`${base}/${leaf}`, window.location.origin);
      url.searchParams.set('cast_session', webSession);
      return url.toString();
    };

    const bootstrap = new URL(streamUrl, window.location.origin);
    bootstrap.searchParams.set('output_format', 'mpegts');
    bootstrap.searchParams.set('adaptive_output_profile', profile);
    bootstrap.searchParams.set('adaptive_delivery', 'transcode');
    bootstrap.searchParams.delete('output_profile');
    bootstrap.searchParams.delete('adaptive_video_copy');

    return {
      sourceUrl: streamUrl,
      channelId,
      profile,
      webSession,
      mediaUrl: withSession('index.m3u8'),
      masterUrl: withSession('master.m3u8'),
      renewUrl: withSession('renew'),
      stopUrl: withSession('stop'),
      bootstrapUrl: bootstrap.toString(),
      leaseTimer: null,
      bootstrapController: null,
      bootstrapReader: null,
    };
  };

  const renewLiveWebFmp4Lease = async (target) => {
    // DISPATCHARR_STATS_LIVE_CLIENT_DETAILS_R2
    const token = useAuthStore.getState().accessToken;
    const response = await fetch(target.renewUrl, {
      method: 'POST',
      cache: 'no-store',
      credentials: 'same-origin',
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });
    if (!response.ok) {
      throw new Error(`Live fMP4 lease failed: HTTP ${response.status}`);
    }
    return true;
  };

  const stopLiveWebFmp4Session = async (
    { release = true, targetOverride = null } = {}
  ) => {
    const current = targetOverride || liveWebFmp4SessionRef.current;
    if (!current) return true;
    if (liveWebFmp4SessionRef.current === current) {
      liveWebFmp4SessionRef.current = null;
    }
    if (current.leaseTimer != null) {
      window.clearInterval(current.leaseTimer);
      current.leaseTimer = null;
    }
    try { current.bootstrapController?.abort?.(); } catch {}
    try { await current.bootstrapReader?.cancel?.(); } catch {}
    current.bootstrapController = null;
    current.bootstrapReader = null;
    if (!release) return true;
    try {
      const response = await fetch(current.stopUrl, {
        method: 'POST',
        keepalive: true,
        cache: 'no-store',
        credentials: 'same-origin',
      });
      return response.ok;
    } catch {
      return false;
    }
  };


  const startLiveWebFmp4Lease = async (target) => {
    await renewLiveWebFmp4Lease(target);
    target.leaseTimer = window.setInterval(() => {
      void renewLiveWebFmp4Lease(target).catch((error) =>
        console.debug('[live-cmaf-r3] Web lease renew skipped:', error)
      );
    }, 30000);
  };

  const startLiveWebFmp4Bootstrap = async (target, maxAttempts = 6) => {
    const controller = new AbortController();
    target.bootstrapController = controller;
    const token = useAuthStore.getState().accessToken;
    const headers = {
      'X-Dispatcharr-Cast-Bootstrap': '1',
      'X-Dispatcharr-Cast-Session': String(target.webSession || ''),
    };
    if (token) headers.Authorization = `Bearer ${token}`;

    let lastStatus = 0;
    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
      let response;
      try {
        response = await fetch(target.bootstrapUrl, {
          method: 'GET',
          cache: 'no-store',
          credentials: 'same-origin',
          headers,
          signal: controller.signal,
        });
      } catch (error) {
        controller.abort();
        throw error;
      }

      lastStatus = response.status;
      if (response.ok) {
        const reader = response.body?.getReader?.() || null;
        target.bootstrapReader = reader;
        if (reader) {
          void (async () => {
            try {
              while (true) {
                const { done } = await reader.read();
                if (done) break;
              }
            } catch {}
          })();
        }
        return controller;
      }

      try { await response.body?.cancel?.(); } catch {}
      if (response.status !== 503 || attempt >= maxAttempts) {
        controller.abort();
        throw new Error(
          `Live fMP4 producer bootstrap failed: HTTP ${response.status}`
        );
      }

      const retryAfterSeconds =
        Number(response.headers.get('Retry-After')) || 1;
      const delayMs = Math.max(
        250,
        Math.min(1500, retryAfterSeconds * 1000)
      );
      console.debug(
        `[live-cmaf-r3] bootstrap HTTP 503; retry ${attempt}/${maxAttempts} ` +
          `in ${delayMs}ms`
      );
      await new Promise((resolve) => window.setTimeout(resolve, delayMs));
      if (controller.signal.aborted) {
        throw new Error('Live fMP4 bootstrap was aborted');
      }
    }

    controller.abort();
    throw new Error(
      `Live fMP4 producer bootstrap failed: HTTP ${lastStatus || 'n/a'}`
    );
  };


  const waitForLiveWebFmp4Manifest = async (target, timeoutMs = 20000) => {
    const deadline = Date.now() + timeoutMs;
    let lastStatus = 0;
    while (Date.now() < deadline) {
      if (liveWebFmp4SessionRef.current !== target) {
        throw new Error('Live fMP4 startup was superseded by another session');
      }
      try {
        const probe = new URL(target.mediaUrl, window.location.origin);
        probe.searchParams.set('web_probe', String(Date.now()));
        const response = await fetch(probe.toString(), {
          cache: 'no-store',
          credentials: 'same-origin',
        });
        lastStatus = response.status;

        if (response.status === 410) {
          // Exact sender membership can disappear while an older same-channel
          // session is being retired. Only the still-current Web target may
          // renew itself; superseded targets are fenced above.
          await renewLiveWebFmp4Lease(target);
          await new Promise((resolve) => window.setTimeout(resolve, 120));
          continue;
        }

        if (response.ok) {
          const body = await response.text();
          if (
            body.includes('#EXT-X-MAP:') &&
            body.includes('init.mp4') &&
            body.includes('.m4s')
          ) {
            return body;
          }
        }
      } catch (error) {
        if (liveWebFmp4SessionRef.current !== target) throw error;
      }
      await new Promise((resolve) => window.setTimeout(resolve, 120));
    }
    throw new Error(
      `Live fMP4 manifest timed out (last HTTP ${lastStatus || 'n/a'})`
    );
  };


  const attachLiveWebFmp4Player = async (target) => {
    const video = videoRef.current;
    if (!video) throw new Error('Live video element is unavailable');

    safeDestroyPlayer();
    setIsLoading(true);
    setLoadError(null);
    setShowControls(false);
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    video.preload = 'auto';
    video.crossOrigin = 'anonymous';

    const { volume: savedVolume, muted: savedMuted } = getPlayerPrefs();
    if (typeof savedVolume === 'number') video.volume = savedVolume;
    if (typeof savedMuted === 'boolean') video.muted = savedMuted;

    let bootstrapReleased = false;
    const releaseBootstrap = () => {
      if (bootstrapReleased) return;
      bootstrapReleased = true;
      try { target.bootstrapController?.abort?.(); } catch {}
      target.bootstrapController = null;
      target.bootstrapReader = null;
      console.info('[live-cmaf-r3] Web switched from bootstrap pipe to fMP4 HLS');
    };

    const nativeHls = Boolean(video.canPlayType('application/vnd.apple.mpegurl'));
    const ua = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
    const safariFamily =
      /Safari/i.test(ua) && !/Chrome|Chromium|CriOS|Edg|EdgiOS|OPR|FxiOS/i.test(ua);
    // DISPATCHARR_LIVE_IOS_NATIVE_HLS_R24: this used to pick the player by
    // BRAND (UA says "Safari") instead of by ENGINE. Every browser on iOS is
    // WebKit and plays HLS natively, so Chrome/Edge/Firefox for iOS were sent
    // down the Hls.js/MSE path while Safari got the native one -- which is
    // exactly why Live stepped frames like a paused video on iOS Chrome and
    // was fine on iOS Safari. Native HLS lets WebKit own the live edge, so it
    // stays as close to live as Safari already was: no added latency, on any
    // platform. Desktop Chrome/Firefox report no native HLS, so they keep
    // Hls.js unchanged.
    const preferNative = nativeHls && (safariFamily || isWebKitMobile());

    if (Hls.isSupported() && !preferNative) {
      // DISPATCHARR_LIVE_IOS_NATIVE_HLS_R24: R23 bought iOS a bigger buffer
      // (and several seconds of latency) to survive Hls.js at the live edge.
      // Routing iOS to native HLS removes the need entirely, so the tight
      // live sync is restored for everyone who really uses Hls.js.
      const mobileLive = false;
      const hls = new Hls({
        enableWorker: true,
        lowLatencyMode: false,
        startFragPrefetch: true,
        // DISPATCHARR_LIVE_HLSJS_TIGHTER_LIVE_SYNC: 1s
        // fMP4 segments (hls_time=1, confirmed server-side) make the old
        // liveSyncDurationCount: 3 worth up to ~3s of manifest content
        // Hls.js waited to see published before starting -- the
        // best-evidenced source of the reported startup buffering, on
        // top of the encoder's own ~700ms startup transient. 1 is the
        // most aggressive value Hls.js accepts; liveMaxLatencyDurationCount
        // scaled down with it so catch-up-seek still triggers at a
        // sensible multiple of the new target.
        liveSyncDurationCount: mobileLive ? 6 : 1,
        liveMaxLatencyDurationCount: mobileLive ? 20 : 4,
        maxBufferLength: mobileLive ? 30 : 15,
        maxMaxBufferLength: mobileLive ? 60 : 30,
        backBufferLength: mobileLive ? 15 : 8,
        xhrSetup: (xhr) => {
          const token = useAuthStore.getState().accessToken;
          if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        },
      });
      hls.on(Hls.Events.ERROR, (_evt, data) => {
        if (!data.fatal) return;
        console.error('[live-cmaf-r3] HLS fatal error:', data.type, data.details);
        if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
          try { hls.startLoad(); } catch {}
          return;
        }
        if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
          try { hls.recoverMediaError(); } catch {}
          return;
        }
        setIsLoading(false);
        setLoadError(`Live fMP4 HLS error: ${data.details || data.type}`);
      });
      hls.on(Hls.Events.MEDIA_ATTACHED, () => hls.loadSource(target.mediaUrl));
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        setIsLoading(false);
        // DISPATCHARR_LIVE_MOBILE_LIVE_SYNC_R23
        liveDiagBeacon('manifest', { eng: 'mse', sync: String(mobileLive ? 6 : 1) });
        video.play().catch((error) =>
          setLoadError(`Auto-play was prevented: ${error?.message || error}`)
        );
      });
      hls.on(Hls.Events.FRAG_BUFFERED, () => releaseBootstrap());
      hls.attachMedia(video);
      playerRef.current = {
        pause: () => video.pause(),
        destroy: () => {
          try { hls.destroy(); } catch {}
          video.removeAttribute('src');
          video.load();
        },
      };
    } else if (nativeHls) {
      const ready = () => {
        setIsLoading(false);
        releaseBootstrap();
        // DISPATCHARR_LIVE_IOS_NATIVE_HLS_R24
        liveDiagBeacon('native-ready', { eng: 'native' });
        video.play().catch((error) =>
          setLoadError(`Auto-play was prevented: ${error?.message || error}`)
        );
      };
      video.addEventListener('canplay', ready, { once: true });
      video.src = target.mediaUrl;
      video.load();
      playerRef.current = {
        pause: () => video.pause(),
        destroy: () => {
          video.removeEventListener('canplay', ready);
          video.removeAttribute('src');
          video.load();
        },
      };
    } else {
      throw new Error('This browser cannot play HLS/fMP4 Live video');
    }
  };

  // DISPATCHARR_LIVE_PROVIDER_TIMESHIFT_POSITION_REOPEN_R5_2
  // DISPATCHARR_LIVE_REOPEN_TIMEOUT_MATCH_SERVER_TTL
  const waitForLiveFmp4ReopenReady = async (timeoutMs = 30000) => {
    const reopenUrl = buildDispatcharrLiveReopenUrl();
    if (!reopenUrl) return true;
    const deadline = Date.now() + timeoutMs;
    let lastStatus = 0;
    let lastTtl = null;

    while (Date.now() < deadline) {
      try {
        const response = await fetch(reopenUrl, {
          method: 'POST',
          cache: 'no-store',
          credentials: 'same-origin',
        });
        lastStatus = response.status;
        lastTtl = response.headers.get('X-Dispatcharr-Live-Reopen-Stop-TTL');
        noteLiveRewindFloor(response); // DISPATCHARR_ARCHIVE_PTS_ALIGN_R137
        const ready =
          response.status === 204 &&
          response.headers.get('X-Dispatcharr-Live-Reopen-Ready') !== '0';
        if (ready) {
          console.info(
            `[live-cmaf-r5.2] Live reopen ready HTTP ${response.status} ttl=${lastTtl ?? 'n/a'}`
          );
          return true;
        }
        if (response.status !== 202 && response.status !== 503) {
          throw new Error(`unexpected HTTP ${response.status}`);
        }
      } catch (error) {
        console.debug('[live-cmaf-r5.2] Live reopen poll:', error);
      }
      await new Promise((resolve) => window.setTimeout(resolve, 200));
    }

    throw new Error(
      `Live channel teardown did not finish before restart ` +
        `(last HTTP ${lastStatus || 'n/a'}, ttl=${lastTtl ?? 'n/a'})`
    );
  };

  const initializeLiveFmp4HlsPlayer = async (
    { preferredProfile = null, reuseExisting = true } = {}
  ) => {
    if (!videoRef.current || !streamUrl) return;
    const startId = ++liveWebFmp4StartIdRef.current;
    liveWebFmp4TransitionRef.current = true;
    let target = null;
    let previous = null;

    try {
      // DISPATCHARR_LIVE_PROVIDER_TIMESHIFT_POSITION_REOPEN_R5_2
      await waitForLiveFmp4ReopenReady();
      target = liveWebFmp4SessionRef.current;
      const canReuse = Boolean(
        reuseExisting &&
          target &&
          target.sourceUrl === streamUrl &&
          (!preferredProfile || target.profile === preferredProfile)
      );

      if (!canReuse) {
        previous = target;
        const nextTarget = await buildLiveWebFmp4Target(preferredProfile);
        const sameChannelHandover = Boolean(
          previous &&
            previous.channelId &&
            previous.channelId === nextTarget.channelId
        );

        // On a same-channel restart, keep the old sender lease until the new
        // exact session has acquired its lease AND opened the bootstrap pipe.
        // This removes the zero-sender window that used to stop the profile/
        // channel underneath burn-in startup. Different-channel navigation
        // still releases the old session first.
        if (previous && !sameChannelHandover) {
          await stopLiveWebFmp4Session({ targetOverride: previous });
          previous = null;
        }

        await startLiveWebFmp4Lease(nextTarget);
        liveWebFmp4SessionRef.current = nextTarget;
        target = nextTarget;

        try {
          await startLiveWebFmp4Bootstrap(target);
        } catch (error) {
          if (liveWebFmp4SessionRef.current === target) {
            liveWebFmp4SessionRef.current = previous;
          }
          await stopLiveWebFmp4Session({ targetOverride: target });
          throw error;
        }

        if (previous && previous !== target) {
          await stopLiveWebFmp4Session({ targetOverride: previous });
          previous = null;
        }
      }

      await waitForLiveWebFmp4Manifest(target);
      if (startId !== liveWebFmp4StartIdRef.current) return;

      liveAdaptiveStreamUrlRef.current = target.bootstrapUrl;
      castAdaptiveProfileRef.current = target.profile;
      transportSuspendedKindRef.current = null;
      setTransportSuspendedKind(null);
      await attachLiveWebFmp4Player(target);
      console.info(
        `[live-cmaf-r3] Web Live active profile=${target.profile} ` +
          'container=fmp4 shared-tee=1'
      );
    } catch (error) {
      if (startId !== liveWebFmp4StartIdRef.current) return;
      console.error('[live-cmaf-r3] Web Live startup failed:', error);
      setIsLoading(false);
      setLoadError(`Live fMP4 startup failed: ${error?.message || error}`);
      if (target && liveWebFmp4SessionRef.current === target) {
        await stopLiveWebFmp4Session({ targetOverride: target });
      }
    } finally {
      if (startId === liveWebFmp4StartIdRef.current) {
        liveWebFmp4TransitionRef.current = false;
      }
    }
  };

  // DISPATCHARR_LIVE_DESKTOP_PROGRESSIVE_TS_R26: Live on the desktop goes back
  // to the progressive MPEG-TS transport — the same one TiviMate and every
  // other Xtream client pulls from /live/<user>/<pass>/<id>.ts. Measured
  // 2026-09-13 by capturing two outputs of ONE transcode simultaneously and
  // cross-correlating their audio: the fMP4 HLS tee runs 1.60s behind
  // progressive TS (a 1s segment is published only once complete) and hls.js
  // then holds another target duration off the live edge, while the NVENC
  // transcode itself costs just 0.21s. WebKit mobile has no MSE for mpegts.js
  // and keeps native HLS (R24).
  //
  // Scope is deliberately Live-only: catch-up and VOD keep the HLS/VOD players
  // they use today, because the archive paths depend on them.
  const liveWebPrefersProgressiveTs = () => {
    try {
      if (isWebKitMobile()) return false;
      return Boolean(mpegts?.getFeatureList?.()?.mseLivePlayback);
    } catch {
      return false;
    }
  };

  const startLiveWebPlayback = async (fmp4Options = undefined) => {
    if (liveWebPrefersProgressiveTs()) {
      // A leftover fMP4 sender lease (e.g. from a Cast handback fallback)
      // would otherwise keep renewing with nothing playing it.
      void stopLiveWebFmp4Session();
      console.info('[live-r26] Web Live transport: progressive MPEG-TS');
      liveDiagBeacon('transport', { eng: 'mpegts' });
      return initializeLivePlayer(false);
    }
    console.info('[live-r26] Web Live transport: fMP4 HLS');
    liveDiagBeacon('transport', { eng: 'fmp4-hls' });
    return fmp4Options
      ? initializeLiveFmp4HlsPlayer(fmp4Options)
      : initializeLiveFmp4HlsPlayer();
  };


  // DISPATCHARR_LIVE_PROVIDER_TIMESHIFT_R4
  // Provider-backed Live DVR: no local recording/ring-buffer is created.
  // We bind an ordinary catch-up session to (now - rewind), then reuse the
  // already-proven finite Archive fMP4 HLS path and the current adaptive tier.

  // DISPATCHARR_LIVE_REMOVE_FS_BADGE_STYLE_REWIND_GREEN: Safari puts a native
  // icon at the video's top-left that Rewind/Go Live would cover;
  // confirmed live that Chrome puts nothing there, so only Safari
  // needs the right-side position.
  const isSafariBrowser = () => {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent || '';
    return (
      /Safari/.test(ua) &&
      !/Chrome|Chromium|CriOS|FxiOS|EdgiOS|OPiOS|Edg\//.test(ua)
    );
  };

  // DISPATCHARR_LIVE_REWIND_GOLIVE_CLEAR_SAFARI_ICON: Safari's native
  // fullscreen icon turned out to be present all along, at the
  // video's top-left -- the Rewind/Go Live badges (moved to the
  // right below) were just sitting on top of it. No custom
  // stand-in icon is needed.

  // DISPATCHARR_LIVE_PROVIDER_TIMESHIFT_EPG_BOUND_R4
  // EPG-originated Live playback is bounded by the selected programme start.
  // Example: programme 22:30-23:00 opened at 22:40 => available range is only
  // roughly -10:00..LIVE. Generic Live without this metadata keeps the 6h cap.
  // DISPATCHARR_LIVE_PROVIDER_TIMESHIFT_SEEK_FULLSCREEN_R5
  const liveProviderTimeshiftWindowStartMs = (nowMs = Date.now()) => {
    // DISPATCHARR_ARCHIVE_PTS_ALIGN_R137: always a whole second. The backend
    // cuts the session start down to one and positions are whole seconds
    // inside the session, so a start that carried the click's milliseconds
    // put every "Resume from" exactly one second before the instant on its
    // label. Nobody could see that while the provider itself was seconds
    // off; now that a start is frame-exact it would be the whole error.
    const fallback =
      Math.floor(nowMs / 1000) * 1000 - LIVE_PROVIDER_TIMESHIFT_MAX_SECONDS * 1000;
    const rawStart = metadata?.liveProgramStart;
    if (rawStart == null || rawStart === '') return fallback;
    const programmeStartMs = new Date(rawStart).getTime();
    if (!Number.isFinite(programmeStartMs) || programmeStartMs >= nowMs) {
      return fallback;
    }
    // up, not down: the window never reaches before the programme
    return Math.max(fallback, Math.ceil(programmeStartMs / 1000) * 1000);
  };

  const liveProviderTimeshiftWindowSeconds = (nowMs = Date.now()) => {
    const startMs = liveProviderTimeshiftWindowStartMs(nowMs);
    return Math.max(0, Math.floor((nowMs - startMs) / 1000));
  };

  // DISPATCHARR_ARCHIVE_PTS_ALIGN_R137: the floor that applies to a rewind of
  // the Live at sourceUrl -- the Cast floor for one that plays on a TV, else
  // what the gate said for exactly this Live, else the default.
  const noteLiveRewindFloor = (response, liveUrl = streamUrl) => {
    if (!liveUrl) return;
    const seconds = liveRewindFloorFromGate(response);
    const notes = liveRewindFloorRef.current;
    const before = notes.get(liveUrl);
    notes.delete(liveUrl);
    notes.set(liveUrl, seconds);
    while (notes.size > 8) notes.delete(notes.keys().next().value);
    if (before !== seconds) {
      console.info(`[live-rewind-r137] floor=${seconds}s (live-reopen gate)`);
      // a label that names the floor (an open Rewind picker, a shown pause)
      // follows at once, not at its next tick
      refreshLiveTimeshiftWindow((value) => (value + 1) % 1000000);
    }
  };
  const liveRewindFloorSeconds = ({ onCast = false, sourceUrl = streamUrl } = {}) => {
    if (onCast) return LIVE_REWIND_CAST_FLOOR_SECONDS;
    const noted = liveRewindFloorRef.current.get(sourceUrl);
    return Number.isFinite(noted) ? noted : LIVE_REWIND_FLOOR_DEFAULT_SECONDS;
  };
  // The floor is named when Live starts, and a Live can stay on for hours
  // while the backend stops following its archive (the kill switch, a
  // stream on which an aligned start has failed). So it is asked again
  // wherever it is shown or about to be used: when a pause offers "Resume
  // from", while the Rewind picker is open, before a resume is planned and
  // right before a rewind is committed. `rewind_floor=1` makes the gate
  // answer with the floor alone, none of its teardown bookkeeping. A
  // question that fails or takes too long leaves the note as it is.
  // One question at a time per Live, and an answer younger than
  // LIVE_REWIND_FLOOR_FRESH_MS is not asked for again: a label and the
  // commit that follows it go by the same answer. `force` asks whatever the
  // age of the last answer (the open Rewind picker's tick, which is what
  // keeps that answer young enough for the commit).
  const refreshLiveRewindFloor = async (timeoutMs = 900, { force = false } = {}) => {
    const liveUrl = streamUrl;
    if (!liveUrl) return;
    const asked = liveRewindFloorAskedRef.current;
    const last = asked.get(liveUrl);
    if (last?.pending) {
      await last.pending;
      return;
    }
    if (!force && last && Date.now() - last.at < LIVE_REWIND_FLOOR_FRESH_MS) return;
    let gateUrl = null;
    try {
      const base = buildDispatcharrLiveReopenUrl();
      if (!base) return;
      const question = new URL(base, window.location.origin);
      question.searchParams.set('rewind_floor', '1');
      gateUrl = question.toString();
    } catch {
      return;
    }
    const ask = async () => {
      const controller = typeof AbortController === 'function' ? new AbortController() : null;
      const timer = window.setTimeout(() => controller?.abort(), timeoutMs);
      try {
        const response = await fetch(gateUrl, {
          method: 'GET',
          cache: 'no-store',
          credentials: 'same-origin',
          signal: controller?.signal,
        });
        if (response.status === 204 || response.ok) noteLiveRewindFloor(response, liveUrl);
      } catch {
        // keep what is noted
      } finally {
        window.clearTimeout(timer);
      }
    };
    const pending = ask();
    asked.delete(liveUrl);
    asked.set(liveUrl, { at: Date.now(), pending });
    while (asked.size > 8) asked.delete(asked.keys().next().value);
    try {
      await pending;
    } finally {
      asked.set(liveUrl, { at: Date.now(), pending: null });
    }
  };
  const liveRewindCastingLive = () =>
    Boolean(
      (castActiveRef.current || dispatcharrCastRuntime.active) &&
        dispatcharrCastRuntime.kind === 'live'
    );

  // DISPATCHARR_LIVE_PAUSE_CHOICE_R136: a shown pause ticks (its "ago" and its
  // "Resume from" label move with the clock) -- never while playing (R122).
  const livePauseTicking = Boolean(
    livePause &&
      ((livePause.where === 'local' && transportSuspendedKind === 'live' && !castActive) ||
        (livePause.where === 'tv' && castActive))
  );

  // The EPG window grows while the programme is on air. A lightweight tick only
  // refreshes the slider bounds; seek math itself always uses a fresh Date.now().
  // DISPATCHARR_LIVE_PROVIDER_TIMESHIFT_DYNAMIC_EDGE_NATIVE_FULLSCREEN_R5_4
  useEffect(() => {
    // DISPATCHARR_LIVE_TICK_ONLY_WITH_PICKER_R122: plain Live reads this window
    // only inside the open Rewind picker; ticking the whole player every second
    // while just watching re-rendered this component for nothing (main-thread
    // time right next to the MSE appends of a 0.3s live buffer).
    const activeProviderTimeline =
      (contentType === 'live' && (showLiveRewindPicker || livePauseTicking)) || // R136
      (contentType === 'catchup-hls' && metadata?.liveTimeshift);
    if (!activeProviderTimeline) return undefined;
    // DISPATCHARR_LIVE_REWIND_PROGRAMME_TIME_R135: each tick lands just after a
    // whole wall-clock second, so a label read from Date.now() advances by
    // exactly one second per tick (a free-running interval skipped/repeated).
    let timer = null;
    let stopped = false;
    const schedule = () => {
      timer = window.setTimeout(() => {
        if (stopped) return;
        refreshLiveTimeshiftWindow((value) => (value + 1) % 1000000);
        schedule();
      }, 1000 - (Date.now() % 1000) + 20);
    };
    schedule();
    return () => {
      stopped = true;
      window.clearTimeout(timer);
    };
  }, [
    contentType,
    showLiveRewindPicker,
    livePauseTicking,
    metadata?.liveProgramStart,
    metadata?.liveTimeshift,
  ]);

  // DISPATCHARR_ARCHIVE_PTS_ALIGN_R137: the picker's label and tip say where
  // a release of the thumb starts, and the commit goes by the floor the gate
  // names then. So the picker asks as well -- when it opens and, while it
  // stays open, often enough for the commit to find that answer still fresh.
  useEffect(() => {
    if (contentType !== 'live' || !showLiveRewindPicker) return undefined;
    void refreshLiveRewindFloor();
    const timer = window.setInterval(() => {
      void refreshLiveRewindFloor(900, { force: true });
    }, LIVE_REWIND_FLOOR_FRESH_MS - 1000);
    return () => window.clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentType, showLiveRewindPicker, streamUrl]);

  // DISPATCHARR_NATIVE_RANGE_TIP_R133: the picker's step="10" grid starts at
  // min. With min = -window every value sat off the multiples of 10 and the
  // browser re-snapped the thumb each second as the window grew, so the label
  // (draft), the thumb and the committed rewind disagreed by up to 5 s -- and
  // with the thumb at LIVE a plain click committed (window % 10) s of rewind.
  // A multiple-of-10 min keeps 0 and every multiple of 10 on the grid.
  // DISPATCHARR_LIVE_REWIND_PROGRAMME_TIME_R135: while the pointer is on the
  // slider (or it is pressed / touched) its scale stays put, so a still pointer
  // keeps one distance behind Live and its programme-time label ticks +1 s per
  // second; the window catches up as soon as the pointer leaves. The draft is
  // clamped into the rendered range so the thumb never drifts off its label.
  const liveRewindWindowNow = showLiveRewindPicker ? liveProviderTimeshiftWindowSeconds() : 0;
  const liveRewindLiveMin = -Math.max(10, Math.floor(liveRewindWindowNow / 10) * 10);
  const liveRewindFrozenMin = rewindPickerFrozenMinRef.current;
  const liveRewindPickerMin = showLiveRewindPicker
    ? Number.isFinite(liveRewindFrozenMin) &&
      liveRewindFrozenMin < 0 &&
      -liveRewindFrozenMin <= Math.max(10, liveRewindWindowNow)
      ? liveRewindFrozenMin
      : liveRewindLiveMin
    : -10;
  const liveRewindPickerDraft = Math.min(
    0,
    Math.max(liveRewindPickerMin, Number(liveTimeshiftDraftSeconds) || 0)
  );
  const freezeRewindPickerScale = (input) => {
    const min = Number(input?.min);
    if (rewindPickerFrozenMinRef.current == null && Number.isFinite(min) && min < 0) {
      rewindPickerFrozenMinRef.current = min;
    }
  };

  // The channel this Live (or Live time-shift) belongs to, as a uuid.
  const livePickerChannelUuid = () => {
    if (contentType === 'live') return liveChannelUuid || null;
    if (contentType === 'catchup-hls' && metadata?.liveTimeshift) {
      return liveChannelIdFromUrl(metadata?.liveSourceUrl || '') || null;
    }
    return null;
  };

  // The programme on air at wall-clock ms: the Guide programme first (it is
  // the R4 window bound, so the left end reads 0:00), then the fetched EPG.
  const livePickerProgrammeAt = (ms) => {
    const fetched =
      livePickerProgrammes &&
      livePickerProgrammes.channelUuid === livePickerChannelUuid() &&
      Array.isArray(livePickerProgrammes.list)
        ? livePickerProgrammes.list
        : [];
    const seedStart = new Date(metadata?.liveProgramStart || '').getTime();
    if (Number.isFinite(seedStart) && ms >= seedStart) {
      // R135 review: a time-shift from an ended Guide programme has its
      // liveProgramEnd nulled (horizon/timeline); the label keeps the real end.
      let seedEnd = new Date(
        metadata?.liveProgramEnd ||
          (metadata?.liveTimeshift ? metadata?.liveSourceMetadata?.liveProgramEnd : '') ||
          ''
      ).getTime();
      if (!Number.isFinite(seedEnd)) {
        const next = fetched.find((programme) => programme.startMs > seedStart + 60000);
        seedEnd = next ? next.startMs : Infinity;
      }
      if (ms < seedEnd) {
        return {
          startMs: seedStart,
          endMs: seedEnd,
          title: metadata?.liveProgramTitle || metadata?.title || '',
        };
      }
    }
    return (
      fetched.find((programme) => programme.startMs <= ms && ms < programme.endMs) || null
    );
  };

  // What a commit at this slider value starts: startLiveProviderTimeshift
  // floors any rewind to min(floor, window) and caps it at the window (R133a;
  // R137: the floor is liveRewindFloorSeconds, the same call the commit makes).
  const liveRewindEffectiveSeconds = (value, nowMs = Date.now()) => {
    const magnitude = Math.abs(Number(value) || 0);
    if (magnitude === 0) return 0;
    const windowSeconds = Math.max(1, liveProviderTimeshiftWindowSeconds(nowMs));
    // (a Cast start that is still loading: the commit waits for it and then
    // plays on the TV, with the TV's floor -- the label must not promise less)
    const floorSeconds = liveRewindFloorSeconds({
      onCast: liveRewindCastingLive() || Boolean(castOpInFlight(['start'])),
    });
    return Math.min(windowSeconds, Math.max(magnitude, Math.min(floorSeconds, windowSeconds)));
  };

  // "7:00" = seven minutes after the programme airing at that instant started;
  // an instant in an earlier programme names it in the tip ("45:12 · News");
  // without a programme for it, the time of day ("20:47:30"). Never negative.
  const liveRewindPointLabel = (value, { withTitle = false, nowMs = Date.now() } = {}) => {
    const nowSecond = Math.floor(nowMs / 1000);
    const effective = liveRewindEffectiveSeconds(value, nowMs);
    if (effective <= 0) {
      if (!withTitle) return 'LIVE';
      const onAir = livePickerProgrammeAt(nowSecond * 1000);
      return onAir ? `LIVE · ${liveRewindEdgeLabel(nowMs)}` : 'LIVE';
    }
    const instantMs = (nowSecond - effective) * 1000;
    const programme = livePickerProgrammeAt(instantMs);
    if (!programme) return formatWallClock(instantMs);
    const clock = formatArchiveClock(
      (instantMs - Math.floor(programme.startMs / 1000) * 1000) / 1000
    );
    if (!withTitle || !programme.title) return clock;
    const onAir = livePickerProgrammeAt(nowSecond * 1000);
    if (onAir && onAir.startMs === programme.startMs) return clock;
    const title =
      programme.title.length > 24 ? `${programme.title.slice(0, 24)}…` : programme.title;
    return `${clock} · ${title}`;
  };

  // The live programme's running time (or the time of day without EPG).
  const liveRewindEdgeLabel = (nowMs = Date.now()) => {
    const nowSecond = Math.floor(nowMs / 1000);
    const onAir = livePickerProgrammeAt(nowSecond * 1000);
    if (!onAir) return formatWallClock(nowSecond * 1000);
    return formatArchiveClock(nowSecond - Math.floor(onAir.startMs / 1000));
  };

  // DISPATCHARR_LIVE_PAUSE_CHOICE_R136: programme time of an absolute instant
  // ("17:26"), time of day without a programme -- the picker's convention
  // (liveRewindPointLabel stays untouched).
  const liveInstantLabel = (instantMs) => {
    if (!Number.isFinite(instantMs)) return '';
    const atMs = Math.floor(instantMs / 1000) * 1000;
    const programme = livePickerProgrammeAt(atMs);
    if (!programme) return formatWallClock(atMs);
    return formatArchiveClock((atMs - Math.floor(programme.startMs / 1000) * 1000) / 1000);
  };

  // DISPATCHARR_LIVE_REWIND_PROGRAMME_TIME_R135: the time-shift bars count
  // seconds from the catch-up session start -- for a rewind opened outside
  // the Guide that start is "6 h ago", so "5:52:13" meant nothing. They show
  // the programme time of that instant (the picker's labels) instead, the
  // time of day without EPG. null = not a Live time-shift: keep your clock.
  const liveTimeshiftClockLabel = (seconds, { kind = 'position' } = {}) => {
    if (contentType !== 'catchup-hls' || !metadata?.liveTimeshift) return null;
    const startMs = Date.parse(metadata?.liveTimeshiftStart || '');
    if (!Number.isFinite(startMs)) return null;
    const instantMs =
      Math.floor(startMs / 1000) * 1000 +
      Math.floor(Math.max(0, Number(seconds) || 0)) * 1000;
    // R135 review: the right end of a finished Guide programme is its last
    // instant ("30:00"), not the next programme's first ("0:00").
    const horizon = Number(metadata?.liveTimeshiftSessionDurationSeconds) || 0;
    const atEnd =
      kind === 'edge' || (horizon > 0 && Math.floor(Math.max(0, Number(seconds) || 0)) >= horizon);
    const programme = livePickerProgrammeAt(atEnd ? instantMs - 1 : instantMs);
    if (!programme) return formatWallClock(instantMs);
    const clock = formatArchiveClock(
      (instantMs - Math.floor(programme.startMs / 1000) * 1000) / 1000
    );
    if (kind !== 'tip' || !programme.title) return clock;
    const onAir = livePickerProgrammeAt(Math.floor(Date.now() / 1000) * 1000);
    if (onAir && onAir.startMs === programme.startMs) return clock;
    const title =
      programme.title.length > 24 ? `${programme.title.slice(0, 24)}…` : programme.title;
    return `${clock} · ${title}`;
  };

  // DISPATCHARR_ARCHIVE_PTS_ALIGN_R137: the bar's right end is the player's
  // seek range, about three seconds behind the wall clock -- closer to Live
  // than anything can be played without running dry at once. A drop there
  // lands on the floor a rewind starts from instead (media seconds; null =
  // no limit of ours). A programme that has ended is past this long before
  // its last second, so the limit never shortens a finished one.
  const liveTimeshiftLatestSeekSeconds = () => {
    if (contentType !== 'catchup-hls' || !metadata?.liveTimeshift) return null;
    const startMs = Date.parse(metadata?.liveTimeshiftStart || '');
    if (!Number.isFinite(startMs)) return null;
    const floorSeconds = liveRewindFloorSeconds({ sourceUrl: metadata?.liveSourceUrl });
    return Math.max(0, (Date.now() - Math.floor(startMs / 1000) * 1000) / 1000 - floorSeconds);
  };

  // DISPATCHARR_NATIVE_RANGE_TIP_R133: same rules as refreshCastSeekTip, in
  // the label's own reading (how far back from live): over the thumb and
  // while pressed the label's text, elsewhere the rewind a click selects.
  const refreshRewindPickerTip = () => {
    const tip = rewindHoverTipRef.current;
    const input = rewindPickerInputRef.current;
    const pointer = rewindPickerPointerRef.current;
    if (!tip || !input) {
      // The picker closes on mouseup/Enter while the pointer is still on it.
      pointer.x = null;
      pointer.y = null;
      pointer.pressed = false;
      return;
    }
    if (!pointer.pressed && pointer.x == null) {
      tip.style.opacity = '0';
      return;
    }
    const pressValue = pointer.pressed
      ? null
      : nativeRangePressValue(input, pointer.x, pointer.y);
    if (pressValue === undefined) return;
    // R133a review: startLiveProviderTimeshift floors any rewind to
    // min(floor, window) s -- say what the click actually starts.
    // DISPATCHARR_LIVE_REWIND_PROGRAMME_TIME_R135: in programme time, with the
    // commit floor in every branch (the pressed/over-thumb one included).
    tip.textContent = liveRewindPointLabel(
      pressValue === null ? liveRewindPickerDraft : pressValue,
      { withTitle: true }
    );
    const rect = input.getBoundingClientRect();
    const barRect = input.parentElement.getBoundingClientRect();
    const anchorX =
      pressValue === null ? nativeRangeThumbCenterX(input) : pointer.x;
    // Keep the whole readout inside the bar (titles make it wider).
    const halfTip = (tip.offsetWidth || 0) / 2;
    tip.style.left = `${Math.min(
      Math.max(anchorX - barRect.left, halfTip),
      Math.max(halfTip, barRect.width - halfTip)
    )}px`;
    // Sit against the slider itself; anchoring to the bar's full height
    // floated it well above the control.
    tip.style.top = `${rect.top - barRect.top - 4}px`;
    tip.style.opacity = '1';
  };

  // After every render (no deps): the picker re-renders whenever its draft,
  // window or visibility moves, and the refresh is cheap while it is closed.
  useLayoutEffect(() => {
    refreshRewindPickerTip();
  });

  // DISPATCHARR_LIVE_PROVIDER_TIMESHIFT_WORKER_SAFETY_R5_7
  const buildLiveTimeshiftHlsUrl = (
    channelId,
    sessionId,
    profile,
    initialPositionSeconds = 0
  ) => {
    const url = new URL(
      `/proxy/catchup-hls/${encodeURIComponent(channelId)}/index.m3u8`,
      window.location.origin
    );
    url.searchParams.set('session_id', String(sessionId));
    // DISPATCHARR_LIVE_PROVIDER_TIMESHIFT_DYNAMIC_EDGE_NATIVE_FULLSCREEN_R5_4
    url.searchParams.set('live_timeshift', '1');
    // DISPATCHARR_LIVE_PROVIDER_TIMESHIFT_WORKER_SAFETY_R5_7
    url.searchParams.set(
      'cast_start_position',
      Math.max(0, Number(initialPositionSeconds) || 0).toFixed(3)
    );
    url.searchParams.set(
      'adaptive_output_profile',
      String(profile || '2.5-3.5m')
    );
    return url.toString();
  };

  const startLiveProviderTimeshift = async (
    requestedRewindSeconds,
    // R135: origin 'web' | 'tv-remote'. R136: 'pause' (web semantics) with an
    // absolute targetInstantMs, turned into a rewind only once nowMs is frozen.
    { origin = 'web', targetInstantMs = null } = {}
  ) => {
    // DISPATCHARR_CAST_REMOTE_REWIND_R135: a TV-remote rewind never queues
    // behind a transition -- it is simply not taken then.
    if (origin === 'tv-remote' && (castOpInFlight() || liveTimeshiftStartingRef.current)) {
      return false;
    }
    // DISPATCHARR_CAST_TRANSITION_OPS_R134: Rewind pressed while a Cast start
    // is still loading waits for that start (bounded by its own timeouts)
    // instead of racing it: the TV first finishes the Live load, then gets
    // the rewind exactly like a Rewind on an established cast. Racing it left
    // the TV on Live under a rewind UI, or killed the local rewind.
    const pendingCastStart = castOpInFlight(['start']);
    if (
      pendingCastStart &&
      contentType === 'live' &&
      !liveTimeshiftStartingRef.current &&
      castContextRef.current?.getCurrentSession?.()
    ) {
      liveTimeshiftStartingRef.current = true;
      setShowLiveRewindPicker(false);
      setIsLoading(true);
      console.info(
        `[chromecast-r134] Rewind waits for Cast start #${pendingCastStart.seq} to finish`
      );
      try {
        await awaitCastOpUnwound(pendingCastStart, 75000);
      } finally {
        liveTimeshiftStartingRef.current = false;
      }
      if (castLatestRef.current && castLatestRef.current.streamUrl !== streamUrl) {
        // The user switched to another stream meanwhile; this Rewind is moot.
        setIsLoading(false);
        return false;
      }
      const latestStart = castLatestRef.current?.startLiveProviderTimeshift;
      if (latestStart && latestStart !== startLiveProviderTimeshift) {
        const started = await latestStart(requestedRewindSeconds, { origin, targetInstantMs });
        if (!started) setIsLoading(false);
        return started;
      }
    }
    // DISPATCHARR_CAST_TRANSITION_OPS_R134: Rewind right after Stop Casting
    // waits for that teardown (its Web handback is starting local Live), then
    // rewinds locally from a clean state instead of parking a dying lease.
    if (castOpInFlight(['stop', 'handback']) && contentType === 'live') {
      liveTimeshiftStartingRef.current = true;
      try {
        await awaitCastOpUnwound(castOpRef.current, 10000);
      } finally {
        liveTimeshiftStartingRef.current = false;
      }
      if (castLatestRef.current && castLatestRef.current.streamUrl !== streamUrl) {
        return false;
      }
      const latestAfterTeardown = castLatestRef.current?.startLiveProviderTimeshift;
      if (latestAfterTeardown && latestAfterTeardown !== startLiveProviderTimeshift) {
        return latestAfterTeardown(requestedRewindSeconds, { origin, targetInstantMs });
      }
    }
    // DISPATCHARR_CAST_LIVE_REWIND_R17: Rewind is allowed while Live is Cast;
    // the rewound stream then goes to the TV (cast branch further down).
    const castingLive = Boolean(
      (castActiveRef.current || dispatcharrCastRuntime.active) &&
        dispatcharrCastRuntime.kind === 'live'
    );
    if (
      contentType !== 'live' ||
      ((castActiveRef.current || dispatcharrCastRuntime.active) && !castingLive) ||
      liveTimeshiftStartingRef.current
    ) {
      return false;
    }

    const channelId = liveChannelIdFromUrl(streamUrl);
    if (!channelId) {
      setLoadError('Provider time-shift is unavailable for this Live URL.');
      return false;
    }

    // DISPATCHARR_ARCHIVE_PTS_ALIGN_R137: the floor this commit is about to
    // use may be hours old (see refreshLiveRewindFloor). A TV rewind uses its
    // own fixed floor and needs no answer.
    if (!castingLive) {
      liveTimeshiftStartingRef.current = true;
      try {
        await refreshLiveRewindFloor();
      } finally {
        liveTimeshiftStartingRef.current = false;
      }
      const current = useVideoStore.getState();
      if (current.streamUrl !== streamUrl || current.contentType !== 'live') return false;
    }

    // DISPATCHARR_LIVE_PROVIDER_TIMESHIFT_EPG_BOUND_R4
    // Never cross the selected current programme's EPG start. Keep the rewind
    // floor (R137: liveRewindFloorSeconds) only when that much programme
    // history exists.
    // DISPATCHARR_LIVE_PROVIDER_TIMESHIFT_SEEK_FULLSCREEN_R5
    // Freeze `now` once so slider math, session start and initial position are
    // all derived from the same wall-clock instant. The HLS session starts at
    // the left edge of the DVR window; the requested rewind becomes a seek
    // inside that session instead of accidentally becoming position zero.
    const nowMs = Date.now();
    const windowStartMs = liveProviderTimeshiftWindowStartMs(nowMs);
    const maxRewindSeconds = liveProviderTimeshiftWindowSeconds(nowMs);
    if (maxRewindSeconds < 1) return false;
    const requestedRewind = Number.isFinite(targetInstantMs)
      ? // R136: the session position of the target. R137: to the millisecond
        // -- the window starts on a whole second now, the backend places the
        // producer on the second that holds the position and the manifest
        // starts playback on the position itself (EXT-X-START, PRECISE).
        Math.max(
          0,
          maxRewindSeconds - Math.max(0, Math.round(targetInstantMs - windowStartMs)) / 1000
        )
      : Math.max(0, Math.round(Number(requestedRewindSeconds) || 0));
    const providerFloorSeconds = Math.min(
      liveRewindFloorSeconds({ onCast: castingLive }),
      maxRewindSeconds
    );
    const rewindSeconds =
      Math.round(
        Math.min(maxRewindSeconds, Math.max(providerFloorSeconds, requestedRewind)) * 1000
      ) / 1000;
    const initialPositionSeconds =
      Math.round(Math.max(0, maxRewindSeconds - rewindSeconds) * 1000) / 1000;
    const profile =
      liveWebFmp4SessionRef.current?.profile ||
      currentLiveAdaptiveProfile() ||
      '2.5-3.5m';
    const start = new Date(windowStartMs);
    // The session covers the complete currently available provider DVR window
    // so the user can seek both backward and forward after the first rewind.
    // DISPATCHARR_LIVE_PROVIDER_TIMESHIFT_DYNAMIC_EDGE_NATIVE_FULLSCREEN_R5_4
    // Authorize the provider session through the selected EPG programme end.
    // The backend EVENT manifest still publishes only segments available now.
    const rawProgrammeEnd = metadata?.liveProgramEnd;
    const programmeEndMs = new Date(rawProgrammeEnd || '').getTime();
    const sessionHorizonSeconds =
      // DISPATCHARR_LIVE_REWIND_PROGRAMME_TIME_R135: an already ended Guide
      // programme must not cut the session to a minute.
      Number.isFinite(programmeEndMs) && programmeEndMs > nowMs
        ? Math.min(
            8 * 60 * 60,
            Math.max(
              maxRewindSeconds + 2,
              Math.ceil((programmeEndMs - windowStartMs) / 1000)
            )
          )
        : 8 * 60 * 60;
    const durationMinutes = Math.max(
      2,
      Math.min(480, Math.ceil(sessionHorizonSeconds / 60))
    );

    liveTimeshiftStartingRef.current = true;
    setLoadError(null);
    setIsLoading(true);
    try {
      const token = useAuthStore.getState().accessToken;
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;

      const response = await fetch('/api/catchup/sessions/', {
        method: 'POST',
        cache: 'no-store',
        credentials: 'same-origin',
        headers,
        body: JSON.stringify({
          channel_uuid: channelId,
          start: start.toISOString(),
          duration: durationMinutes,
        }),
      });
      let payload = {};
      try { payload = await response.json(); } catch {}
      if (!response.ok || !payload?.session_id) {
        throw new Error(
          payload?.error || payload?.detail || `HTTP ${response.status}`
        );
      }

      const playbackUrl = buildLiveTimeshiftHlsUrl(
        channelId,
        payload.session_id,
        profile,
        initialPositionSeconds
      );
      const liveSourceMetadata = metadata?.liveTimeshift
        ? metadata?.liveSourceMetadata || null
        : metadata || null;

      // DISPATCHARR_CAST_REMOTE_REWIND_R135: a rewind the TV remote asked for
      // belongs on the TV. If the cast ended, a Stop/X began or the stream
      // changed while the catch-up session was being created, drop it HERE --
      // before the local player is touched (a Stop's handback may have just
      // restarted local Live). The orphan session expires on its own TTL.
      // R136 review: a "Resume from" of a paused cast is TV-bound the same way.
      if (origin === 'tv-remote' || (origin === 'pause' && castingLive)) {
        const stillCastingLive = Boolean(
          (castActiveRef.current || dispatcharrCastRuntime.active) &&
            dispatcharrCastRuntime.kind === 'live' &&
            castContextRef.current?.getCurrentSession?.() &&
            !dispatcharrPlayerClosingRef.current &&
            !castOpInFlight(['stop', 'handback', 'close']) &&
            (!castLatestRef.current || castLatestRef.current.streamUrl === streamUrl)
        );
        if (!stillCastingLive) {
          console.info(
            origin === 'pause'
              ? '[live-pause-r136] Resume from dropped: the cast changed meanwhile'
              : '[chromecast-r135] TV remote rewind dropped: the cast changed meanwhile'
          );
          // a Stop / handback / close that superseded this start owns isLoading
          if (!castOpInFlight(['stop', 'handback', 'close'])) setIsLoading(false);
          setCastRewindingOnTv(null);
          return false;
        }
      }
      // Do not kill Live until the provider-backed catch-up session exists.
      // The normal source-change cleanup then releases any remaining Web lease.
      await stopLiveWebFmp4Session();
      safeDestroyPlayer();
      setLiveTimeshiftDraftSeconds(0);
      setShowLiveRewindPicker(false);
      console.info(
        `[live-provider-timeshift] start rewind=${rewindSeconds}s ` +
          `window=${maxRewindSeconds}s initial=${initialPositionSeconds}s ` +
          `profile=${profile} provider-catchup=1 local-recording=0 ` +
          // DISPATCHARR_LIVE_REWIND_PROGRAMME_TIME_R135 / REMOTE_REWIND_R135
          `duration=${durationMinutes}m origin=${origin} ` +
          `landing="${liveRewindPointLabel(-rewindSeconds, { nowMs })}"` +
          (Number.isFinite(targetInstantMs) // DISPATCHARR_LIVE_PAUSE_CHOICE_R136
            ? ` target="${liveInstantLabel(targetInstantMs)}" target_at=${new Date(targetInstantMs).toISOString()}`
            : '') +
          ` session_start=${start.toISOString()}` // DISPATCHARR_ARCHIVE_PTS_ALIGN_R137
      );
      // DISPATCHARR_LIVE_PROVIDER_TIMESHIFT_PLAYBACK_FULLSCREEN_R5_6
      liveTimeshiftScrubbingRef.current = false;
      liveTimeshiftInitialSeekSettledUrlRef.current = null;
      liveTimeshiftInitialPositionConsumedUrlRef.current = null;
      setLiveTimeshiftPlaybackSeconds(initialPositionSeconds);
      // DISPATCHARR_CAST_LIVE_REWIND_R17: casting Live -> send the rewound
      // stream to the TV instead of starting it locally. Order matters:
      // 1) park the Live Cast lease (R21). It used to be released here on
      //    the belief that TiVi starves a second connection for the same
      //    channel; measured 2026-09-11, it does not;
      // 2) point the Cast runtime at the new URL BEFORE showVideo, exactly as
      //    Continue Live does, so the source-change effect keeps the Cast
      //    session and the player effect keeps the local player off;
      // 3) the effect after loadArchiveOnChromecast loads it on the TV once
      //    the component has re-rendered as catchup-hls.
      // archiveLocalRuntimeUrlRef can still hold an old Archive handback URL,
      // which loadArchiveOnChromecast would otherwise suspend/resume.
      // DISPATCHARR_CAST_TRANSITION_OPS_R134: re-check at commit time -- the
      // cast may have ended while the catch-up session was being created.
      const castingLiveNow = Boolean(
        castingLive &&
          (castActiveRef.current || dispatcharrCastRuntime.active) &&
          dispatcharrCastRuntime.kind === 'live' &&
          castContextRef.current?.getCurrentSession?.()
      );
      if (castingLiveNow) {
        castFailureRecoveryRef.current = true;
        setCastReturningToLive(false);
        const rewindOp = beginCastOp('rewind-on-cast', { url: playbackUrl, origin });
        // DISPATCHARR_CAST_REMOTE_REWIND_R135
        setCastRewindingOnTv(
          origin === 'tv-remote' ? 'tv-remote' : origin === 'pause' ? 'pause' : 'web' // R136
        );
        // DISPATCHARR_CAST_LIVE_WARM_DURING_REWIND_R21
        if (!parkLiveCastLease()) {
          await awaitCastOperation(
            stopCastSessionLease(true),
            3500,
            'Rewind on Cast: release Live lease',
            false
          );
        }
        archiveLocalRuntimeUrlRef.current = null;
        dispatcharrCastRuntime.sourceUrl = playbackUrl;
        pendingCastTimeshiftRef.current = { url: playbackUrl, op: rewindOp };
        console.info(
          `[live-provider-timeshift] Rewind on Cast: rewind=${rewindSeconds}s -> TV`
        );
      }
      showVideo(playbackUrl, 'catchup-hls', {
        ...(liveSourceMetadata || {}),
        name: metadata?.name || liveSourceMetadata?.name || 'Live TV',
        liveTimeshift: true,
        liveSourceUrl: streamUrl,
        liveSourceMetadata,
        liveTimeshiftRewindSeconds: rewindSeconds,
        liveTimeshiftStart: start.toISOString(),
        liveTimeshiftInitialPositionSeconds: initialPositionSeconds,
        liveTimeshiftWindowSeconds: maxRewindSeconds,
        // DISPATCHARR_LIVE_PROVIDER_TIMESHIFT_DYNAMIC_EDGE_NATIVE_FULLSCREEN_R5_4
        liveTimeshiftSessionDurationSeconds: sessionHorizonSeconds,
        durationMinutes,
        adaptiveProfile: profile,
        // DISPATCHARR_LIVE_REWIND_PROGRAMME_TIME_R135: an ended Guide programme
        // no longer bounds this session (liveSourceMetadata keeps the original).
        liveProgramEnd:
          Number.isFinite(programmeEndMs) && programmeEndMs > nowMs
            ? metadata?.liveProgramEnd ?? null
            : null,
        liveTimeshiftOrigin: origin, // DISPATCHARR_CAST_REMOTE_REWIND_R135
      });
      return true;
    } catch (error) {
      console.warn('[live-provider-timeshift] start failed:', error);
      setCastRewindingOnTv(null); // DISPATCHARR_CAST_REMOTE_REWIND_R135
      setIsLoading(false);
      setLoadError(
        `Provider time-shift failed: ${error?.message || error}`
      );
      return false;
    } finally {
      liveTimeshiftStartingRef.current = false;
    }
  };

  // DISPATCHARR_LIVE_PROVIDER_TIMESHIFT_SEEK_FULLSCREEN_R5
  // DISPATCHARR_LIVE_PROVIDER_TIMESHIFT_DYNAMIC_EDGE_NATIVE_FULLSCREEN_R5_4
  const liveTimeshiftTimelineSeconds = (nowMs = Date.now()) => {
    const horizon = Math.max(
      1,
      Number(metadata?.liveTimeshiftSessionDurationSeconds) ||
        Math.max(0, Number(metadata?.durationMinutes) || 0) * 60 ||
        Number(metadata?.liveTimeshiftWindowSeconds) ||
        1
    );
    const rawStart = metadata?.liveTimeshiftStart || metadata?.liveProgramStart;
    const startMs = new Date(rawStart || '').getTime();
    if (!Number.isFinite(startMs)) {
      return Math.min(
        horizon,
        Math.max(1, Number(metadata?.liveTimeshiftWindowSeconds) || 1)
      );
    }

    let edgeMs = nowMs;
    const programmeEndMs = new Date(metadata?.liveProgramEnd || '').getTime();
    if (Number.isFinite(programmeEndMs)) {
      edgeMs = Math.min(edgeMs, programmeEndMs);
    }
    const elapsed = Math.max(0, (edgeMs - startMs) / 1000);
    return Math.max(1, Math.min(horizon, elapsed));
  };

  // DISPATCHARR_LIVE_PROVIDER_TIMESHIFT_UI_EDGE_R5_1
  const liveTimeshiftMediaBounds = (video = videoRef.current) => {
    const duration = liveTimeshiftTimelineSeconds();
    let first = 0;
    let last = null;
    try {
      if (video?.seekable && video.seekable.length > 0) {
        const rawFirst = Number(video.seekable.start(0));
        const rawLast = Number(video.seekable.end(video.seekable.length - 1));
        if (Number.isFinite(rawFirst)) first = rawFirst;
        if (Number.isFinite(rawLast)) last = rawLast;
      }
    } catch {}
    return { duration, first, last };
  };

  const liveTimeshiftProgrammePosition = (video = videoRef.current) => {
    const { duration, first } = liveTimeshiftMediaBounds(video);
    const mediaTime = Math.max(0, Number(video?.currentTime) || 0);
    return Math.min(duration, Math.max(0, mediaTime - first));
  };

  const liveTimeshiftBehindLiveSeconds = () => {
    const duration = liveTimeshiftTimelineSeconds();
    const position = Math.min(
      duration,
      Math.max(0, Number(liveTimeshiftPlaybackSeconds) || 0)
    );
    return Math.max(0, duration - position);
  };

  const commitLiveTimeshiftPlaybackSeek = (requestedSeconds) => {
    const video = videoRef.current;
    if (!video || contentType !== 'catchup-hls' || !metadata?.liveTimeshift) return;
    const { duration, first, last } = liveTimeshiftMediaBounds(video);
    const target = Math.min(
      Math.max(0, Number(requestedSeconds) || 0),
      Math.max(0, duration - 0.05)
    );
    // DISPATCHARR_LIVE_PROVIDER_TIMESHIFT_DYNAMIC_EDGE_NATIVE_FULLSCREEN_R5_4
    // The moving right edge is real Live. Switch transports instead of asking
    // provider catch-up for a not-yet-complete edge fragment.
    if (duration - target <= 1.5) {
      liveTimeshiftScrubbingRef.current = false;
      setLiveTimeshiftPlaybackSeconds(duration);
      void returnToLiveFromProviderTimeshift();
      return;
    }
    let mediaTarget = first + target;
    if (Number.isFinite(last) && last > first) {
      mediaTarget = Math.min(mediaTarget, Math.max(first, last - 0.05));
    }
    liveTimeshiftScrubbingRef.current = false;
    // DISPATCHARR_LIVE_PROVIDER_TIMESHIFT_POSITION_REOPEN_R5_2
    // DISPATCHARR_LIVE_PROVIDER_TIMESHIFT_CAST_HANDBACK_R5_3
    liveTimeshiftInitialSeekSettledUrlRef.current = streamUrl;
    liveTimeshiftExpectedPositionRef.current = target;
    setLiveTimeshiftPlaybackSeconds(target);
    try {
      video.currentTime = mediaTarget;
      video.play().catch(() => {});
    } catch (error) {
      console.debug('[live-r5.1] provider timeshift seek skipped:', error);
    }
  };

  useEffect(() => {
    if (
      !isVisible ||
      contentType !== 'catchup-hls' ||
      !metadata?.liveTimeshift
    ) {
      liveTimeshiftScrubbingRef.current = false;
      setLiveTimeshiftPlaybackSeconds(0);
      return undefined;
    }
    const video = videoRef.current;
    if (!video) return undefined;
    const sync = () => {
      if (liveTimeshiftScrubbingRef.current) return;
      const programmePosition = liveTimeshiftProgrammePosition(video);
      // DISPATCHARR_LIVE_PROVIDER_TIMESHIFT_CAST_HANDBACK_R5_3
      const expectedPosition = Number(liveTimeshiftExpectedPositionRef.current);
      const initialTarget = Math.min(
        liveTimeshiftTimelineSeconds(),
        Math.max(
          0,
          Number.isFinite(expectedPosition)
            ? expectedPosition
            : Number(metadata?.liveTimeshiftInitialPositionSeconds) || 0
        )
      );

      // DISPATCHARR_LIVE_PROVIDER_TIMESHIFT_POSITION_REOPEN_R5_2
      // Until the media clock reaches the requested initial programme point,
      // keep the UI on that requested point instead of publishing the transient
      // zero emitted by HLS metadata/startup events.
      if (liveTimeshiftInitialSeekSettledUrlRef.current !== streamUrl) {
        if (Math.abs(programmePosition - initialTarget) > 1.0) {
          setLiveTimeshiftPlaybackSeconds(initialTarget);
          return;
        }
        liveTimeshiftInitialSeekSettledUrlRef.current = streamUrl;
        console.info(
          `[live-r5.2] provider initial position settled ` +
            `position=${programmePosition.toFixed(2)}s ` +
            `behind=${Math.max(0, liveTimeshiftTimelineSeconds() - programmePosition).toFixed(2)}s`
        );
      }
      setLiveTimeshiftPlaybackSeconds(programmePosition);
    };
    video.addEventListener('timeupdate', sync);
    video.addEventListener('loadedmetadata', sync);
    video.addEventListener('seeked', sync);
    sync();
    return () => {
      video.removeEventListener('timeupdate', sync);
      video.removeEventListener('loadedmetadata', sync);
      video.removeEventListener('seeked', sync);
      liveTimeshiftScrubbingRef.current = false;
    };
  }, [isVisible, streamUrl, contentType, metadata?.liveTimeshift]);

  // DISPATCHARR_LIVE_PROVIDER_TIMESHIFT_UI_EDGE_R5_1
  const returnToLiveFromProviderTimeshift = async () => {
    if (contentType !== 'catchup-hls' || !metadata?.liveTimeshift) return;
    if (liveTimeshiftReturningToLiveRef.current) return;

    // DISPATCHARR_CAST_TRANSITION_OPS_R134: a Cast start that is still loading
    // (Cast pressed on a local rewind) finishes first; this Continue Live then
    // runs on its result -- on the TV if the cast came up, locally if not.
    // Racing it killed the new local Live and failed the cast with "Archive
    // Web producer could not suspend for Chromecast".
    const pendingCastStart = castOpInFlight(['start']);
    if (pendingCastStart && castContextRef.current?.getCurrentSession?.()) {
      liveTimeshiftReturningToLiveRef.current = true;
      setIsLoading(true);
      console.info(
        `[chromecast-r134] Continue Live waits for Cast start #${pendingCastStart.seq} to finish`
      );
      try {
        await awaitCastOpUnwound(pendingCastStart, 75000);
      } finally {
        liveTimeshiftReturningToLiveRef.current = false;
        // The cast branch shows its own "Returning to Live" indicator; the
        // local branch's player raises loading again by itself.
        setIsLoading(false);
      }
      if (castLatestRef.current && castLatestRef.current.streamUrl !== streamUrl) {
        return; // the user switched to another stream meanwhile
      }
      const latestReturn = castLatestRef.current?.returnToLiveFromProviderTimeshift;
      if (latestReturn && latestReturn !== returnToLiveFromProviderTimeshift) {
        return latestReturn();
      }
    }

    // DISPATCHARR_CAST_TRANSITION_OPS_R134: Continue Live right after Stop
    // Casting -- the cast is being torn down. Wait for that (bounded) and
    // switch locally; loading Live onto the dying session ended in R109 and
    // destroyed the restored local rewind. The local switch supersedes the
    // Stop's own Web handback, which then stands down.
    if (castOpInFlight(['stop', 'handback'])) {
      liveTimeshiftReturningToLiveRef.current = true;
      try {
        const teardownDeadline = Date.now() + 8000;
        while (
          (castActiveRef.current || dispatcharrCastRuntime.active) &&
          castOpInFlight(['stop', 'handback']) &&
          Date.now() < teardownDeadline
        ) {
          await new Promise((resolve) => window.setTimeout(resolve, 100));
        }
      } finally {
        liveTimeshiftReturningToLiveRef.current = false;
      }
      const latestAfterTeardown = castLatestRef.current?.returnToLiveFromProviderTimeshift;
      if (latestAfterTeardown && latestAfterTeardown !== returnToLiveFromProviderTimeshift) {
        return latestAfterTeardown();
      }
    }

    const liveSourceUrl = metadata?.liveSourceUrl;
    if (!liveSourceUrl) {
      setLoadError('Original Live URL is unavailable. Close and reopen the channel.');
      return;
    }

    const restoredMetadata =
      metadata?.liveSourceMetadata || { name: metadata?.name || 'Live TV' };
    liveTimeshiftReturningToLiveRef.current = true;
    // DISPATCHARR_LIVE_PROVIDER_TIMESHIFT_CAST_HANDBACK_R5_3
    liveTimeshiftExpectedPositionRef.current = null;
    setLoadError(null);
    console.info('[live-provider-timeshift] return to Live: draining provider HLS first');

    // DISPATCHARR_LIVE_CONTINUE_LIVE_CAST_INPLACE_SWITCH: while
    // actively casting the rewound stream, switch the SAME connected Cast
    // session's content to plain Live in place -- the TV keeps playing,
    // now caught up to live -- instead of stopping Cast and falling back
    // to the local web player. Only when Cast is NOT active does this fall
    // through to the existing local-only switch below.
    // DISPATCHARR_CAST_TRANSITION_OPS_R134: a Rewind on Cast that is still
    // switching the TV belongs to the cast as well -- Continue Live supersedes
    // it instead of falling through to the local switch.
    const rewindOnCastInFlight = castOpInFlight(['rewind-on-cast']);
    if (
      castActiveRef.current ||
      dispatcharrCastRuntime.active ||
      (rewindOnCastInFlight && castContextRef.current?.getCurrentSession?.())
    ) {
      console.info(
        '[live-provider-timeshift] Continue Live: switching active Cast session to plain Live'
      );
      // DISPATCHARR_LIVE_CONTINUE_LIVE_CAST_SWITCH_GUARDED: block
      // any concurrent recovery entry (recoverFailedChromecast's own
      // re-entrancy check and tick()'s entry guard both read this same
      // ref) and fence syncCastState against the transient IDLE this
      // switch's own archiveController.stop() causes, exactly like
      // recoverFailedChromecast guards its own operation.
      castFailureRecoveryRef.current = true;
      beginDispatcharrCastIntentionalHandback();
      setCastReturningToLive(true); // DISPATCHARR_CAST_CONTINUE_LIVE_INDICATOR
      const op = beginCastOp('continue-live', {
        url: liveSourceUrl,
        metadata: restoredMetadata,
      });
      let outcome = 'pending';
      try {
        const previous = op.prev;
        if (previous) await awaitCastOpUnwound(previous, 8000);
        assertCastOp(op);
        // Rewind abandoned before its LOAD reached the TV: the TV is still on
        // the warm Live tee -- just take that lease back.
        if (
          previous?.kind === 'rewind-on-cast' &&
          !previous.archiveLoadSent &&
          unparkLiveCastLease(liveSourceUrl)
        ) {
          // DISPATCHARR_LIVE_PAUSE_CHOICE_R136 (review): "Resume from" starts
          // Rewind on Cast from a PAUSED TV; abandoned before its LOAD, the TV
          // still holds that paused Live -- Continue Live means play it, at the
          // edge (the gate of continueLiveCast refuses inside this op).
          const heldLive = readLiveCastMedia(liveSourceUrl);
          if (heldLive?.state === 'PAUSED') {
            const end = Number(heldLive.range?.end);
            const start = Number(heldLive.range?.start);
            const behind =
              Number.isFinite(end) && end > 0 && Number.isFinite(heldLive.t)
                ? end - heldLive.t
                : null;
            const seekTo =
              behind != null && behind > DISPATCHARR_CAST_LIVE_EDGE_HOLDBACK_SECONDS + 2
                ? Math.max(
                    Number.isFinite(start) ? start + 1 : 0,
                    end - DISPATCHARR_CAST_LIVE_EDGE_HOLDBACK_SECONDS
                  )
                : null;
            try {
              await castLiveMediaCommand(heldLive.media, seekTo == null ? 'play' : 'seek-play', {
                seekTo,
              });
              console.info(
                `[live-pause-r136] Continue Live: the un-parked TV was paused; ` +
                  (seekTo == null ? 'play' : `seek -> ${seekTo.toFixed(1)}`)
              );
            } catch (error) {
              console.warn('[live-pause-r136] Continue Live: the un-parked TV stayed paused', error);
            }
            assertCastOp(op);
          }
          setCastReturningToLive(false);
          requestArchiveStop(streamUrl);
          setLiveTimeshiftDraftSeconds(0);
          setShowLiveRewindPicker(false);
          setLiveTimeshiftPlaybackSeconds(0);
          showVideo(liveSourceUrl, 'live', restoredMetadata);
          outcome = 'ok';
          console.info('[live-provider-timeshift] Continue Live: TV kept the warm Live tee');
          return;
        }
        const archiveController =
          archiveCastControllerRef.current || dispatcharrCastRuntime.archiveController;
        if (archiveController) {
          await awaitCastOperation(
            archiveController.stop(),
            3500,
            'Continue Live: stop archive Cast pipeline',
            false
          );
        }
        archiveCastControllerRef.current = null;
        assertCastOp(op);
        // One retry: a first attempt can lose to a Live channel that is still
        // finishing a teardown; the TV keeps the rewind meanwhile (R14).
        let lastError = null;
        for (let attempt = 1; attempt <= 2; attempt += 1) {
          const session = castContextRef.current?.getCurrentSession?.();
          if (!session) throw new Error('Chromecast session is no longer available');
          try {
            await loadLiveOnChromecast(session, liveSourceUrl, {
              keepPreviousLease: true,
              op,
            });
            lastError = null;
            break;
          } catch (error) {
            if (isCastSuperseded(error) || !castOpIsCurrent(op)) throw error;
            lastError = error;
            console.warn(
              `[chromecast-r134] Continue Live attempt ${attempt}/2 failed:`,
              error
            );
            if (attempt < 2) {
              await new Promise((resolve) => window.setTimeout(resolve, 1500));
              assertCastOp(op);
            }
          }
        }
        if (lastError) throw lastError;
        assertCastOp(op);
        // DISPATCHARR_CAST_LIVE_WARM_DURING_REWIND_R21
        releaseParkedLiveLease('Continue Live is on the warm tee');
        showVideo(liveSourceUrl, 'live', restoredMetadata);
        outcome = 'ok';
        console.info('[live-provider-timeshift] Continue Live: Cast now playing plain Live');
      } catch (error) {
        if (isCastSuperseded(error) || !castOpIsCurrent(op)) {
          // Stop Casting / X / a source change took over; it owns the outcome.
          outcome = 'superseded';
          console.info(`[chromecast-r134] Continue Live #${op.seq} superseded`);
        } else if (
          !castContextRef.current?.getCurrentSession?.() &&
          !['stop', 'handback', 'close'].includes(op.prev?.kind)
        ) {
          // The TV session really ended (TV off, app closed): R109 applies.
          outcome = 'gone';
          console.warn('[live-provider-timeshift] Continue Live (Cast): session gone:', error);
          setCastReturningToLive(false);
          releaseParkedLiveLease('Continue Live failed'); // DISPATCHARR_CAST_LIVE_WARM_DURING_REWIND_R21
          // A failed attempt restored the archive lease for the retry; with the
          // TV gone nothing renews it for good -- recovery may be in cooldown.
          void stopCastSessionLease(true, 'continue-live-session-gone');
          void releaseDeferredCastLease();
          castFailureRecoveryRef.current = false;
          void recoverFailedChromecast('Continue Live: the Chromecast session ended');
        } else {
          // The TV is alive but would not switch: Live continues in the Web
          // player ("Loading video..." until it plays) instead of a dead end.
          outcome = 'handback';
          console.warn(
            '[live-provider-timeshift] Continue Live (Cast) failed; Live continues in the Web player:',
            error
          );
          void handBackCastTransition({
            mode: 'live',
            url: liveSourceUrl,
            metadata: restoredMetadata,
            reason: `Continue Live failed: ${error?.message || error}`,
            supersededOp: op,
          });
        }
      } finally {
        if (outcome === 'ok') {
          // The TV plays Live; the Rewind start raised loading for a local
          // player that never came (a superseded Rewind on Cast never clears it).
          setIsLoading(false);
          castFailureRecoveryRef.current = false;
          // DISPATCHARR_LIVE_CONTINUE_LIVE_SWITCH_FINISH_NOT_REARM: finish,
          // not re-arm. The danger window this fence covered
          // (archiveController.stop()'s transient IDLE on the OLD
          // content) is already behind us here -- castActive is
          // already correctly true. A re-armed fence would never
          // self-clear via syncCastState's !session branch (the session
          // stays connected) and would force castActive back to false on
          // the next routine CAST_STATE_CHANGED/SESSION_STATE_CHANGED.
          finishDispatcharrCastIntentionalHandback();
          castRecoveryCooldownUntil = Date.now() + 15000;
        }
        settleCastOp(op);
        window.setTimeout(() => {
          liveTimeshiftReturningToLiveRef.current = false;
        }, 1200);
      }
      return;
    }

    // DISPATCHARR_CONTINUE_LIVE_KEEPS_FULLSCREEN_R123: the unconditional exit
    // here predates R121, when plain Live went fullscreen on the native
    // <video> and could not inherit the time-shift's stage fullscreen. Since
    // R121 BOTH modes fullscreen the SAME stage element (which never leaves
    // the DOM across showVideo -- only the <video> src and the overlays
    // change), so the switch can simply stay fullscreen, exactly like the
    // Rewind direction already does (it has no fullscreen code at all). Only
    // a fullscreen element that does NOT host our player is still exited.
    // iOS is unchanged: its pseudo-fullscreen is cleared below and it has no
    // element fullscreen to preserve.
    // DISPATCHARR_CAST_TRANSITION_OPS_R134: a pending Web handback of a failed
    // Rewind on Cast must not resume the rewind underneath this switch.
    if (castOpInFlight()) {
      settleCastOp(beginCastOp('local-continue-live'));
      castHandoffInProgressRef.current = false;
    }
    clearArchivePseudoFullscreen();
    try {
      if (typeof document !== 'undefined') {
        const active =
          document.fullscreenElement || document.webkitFullscreenElement;
        const stageStaysFullscreen = Boolean(
          active && videoRef.current && active.contains(videoRef.current)
        );
        if (active && !stageStaysFullscreen) {
          if (document.exitFullscreen) {
            await document.exitFullscreen().catch(() => {});
          } else if (document.webkitExitFullscreen) {
            document.webkitExitFullscreen();
          } else if (document.webkitCancelFullScreen) {
            document.webkitCancelFullScreen();
          }
        }
      }
    } catch {}

    // Destroy browser HLS first so no late fragment request can race /stop. The
    // return-to-Live fence makes the resulting pause event transport-neutral.
    safeDestroyPlayer();
    const stopped = await requestArchiveStop(streamUrl);
    if (!stopped) {
      console.debug('[live-r5.1] provider time-shift stop was already complete or unavailable');
    }
    await new Promise((resolve) => window.setTimeout(resolve, 80));

    setLiveTimeshiftDraftSeconds(0);
    setShowLiveRewindPicker(false);
    setLiveTimeshiftPlaybackSeconds(0);
    showVideo(liveSourceUrl, 'live', restoredMetadata);

    // Keep the fence across React effect cleanup/source replacement.
    window.setTimeout(() => {
      liveTimeshiftReturningToLiveRef.current = false;
    }, 1200);
  };

  // Initialize live stream player (mpegts.js)
  const initializeLivePlayer = async (
    isCatchup = false,
    forcedAdaptiveStreamUrl = null,
    forcedArchiveOffset = null
  ) => {
    // DISPATCHARR_LIVE_GATE_KNOWS_TIER_R37
    // The gate answers with the live-sync band for this client's speed, so the
    // speed must be known before it is called -- otherwise the first Watch Live
    // after a page load silently falls back to the flat web_live values. The
    // result is reused below, so the speed test still runs once.
    let liveMeasurement = null;
    if (!isCatchup && !forcedAdaptiveStreamUrl) {
      try {
        liveMeasurement = await measureAdaptiveOutputProfile();
      } catch {
        liveMeasurement = null;
      }
    }

    // DISPATCHARR_UNIFIED_CAST_LEASE_NO_WEB_HEARTBEATS_R8_8_54: one-shot completed-tail cleanup; the MPEG-TS request itself owns Web Live.
    if (!isCatchup) {
      const reopenUrl = buildDispatcharrLiveReopenUrl(liveMeasurement?.profile);
      if (reopenUrl) {
        try {
          const reopenResponse = await fetch(reopenUrl, {
            method: 'POST',
            cache: 'no-store',
            credentials: 'same-origin',
          });
          // DISPATCHARR_WEB_LIVE_TUNING_FROM_CONFIG_R29: how close to the live
          // edge this player sits is an operational setting, so it travels on
          // this gate's response headers straight out of the bind-mounted
          // tools JSON (section "web_live"). Edit that file and the next Watch
          // Live uses the new values — no rebuild, no restart. Anything the
          // server does not send keeps the defaults below.
          const tunedHeader = (name, fallback) => {
            const value = Number(reopenResponse?.headers?.get?.(name));
            return Number.isFinite(value) && value > 0 ? value : fallback;
          };
          noteLiveRewindFloor(reopenResponse); // DISPATCHARR_ARCHIVE_PTS_ALIGN_R137
          // DISPATCHARR_WEB_LIVE_DIRECT_ADVERTISED_R49
          setServerDirectAllowed(
            String(
              reopenResponse?.headers?.get?.('X-Dispatcharr-Live-Direct') || 'auto'
            ).toLowerCase() !== 'off'
          );
          // DISPATCHARR_WEB_LIVE_SYNC_MODE_R30
          const mode = String(
            reopenResponse?.headers?.get?.('X-Dispatcharr-Live-Sync-Mode') || 'seek'
          ).toLowerCase();
          liveSyncTuningRef.current = {
            mode: ['seek', 'rate', 'off'].includes(mode) ? mode : 'seek',
            target: tunedHeader('X-Dispatcharr-Live-Sync-Target', 1.0),
            max: tunedHeader('X-Dispatcharr-Live-Sync-Max', 2.0),
            rate: tunedHeader('X-Dispatcharr-Live-Sync-Rate', 1.15),
            chaseMax: tunedHeader('X-Dispatcharr-Live-Chase-Max', 5.0),
            chaseRemain: tunedHeader('X-Dispatcharr-Live-Chase-Remain', 1.0),
            // DISPATCHARR_WEB_LIVE_TUNING_PER_PROFILE_R34: which tier answered.
            profile:
              reopenResponse?.headers?.get?.(
                'X-Dispatcharr-Live-Sync-Profile'
              ) || 'default',
            // DISPATCHARR_LIVE_ADAPTIVE_BAND_R64
            adaptive:
              String(
                reopenResponse?.headers?.get?.('X-Dispatcharr-Live-Adaptive') || '1'
              ) !== '0',
            widenStep: tunedHeader('X-Dispatcharr-Live-Widen-Step', 0.4),
            widenMax: tunedHeader('X-Dispatcharr-Live-Widen-Max', 2.0),
            recoverSeconds: tunedHeader('X-Dispatcharr-Live-Recover-Seconds', 60),
          };
        } catch {
          // Best effort only; the real MPEG-TS request remains authoritative.
        }
      }
    }
    if (!videoRef.current || !streamUrl) return;

    // DISPATCHARR_LIVE_MOBILE_STABILITY_R8_8_22
    const startupVideo = videoRef.current;
    startupVideo.playsInline = true;
    startupVideo.setAttribute('playsinline', '');
    startupVideo.setAttribute('webkit-playsinline', '');


    const adaptiveRequestId = ++adaptiveOutputRequestRef.current;
    let catchupStreamUrl = streamUrl;
    if (isCatchup) {
      const directUrl = new URL(streamUrl, window.location.origin);
      directUrl.searchParams.set(
        'offset',
        String(
          Math.max(
            0,
            Math.floor(
              forcedArchiveOffset == null
                ? Number(archiveOffsetSeconds) || 0
                : Number(forcedArchiveOffset) || 0
            )
          )
        )
      );
      catchupStreamUrl = directUrl.toString();
    }
    // DISPATCHARR_LIVE_GATE_KNOWS_TIER_R37: the measurement the gate used.
    const adaptiveStreamUrl = isCatchup
      ? catchupStreamUrl
      : forcedAdaptiveStreamUrl ||
        (liveMeasurement
          ? appendAdaptiveOutputProfile(
              streamUrl,
              liveMeasurement.profile,
              liveMeasurement.delivery
            )
          : await withAdaptiveLiveOutputProfile(streamUrl));
    if (
      adaptiveRequestId !== adaptiveOutputRequestRef.current ||
      !videoRef.current
    ) {
      return;
    }

    setIsLoading(true);
    setLoadError(null);
    setShowControls(false);

    const directLiveRequest =
      !isCatchup &&
      new URL(adaptiveStreamUrl, window.location.origin).searchParams.get(
        'adaptive_delivery'
      ) === 'direct';
    const directArchiveRequest =
      isCatchup && Boolean(archiveChannelIdFromDirectUrl(adaptiveStreamUrl));
    let retriedDirectMediaError = false;
    let retriedArchiveMediaError = false;

    try {
      if (!mpegts.getFeatureList().mseLivePlayback) {
        setIsLoading(false);
        setLoadError(
          "Your browser doesn't support live video streaming. Please try Chrome or Edge."
        );
        return;
      }

      // mpegts.js workers run in WorkerGlobalScope where relative URLs are
      // not resolved against the page origin. Always pass an absolute URL.
      const absoluteStreamUrl =
        adaptiveStreamUrl.startsWith('/') && typeof window !== 'undefined'
          ? `${window.location.origin}${adaptiveStreamUrl}`
          : adaptiveStreamUrl;

      // DISPATCHARR_CHROMECAST_R8_8_28
      if (!isCatchup) {
        liveAdaptiveStreamUrlRef.current = absoluteStreamUrl;
        try {
          const selectedUrl = new URL(absoluteStreamUrl, window.location.origin);
          const selectedProfile =
            selectedUrl.searchParams.get('adaptive_output_profile');
          if (selectedProfile) castAdaptiveProfileRef.current = selectedProfile;
        } catch {
          // buildChromecastLiveUrl will reject if no adaptive key can be found.
        }
      }

      // Read the JWT from the auth store at player-creation time rather than
      // relying on the closure-captured `accessToken` value.  mpegts.js has
      // no per-request setup hook (unlike hls.js's xhrSetup), so this header
      // is baked into the IO loader for the life of the player; we just want
      // to be sure we use the freshest token available at the moment of
      // connection rather than whatever React-render snapshot we closed over.
      const liveAccessToken = useAuthStore.getState().accessToken;

      // DISPATCHARR_WEB_LIVE_TUNING_FROM_CONFIG_R29
      // DISPATCHARR_WEB_LIVE_SYNC_MODE_R30
      const liveTuning = liveSyncTuningRef.current || {
        mode: 'seek',
        target: 1.0,
        max: 2.0,
        rate: 1.15,
        chaseMax: 5.0,
        chaseRemain: 1.0,
      };
      // seek: one jump back to target when the buffer passes max, then the
      // playback rate is left alone — no continuous speed changes to hear.
      // rate: play faster until the target is reached (mpegts.js liveSync).
      // off:  keep the head start the server hands out.
      const liveSeekMode = liveTuning.mode === 'seek';
      // R64: remember the floor the server handed out; the band may drift above
      // it while the link misbehaves, never below.
      liveSyncBandRef.current = {
        adaptive: liveTuning.adaptive !== false && liveSeekMode,
        floorTarget: liveTuning.target,
        floorMax: liveTuning.max,
        target: liveTuning.target,
        max: liveTuning.max,
        ceilMax: liveTuning.max + (liveTuning.widenMax ?? 2.0),
        step: liveTuning.widenStep ?? 0.4,
        recoverMs: (liveTuning.recoverSeconds ?? 60) * 1000,
        lastStallAt: 0,
      };
      const liveRateMode = liveTuning.mode === 'rate';
      if (!isCatchup) {
        console.info(
          `[live-r34] live edge mode=${liveTuning.mode} target=${liveTuning.target}s ` +
            `max=${liveTuning.max}s rate=${liveTuning.rate}x ` +
            `profile=${liveTuning.profile ?? 'default'}Mbps (web_live in the tools JSON)` +
            // DISPATCHARR_LIVE_ADAPTIVE_BAND_R64: without this a quiet session
            // is indistinguishable from the feature being off, because
            // [live-r64] only speaks when the band actually moves.
            (liveSyncBandRef.current?.adaptive
              ? ` | adaptive: floor ${liveSyncBandRef.current.floorTarget}/` +
                `${liveSyncBandRef.current.floorMax}, ceiling ` +
                `${liveSyncBandRef.current.ceilMax.toFixed(1)}, step ` +
                `${liveSyncBandRef.current.step}, recover ` +
                `${Math.round(liveSyncBandRef.current.recoverMs / 1000)}s`
              : ' | adaptive: off')
        );
      }

      // R64: after a quiet spell the extra room is no longer earned, so give
      // it back a little at a time and let the player return toward the edge.
      if (liveBandDecayTimerRef.current) {
        clearInterval(liveBandDecayTimerRef.current);
      }
      liveBandDecayTimerRef.current = window.setInterval(() => {
        const band = liveSyncBandRef.current;
        if (!band || !band.adaptive) return;
        if (band.max <= band.floorMax && band.target <= band.floorTarget) return;
        if (!band.lastStallAt || Date.now() - band.lastStallAt < band.recoverMs) return;
        band.max = Math.max(band.floorMax, band.max - band.step / 2);
        band.target = Math.max(band.floorTarget, band.target - band.step / 2);
        band.lastStallAt = Date.now();
        applyLiveBand('quiet, narrowing');
      }, 10000);

      const player = mpegts.createPlayer(
        {
          type: 'mpegts',
          url: absoluteStreamUrl,
          isLive: !isCatchup,
          cors: true,
        },
        {
          enableWorker: true,
          enableStashBuffer: false,
          // DISPATCHARR_LIVE_TS_LATENCY_SYNC_R27: the server hands a new client
          // roughly two seconds out of its ring buffer the moment it connects
          // — measured 2026-09-13: 40.8 Mbps in the first half second, then
          // real time, so ~11.9s of content arrived in ~10s of wall clock.
          // With both catch-up mechanisms off, mpegts.js played that head start
          // from its beginning and stayed that far behind for the whole
          // session: exactly the 1-1.5s the Web player trailed TiviMate by,
          // even after R26 put both on the same transport.
          //
          // liveSync drains it smoothly — it nudges playbackRate to 1.1 while
          // more than liveSyncMaxLatency is buffered ahead and returns to 1.0
          // at the target, so the head start is gone within ~15s and nothing
          // jumps. The seek-based chaser stays as a safety net for
          // catastrophic drift only (>5s, e.g. after a long stall), where
          // playing 10% faster would take minutes to recover. Both are gated
          // on isLive inside mpegts.js, so the catch-up path (isCatchup =>
          // isLive: false) is untouched.
          // DISPATCHARR_LIVE_TS_EDGE_R28: 0.8s of buffer *is* 0.8s behind the
          // live edge, which was the second the user still saw against
          // TiviMate. Held as close to the edge as the pipeline allows: the
          // floor underneath this is the transcode's own 0.21s (measured),
          // since TiviMate pulls the channel passthrough while the browser
          // needs H.264. 1.15 drains the server's ~2s connect burst in ~13s.
          // If remote viewers on weaker links start stuttering, this target is
          // the one number to raise.
          liveSync: liveRateMode,
          liveSyncTargetLatency: liveTuning.target,
          liveSyncMaxLatency: liveTuning.max,
          liveSyncPlaybackRate: liveTuning.rate,
          // In seek mode the chaser IS the correction, so it works to the
          // configured band; in rate mode it stays a far-away safety net for
          // drift the playback rate could never claw back.
          liveBufferLatencyChasing: liveTuning.mode !== 'off',
          liveBufferLatencyChasingOnPaused: false,
          liveBufferLatencyMaxLatency: liveSeekMode
            ? liveTuning.max
            : liveTuning.chaseMax,
          liveBufferLatencyMinRemain: liveSeekMode
            ? liveTuning.target
            : liveTuning.chaseRemain,
          autoCleanupSourceBuffer: true,
          autoCleanupMaxBackwardDuration: 120,
          autoCleanupMinBackwardDuration: 60,
          reuseRedirectedURL: true,
          headers: liveAccessToken
            ? { Authorization: `Bearer ${liveAccessToken}` }
            : undefined,
        }
      );

      player.attachMediaElement(videoRef.current);

      const retryDirectWithTranscode = (reason) => {
        if (!directLiveRequest || retriedDirectMediaError) return false;
        retriedDirectMediaError = true;
        const fallbackUrl = withAdaptiveLiveTranscodeFallback(
          adaptiveStreamUrl
        );
        console.warn(
          `[adaptive-output] Direct ${reason}; retrying with transcode`
        );
        try {
          player.destroy();
        } catch {}
        if (playerRef.current === player) playerRef.current = null;
        initializeLivePlayer(false, fallbackUrl);
        return true;
      };

      const retryArchiveWithCompatibleHls = (reason) => {
        if (!directArchiveRequest || retriedArchiveMediaError) return false;
        retriedArchiveMediaError = true;
        try {
          player.destroy();
        } catch {}
        if (playerRef.current === player) playerRef.current = null;
        void startArchiveCompatibilityFallback(adaptiveStreamUrl, reason).catch(
          (error) => {
            setIsLoading(false);
            setLoadError(
              `Archive compatibility startup failed: ${error?.message || error}`
            );
          }
        );
        return true;
      };

      // Restore saved volume
      const { volume: savedVolume, muted: savedMuted } = getPlayerPrefs();
      if (typeof savedVolume === 'number')
        videoRef.current.volume = savedVolume;
      if (typeof savedMuted === 'boolean') videoRef.current.muted = savedMuted;

      // Active before load() so sync events from this instance pass the guard.
      playerRef.current = player;
      // DISPATCHARR_LIVE_ADAPTIVE_BAND_R64: prove the write path once, at the
      // floor values, so the mechanism is observable without waiting for a
      // stall - and so an unreachable engine config is reported immediately.
      applyLiveBand('initial');

      player.on(mpegts.Events.LOADING_COMPLETE, () => {
        // Ignore events from a player that reconnect already replaced.
        if (playerRef.current !== player) return;
        setIsLoading(false);
        if (isCatchup) {
          setArchivePositionSeconds(archiveDurationSeconds);
          setArchivePaused(true);
          if (!archiveCompatibilityStartingRef.current) requestArchiveStop();
        }
      });

      player.on(mpegts.Events.METADATA_ARRIVED, () => {
        if (playerRef.current !== player) return;
        setIsLoading(false);
      });

      player.on(mpegts.Events.ERROR, (errorType, errorDetail) => {
        if (playerRef.current !== player) return;
        setIsLoading(false);

        if (
          directLiveRequest &&
          !retriedDirectMediaError &&
          errorType === 'MediaError'
        ) {
          if (retryDirectWithTranscode('MSE media error')) return;
        }

        if (
          directArchiveRequest &&
          !retriedArchiveMediaError &&
          errorType === 'MediaError'
        ) {
          if (retryArchiveWithCompatibleHls('MSE media error')) return;
        }

        if (errorType === 'NetworkError' && errorDetail?.includes('aborted')) {
          return;
        }

        console.error('Player error:', errorType, errorDetail);

        // mpegts.js has no partial recovery; NetworkError is the only transient
        // class worth a full destroy + rebuild.
        if (
          errorType === 'NetworkError' &&
          reconnectAttemptsRef.current < MAX_LIVE_RECONNECT_ATTEMPTS
        ) {
          if (reconnectTimeoutRef.current) return;

          reconnectAttemptsRef.current += 1;
          const attempt = reconnectAttemptsRef.current;
          const delay = Math.min(
            LIVE_RECONNECT_BASE_DELAY_MS * 2 ** (attempt - 1),
            LIVE_RECONNECT_MAX_DELAY_MS
          );

          // Destroy first (frees the failed session); set message after because
          // safeDestroyPlayer clears loadError.
          safeDestroyPlayer();
          setLoadError(
            `Connection lost, reconnecting... (attempt ${attempt}/${MAX_LIVE_RECONNECT_ATTEMPTS})`
          );
          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectTimeoutRef.current = null;
            initializeLivePlayer();
          }, delay);
        } else {
          setLoadError(getLivePlayerErrorMessage(errorType, errorDetail));
        }
      });

      player.on(mpegts.Events.MEDIA_INFO, (mediaInfo) => {
        if (playerRef.current !== player) return;
        setIsLoading(false);
        reconnectAttemptsRef.current = 0;
        // DISPATCHARR_RECOVERY_LIVE_NO_VIDEO_COPY_R8_8_48
        // Recovery prioritizes deterministic playback over c:v copy. A raw
        // direct H.264+AАC source stays direct; every incompatible elementary
        // stream combination is normalized by the existing full NVENC tier.
        if (
          directLiveRequest &&
          !directMseCodecsAreSupported(mediaInfo) &&
          retryDirectWithTranscode(
            `source codecs video=${mediaInfo?.videoCodec || 'unknown'} ` +
              `audio=${mediaInfo?.audioCodec || 'unknown'}`
          )
        ) {
          return;
        }
        if (
          directArchiveRequest &&
          !directMseCodecsAreSupported(mediaInfo) &&
          retryArchiveWithCompatibleHls(
            `source codecs video=${mediaInfo?.videoCodec || 'unknown'} ` +
              `audio=${mediaInfo?.audioCodec || 'unknown'}`
          )
        ) {
          return;
        }
        try {
          player.play().catch((e) => {
            if (playerRef.current !== player) return;
            console.log('Auto-play prevented:', e);
            if (isCatchup) {
              setArchivePaused(true);
              setIsLoading(false);
              setLoadError(null);
            } else {
              setLoadError('Auto-play was prevented. Click play to start.');
            }
          });
        } catch (e) {
          if (playerRef.current !== player) return;
          console.log('Error during play:', e);
          setLoadError(`Playback error: ${e.message}`);
        }
      });

      player.load();

      if (directLiveRequest) {
        window.setTimeout(() => {
          if (
            playerRef.current === player &&
            (videoRef.current?.paused || videoRef.current?.currentTime < 0.25)
          ) {
            retryDirectWithTranscode('startup stall');
          }
        }, 2500);
      }
    } catch (error) {
      setIsLoading(false);
      console.error('Error initializing player:', error);

      if (
        error.message?.includes('codec') ||
        error.message?.includes('format')
      ) {
        setLoadError(
          'Codec not supported by your browser. Please try a different browser (Chrome/Edge recommended).'
        );
      } else {
        setLoadError(`Initialization error: ${error.message}`);
      }
    }
  };

  useEffect(() => {
    if (!isVisible || !streamUrl) {
      safeDestroyPlayer();
      return;
    }

    // DISPATCHARR_CHROMECAST_PROFILE_SWITCH_R8_8_35
    // DISPATCHARR_CAST_FAILSAFE_ARCHIVE_RESEEK_R8_8_47: Cast ownership is scoped to the exact source URL. A stale
    // receiver from another channel/archive must never block Watch Live.
    const castOwnsCurrentSource =
      (castActiveRef.current || dispatcharrCastRuntime.active) &&
      (!dispatcharrCastRuntime.sourceUrl ||
        dispatcharrCastRuntime.sourceUrl === streamUrl);
    if (
      (contentType === 'live' || contentType === 'catchup' || contentType === 'catchup-hls') &&
      castOwnsCurrentSource
    ) {
      safeDestroyPlayer();
      return;
    }

    const archiveCompatibilityActive = Boolean(
      contentType === 'catchup' && archiveCompatibilityControllerRef.current?.active
    );

    // Clean up any existing player
    if (!archiveCompatibilityActive) safeDestroyPlayer();
    reconnectAttemptsRef.current = 0;

    // Initialize the appropriate player based on content type
    if (contentType === 'vod' || contentType === 'catchup-hls') {
      // DISPATCHARR_NATIVE_HLS_CATCHUP_R8_8_0
      // catchup-hls intentionally reuses the native VOD path; on iOS we bypass
      // hls.js so WebKit owns duration, seeking and true system fullscreen.
      initializeVODPlayer();
    } else if (contentType === 'catchup') {
      if (!archiveCompatibilityActive) initializeLivePlayer(true);
    } else {
      // DISPATCHARR_LIVE_DESKTOP_PROGRESSIVE_TS_R26
      void startLiveWebPlayback();
    }

    // Attach volume/player listeners now that the video element is in the DOM.
    const video = videoRef.current;
    if (video) {
      // Live time-shift draws its own bar (TimeshiftSeekBar), which owns the
      // position readout the native timeline cannot provide. Leaving the
      // native controls on here as well showed two scrubbers and two
      // fullscreen buttons stacked on top of each other.
      // DISPATCHARR_LIVE_DESKTOP_NO_NATIVE_CONTROLS_R121: desktop Live now has
      // its own bar too (R120), so it must get controls=false HERE as well --
      // this imperative write runs after render and silently overrode R120's
      // JSX prop (React never re-applies an unchanged prop). With native
      // controls left on, the browser's shadow-DOM fullscreen button put the
      // <video> itself into fullscreen, where Chromium forces its own controls,
      // our "Resume Live" overlay cannot render, and the focused UA button ate
      // Space (exiting fullscreen). Touch devices keep native controls.
      video.controls =
        contentType === 'catchup'
          ? false
          : contentType === 'catchup-hls' || contentType === 'live'
            ? coarsePointer
            : true;
      // DISPATCHARR_LIVE_MOBILE_STABILITY_R8_8_22
      video.playsInline = true;
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
    }
    const handleVolumeChange = () => {
      savePlayerPrefs({ volume: video.volume, muted: video.muted });
      if (contentType === 'catchup') setArchiveMuted(video.muted);
      // DISPATCHARR_LIVE_DESKTOP_CUSTOM_CONTROLS_R121: the Live bar's mute icon
      // and volume slider follow the restored pref (video.muted/volume are set
      // from getPlayerPrefs at attach) and every later change.
      if (contentType === 'live') {
        setLiveMuted(video.muted);
        setLiveVolume(video.volume);
      }
    };
    const handleArchiveTimeUpdate = () => {
      if (contentType !== 'catchup' || archiveScrubbingRef.current) return;
      const mediaTime = Math.max(0, Number(video.currentTime) || 0);
      if (archiveSegmentStartMediaTimeRef.current == null) {
        archiveSegmentStartMediaTimeRef.current = mediaTime;
      }
      const segmentTime = Math.max(
        0,
        mediaTime - archiveSegmentStartMediaTimeRef.current
      );
      setArchivePositionSeconds(
        Math.min(archiveDurationSeconds, archiveOffsetSeconds + segmentTime)
      );
    };
    const handleArchivePlay = () => {
      if (contentType === 'catchup') setArchivePaused(false);
    };
    const handleArchivePause = () => {
      if (contentType === 'catchup') setArchivePaused(true);
    };
    const handleArchiveEnded = () => {
      if (contentType === 'catchup') {
        setArchivePositionSeconds(archiveDurationSeconds);
        setArchivePaused(true);
        clearArchivePseudoFullscreen();
        requestArchiveStop();
        safeDestroyPlayer();
        return;
      }
      if (contentType === 'catchup-hls') {
        // The finite HLS producer naturally reaches EOF at programme end. Keep
        // the session unsealed so native Replay can request segment 0 again; if
        // that segment has been pruned, the backend restarts one producer at 0.
        setIsLoading(false);
      }
    };
    // DISPATCHARR_LIVE_REMOVE_NATIVE_SEEK_TRIGGER: the native-seek-triggered provider
    // time-shift start is removed -- an explicit Rewind button now
    // owns this instead (see the badge further down this component).
    // DISPATCHARR_IOS_BADGE_FOLLOWS_NATIVE_CONTROLS_R81: WebKit shows its
    // controls when playback pauses and only fades them while playing, so the
    // badges beside them do the same.
    const handleBadgePause = () => {
      if (castHoverRequired) return;
      showBadgesWithNativeControls({ fade: false });
    };
    const handleBadgePlay = () => {
      if (castHoverRequired) return;
      if (!castPlayerHover) return;
      showBadgesWithNativeControls();
    };
    video?.addEventListener('pause', handleBadgePause);
    video?.addEventListener('play', handleBadgePlay);
    video?.addEventListener('volumechange', handleVolumeChange);
    video?.addEventListener('timeupdate', handleArchiveTimeUpdate);
    video?.addEventListener('play', handleArchivePlay);
    video?.addEventListener('pause', handleArchivePause);
    video?.addEventListener('ended', handleArchiveEnded);

    return () => {
      video?.removeEventListener('pause', handleBadgePause);
      video?.removeEventListener('play', handleBadgePlay);
      video?.removeEventListener('volumechange', handleVolumeChange);
      video?.removeEventListener('timeupdate', handleArchiveTimeUpdate);
      video?.removeEventListener('play', handleArchivePlay);
      video?.removeEventListener('pause', handleArchivePause);
      video?.removeEventListener('ended', handleArchiveEnded);
      const activeUrl = archiveLocalRuntimeUrlRef.current || streamUrl;
      const compatibilityHlsIsActive =
        contentType === 'catchup' &&
        archiveCompatibilityControllerRef.current?.active;
      if (!compatibilityHlsIsActive) safeDestroyPlayer();
      if (contentType === 'live') void stopLiveWebFmp4Session();
      if (
        contentType === 'catchup-hls' &&
        !liveTimeshiftReturningToLiveRef.current
      ) {
        requestArchiveStop(activeUrl);
      }
    };
  }, [
    isVisible,
    streamUrl,
    contentType,
    archiveOffsetSeconds,
    archiveDurationSeconds,
  ]);

  // DISPATCHARR_LIVE_DESKTOP_NO_NATIVE_CONTROLS_R121: the imperative controls
  // write above runs once per source; a hybrid touch laptop can flip
  // coarsePointer afterwards, and React only rewrites the attribute when its
  // own prop value changes. Once the pointer is a mouse again, never leave a
  // stale native bar (and its <video>-element fullscreen) on desktop Live.
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !isVisible || contentType !== 'live' || coarsePointer) return;
    video.controls = false;
  }, [isVisible, contentType, coarsePointer]);

  // DISPATCHARR_LIVE_DESKTOP_CLICK_DBLCLICK_R121: a pending single-click toggle
  // must not fire into a closed player or the next channel.
  useEffect(
    () => () => {
      window.clearTimeout(liveClickTimerRef.current);
      liveClickTimerRef.current = null;
    },
    [isVisible, streamUrl, contentType]
  );

  // DISPATCHARR_LIVE_MOBILE_STABILITY_R8_8_22
  // DISPATCHARR_LIVE_FULLSCREEN_RESUME_R8_8_24
  // DISPATCHARR_TRANSPORT_SUSPEND_R8_8_27
  // Pause is now a UI/transport state transition: release mpegts.js immediately
  // and let live_proxy client_count / Channel Shutdown Delay own backend cleanup.
  // The FloatingVideo component stays open and exposes Resume Live.
  useEffect(() => {
    if (!isVisible || contentType !== 'live') {
      liveInlineGuardUntilRef.current = 0;
      liveWasPlayingBeforeFullscreenRef.current = false;
      liveLastPointerAtRef.current = 0;
      liveFullscreenResumeUntilRef.current = 0;
      livePlaybackStartedRef.current = false;
      liveFullscreenActiveRef.current = false;
      if (liveFullscreenPauseDecisionTimerRef.current != null) {
        window.clearTimeout(liveFullscreenPauseDecisionTimerRef.current);
        liveFullscreenPauseDecisionTimerRef.current = null;
      }
      if (liveFullscreenResumeTimerRef.current != null) {
        window.clearTimeout(liveFullscreenResumeTimerRef.current);
        liveFullscreenResumeTimerRef.current = null;
      }
      return undefined;
    }

    const video = videoRef.current;
    if (!video) return undefined;

    liveInlineGuardUntilRef.current = Date.now() + 10000;
    // DISPATCHARR_LIVE_IOS_SPURIOUS_PAUSE_R22
    liveDiagCountRef.current = 0;
    liveStartedAtRef.current = 0;
    liveAutoResumeCountRef.current = 0;

    const fullscreenResumePending = () =>
      liveWasPlayingBeforeFullscreenRef.current &&
      !video.ended &&
      Date.now() <= liveFullscreenResumeUntilRef.current;

    const tryFullscreenResume = (reason) => {
      if (transportSuspendedKindRef.current === 'live') return false;
      if (!fullscreenResumePending()) return false;
      if (!video.paused) return true;
      video
        .play()
        .then(() => {
          console.debug(`[live-inline] resumed after fullscreen exit (${reason})`);
        })
        .catch((error) => {
          console.debug(
            `[live-inline] fullscreen-exit resume skipped (${reason}):`,
            error
          );
        });
      return true;
    };

    const suspendLiveTransport = (reason) => {
      // DISPATCHARR_LIVE_FMP4_R3_STARTUP_RACE_HOTFIX
      // safeDestroyPlayer() emits synthetic pause events while a new
      // fMP4 HLS session is attaching. They must not release the exact
      // sender lease that the startup transaction just acquired.
      if (liveWebFmp4TransitionRef.current) {
        console.debug(
          `[live-cmaf-r3] synthetic ${reason} ignored during HLS transition`
        );
        return;
      }
      // DISPATCHARR_CHROMECAST_TAB_VISIBILITY_R8_8_34
      if (castActiveRef.current || castHandoffInProgressRef.current) {
        console.debug(
          `[transport-suspend] Live ${reason} ignored while Chromecast owns playback`
        );
        return;
      }
      // DISPATCHARR_LIVE_NATIVE_SEEK_PAUSE_RACE_FIX
      if (liveTimeshiftStartingRef.current) {
        console.debug(
          `[transport-suspend] Live ${reason} ignored while a provider time-shift session is starting`
        );
        return;
      }
      if (transportSuspendedKindRef.current === 'live') return;
      if (!livePlaybackStartedRef.current || video.ended) return;

      // DISPATCHARR_LIVE_PAUSE_CHOICE_R136: record the paused moment.
      {
        const now = Date.now();
        const fresh = livePauseSampleRef.current;
        const sample =
          fresh && now - fresh.at <= 2000
            ? fresh
            : {
                at: now,
                ahead: liveBufferAheadSeconds(video),
                hls: Boolean(liveWebFmp4SessionRef.current),
                sig: livePauseFrameSignature(video), // R137
              };
        livePauseSampleRef.current = null;
        // DISPATCHARR_ARCHIVE_PTS_ALIGN_R137: the constant learned for this
        // channel and player plus its margin, else the first guess.
        const lagChannel = liveChannelIdFromUrl(streamUrl);
        const lagKey = lagChannel ? `${lagChannel}|${sample.hls ? 'hls' : 'ts'}` : null;
        const learnedLag = readLivePauseLag(lagKey);
        const lagBase =
          learnedLag != null
            ? learnedLag.value
            : sample.hls
              ? LIVE_PAUSE_LOCAL_HLS_LAG_SECONDS
              : LIVE_PAUSE_LOCAL_TS_LAG_SECONDS;
        const lagGuess = sample.hls
          ? LIVE_PAUSE_LOCAL_HLS_LAG_SECONDS
          : LIVE_PAUSE_LOCAL_TS_LAG_SECONDS;
        const lagMargin =
          learnedLag != null
            ? LIVE_PAUSE_LAG_MARGIN_SECONDS +
              (sample.hls
                ? Math.max(0, LIVE_PAUSE_LAG_HLS_CYCLE_SECONDS - learnedLag.spread)
                : 0)
            : 0;
        const lagConstant = lagBase + lagMargin;
        const lag = (sample.ahead ?? 1) + lagConstant;
        const frameInstantMs = Math.round(sample.at - lag * 1000);
        setLivePause({
          where: 'local',
          streamUrl,
          pausedAtMs: sample.at,
          frameInstantMs,
          approx: sample.ahead == null,
          by: String(reason),
          busy: null,
          // R137: what a resume needs to check this estimate against the picture
          calibration:
            lagKey && sample.sig && sample.ahead != null
              ? {
                  key: lagKey,
                  sig: sample.sig,
                  at: sample.at,
                  ahead: sample.ahead,
                  base: lagBase,
                  constant: lagConstant,
                  guess: lagGuess,
                }
              : null,
        });
        setShowLiveRewindPicker(false);
        console.info(
          `[transport-suspend] Live paused (R136): frame ${lag.toFixed(1)}s behind ` +
            `(buffered ${sample.ahead == null ? '?' : sample.ahead.toFixed(2)}s, ` +
            `${sample.hls ? 'fmp4-hls' : 'mpegts'}) frame_at=${new Date(frameInstantMs).toISOString()} ` +
            `lag_constant=${lagConstant.toFixed(2)}s ${learnedLag != null ? 'learned' : 'default'}`
        );
        // R137: "Resume from" is on offer now; make sure its floor is current
        void refreshLiveRewindFloor();
      }

      transportSuspendedKindRef.current = 'live';
      setTransportSuspendedKind('live');
      livePlaybackStartedRef.current = false;
      console.debug(
        `[transport-suspend] Live ${reason}; releasing HTTP/mpegts transport`
      );
      // DISPATCHARR_LIVE_IOS_SPURIOUS_PAUSE_R22
      liveDiagBeacon('suspend', { r: String(reason).slice(0, 24) });

      // No explicit backend stop here. Destroying mpegts.js closes the HTTP
      // client; live_proxy observes client_count=0 and owns the resource policy.
      safeDestroyPlayer();
      void stopLiveWebFmp4Session();
    };

    const clearLiveFullscreenPauseDecision = () => {
      if (liveFullscreenPauseDecisionTimerRef.current != null) {
        window.clearTimeout(liveFullscreenPauseDecisionTimerRef.current);
        liveFullscreenPauseDecisionTimerRef.current = null;
      }
    };

    const liveFullscreenCurrentlyActive = () => {
      if (liveFullscreenActiveRef.current) return true;
      if (video.webkitDisplayingFullscreen) return true;
      if (typeof document === 'undefined') return false;
      return Boolean(
        document.fullscreenElement || document.webkitFullscreenElement
      );
    };

    const deferLiveFullscreenPauseDecision = (reason) => {
      if (!liveFullscreenCurrentlyActive()) return false;
      clearLiveFullscreenPauseDecision();
      console.debug(
        `[live-inline] fullscreen-pause-decision deferred (${reason})`
      );

      liveFullscreenPauseDecisionTimerRef.current = window.setTimeout(() => {
        liveFullscreenPauseDecisionTimerRef.current = null;
        if (
          transportSuspendedKindRef.current === 'live' ||
          liveTimeshiftStartingRef.current ||
          video.ended ||
          !video.paused
        ) {
          return;
        }

        if (tryFullscreenResume('pause-decision')) return;

        // No fullscreen-exit event arrived: this is a real user Pause while
        // fullscreen, so use the normal immediate r8.8.27 suspension.
        suspendLiveTransport(reason);
      }, 750);

      return true;
    };

    // DISPATCHARR_LIVE_REMOVE_SCRUB_PAUSE_DEFER
    const handleLivePlay = () => {
      // DISPATCHARR_LIVE_PLAY_REVIVES_SUSPENDED_R121: mirror of the archive's
      // R116 rule. A 'play' on the suspended (src-less) <video> -- e.g. the
      // native play button on touch devices, which keep native controls --
      // must restart Live instead of being a no-op. resumeSuspendedTransport
      // clears the suspended ref before its own play(), so this cannot loop,
      // and it only clears the flag while a Cast handoff owns playback.
      if (transportSuspendedKindRef.current === 'live') {
        void resumeSuspendedTransport();
        return;
      }
      livePlaybackStartedRef.current = true;
      clearLiveFullscreenPauseDecision();
      // DISPATCHARR_LIVE_IOS_AUTORESUME_BUDGET_R39: give the R22 budget back
      // once playback has actually held, so the limit bounds a pause/play loop
      // rather than the whole session. Each fullscreen exit costs one attempt.
      if (liveAutoResumeResetTimerRef.current != null) {
        window.clearTimeout(liveAutoResumeResetTimerRef.current);
      }
      liveAutoResumeResetTimerRef.current = window.setTimeout(() => {
        liveAutoResumeResetTimerRef.current = null;
        if (!video.paused && !video.ended) {
          liveAutoResumeCountRef.current = 0;
        }
      }, 5000);
      // DISPATCHARR_LIVE_IOS_SPURIOUS_PAUSE_R22
      if (!liveStartedAtRef.current) liveStartedAtRef.current = Date.now();
      liveDiagBeacon('play');
    };

    // DISPATCHARR_LIVE_IOS_SPURIOUS_PAUSE_R22: buffering/teardown signals
    // that distinguish "the browser paused us" from "the stream starved".
    // DISPATCHARR_LIVE_ADAPTIVE_BAND_R64: a stall means the band is tighter
    // than this link can hold right now, so give it room; a quiet spell means
    // the room is no longer needed and the player can move back toward the
    // edge. live-latency-chaser.ts re-reads these on every check, so writing
    // them into the live config is enough.
    const widenLiveBand = (reason) => {
      const band = liveSyncBandRef.current;
      if (!band || !band.adaptive) return;
      const before = band.max;
      band.max = Math.min(band.ceilMax, band.max + band.step);
      band.target = Math.min(band.max - 0.3, band.target + band.step);
      band.lastStallAt = Date.now();
      if (band.max !== before) {
        applyLiveBand(`widened after ${reason}`);
      }
    };

    const handleLiveWaiting = () => {
      liveDiagBeacon('waiting');
      widenLiveBand('waiting');
    };
    const handleLiveStalled = () => {
      liveDiagBeacon('stalled');
      widenLiveBand('stalled');
    };
    const handleLiveEmptied = () => liveDiagBeacon('emptied');

    const handleLivePause = () => {
      // DISPATCHARR_LIVE_IOS_AUTORESUME_BUDGET_R39
      if (liveAutoResumeResetTimerRef.current != null) {
        window.clearTimeout(liveAutoResumeResetTimerRef.current);
        liveAutoResumeResetTimerRef.current = null;
      }
      if (
        video.ended ||
        transportSuspendedKindRef.current === 'live' ||
        castHandoffInProgressRef.current ||
        castActiveRef.current
      ) return;
      // DISPATCHARR_LIVE_PAUSE_CHOICE_R136: where the picture stopped, sampled
      // before any deferral or suspend destroys the buffer.
      livePauseSampleRef.current = {
        at: Date.now(),
        ahead: liveBufferAheadSeconds(video),
        hls: Boolean(liveWebFmp4SessionRef.current),
        sig: livePauseFrameSignature(video), // R137
      };

      // DISPATCHARR_CAST_HANDBACK_GUARD_R19: twice (2026-09-11 15:25 and
      // 17:04) the browser closed the fresh handback MPEG-TS client 1-7s
      // after Stop Cast and never opened another request; the server then
      // saw no clients and tore the channel down. This Pause path is the
      // one route that releases Web Live without restarting it, so inside
      // the handback window a pause with no click/key behind it is
      // answered with play() once. A real user Pause still suspends.
      const pauseAt = Date.now();
      const userGesture =
        pauseAt - liveLastPointerAtRef.current <= 1500 ||
        pauseAt - liveLastKeyAtRef.current <= 1500;
      if (userGesture) {
        liveUserPauseAtRef.current = pauseAt;
        // DISPATCHARR_LIVE_GESTURE_PAUSE_NO_FS_RESUME_R121: on desktop a
        // deliberate pause is never the platform pausing us on fullscreen exit,
        // so drop the R8.8.24 resume intent -- Esc right after Space (or a Pause
        // within 2.5s of leaving fullscreen) must not be undone by
        // tryFullscreenResume. iOS keeps its own classification.
        if (!isWebKitMobile()) liveWasPlayingBeforeFullscreenRef.current = false;
      }
      const handbackAgeMs = pauseAt - castLiveHandbackAtRef.current;
      if (
        castLiveHandbackAtRef.current &&
        handbackAgeMs < 15000 &&
        !userGesture &&
        !castLiveHandbackPauseResumedRef.current
      ) {
        castLiveHandbackPauseResumedRef.current = true;
        console.warn(
          `[chromecast-r19] pause ${handbackAgeMs}ms after Stop Cast handback without a user gesture ` +
            `(src=${video.currentSrc ? 'set' : 'empty'}); resuming instead of releasing Live`
        );
        try {
          const resumed = video.play();
          if (resumed?.catch) resumed.catch(() => undefined);
        } catch {}
        return;
      }

      // DISPATCHARR_LIVE_IOS_FULLSCREEN_EXIT_TIMING_R38: wdf says whether iOS
      // had already left fullscreen when it paused us; if it had, the exit is
      // detectable right here and the 750ms deferral is avoidable.
      liveDiagBeacon('pause', {
        fs: String(liveFullscreenCurrentlyActive()),
        wdf: String(Boolean(video.webkitDisplayingFullscreen)),
        ref: String(liveFullscreenActiveRef.current),
      });

      // DISPATCHARR_LIVE_IOS_SPURIOUS_PAUSE_R22: on iOS (Chrome and Safari
      // are both WebKit there) Live pauses itself seconds after it starts --
      // measured 2026-09-13 at 6s and ~15s, with segments still arriving and
      // no touch involved. The ordinary Pause path below then released the
      // transport, which is why it needed a manual Play. Answer a pause that
      // no gesture caused, while the page is visible, with play() instead --
      // at most twice per player start, so a browser that truly insists on
      // pausing is not fought in a loop.
      if (
        isWebKitMobile() &&
        !userGesture &&
        typeof document !== 'undefined' &&
        !document.hidden &&
        !liveFullscreenCurrentlyActive() &&
        liveAutoResumeCountRef.current < 2
      ) {
        liveAutoResumeCountRef.current += 1;
        const attempt = liveAutoResumeCountRef.current;
        liveDiagBeacon('auto-resume', { att: String(attempt) });
        console.warn(
          `[live-r22] spurious iOS pause with no gesture; resuming (${attempt}/2)`
        );
        try {
          const resumed = video.play();
          if (resumed?.catch) {
            resumed.catch((error) =>
              liveDiagBeacon('resume-failed', {
                err: String(error?.name || error).slice(0, 32),
              })
            );
          }
        } catch {}
        return;
      }

      // Exit may already have armed the normal r8.8.24 resume window.
      if (tryFullscreenResume('late-pause')) return;

      // Or iOS may emit Pause first and webkitendfullscreen just afterwards.
      // DISPATCHARR_LIVE_DESKTOP_IMMEDIATE_SUSPEND_R121: a deliberate desktop
      // Pause (key/click) needs no such classification -- suspend at once so
      // "Resume Live" appears immediately in stage fullscreen, exactly as in
      // the minimized player.
      if (
        !(userGesture && !isWebKitMobile()) &&
        deferLiveFullscreenPauseDecision('paused')
      ) {
        return;
      }

      suspendLiveTransport('paused');
    };

    const handleLiveEnded = () => {
      livePlaybackStartedRef.current = false;
    };

    const handleLiveLoadedMetadata = () => {
      liveInlineGuardUntilRef.current = Date.now() + 2500;
    };

    const handleVisibilityChange = () => {
      // DISPATCHARR_CHROMECAST_TAB_VISIBILITY_R8_8_34
      if (castActiveRef.current || castHandoffInProgressRef.current) return;
      if (
        typeof document !== 'undefined' &&
        document.hidden &&
        video.paused &&
        !video.ended &&
        transportSuspendedKindRef.current !== 'live'
      ) {
        if (tryFullscreenResume('visibility-change')) return;
        if (deferLiveFullscreenPauseDecision('paused while page hidden')) return;
        suspendLiveTransport('paused while page hidden');
      }
    };

    const isIPhoneSafari = () => {
      if (typeof navigator === 'undefined') return false;
      const ua = navigator.userAgent || '';
      return (
        /iPhone|iPod/.test(ua) &&
        /Safari/.test(ua) &&
        !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua)
      );
    };

    const clearFullscreenResumeWindow = () => {
      if (liveFullscreenResumeTimerRef.current != null) {
        window.clearTimeout(liveFullscreenResumeTimerRef.current);
        liveFullscreenResumeTimerRef.current = null;
      }
      liveFullscreenResumeUntilRef.current = 0;
      liveWasPlayingBeforeFullscreenRef.current = false;
    };

    const armFullscreenResumeWindow = () => {
      if (transportSuspendedKindRef.current === 'live') {
        clearFullscreenResumeWindow();
        return;
      }
      if (!liveWasPlayingBeforeFullscreenRef.current || video.ended) {
        clearFullscreenResumeWindow();
        return;
      }

      liveFullscreenResumeUntilRef.current = Date.now() + 2500;
      if (liveFullscreenResumeTimerRef.current != null) {
        window.clearTimeout(liveFullscreenResumeTimerRef.current);
      }
      liveFullscreenResumeTimerRef.current = window.setTimeout(() => {
        liveFullscreenResumeTimerRef.current = null;
        liveFullscreenResumeUntilRef.current = 0;
        liveWasPlayingBeforeFullscreenRef.current = false;
      }, 2500);

      tryFullscreenResume('exit-event');
      window.setTimeout(() => tryFullscreenResume('exit-followup'), 120);
    };

    const handleLivePointerIntent = () => {
      liveLastPointerAtRef.current = Date.now();
    };

    // DISPATCHARR_CAST_HANDBACK_GUARD_R19
    const handleLiveKeyIntent = () => {
      liveLastKeyAtRef.current = Date.now();
    };

    const handleWebKitBeginFullscreen = () => {
      liveFullscreenActiveRef.current = true;
      clearLiveFullscreenPauseDecision();
      if (liveFullscreenResumeTimerRef.current != null) {
        window.clearTimeout(liveFullscreenResumeTimerRef.current);
        liveFullscreenResumeTimerRef.current = null;
      }
      liveFullscreenResumeUntilRef.current = 0;
      liveWasPlayingBeforeFullscreenRef.current = !video.paused;

      const recentVideoGesture =
        Date.now() - liveLastPointerAtRef.current <= 1500;
      if (
        isIPhoneSafari() &&
        Date.now() <= liveInlineGuardUntilRef.current &&
        !recentVideoGesture
      ) {
        window.setTimeout(() => {
          try {
            if (
              video.webkitDisplayingFullscreen &&
              typeof video.webkitExitFullscreen === 'function'
            ) {
              console.debug('[live-inline] startup native fullscreen suppressed');
              video.webkitExitFullscreen();
            }
          } catch (error) {
            console.debug('[live-inline] startup fullscreen exit skipped:', error);
          }
        }, 0);
      }
    };

    const handleWebKitEndFullscreen = () => {
      // DISPATCHARR_IOS_FULLSCREEN_STAGE_ESCAPES_TRANSFORM_R83: whatever
      // route ended fullscreen, the stage must not be left standing - that is
      // how the player ended up filling the page afterwards.
      if (!archiveOwnFullscreenRef.current) {
        clearArchivePseudoFullscreen();
      }

      // DISPATCHARR_LIVE_IOS_FULLSCREEN_EXIT_TIMING_R38: timestamps the event,
      // so its distance from the pause is measurable.
      liveDiagBeacon('fs-end', {
        wdf: String(Boolean(video.webkitDisplayingFullscreen)),
      });
      liveFullscreenActiveRef.current = false;
      armFullscreenResumeWindow();
      clearLiveFullscreenPauseDecision();
    };

    const handleStandardFullscreenChange = () => {
      if (typeof document === 'undefined') return;
      const active =
        document.fullscreenElement || document.webkitFullscreenElement;
      if (active) {
        liveFullscreenActiveRef.current = true;
        clearLiveFullscreenPauseDecision();
        if (liveFullscreenResumeTimerRef.current != null) {
          window.clearTimeout(liveFullscreenResumeTimerRef.current);
          liveFullscreenResumeTimerRef.current = null;
        }
        liveFullscreenResumeUntilRef.current = 0;
        liveWasPlayingBeforeFullscreenRef.current = !video.paused;
        return;
      }
      liveFullscreenActiveRef.current = false;
      armFullscreenResumeWindow();
      clearLiveFullscreenPauseDecision();
    };

    video.addEventListener('play', handleLivePlay);
    video.addEventListener('waiting', handleLiveWaiting);
    video.addEventListener('stalled', handleLiveStalled);
    video.addEventListener('emptied', handleLiveEmptied);
    video.addEventListener('pause', handleLivePause);
    video.addEventListener('ended', handleLiveEnded);
    video.addEventListener('loadedmetadata', handleLiveLoadedMetadata);
    video.addEventListener('pointerdown', handleLivePointerIntent);
    document.addEventListener('keydown', handleLiveKeyIntent, true);
    video.addEventListener('touchstart', handleLivePointerIntent, {
      passive: true,
    });
    video.addEventListener('webkitbeginfullscreen', handleWebKitBeginFullscreen);
    video.addEventListener('webkitendfullscreen', handleWebKitEndFullscreen);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    document.addEventListener('fullscreenchange', handleStandardFullscreenChange);
    document.addEventListener(
      'webkitfullscreenchange',
      handleStandardFullscreenChange
    );

    return () => {
      // DISPATCHARR_LIVE_ADAPTIVE_BAND_R64
      if (liveBandDecayTimerRef.current) {
        clearInterval(liveBandDecayTimerRef.current);
        liveBandDecayTimerRef.current = null;
      }
      liveSyncBandRef.current = null;
      video.removeEventListener('play', handleLivePlay);
      video.removeEventListener('waiting', handleLiveWaiting);
      video.removeEventListener('stalled', handleLiveStalled);
      video.removeEventListener('emptied', handleLiveEmptied);
      video.removeEventListener('pause', handleLivePause);
      video.removeEventListener('ended', handleLiveEnded);
      video.removeEventListener('loadedmetadata', handleLiveLoadedMetadata);
      video.removeEventListener('pointerdown', handleLivePointerIntent);
      document.removeEventListener('keydown', handleLiveKeyIntent, true);
      video.removeEventListener('touchstart', handleLivePointerIntent);
      video.removeEventListener(
        'webkitbeginfullscreen',
        handleWebKitBeginFullscreen
      );
      video.removeEventListener(
        'webkitendfullscreen',
        handleWebKitEndFullscreen
      );
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      document.removeEventListener(
        'fullscreenchange',
        handleStandardFullscreenChange
      );
      document.removeEventListener(
        'webkitfullscreenchange',
        handleStandardFullscreenChange
      );
      clearLiveFullscreenPauseDecision();
      if (liveFullscreenResumeTimerRef.current != null) {
        window.clearTimeout(liveFullscreenResumeTimerRef.current);
        liveFullscreenResumeTimerRef.current = null;
      }
      liveFullscreenResumeUntilRef.current = 0;
      liveWasPlayingBeforeFullscreenRef.current = false;
      liveFullscreenActiveRef.current = false;
      livePlaybackStartedRef.current = false;
    };
  }, [isVisible, streamUrl, contentType]);

  // DISPATCHARR_ARCHIVE_PAUSE_WATCHDOG_R8_8_25
  // DISPATCHARR_ARCHIVE_FULLSCREEN_RESUME_R8_8_26
  // DISPATCHARR_TRANSPORT_SUSPEND_R8_8_27
  // Archive Pause immediately releases HLS transport and asks the backend to
  // suspend (not seal) the opaque session. FloatingVideo remains open.
  useEffect(() => {
    if (!isVisible || contentType !== 'catchup-hls' || !streamUrl) {
      archiveWasPlayingBeforeFullscreenRef.current = false;
      archiveFullscreenResumeUntilRef.current = 0;
      archivePlaybackStartedRef.current = false;
      archiveFullscreenActiveRef.current = false;
      if (archiveFullscreenPauseDecisionTimerRef.current != null) {
        window.clearTimeout(archiveFullscreenPauseDecisionTimerRef.current);
        archiveFullscreenPauseDecisionTimerRef.current = null;
      }
      if (archiveFullscreenResumeTimerRef.current != null) {
        window.clearTimeout(archiveFullscreenResumeTimerRef.current);
        archiveFullscreenResumeTimerRef.current = null;
      }
      return undefined;
    }

    const video = videoRef.current;
    if (!video) return undefined;
    const activeUrl = archiveLocalRuntimeUrlRef.current || streamUrl;

    const clearArchiveFullscreenResumeWindow = () => {
      if (archiveFullscreenResumeTimerRef.current != null) {
        window.clearTimeout(archiveFullscreenResumeTimerRef.current);
        archiveFullscreenResumeTimerRef.current = null;
      }
      archiveFullscreenResumeUntilRef.current = 0;
      archiveWasPlayingBeforeFullscreenRef.current = false;
    };

    const archiveFullscreenResumePending = () =>
      archiveWasPlayingBeforeFullscreenRef.current &&
      !video.ended &&
      Date.now() <= archiveFullscreenResumeUntilRef.current;

    const tryArchiveFullscreenResume = (reason) => {
      if (liveTimeshiftReturningToLiveRef.current) return true;
      if (transportSuspendedKindRef.current === 'archive') return false;
      if (!archiveFullscreenResumePending()) return false;
      if (!video.paused) return true;

      video
        .play()
        .then(() => {
          console.debug(
            `[archive-inline] resumed after fullscreen exit (${reason})`
          );
        })
        .catch((error) => {
          console.debug(
            `[archive-inline] fullscreen-exit resume skipped (${reason}):`,
            error
          );
        });
      return true;
    };

    const armArchiveFullscreenResumeWindow = () => {
      if (transportSuspendedKindRef.current === 'archive') {
        clearArchiveFullscreenResumeWindow();
        return;
      }
      if (!archiveWasPlayingBeforeFullscreenRef.current || video.ended) {
        clearArchiveFullscreenResumeWindow();
        return;
      }

      // DISPATCHARR_IOS_FULLSCREEN_EXIT_RESUME_R81: 2.5s was not always
      // enough for the animation plus the pause that follows it.
      archiveFullscreenResumeUntilRef.current = Date.now() + 4000;
      if (archiveFullscreenResumeTimerRef.current != null) {
        window.clearTimeout(archiveFullscreenResumeTimerRef.current);
      }
      archiveFullscreenResumeTimerRef.current = window.setTimeout(() => {
        archiveFullscreenResumeTimerRef.current = null;
        archiveFullscreenResumeUntilRef.current = 0;
        archiveWasPlayingBeforeFullscreenRef.current = false;
      }, 4000);

      tryArchiveFullscreenResume('exit-event');
      window.setTimeout(
        () => tryArchiveFullscreenResume('exit-followup'),
        120
      );
    };

    // DISPATCHARR_ARCHIVE_PAUSE_LINGER_R128: Live got an instant overlay and a
    // warm reattach (R121/R126); the archive still destroyed the player and
    // killed the producer on every pause, so play meant a producer restart +
    // HLS reload + seek. Now a desktop pause shows "Paused / Resume Archive"
    // right away but KEEPS the loaded player; play within the linger is a bare
    // video.play(). Only an unanswered linger hands over to the real suspend.
    const clearArchivePauseLinger = () => {
      if (archivePauseLingerTimerRef.current != null) {
        window.clearTimeout(archivePauseLingerTimerRef.current);
        archivePauseLingerTimerRef.current = null;
      }
    };

    const armArchivePauseLinger = (reason) => {
      if (transportSuspendedKindRef.current === 'archive') return;
      if (castActiveRef.current || castHandoffInProgressRef.current || dispatcharrCastRuntime.active) return;
      archiveSuspendedPositionRef.current = Math.max(0, Number(video.currentTime) || 0);
      archivePlayerRetainedRef.current = true;
      setArchiveSoftPaused(true);
      transportSuspendedKindRef.current = 'archive';
      setTransportSuspendedKind('archive');
      console.debug(
        `[transport-suspend] Archive ${reason}: player kept, ` +
          `suspending in ${ARCHIVE_PAUSE_LINGER_MS}ms unless resumed`
      );
      clearArchivePauseLinger();
      archivePauseLingerTimerRef.current = window.setTimeout(() => {
        archivePauseLingerTimerRef.current = null;
        if (!archivePlayerRetainedRef.current) return;
        archivePlayerRetainedRef.current = false;
        setArchiveSoftPaused(false);
        // suspendArchiveTransport refuses while the kind is already 'archive':
        // the soft marker hands over to the real one here.
        transportSuspendedKindRef.current = null;
        if (videoRef.current !== video || !video.paused || video.ended) {
          setTransportSuspendedKind(null);
          return;
        }
        suspendArchiveTransport(`${reason} (linger expired)`);
      }, ARCHIVE_PAUSE_LINGER_MS);
    };

    const suspendArchiveTransport = (reason) => {
      // DISPATCHARR_LIVE_PROVIDER_TIMESHIFT_UI_EDGE_R5_1
      if (liveTimeshiftReturningToLiveRef.current) return;
      // DISPATCHARR_CHROMECAST_PROFILE_SWITCH_R8_8_35
      if (castActiveRef.current || castHandoffInProgressRef.current || dispatcharrCastRuntime.active) return;
      if (transportSuspendedKindRef.current === 'archive') return;
      if (!archivePlaybackStartedRef.current || video.ended) return;

      const currentPosition = Math.max(0, Number(video.currentTime) || 0);
      archiveSuspendedPositionRef.current = currentPosition;
      archiveLeaseSuspendedRef.current = true;
      archivePlaybackStartedRef.current = false;
      transportSuspendedKindRef.current = 'archive';
      setTransportSuspendedKind('archive');
      console.debug(
        `[transport-suspend] Archive ${reason} at ${currentPosition.toFixed(2)}s; ` +
          'releasing HLS transport'
      );

      // Start the backend suspend request first. The endpoint marks the session
      // suspended before stopping producer work, so late native-HLS requests
      // cannot recreate FFmpeg. Then release the browser HLS transport.
      const suspendRequest = requestArchiveSuspend(activeUrl);
      safeDestroyPlayer();
      suspendRequest.then((ok) => {
        if (!ok) {
          console.debug('[transport-suspend] Archive suspend request failed');
        }
      });
    };

    const clearArchiveFullscreenPauseDecision = () => {
      if (archiveFullscreenPauseDecisionTimerRef.current != null) {
        window.clearTimeout(archiveFullscreenPauseDecisionTimerRef.current);
        archiveFullscreenPauseDecisionTimerRef.current = null;
      }
    };

    const archiveFullscreenCurrentlyActive = () => {
      if (archiveFullscreenActiveRef.current) return true;
      if (video.webkitDisplayingFullscreen) return true;
      if (typeof document === 'undefined') return false;
      return Boolean(
        document.fullscreenElement || document.webkitFullscreenElement
      );
    };

    const deferArchiveFullscreenPauseDecision = (reason) => {
      if (!archiveFullscreenCurrentlyActive()) return false;
      clearArchiveFullscreenPauseDecision();
      console.debug(
        `[archive-inline] fullscreen-pause-decision deferred (${reason})`
      );

      archiveFullscreenPauseDecisionTimerRef.current = window.setTimeout(() => {
        archiveFullscreenPauseDecisionTimerRef.current = null;
        if (
          transportSuspendedKindRef.current === 'archive' ||
          video.ended ||
          !video.paused
        ) {
          return;
        }

        if (tryArchiveFullscreenResume('pause-decision')) return;
        suspendArchiveTransport(reason);
      }, 750);

      return true;
    };

    // DISPATCHARR_ARCHIVE_R2_SEEK_HANDBACK_FIXES
    // Safari/native-HLS and some desktop media stacks briefly pause while a
    // native timeline scrub is committing. Defer the destructive transport
    // suspension long enough for `seeking` to claim that pause. A real Pause
    // still follows the existing suspend policy after the short fence expires.
    const clearArchiveNativeSeekPauseDecision = () => {
      if (archiveNativeSeekPauseTimerRef.current != null) {
        window.clearTimeout(archiveNativeSeekPauseTimerRef.current);
        archiveNativeSeekPauseTimerRef.current = null;
      }
    };

    // DISPATCHARR_LIVE_REWIND_SEEK_FENCE_R77
    // Everything that is not the hls.js/MMS transport (native AVPlayer for Live
    // Rewind on iOS, Shaka on the desktop) reaches the backend only through
    // fragment requests, and the backend no longer lets those move the
    // producer: after a rewind AVPlayer asks for fragments all over the
    // seekable window, and each one used to restart FFmpeg somewhere else, so
    // playback froze on the first frame it had. One debounced /resume names
    // the position instead, and waits for that fragment plus one more.
    const nativeSeekOwnsProducer =
      contentType === 'catchup-hls' &&
      video?.dataset?.dispatcharrArchiveIosMms !== '1';

    const sendSeekCommit = (position) => {
      archiveSeekCommittingRef.current = true;
      archiveCommittedPositionRef.current = position;
      archiveWarmPositionRef.current = position;
      archiveLastCommitAtRef.current = Date.now();

      const sourceUrl = archiveLocalRuntimeUrlRef.current || streamUrl;
      void requestArchiveResume(sourceUrl, position).then((ok) => {
        if (!ok) {
          console.debug(
            `[live-rewind-seek] backend resume failed at ${position.toFixed(2)}s`
          );
        }
        window.setTimeout(() => {
          archiveSeekCommittingRef.current = false;
        }, 1200);
      });
    };

    // DISPATCHARR_LIVE_REWIND_INSTANT_SEEK_R80
    // Commit while the thumb is still moving, not only when it is released.
    // The producer is then already at (or a few seconds from) the position the
    // user lets go at, and the fragments are there when the player asks: the
    // same measurement that took 0.9s after a release-only commit serves in
    // 11-26ms when the position was committed a second earlier. The provider
    // tolerates this - measured, a placement every 0.5s produced no
    // starvation - but the pace is still limited, because four placements
    // inside one second is what starved it before.
    const queueNativeSeekCommit = (options = {}) => {
      if (!nativeSeekOwnsProducer) return;
      const settle = options.final ? 150 : 250;
      if (archiveNativeSeekCommitTimerRef.current != null) {
        window.clearTimeout(archiveNativeSeekCommitTimerRef.current);
      }
      archiveNativeSeekCommitTimerRef.current = window.setTimeout(() => {
        archiveNativeSeekCommitTimerRef.current = null;
        if (videoRef.current !== video) return;

        const target = Math.max(0, Number(video.currentTime) || 0);
        if (!Number.isFinite(target)) return;
        if (Math.abs(target - archiveCommittedPositionRef.current) < 1) return;

        const sinceLast = Date.now() - archiveLastCommitAtRef.current;
        if (!options.final && sinceLast < 800) {
          // Too soon after the last placement: let the thumb travel a little
          // further and commit that instead.
          queueNativeSeekCommit(options);
          return;
        }

        sendSeekCommit(target);
      }, settle);
    };

    const handleArchiveNativeSeeking = () => {
      archiveNativeSeekInProgressRef.current = true;
      // DISPATCHARR_ARCHIVE_SEEK_WARMUP_R75: begin producing at the position
      // under the thumb while the user is still dragging, so the seek they
      // finally commit to finds the fragments already there.
      warmArchiveSeekTarget(Math.max(0, Number(video.currentTime) || 0));
      // DISPATCHARR_LIVE_REWIND_INSTANT_SEEK_R80: move the producer with the
      // thumb, so the release finds the fragments already made.
      queueNativeSeekCommit();
      if (video?.dataset?.dispatcharrArchiveIosMms === '1') {
        // MMS may emit pause/seeked before the newly requested fragment has
        // arrived. Keep the backend producer alive while hls.js repositions it.
        video.dataset.dispatcharrArchiveIosMmsSeekGraceUntil = String(
          Date.now() + 12000
        );
        // DISPATCHARR_ARCHIVE_IOS_MMS_TRANSACTIONAL_SEEK_R8
        // Native iOS controls emit a stream of intermediate currentTime
        // positions while the scrub thumb moves. Queue one final producer seek
        // instead of letting every intermediate point drive fragment loading.
        try {
          playerRef.current?.queueArchiveMmsSeek?.(
            Math.max(0, Number(video.currentTime) || 0)
          );
        } catch {
          // Ordinary hls.js seek behavior remains the fallback.
        }
      }
      clearArchiveNativeSeekPauseDecision();
    };

    const handleArchiveNativeSeeked = () => {
      archiveNativeSeekInProgressRef.current = false;
      if (video?.dataset?.dispatcharrArchiveIosMms === '1') {
        video.dataset.dispatcharrArchiveIosMmsSeekGraceUntil = String(
          Date.now() + 12000
        );
      }
      // DISPATCHARR_LIVE_REWIND_SEEK_FENCE_R77: `seeked` carries the position
      // the user actually let go at, which is the one worth committing, and
      // it is never held back by the pacing above.
      queueNativeSeekCommit({ final: true });
      clearArchiveNativeSeekPauseDecision();

      // If playback was active before the native scrub, continue immediately
      // from the selected position. Do not expose a manual Resume Archive step.
      if (
        archivePlaybackStartedRef.current &&
        !video.ended &&
        video.paused &&
        transportSuspendedKindRef.current !== 'archive' &&
        !castActiveRef.current &&
        !castHandoffInProgressRef.current
      ) {
        video.play().catch((error) => {
          console.debug('[archive-r2] post-seek autoplay skipped:', error);
        });
      }
    };

    const handleArchiveTransportPlay = () => {
      // DISPATCHARR_ARCHIVE_RESUME_ON_PLAY_R116: pressing play on a suspended
      // archive must revive it, not sit as a no-op on a src-less <video>. This
      // is the ONLY resume affordance that works inside native <video>
      // fullscreen (where React overlays cannot render), and it also drives the
      // custom seek bar's raw video.play(). resumeSuspendedTransport re-mints if
      // the backend session expired, so this works after an arbitrarily long
      // pause. It clears the suspended ref before its own play(), so no loop.
      if (transportSuspendedKindRef.current === 'archive') {
        void resumeSuspendedTransport();
        return;
      }
      clearArchiveNativeSeekPauseDecision();
      // DISPATCHARR_LIVE_PROVIDER_TIMESHIFT_PLAYBACK_FULLSCREEN_R5_6
      // DISPATCHARR_ARCHIVE_IOS_MMS_TRANSACTIONAL_SEEK_R8
      if (
        !(
          video?.dataset?.dispatcharrArchiveIosMms === '1' &&
          archiveNativeSeekInProgressRef.current
        )
      ) {
        try { playerRef.current?.startLoad?.(); } catch {}
      }
      archivePlaybackStartedRef.current = true;
      clearArchiveFullscreenPauseDecision();
    };

    const handleArchiveTransportPause = () => {
      if (video.ended || transportSuspendedKindRef.current === 'archive') return;
      if (video.seeking || archiveNativeSeekInProgressRef.current) return;
      // DISPATCHARR_IOS_FULLSCREEN_EXIT_RESUME_R81
      // Coming out of the iOS player pauses the video, and that pause is the
      // platform's, not the viewer's: treating it as intent suspended the
      // transport and left them pressing Play to continue from the same spot.
      // A pause within a moment of a fullscreen change is always resumed.
      if (
        archiveWasPlayingBeforeFullscreenRef.current &&
        Date.now() - archiveFullscreenChangedAtRef.current < 2500
      ) {
        // DISPATCHARR_IOS_FULLSCREEN_EXIT_TIMING_R83: the resume itself is
        // immediate, so anything the viewer waits for after it is the media
        // pipeline refilling. These two lines say which of the two it was.
        const pausedAt = Date.now();
        console.info(
          '[archive-fullscreen] pause ' +
            (pausedAt - archiveFullscreenChangedAtRef.current) +
            'ms after leaving fullscreen; resuming now'
        );
        const reportPlaying = () => {
          video.removeEventListener('playing', reportPlaying);
          console.info(
            `[archive-fullscreen] playing again after ${Date.now() - pausedAt}ms`
          );
        };
        video.addEventListener('playing', reportPlaying, { once: true });
        const resumeAfterFullscreen = () => {
          if (
            videoRef.current !== video ||
            video.ended ||
            !video.paused ||
            transportSuspendedKindRef.current === 'archive'
          ) {
            return;
          }
          video.play().catch((error) => {
            console.debug('[archive-inline] fullscreen-exit resume:', error);
          });
        };
        resumeAfterFullscreen();
        window.setTimeout(resumeAfterFullscreen, 150);
        window.setTimeout(resumeAfterFullscreen, 500);
        return;
      }
      const archiveIosMms =
        video?.dataset?.dispatcharrArchiveIosMms === '1';
      const archiveIosMmsSeekGraceUntil = Number(
        video?.dataset?.dispatcharrArchiveIosMmsSeekGraceUntil || 0
      );
      // WebKit/MMS can emit transient pause after attach and again just after
      // seeked while the target fragment is still being produced. These are
      // buffering transitions, not user intent; suspending here kills the very
      // producer hls.js is waiting for.
      if (
        archiveIosMms &&
        (
          (!Number.isFinite(Number(video.currentTime)) || Number(video.currentTime) <= 0) ||
          Date.now() < archiveIosMmsSeekGraceUntil
        )
      ) {
        return;
      }
      if (tryArchiveFullscreenResume('late-pause')) return;
      // DISPATCHARR_ARCHIVE_PAUSE_LINGER_R128: the 750 ms fullscreen deferral
      // exists for iOS (pause can precede webkitendfullscreen). A desktop
      // stage-fullscreen exit never pauses, so desktop takes the same 300 ms
      // path as the minimized player -- the overlay shows just as fast there.
      if (isWebKitMobile() && deferArchiveFullscreenPauseDecision('paused')) return;

      clearArchiveNativeSeekPauseDecision();
      archiveNativeSeekPauseTimerRef.current = window.setTimeout(() => {
        archiveNativeSeekPauseTimerRef.current = null;
        if (
          video.ended ||
          !video.paused ||
          video.seeking ||
          archiveNativeSeekInProgressRef.current ||
          transportSuspendedKindRef.current === 'archive' ||
          castActiveRef.current ||
          castHandoffInProgressRef.current ||
          dispatcharrCastRuntime.active
        ) {
          return;
        }
        // DISPATCHARR_LIVE_PROVIDER_TIMESHIFT_PLAYBACK_FULLSCREEN_R5_6
        // Do not let a startup/startPosition pause stop the very fragments that
        // are needed to reach the requested provider DVR position.
        if (!archivePlaybackStartedRef.current) return;
        if (
          metadata?.liveTimeshift &&
          liveTimeshiftInitialSeekSettledUrlRef.current !== streamUrl
        ) {
          console.debug('[live-r5.6] transient provider startup pause ignored');
          return;
        }

        // DISPATCHARR_ARCHIVE_MINIMIZED_PAUSE_SUSPEND_R118: a minimized pause
        // used to only stopLoad (player kept, no overlay), so unlike fullscreen
        // it showed no "Resume Archive" affordance -- and a pause longer than
        // the backend's ~30s producer reap left a stale producer the retained
        // player could not resume. Suspend here too, exactly like fullscreen, so
        // the overlay shows consistently and resume always goes through the
        // robust R116 path (re-mint on expiry). Resume is usually fast: the
        // backend keeps the cached spool prefix around the paused position.
        // DISPATCHARR_ARCHIVE_PAUSE_LINGER_R128: desktop keeps the player for
        // a linger first (instant resume); mobile suspends as before.
        if (!isWebKitMobile()) {
          armArchivePauseLinger('paused');
          return;
        }
        suspendArchiveTransport('paused');
      }, 300);
    };

    const handleArchiveTransportEnded = () => {
      archivePlaybackStartedRef.current = false;
    };

    const handleArchiveVisibilityChange = () => {
      // DISPATCHARR_CHROMECAST_PROFILE_SWITCH_R8_8_35
      if (castActiveRef.current || castHandoffInProgressRef.current || dispatcharrCastRuntime.active) return;
      if (
        typeof document !== 'undefined' &&
        document.hidden &&
        video.paused &&
        !video.ended &&
        transportSuspendedKindRef.current !== 'archive'
      ) {
        if (tryArchiveFullscreenResume('visibility-change')) return;
        if (deferArchiveFullscreenPauseDecision('paused while page hidden')) return;
        suspendArchiveTransport('paused while page hidden');
      }
    };

    const handleArchiveWebKitBeginFullscreen = () => {
      archiveFullscreenActiveRef.current = true;
      archiveFullscreenChangedAtRef.current = Date.now();
      clearArchiveFullscreenPauseDecision();
      if (archiveFullscreenResumeTimerRef.current != null) {
        window.clearTimeout(archiveFullscreenResumeTimerRef.current);
        archiveFullscreenResumeTimerRef.current = null;
      }
      archiveFullscreenResumeUntilRef.current = 0;
      archiveWasPlayingBeforeFullscreenRef.current = !video.paused;
    };

    const handleArchiveWebKitEndFullscreen = () => {
      archiveFullscreenActiveRef.current = false;
      archiveFullscreenChangedAtRef.current = Date.now();
      // DISPATCHARR_IOS_FULLSCREEN_STAGE_ESCAPES_TRANSFORM_R83: whatever
      // route ended fullscreen, the stage must not be left standing - that is
      // how the player ended up filling the page afterwards.
      if (!archiveOwnFullscreenRef.current) {
        clearArchivePseudoFullscreen();
      }

      armArchiveFullscreenResumeWindow();
      clearArchiveFullscreenPauseDecision();
    };

    // DISPATCHARR_ARCHIVE_PRESENTATION_EARLY_RESUME_R8_8_27
    const handleArchivePresentationModeChanged = () => {
      if (transportSuspendedKindRef.current === 'archive') return;

      archiveFullscreenChangedAtRef.current = Date.now();
      const mode = video.webkitPresentationMode;
      if (mode === 'fullscreen') {
        archiveFullscreenActiveRef.current = true;
        return;
      }

      if (!archiveWasPlayingBeforeFullscreenRef.current || video.ended) return;

      archiveFullscreenActiveRef.current = false;
      console.debug(
        `[archive-inline] presentation-mode ${mode || 'inline'}; ` +
          'early fullscreen resume'
      );
      armArchiveFullscreenResumeWindow();
      clearArchiveFullscreenPauseDecision();
    };

    const handleArchiveStandardFullscreenChange = () => {
      if (typeof document === 'undefined') return;
      const active =
        document.fullscreenElement || document.webkitFullscreenElement;
      if (active) {
        archiveFullscreenActiveRef.current = true;
        clearArchiveFullscreenPauseDecision();
        if (archiveFullscreenResumeTimerRef.current != null) {
          window.clearTimeout(archiveFullscreenResumeTimerRef.current);
          archiveFullscreenResumeTimerRef.current = null;
        }
        archiveFullscreenResumeUntilRef.current = 0;
        archiveWasPlayingBeforeFullscreenRef.current = !video.paused;
        return;
      }
      archiveFullscreenActiveRef.current = false;
      archiveFullscreenChangedAtRef.current = Date.now();
      armArchiveFullscreenResumeWindow();
      clearArchiveFullscreenPauseDecision();
    };

    video.addEventListener('play', handleArchiveTransportPlay);
    video.addEventListener('pause', handleArchiveTransportPause);
    video.addEventListener('seeking', handleArchiveNativeSeeking);
    video.addEventListener('seeked', handleArchiveNativeSeeked);
    video.addEventListener('ended', handleArchiveTransportEnded);
    video.addEventListener(
      'webkitbeginfullscreen',
      handleArchiveWebKitBeginFullscreen
    );
    video.addEventListener(
      'webkitendfullscreen',
      handleArchiveWebKitEndFullscreen
    );
    video.addEventListener(
      'webkitpresentationmodechanged',
      handleArchivePresentationModeChanged
    );
    document.addEventListener(
      'visibilitychange',
      handleArchiveVisibilityChange
    );
    document.addEventListener(
      'fullscreenchange',
      handleArchiveStandardFullscreenChange
    );
    document.addEventListener(
      'webkitfullscreenchange',
      handleArchiveStandardFullscreenChange
    );

    return () => {
      // DISPATCHARR_ARCHIVE_PAUSE_LINGER_R128: a pending soft-pause linger must
      // not fire a suspend into a closed player or the next programme.
      clearArchivePauseLinger();
      archivePlayerRetainedRef.current = false;
      setArchiveSoftPaused(false);
      video.removeEventListener('play', handleArchiveTransportPlay);
      video.removeEventListener('pause', handleArchiveTransportPause);
      video.removeEventListener('seeking', handleArchiveNativeSeeking);
      video.removeEventListener('seeked', handleArchiveNativeSeeked);
      video.removeEventListener('ended', handleArchiveTransportEnded);
      clearArchiveNativeSeekPauseDecision();
      // DISPATCHARR_LIVE_REWIND_SEEK_FENCE_R77
      if (archiveNativeSeekCommitTimerRef.current != null) {
        window.clearTimeout(archiveNativeSeekCommitTimerRef.current);
        archiveNativeSeekCommitTimerRef.current = null;
      }
      archiveSeekCommittingRef.current = false;
      archiveCommittedPositionRef.current = Number.NEGATIVE_INFINITY;
      archiveLastCommitAtRef.current = 0;
      archiveNativeSeekInProgressRef.current = false;
      video.removeEventListener(
        'webkitbeginfullscreen',
        handleArchiveWebKitBeginFullscreen
      );
      video.removeEventListener(
        'webkitendfullscreen',
        handleArchiveWebKitEndFullscreen
      );
      video.removeEventListener(
        'webkitpresentationmodechanged',
        handleArchivePresentationModeChanged
      );
      document.removeEventListener(
        'visibilitychange',
        handleArchiveVisibilityChange
      );
      document.removeEventListener(
        'fullscreenchange',
        handleArchiveStandardFullscreenChange
      );
      document.removeEventListener(
        'webkitfullscreenchange',
        handleArchiveStandardFullscreenChange
      );
      clearArchiveFullscreenPauseDecision();
      if (archiveFullscreenResumeTimerRef.current != null) {
        window.clearTimeout(archiveFullscreenResumeTimerRef.current);
        archiveFullscreenResumeTimerRef.current = null;
      }
      archiveFullscreenResumeUntilRef.current = 0;
      archiveWasPlayingBeforeFullscreenRef.current = false;
      archiveFullscreenActiveRef.current = false;
      archivePlaybackStartedRef.current = false;
    };
  }, [isVisible, streamUrl, contentType]);

  // DISPATCHARR_ARCHIVE_RECOVERY_CAST_HANDBACK_REUSE
  const buildArchiveCastWebHandbackUrl = (url, positionSeconds) => {
    if (!url) return null;
    try {
      const next = new URL(url, window.location.origin);
      if (!next.searchParams.get('cast_pipeline')) return null;
      const position = Math.max(0, Number(positionSeconds) || 0);
      next.searchParams.set('cast_start_position', position.toFixed(3));
      for (const key of [
        'cast_receiver',
        'cast_generation',
        'cast_native_seek',
        'cast_seek_nonce',
        'cast_probe',
        'cast_lease',
        // DISPATCHARR_ARCHIVE_CAST_ABSOLUTE_TIMELINE_R131: receiver-only.
        'cast_timeline',
      ]) {
        next.searchParams.delete(key);
      }
      return next.toString();
    } catch {
      return null;
    }
  };

  // DISPATCHARR_TRANSPORT_SUSPEND_R8_8_27
  const resumeSuspendedTransport = async (
    { reuseRunningArchiveProducer = false } = {}
  ) => {
    const kind = transportSuspendedKindRef.current;
    if (!kind) return;

    if (kind === 'live') {
      // DISPATCHARR_CHROMECAST_TAB_VISIBILITY_R8_8_34
      // DISPATCHARR_RESUME_KEEPS_SUSPENDED_STATE_R124: while Cast owns (or is
      // acquiring) the stream, still refuse to start a competing local player
      // -- but KEEP the suspended state. Clearing it here removed the "Resume
      // Live" overlay with nothing started: if the handoff then failed, the
      // player was a dead black box with no path back to Live. Nothing relies
      // on the old clear: a successful live handoff clears the flag itself
      // (loadLiveOnChromecast), Stop Casting's live handback clears it before
      // its own initializeLivePlayer, and while castActive the overlay is
      // already hidden by its own `!castActive` render condition.
      if (
        castActiveRef.current ||
        castHandoffInProgressRef.current ||
        dispatcharrCastRuntime.active
      ) {
        return;
      }
      // DISPATCHARR_LIVE_PAUSE_CHOICE_R136: "Resume from" is replacing this
      // source -- keep the overlay; the time-shift (or its error) takes over.
      if (liveTimeshiftStartingRef.current) return;
      transportSuspendedKindRef.current = null;
      setTransportSuspendedKind(null);
      if (livePauseRef.current?.where === 'local') setLivePause(null);
      livePauseSampleRef.current = null;
      livePlaybackStartedRef.current = false;
      setLoadError(null);
      setIsLoading(true);
      console.debug('[transport-suspend] Resume Live: opening a new HTTP client');
      safeDestroyPlayer();
      // DISPATCHARR_LIVE_DESKTOP_PROGRESSIVE_TS_R26
      await startLiveWebPlayback();
      return;
    }

    if (kind !== 'archive') return;

    // DISPATCHARR_ARCHIVE_PAUSE_LINGER_R128: a soft pause still has the player
    // loaded and the backend untouched -- resume is a bare play(). If the
    // player is gone meanwhile (Cast handoff destroyed it), fall through to the
    // real resume path; /resume on a never-suspended session is idempotent.
    if (archivePlayerRetainedRef.current) {
      if (archivePauseLingerTimerRef.current != null) {
        window.clearTimeout(archivePauseLingerTimerRef.current);
        archivePauseLingerTimerRef.current = null;
      }
      archivePlayerRetainedRef.current = false;
      setArchiveSoftPaused(false);
      const retainedVideo = videoRef.current;
      const playerAlive = Boolean(
        retainedVideo && (retainedVideo.currentSrc || playerRef.current)
      );
      if (playerAlive) {
        transportSuspendedKindRef.current = null;
        setTransportSuspendedKind(null);
        archivePlaybackStartedRef.current = true;
        try { playerRef.current?.startLoad?.(); } catch { /* native HLS */ }
        retainedVideo.play().catch((error) => {
          console.debug('[transport-suspend] Archive soft resume play skipped:', error);
        });
        return;
      }
    }

    let activeUrl = archiveLocalRuntimeUrlRef.current || streamUrl;
    const savedPosition = Math.max(
      0,
      Number(archiveSuspendedPositionRef.current) || 0
    );
    let resumed = reuseRunningArchiveProducer
      ? true
      : await requestArchiveResume(activeUrl, savedPosition);
    if (!resumed) {
      // DISPATCHARR_ARCHIVE_RESUME_REMINT_R116: the backend session expired
      // (10-min idle TTL during a long pause). Re-mint an identical session and
      // resume into it at the saved position -- the manual "restart + seek" the
      // user had to do, done for them. The fresh session is live, so /resume
      // succeeds and positions the producer; restoreArchivePosition then seeks.
      const freshUrl = await remintArchiveHlsSession(activeUrl);
      if (freshUrl) {
        archiveLocalRuntimeUrlRef.current = freshUrl;
        activeUrl = freshUrl;
        resumed = await requestArchiveResume(freshUrl, savedPosition);
      }
    }
    if (!resumed) {
      setLoadError('Archive session could not be resumed. Close and reopen Archive.');
      return;
    }

    archiveLeaseSuspendedRef.current = false;
    transportSuspendedKindRef.current = null;
    setTransportSuspendedKind(null);
    archivePlaybackStartedRef.current = false;
    setLoadError(null);
    setIsLoading(true);
    requestArchivePing(activeUrl);

    const video = videoRef.current;
    if (!video) return;

    let restoreAttempts = 0;
    const restoreArchivePosition = () => {
      const currentVideo = videoRef.current;
      if (!currentVideo || transportSuspendedKindRef.current === 'archive') return;
      restoreAttempts += 1;

      let target = savedPosition;
      try {
        if (Number.isFinite(currentVideo.duration) && currentVideo.duration > 0) {
          target = Math.min(target, Math.max(0, currentVideo.duration - 0.05));
        }
        if (currentVideo.seekable && currentVideo.seekable.length > 0) {
          const first = currentVideo.seekable.start(0);
          const last = currentVideo.seekable.end(currentVideo.seekable.length - 1);
          target = Math.min(Math.max(target, first), Math.max(first, last - 0.05));
        }
        // DISPATCHARR_ARCHIVE_RESUME_EXT_X_START_R130: with EXT-X-START the
        // player may already sit on P; re-assigning the same position is a
        // second seek (Firefox throws away the in-flight append and stalls,
        // see DISPATCHARR_CATCHUP_HLS_SHAKA_SKIP_REDUNDANT_SEEK, and every
        // seek also costs an R77 seek-fence /resume). Only seek when off.
        if (
          Number.isFinite(target) &&
          !(
            Number.isFinite(currentVideo.currentTime) &&
            Math.abs(currentVideo.currentTime - target) <= 0.5
          )
        ) {
          currentVideo.currentTime = target;
        }
      } catch {
        // Native HLS can reject a seek until its first seekable range exists.
      }

      const closeEnough =
        Number.isFinite(currentVideo.currentTime) &&
        Math.abs(currentVideo.currentTime - target) <= 0.75;
      if (!closeEnough && restoreAttempts < 40) {
        window.setTimeout(restoreArchivePosition, 125);
        return;
      }

      console.debug(
        `[transport-suspend] Resume Archive at ${target.toFixed(2)}s ` +
          `(saved=${savedPosition.toFixed(2)}s)`
      );
      currentVideo.play().catch((error) => {
        console.debug('[transport-suspend] Archive resume play skipped:', error);
      });
      // DISPATCHARR_ARCHIVE_RESUME_POSITION_GUARD_R129: Shaka's own load-time
      // playhead placement (offset 0 on the plain resume URL, no EXT-X-START)
      // can land AFTER this restore and drag the playhead back to 0:00 -- seen
      // on the R127 baseline harness: playback resumed from 0 and the R77 seek
      // fence then re-placed the producer at the programme start. One delayed
      // re-check re-applies the saved position if that happened.
      window.setTimeout(() => {
        const guarded = videoRef.current;
        if (!guarded || guarded !== currentVideo) return;
        if (transportSuspendedKindRef.current === 'archive') return;
        const now = Number(guarded.currentTime) || 0;
        if (target > 2.0 && now < 1.0) {
          console.warn(
            `[transport-suspend] Archive resume playhead dragged to ${now.toFixed(2)}s; ` +
              `re-applying ${target.toFixed(2)}s`
          );
          try { guarded.currentTime = target; } catch { /* not seekable yet */ }
        }
      }, 450);
    };

    video.addEventListener('loadedmetadata', restoreArchivePosition, { once: true });
    video.addEventListener('canplay', restoreArchivePosition, { once: true });
    // DISPATCHARR_ARCHIVE_RESUME_EXT_X_START_R130
    initializeVODPlayer(activeUrl, { archiveResumePosition: savedPosition });
    window.setTimeout(restoreArchivePosition, 125);
  };

  // DISPATCHARR_CHROMECAST_R8_8_28
  // DISPATCHARR_CHROMECAST_HLS_R8_8_29
  // DISPATCHARR_CHROMECAST_PROFILE_SWITCH_R8_8_35
  const isDispatcharrCastContent = (contentId) => {
    const value = String(contentId || '');
    return (
      value.includes('/proxy/ts/cast-hls/') ||
      value.includes('/proxy/catchup-hls/') ||
      value.includes('/archive-cast-media-v2/')
    );
  };

  const liveChannelIdFromUrl = (url) => {
    try {
      const parsed = new URL(url, window.location.origin);
      const marker = '/proxy/ts/stream/';
      const pos = parsed.pathname.indexOf(marker);
      if (pos < 0) return null;
      return parsed.pathname.slice(pos + marker.length).split('/')[0] || null;
    } catch {
      return null;
    }
  };

  // DISPATCHARR_LIVE_REWIND_ONLY_WITH_CATCHUP_R99: Live Rewind replays the
  // channel's catch-up source, so a channel without catch-up (A1 lines with no
  // TiVi counterpart, TiVi feeds without an archive) cannot rewind at all --
  // the button renders only for a CONFIRMED catch-up channel (user rule; the
  // first cut kept it on unknown and the Guide page never loads the channels
  // store, so it kept showing everywhere). Sources, in order: the caller's
  // metadata.isCatchup (ChannelsTable has the row), else the channel record
  // in the channels store, which the effect below lazily loads once per
  // session -- the Guide's summary endpoint is deliberately lightweight and
  // carries no is_catchup.
  const liveChannelUuid =
    contentType === 'live' ? liveChannelIdFromUrl(streamUrl) : null;
  const storeHasChannels = Boolean(
    channelsById &&
      (Array.isArray(channelsById)
        ? channelsById.length > 0
        : Object.keys(channelsById).length > 0)
  );
  useEffect(() => {
    if (!liveChannelUuid || typeof metadata?.isCatchup === 'boolean') return;
    if (storeHasChannels || channelsFetchRequestedRef.current) return;
    channelsFetchRequestedRef.current = true;
    Promise.resolve(fetchChannels()).catch(() => {
      channelsFetchRequestedRef.current = false;
    });
  }, [liveChannelUuid, metadata, storeHasChannels, fetchChannels]);
  const liveRewindAvailable = (() => {
    if (contentType !== 'live') return false;
    if (typeof metadata?.isCatchup === 'boolean') return metadata.isCatchup;
    if (!liveChannelUuid) return false;
    let channel = null;
    if (Array.isArray(channelsById)) {
      channel = channelsById.find((c) => c && c.uuid === liveChannelUuid) || null;
    } else if (channelsById && channelsByUUID) {
      const id = channelsByUUID[liveChannelUuid];
      channel = id !== undefined ? channelsById[id] || null : null;
    }
    return Boolean(channel && channel.is_catchup);
  })();

  // R135 review: while no fetched programme covers now (a boundary passed with
  // the picker open, or during a long time-shift), look again once a minute;
  // the 1 s tick re-renders in both. A channel without any EPG never re-asks.
  const livePickerCache = livePickerProgrammesCacheRef.current;
  const livePickerRecheckSlot =
    livePickerCache && livePickerCache.coversUntil > 0 && Date.now() >= livePickerCache.coversUntil
      ? Math.floor(Date.now() / 60000)
      : 0;

  // DISPATCHARR_LIVE_REWIND_PROGRAMME_TIME_R135: programme boundaries for the
  // picker (and the time-shift bars). Guide "Watch Live" passes its programme
  // in the metadata; every other entry point (channel list, logos, DVR...)
  // passes none, and the 6 h window can span several programmes -- so look
  // the channel's programmes up once per picker opening / time-shift session.
  // GET programs/search needs only a standard user. A non-integer channel_id is
  // silently DROPPED by that API (every channel comes back), hence the integer
  // guard and the one-tvg_id check.
  useEffect(() => {
    if (!showLiveRewindPicker) rewindPickerFrozenMinRef.current = null;
    const liveTimeshiftActive = contentType === 'catchup-hls' && metadata?.liveTimeshift;
    if (!((contentType === 'live' && showLiveRewindPicker) || liveTimeshiftActive)) {
      return undefined;
    }
    const channelUuid =
      contentType === 'live'
        ? liveChannelUuid
        : liveChannelIdFromUrl(metadata?.liveSourceUrl || '');
    // The caller's integer id (ChannelsTable), else the channels store
    // (uuid -> id; the R99 effect loads it once when no caller supplied it).
    const channelId = [
      contentType === 'live'
        ? metadata?.channelId
        : metadata?.liveSourceMetadata?.channelId,
      channelUuid && channelsByUUID ? channelsByUUID[channelUuid] : undefined,
    ]
      .map((value) => (value === null || value === undefined || value === '' ? NaN : Number(value)))
      .find((value) => Number.isInteger(value) && value > 0);
    if (!channelUuid || channelId === undefined) return undefined;
    const nowMs = Date.now();
    const windowStartMs =
      contentType === 'live'
        ? liveProviderTimeshiftWindowStartMs(nowMs)
        : Date.parse(metadata?.liveTimeshiftStart || '') || nowMs - LIVE_PROVIDER_TIMESHIFT_MAX_SECONDS * 1000;
    const cache = livePickerProgrammesCacheRef.current;
    if (
      cache &&
      cache.channelUuid === channelUuid &&
      nowMs - cache.fetchedAt < 5 * 60 * 1000 &&
      (nowMs < cache.coversUntil || nowMs - cache.fetchedAt < 60000) &&
      cache.windowStartMs <= windowStartMs + 60000
    ) {
      setLivePickerProgrammes((current) =>
        current && current.channelUuid === channelUuid
          ? current
          : { channelUuid, list: cache.list }
      );
      return undefined;
    }
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 5000);
    const url = new URL('/api/epg/programs/search/', window.location.origin);
    url.searchParams.set('channel_id', String(channelId));
    url.searchParams.set('end_after', new Date(windowStartMs - 60000).toISOString());
    // R135 review: also what airs next -- the picker's right label and the
    // time-shift bars keep running into it (3 h ahead in Live; the session's
    // own horizon in a time-shift, at most 8 h).
    const sessionStartMs = Date.parse(metadata?.liveTimeshiftStart || '');
    const sessionSpanMs =
      (Number(metadata?.liveTimeshiftSessionDurationSeconds) ||
        (Number(metadata?.durationMinutes) || 0) * 60) * 1000;
    const lookAheadUntil =
      contentType === 'live'
        ? nowMs + 3 * 60 * 60 * 1000
        : Number.isFinite(sessionStartMs) && sessionSpanMs > 0
          ? Math.min(nowMs + 8 * 60 * 60 * 1000, sessionStartMs + sessionSpanMs)
          : nowMs;
    url.searchParams.set(
      'start_before',
      new Date(Math.max(nowMs, lookAheadUntil) + 60000).toISOString()
    );
    url.searchParams.set('fields', 'title,start_time,end_time,tvg_id');
    url.searchParams.set('page_size', '200');
    const token = useAuthStore.getState().accessToken;
    fetch(url.toString(), {
      cache: 'no-store',
      credentials: 'same-origin',
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      signal: controller.signal,
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        if (!data || controller.signal.aborted) return;
        const rows = Array.isArray(data)
          ? data
          : Array.isArray(data.results)
            ? data.results
            : [];
        const tvgIds = new Set(rows.map((row) => String(row?.tvg_id || '')).filter(Boolean));
        if (tvgIds.size > 1) {
          console.warn('[live-rewind-r135] EPG lookup returned several channels; ignored');
          return;
        }
        const list = rows
          .map((row) => ({
            startMs: new Date(row?.start_time).getTime(),
            endMs: new Date(row?.end_time).getTime(),
            title: String(row?.title || ''),
          }))
          .filter(
            (programme) =>
              Number.isFinite(programme.startMs) &&
              Number.isFinite(programme.endMs) &&
              programme.endMs > programme.startMs
          )
          .sort((a, b) => a.startMs - b.startMs);
        livePickerProgrammesCacheRef.current = {
          channelUuid,
          fetchedAt: Date.now(),
          windowStartMs,
          coversUntil: list.reduce((max, programme) => Math.max(max, programme.endMs), 0),
          list,
        };
        setLivePickerProgrammes({ channelUuid, list });
      })
      .catch(() => {
        // No lookup: labels fall back to the time of day.
      })
      .finally(() => window.clearTimeout(timeout));
    return () => {
      window.clearTimeout(timeout);
      controller.abort();
    };
  }, [
    contentType,
    showLiveRewindPicker,
    metadata?.liveTimeshift,
    metadata?.liveTimeshiftStart,
    metadata?.liveSourceUrl,
    metadata?.liveSourceMetadata?.channelId,
    liveChannelUuid,
    metadata?.channelId,
    channelsByUUID,
    livePickerRecheckSlot,
  ]);

  // DISPATCHARR_SAME_PROFILE_LIVE_TEE_REMOTE_SCRUB_R8_8_50
  const currentLiveAdaptiveProfile = () => {
    let profile = null;
    if (liveAdaptiveStreamUrlRef.current) {
      try {
        const selected = new URL(
          liveAdaptiveStreamUrlRef.current,
          window.location.origin
        );
        profile = selected.searchParams.get('adaptive_output_profile');
      } catch {
        profile = null;
      }
    }
    if (!profile) profile = castAdaptiveProfileRef.current;
    if (!profile) return null;
    return String(profile).replace(/^cast-/, '');
  };

  // DISPATCHARR_CAST_MULTI_SENDER_SESSIONS_R8_8_53
  const newDispatcharrCastSessionId = () =>
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `cast-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

  // DISPATCHARR_LIVE_CONTINUE_LIVE_CAST_INPLACE_SWITCH: sourceUrlOverride
  // lets a caller resolve this target against a URL other than the
  // component's own current streamUrl (e.g. Continue Live switching an
  // active archive/timeshift Cast session to plain Live in place, before
  // streamUrl itself has been updated to match). Omitted, this is
  // byte-for-byte the prior behavior.
  const buildChromecastLiveTarget = (normalProfile, sourceUrlOverride) => {
    const channelId = liveChannelIdFromUrl(sourceUrlOverride || streamUrl);
    const profile = String(normalProfile || '').replace(/^cast-/, '');
    if (!channelId || !profile) return null;
    const castSession = newDispatcharrCastSessionId();
    const transport = buildLiveCastTransportUrls(
      liveAdaptiveStreamUrlRef.current || sourceUrlOverride || streamUrl,
      profile,
      window.location.origin
    );
    if (!transport) return null;
    // DISPATCHARR_LIVE_SAME_PROFILE_CAST_TEE_R8_23_0
    // audio_only must lease/read the special H.264-copy+AАC profile tee;
    // normal transcode/direct-bootstrap uses the ordinary adaptive tier.
    const base =
      `/proxy/ts/cast-hls/${encodeURIComponent(channelId)}/` +
      `${encodeURIComponent(transport.castProfileKey || profile)}`;
    const withSession = (leaf) => {
      const url = new URL(`${base}/${leaf}`, window.location.origin);
      url.searchParams.set('cast_session', castSession);
      return url.toString();
    };
    return {
      adaptiveProfile: profile,
      ...transport,
      requiresBootstrap: Boolean(
        transport.requiresBootstrap || !liveWebFmp4SessionRef.current
      ),
      castSession,
      mediaUrl: withSession('master.m3u8'),
      prewarmUrl: withSession('index.m3u8'),
      renewUrl: withSession('renew'),
      stopUrl: withSession('stop'),
    };
  };

  // DISPATCHARR_CAST_STOP_DETERMINISTIC_TEARDOWN_R8_8_44
  // DISPATCHARR_SAME_PROFILE_LIVE_TEE_REMOTE_SCRUB_R8_8_50
  // DISPATCHARR_UNIFIED_CAST_LEASE_NO_WEB_HEARTBEATS_R8_8_54
  const stopCastSessionLease = async (release = true, reason = '') => {
    if (dispatcharrCastRuntime.leaseTimer != null) {
      window.clearInterval(dispatcharrCastRuntime.leaseTimer);
      dispatcharrCastRuntime.leaseTimer = null;
    }
    if (!release) return true;

    const stopUrl = dispatcharrCastRuntime.stopUrl || castStopUrlRef.current;
    dispatcharrCastRuntime.renewUrl = null;
    dispatcharrCastRuntime.stopUrl = null;
    castRenewUrlRef.current = null;
    castStopUrlRef.current = null;
    if (!stopUrl) return true;
    // DISPATCHARR_CAST_RELEASE_REASON_R133: name the cause in the server log
    // (Stop Casting, programme end, receiver failure); the 2026-10-06 20:14
    // teardown could only be reconstructed from file mtimes.
    let releaseUrl = stopUrl;
    if (reason) {
      try {
        const parsed = new URL(stopUrl, window.location.origin);
        parsed.searchParams.set('reason', String(reason).slice(0, 80));
        releaseUrl = parsed.toString();
      } catch {
        releaseUrl = stopUrl;
      }
    }
    try {
      const response = await fetch(releaseUrl, {
        method: 'POST',
        keepalive: true,
        cache: 'no-store',
        credentials: 'same-origin',
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return true;
    } catch (error) {
      console.warn('[chromecast-r8.8.54] Cast session release failed:', error);
      return false;
    }
  };

  // DISPATCHARR_ARCHIVE_CAST_SEEK_LEASE_RACE_R8_8_55
  const renewCastSessionLeaseNow = async (renewUrlOverride = null) => {
    const renewUrl =
      renewUrlOverride || dispatcharrCastRuntime.renewUrl || castRenewUrlRef.current;
    if (!renewUrl) return false;
    try {
      // DISPATCHARR_STATS_LIVE_CLIENT_DETAILS_R2
      const token = useAuthStore.getState().accessToken;
      const response = await fetch(renewUrl, {
        method: 'POST',
        cache: 'no-store',
        credentials: 'same-origin',
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      if (!response.ok) {
        // DISPATCHARR_CAST_RECEIVER_GONE_WATCHDOG_R108: the server watched
        // the receiver go silent (cast ended on the TV/STB itself), already
        // released this lease and stopped the tee. End the sender session so
        // the normal SESSION_STATE_CHANGED cleanup runs instead of this
        // timer renewing a dead cast forever.
        // DISPATCHARR_CAST_TRANSITION_OPS_R134: only the ACTIVE lease speaks
        // for the TV, and never while a transition is switching it (that op
        // owns the outcome). A parked, deferred or superseded lease the server
        // reaped must not tear down the cast that replaced it.
        const activeRenewUrl =
          dispatcharrCastRuntime.renewUrl || castRenewUrlRef.current;
        const receiverGone =
          response.status === 410 &&
          response.headers.get('X-Dispatcharr-Cast-Receiver-Gone') === '1';
        const speaksForTv =
          (!renewUrlOverride || renewUrlOverride === activeRenewUrl) &&
          !castOpInFlight(['start', 'rewind-on-cast', 'continue-live']);
        if (receiverGone && !speaksForTv) {
          console.info('[chromecast-r134] receiver-gone 410 for a non-active lease ignored');
        }
        if (receiverGone && speaksForTv) {
          // DISPATCHARR_CAST_RECEIVER_GONE_UI_RESET_R112: the server watchdog
          // already killed the tee. endSession() alone left the panel stuck on
          // "Stop Casting" (the SESSION_STATE_CHANGED -> disconnect chain did
          // not complete), and the stale renew timer kept POSTing, which could
          // even resurrect the session. Stop the timer and run the full,
          // tested teardown (clears runtime, ends the CAF session, resets the
          // UI, resumes nothing) exactly like a TV-side stop.
          console.info('[chromecast-r113] Receiver gone (server); full teardown');
          if (dispatcharrCastRuntime.leaseTimer != null) {
            window.clearInterval(dispatcharrCastRuntime.leaseTimer);
            dispatcharrCastRuntime.leaseTimer = null;
          }
          castRenewUrlRef.current = null;
          // DISPATCHARR_CAST_NO_SESSION_RESUME_R113: end the CAF session
          // directly and unconditionally -- exactly what the Stop Casting
          // button ultimately does -- so the receiver app is told to stop even
          // if recoverFailedChromecast is inside its post-teardown cooldown.
          // endCurrentSession(true) = stopCasting, which also clears the saved
          // session so CAF cannot rejoin it when the TV comes back.
          try {
            (castContextRef.current ||
              window.cast?.framework?.CastContext?.getInstance?.())
              ?.endCurrentSession?.(true);
          } catch (endError) {
            console.debug('[chromecast-r113] endCurrentSession failed:', endError);
          }
          void recoverFailedChromecast('server watchdog: cast receiver gone');
        }
        throw new Error(`HTTP ${response.status}`);
      }
      return true;
    } catch (error) {
      console.debug('[chromecast-r8.8.55] Cast session lease renew failed:', error);
      return false;
    }
  };

  const startCastSessionLease = async (
    renewUrl,
    stopUrl = null,
    { keepPrevious = false } = {}
  ) => {
    // DISPATCHARR_CAST_HLS_LEASE_SWITCH_ORPHAN_FIX: this used to pass `false`
    // (release=false), which only clears the local renew timer and never
    // calls the PREVIOUS channel's stopUrl. That was invisible before the
    // profile-id fix (the orphaned FFmpeg got killed within seconds anyway
    // by the -- then miss-protected -- idle cleanup), but now that Cast HLS
    // sessions are protected correctly, switching the cast target channel
    // left the old channel's FFmpeg tee running indefinitely (confirmed live:
    // two full NVENC cast-hls tees for two different channels running in
    // parallel for ~1-2 minutes after a channel switch). Always release the
    // previous lease first; stopCastSessionLease() is a safe no-op when
    // there is no previous stopUrl (first-ever lease).
    // DISPATCHARR_CAST_CONTINUE_LIVE_KEEP_OLD_LEASE_R14: Continue Live keeps
    // the catch-up lease alive until the TV has actually switched to Live.
    // Releasing it here first left the receiver on a stream that answered
    // only 409 for the whole Live warm-up -- that dead gap was the visible
    // 'cycling'. Renewals stop now; the release happens after loadMedia.
    if (keepPrevious) {
      if (dispatcharrCastRuntime.leaseTimer != null) {
        window.clearInterval(dispatcharrCastRuntime.leaseTimer);
        dispatcharrCastRuntime.leaseTimer = null;
      }
      castDeferredLeaseStopUrlRef.current =
        dispatcharrCastRuntime.stopUrl || castStopUrlRef.current || null;
    } else {
      await stopCastSessionLease(true);
    }
    dispatcharrCastRuntime.renewUrl = renewUrl;
    dispatcharrCastRuntime.stopUrl = stopUrl;
    castRenewUrlRef.current = renewUrl;
    castStopUrlRef.current = stopUrl;

    if (!(await renewCastSessionLeaseNow(renewUrl))) {
      throw new Error('Chromecast Cast session lease start failed');
    }
    // DISPATCHARR_CAST_TRANSITION_OPS_R134: a Stop / X / handback that landed
    // during the renew above already took (and released) this lease from the
    // slot -- arming a timer now would renew a released lease forever.
    if ((dispatcharrCastRuntime.stopUrl || castStopUrlRef.current) !== stopUrl) {
      console.info('[chromecast-r134] lease slot changed during start; renew timer not armed');
      return true;
    }
    // DISPATCHARR_CAST_RECEIVER_GONE_WATCHDOG_R108: 30s -> 10s. The renew
    // ping is also the receiver-liveness probe now, so its period bounds how
    // fast a TV-side stop is detected (threshold + one period). One 204 per
    // 10s per active cast is negligible.
    dispatcharrCastRuntime.leaseTimer = window.setInterval(() => {
      void renewCastSessionLeaseNow(renewUrl);
    }, 10000);
    return true;
  };

  // DISPATCHARR_CAST_CONTINUE_LIVE_KEEP_OLD_LEASE_R14
  const releaseDeferredCastLease = async () => {
    const stopUrl = castDeferredLeaseStopUrlRef.current;
    castDeferredLeaseStopUrlRef.current = null;
    if (!stopUrl) return true;
    try {
      const response = await fetch(stopUrl, {
        method: 'POST',
        keepalive: true,
        cache: 'no-store',
        credentials: 'same-origin',
      });
      return response.ok;
    } catch (error) {
      console.warn('[chromecast-r14] deferred lease release failed:', error);
      return false;
    }
  };

  // DISPATCHARR_CAST_TRANSITION_OPS_R134 -------------------------------------
  // One op per user-visible Cast transition. beginCastOp() supersedes the
  // previous op; every async step of a loader re-checks assertCastOp() before
  // its next side effect, so a superseded loader unwinds silently, releasing
  // only what it acquired itself, and the newest op alone decides what the TV
  // and the Web player end up on. Before this, fast Cast -> Rewind -> Continue
  // Live -> Stop sequences ran overlapping loaders that shared one lease slot,
  // released each other's leases, sent competing LOADs and ended in the R109
  // label ("Cast stopped on the device...") or a stuck "Loading video...".
  const castSupersededError = (op) => {
    const error = new Error(
      `Cast ${op?.kind || 'transition'} #${op?.seq ?? '?'} superseded by a newer Cast action`
    );
    error.castSuperseded = true;
    return error;
  };

  const isCastSuperseded = (error) => Boolean(error?.castSuperseded);

  const beginCastOp = (kind, target = {}) => {
    const prev = castOpRef.current;
    let resolveDone = () => {};
    const done = new Promise((resolve) => {
      resolveDone = resolve;
    });
    const op = {
      seq: ++castOpSeqRef.current,
      kind,
      target: target || {},
      startedAt: Date.now(),
      settled: false,
      done,
      resolveDone,
      prev: prev && !prev.settled ? prev : null,
    };
    castOpRef.current = op;
    console.info(
      `[chromecast-r134] op #${op.seq} ${kind}` +
        (op.prev ? ` supersedes #${op.prev.seq} ${op.prev.kind}` : '')
    );
    return op;
  };

  const castOpIsCurrent = (op) => !op || castOpRef.current === op;

  const assertCastOp = (op) => {
    if (op && castOpRef.current !== op) throw castSupersededError(op);
  };

  // Superseded -> castSuperseded; the TV session ended -> a plain error (the
  // op's failure path then applies R109, which is right for a real TV stop).
  const assertCastOpSession = (op, session) => {
    assertCastOp(op);
    if (session && !castSessionMatches(session)) {
      throw new Error('Chromecast session ended during the Cast transition');
    }
  };

  const settleCastOp = (op) => {
    if (!op || op.settled) return;
    op.settled = true;
    op.prev = null;
    try {
      op.resolveDone();
    } catch {}
  };

  const castOpInFlight = (kinds = null) => {
    const op = castOpRef.current;
    if (!op || op.settled) return null;
    if (kinds && !kinds.includes(op.kind)) return null;
    return op;
  };

  const awaitCastOpUnwound = async (op, timeoutMs = 8000) => {
    if (!op || op.settled) return true;
    let timer = null;
    const finished = await Promise.race([
      op.done.then(() => true),
      new Promise((resolve) => {
        timer = window.setTimeout(() => resolve(false), timeoutMs);
      }),
    ]);
    if (timer != null) window.clearTimeout(timer);
    if (!finished) {
      console.warn(
        `[chromecast-r134] op #${op.seq} ${op.kind} still running after ${timeoutMs}ms; continuing`
      );
    }
    return finished;
  };

  const castSessionMatches = (expected) => {
    const current = castContextRef.current?.getCurrentSession?.();
    if (!current || !expected) return false;
    if (current === expected) return true;
    try {
      return current.getSessionId?.() === expected.getSessionId?.();
    } catch {
      return false;
    }
  };

  // CAF can leave a LOAD pending for minutes once a newer LOAD or a session
  // change overtakes it (600 s observed). A transition never waits on that.
  const castLoadMediaBounded = (session, request, op = null, timeoutMs = 20000) =>
    new Promise((resolve, reject) => {
      let finished = false;
      let poll = null;
      const finish = (settle, value) => {
        if (finished) return;
        finished = true;
        if (poll != null) window.clearInterval(poll);
        settle(value);
      };
      const startedAt = Date.now();
      poll = window.setInterval(() => {
        if (op && castOpRef.current !== op) {
          finish(reject, castSupersededError(op));
        } else if (!castSessionMatches(session)) {
          finish(reject, new Error('Chromecast session changed during LOAD'));
        } else if (Date.now() - startedAt > timeoutMs) {
          finish(
            reject,
            new Error(`Chromecast LOAD was not acknowledged within ${timeoutMs}ms`)
          );
        }
      }, 250);
      Promise.resolve()
        .then(() => session.loadMedia(request))
        .then(
          (value) => finish(resolve, value),
          (error) => finish(reject, error)
        );
    });

  const postCastStop = (stopUrl, reason = '', { beacon = false } = {}) => {
    if (!stopUrl) return;
    let url = stopUrl;
    if (reason) {
      try {
        const parsed = new URL(stopUrl, window.location.origin);
        parsed.searchParams.set('reason', String(reason).slice(0, 80));
        url = parsed.toString();
      } catch {}
    }
    if (beacon) {
      dispatcharrSendBeaconPost(url);
      return;
    }
    void fetch(url, {
      method: 'POST',
      keepalive: true,
      cache: 'no-store',
      credentials: 'same-origin',
    }).catch(() => undefined);
  };

  // Release exactly ONE lease. The shared active slot is cleared only while it
  // still holds this lease -- a newer transition may own it by now.
  const releaseCastLeaseExact = async (stopUrl, reason = '') => {
    if (!stopUrl) return true;
    const activeStop = dispatcharrCastRuntime.stopUrl || castStopUrlRef.current;
    if (activeStop === stopUrl) {
      if (dispatcharrCastRuntime.leaseTimer != null) {
        window.clearInterval(dispatcharrCastRuntime.leaseTimer);
        dispatcharrCastRuntime.leaseTimer = null;
      }
      dispatcharrCastRuntime.renewUrl = null;
      dispatcharrCastRuntime.stopUrl = null;
      castRenewUrlRef.current = null;
      castStopUrlRef.current = null;
    }
    if (castDeferredLeaseStopUrlRef.current === stopUrl) {
      castDeferredLeaseStopUrlRef.current = null;
    }
    let releaseUrl = stopUrl;
    if (reason) {
      try {
        const parsed = new URL(stopUrl, window.location.origin);
        parsed.searchParams.set('reason', String(reason).slice(0, 80));
        releaseUrl = parsed.toString();
      } catch {}
    }
    try {
      const response = await fetch(releaseUrl, {
        method: 'POST',
        keepalive: true,
        cache: 'no-store',
        credentials: 'same-origin',
      });
      return response.ok;
    } catch (error) {
      console.warn('[chromecast-r134] exact Cast lease release failed:', error);
      return false;
    }
  };

  // Continue Live keeps the archive lease aside (R14) while it switches the
  // TV. When one attempt fails but the cast lives on, put it back so the TV's
  // rewind keeps its producer during the retry / Web handback.
  const restoreDeferredArchiveLease = () => {
    const stopUrl = castDeferredLeaseStopUrlRef.current;
    if (!stopUrl) return false;
    castDeferredLeaseStopUrlRef.current = null;
    let renewUrl = null;
    try {
      const parsed = new URL(stopUrl, window.location.origin);
      if (parsed.searchParams.get('cast_lease') === 'release') {
        parsed.searchParams.set('cast_lease', '1');
        parsed.searchParams.delete('reason');
        renewUrl = parsed.toString();
      }
    } catch {}
    if (!renewUrl) {
      void releaseCastLeaseExact(stopUrl, 'continue-live-failed');
      return false;
    }
    const leftover = dispatcharrCastRuntime.stopUrl || castStopUrlRef.current;
    if (leftover && leftover !== stopUrl) {
      // A lease whose start never completed (renew refused) is still parked
      // in the slot; never orphan it.
      postCastStop(leftover, 'continue-live-failed');
    }
    if (dispatcharrCastRuntime.leaseTimer != null) {
      window.clearInterval(dispatcharrCastRuntime.leaseTimer);
      dispatcharrCastRuntime.leaseTimer = null;
    }
    dispatcharrCastRuntime.renewUrl = renewUrl;
    dispatcharrCastRuntime.stopUrl = stopUrl;
    castRenewUrlRef.current = renewUrl;
    castStopUrlRef.current = stopUrl;
    void renewCastSessionLeaseNow(renewUrl);
    dispatcharrCastRuntime.leaseTimer = window.setInterval(() => {
      void renewCastSessionLeaseNow(renewUrl);
    }, 10000);
    console.info('[chromecast-r134] archive lease restored after a failed Continue Live attempt');
    return true;
  };

  // DISPATCHARR_CAST_PARKED_LIVE_LEASE_R134: the parked Live lease renews with
  // parked=1, which keeps its session out of the server's receiver-gone
  // watchdog (R110) and renew idle check (R108): the TV is on the rewind by
  // design, so its silence on the Live playlist is expected. Without it the
  // watchdog reaped the warm tee ~12 s into every rewind and a Continue Live
  // in the channel's stop tail failed with bootstrap HTTP 503. A refused renew
  // only drops the warm tee -- it never tears down the healthy rewind (R113 on
  // this lease used to end the whole cast).
  const renewParkedLiveLease = async (parked) => {
    if (!parked?.renewUrl) return false;
    let url = parked.renewUrl;
    try {
      const parsed = new URL(parked.renewUrl, window.location.origin);
      parsed.searchParams.set('parked', '1');
      url = parsed.toString();
    } catch {}
    try {
      const token = useAuthStore.getState().accessToken;
      const response = await fetch(url, {
        method: 'POST',
        cache: 'no-store',
        credentials: 'same-origin',
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      if (!response.ok) {
        if (castParkedLiveLeaseRef.current === parked) {
          releaseParkedLiveLease(`warm tee lost: HTTP ${response.status}`);
        }
        return false;
      }
      return true;
    } catch (error) {
      console.debug('[chromecast-r134] parked Live lease renew failed:', error);
      return false;
    }
  };

  // Stop ONE archive cast pipeline (pipeline-scoped /stop; the Web runtime
  // of the same session is untouched).
  const stopArchiveCastPipelineExact = (mediaUrl, { beacon = false } = {}) => {
    if (!mediaUrl) return;
    try {
      const parsed = new URL(mediaUrl, window.location.origin);
      if (!parsed.pathname.includes('/proxy/catchup-hls/')) return;
      if (!parsed.searchParams.get('cast_pipeline')) return;
      parsed.pathname = parsed.pathname.replace(/\/(?:index|master)\.m3u8$/, '/stop');
      parsed.searchParams.delete('adaptive_output_profile');
      postCastStop(parsed.toString(), '', { beacon });
    } catch {}
  };

  // A superseded archive loader gives back what it acquired -- unless its LOAD
  // was already sent: the TV is (or is about to be) on that stream and the op
  // that superseded it owns the lease (Continue Live adopts it as the R14
  // deferred lease, Stop and X release it).
  const unwindArchiveCastOp = (op) => {
    if (!op) return;
    const superseder = castOpRef.current;
    if (
      op.archiveLoadSent &&
      superseder &&
      superseder !== op &&
      superseder.kind === 'continue-live'
    ) {
      return;
    }
    if (op.archiveLeaseStopUrl) {
      void releaseCastLeaseExact(op.archiveLeaseStopUrl, 'archive-cast-superseded');
    }
    if (op.archiveCastMediaUrl) stopArchiveCastPipelineExact(op.archiveCastMediaUrl);
  };

  // Continue Live pressed before the rewind ever reached the TV: the TV is
  // still on the parked Live tee, so take that lease back instead of loading
  // Live again (instant, no re-buffer, no second LOAD).
  const unparkLiveCastLease = (liveSourceUrl) => {
    const parked = castParkedLiveLeaseRef.current;
    const session = castContextRef.current?.getCurrentSession?.();
    if (!parked || !session) return false;
    const mediaSession = session.getMediaSession?.();
    const contentId = String(
      mediaSession?.media?.contentId || mediaSession?.media?.contentUrl || ''
    );
    const state = String(
      castRemotePlayerRef.current?.playerState || mediaSession?.playerState || ''
    );
    const castSessionOf = (url) => {
      try {
        return new URL(url, window.location.origin).searchParams.get('cast_session');
      } catch {
        return null;
      }
    };
    const parkedSession = castSessionOf(parked.renewUrl);
    if (
      !contentId.includes('/proxy/ts/cast-hls/') ||
      !parkedSession ||
      castSessionOf(contentId) !== parkedSession ||
      !['PLAYING', 'BUFFERING', 'PAUSED'].includes(state)
    ) {
      return false;
    }
    castParkedLiveLeaseRef.current = null;
    if (parked.timer != null) window.clearInterval(parked.timer);
    if (dispatcharrCastRuntime.leaseTimer != null) {
      window.clearInterval(dispatcharrCastRuntime.leaseTimer);
      dispatcharrCastRuntime.leaseTimer = null;
    }
    const leftover = dispatcharrCastRuntime.stopUrl || castStopUrlRef.current;
    if (leftover && leftover !== parked.stopUrl) postCastStop(leftover, 'rewind-abandoned');
    dispatcharrCastRuntime.renewUrl = parked.renewUrl;
    dispatcharrCastRuntime.stopUrl = parked.stopUrl;
    castRenewUrlRef.current = parked.renewUrl;
    castStopUrlRef.current = parked.stopUrl;
    void renewCastSessionLeaseNow(parked.renewUrl);
    dispatcharrCastRuntime.leaseTimer = window.setInterval(() => {
      void renewCastSessionLeaseNow(parked.renewUrl);
    }, 10000);
    let runtimeMediaUrl = contentId;
    try {
      const parsed = new URL(contentId, window.location.origin);
      parsed.searchParams.delete('cast_master');
      runtimeMediaUrl = parsed.toString();
    } catch {}
    const device = session.getCastDevice?.();
    Object.assign(dispatcharrCastRuntime, {
      active: true,
      kind: 'live',
      sourceUrl: liveSourceUrl,
      mediaUrl: runtimeMediaUrl,
      ownLiveCastSession: parkedSession, // DISPATCHARR_CAST_REMOTE_REWIND_R135
      deviceName:
        dispatcharrCastRuntime.deviceName || device?.friendlyName || 'Chromecast',
    });
    transportSuspendedKindRef.current = null;
    setTransportSuspendedKind(null);
    castActiveRef.current = true;
    castHandoffInProgressRef.current = false;
    setCastActive(true);
    setCastDeviceName(dispatcharrCastRuntime.deviceName);
    setCastError(null);
    console.info(
      '[chromecast-r134] Continue Live: the TV never left the warm Live tee; lease un-parked'
    );
    return true;
  };

  // DISPATCHARR_LIVE_TIMESHIFT_CAST_CATCHUP_TO_LIVE_R135: the live-rewind TV
  // plays a VOD snapshot (yellow seekable bar). Reaching its end used to read
  // as "programme finished" and ended the whole cast D seconds after a rewind
  // of D seconds; fast-forwarding to the edge did the same. Both now continue
  // with Live on the TV -- exactly what Continue Live does.
  const castLiveTimeshiftToLive = (reason) => {
    const latest = castLatestRef.current || {};
    if (latest.contentType !== 'catchup-hls' || !latest.metadata?.liveTimeshift) {
      return false;
    }
    if (castOpInFlight() || liveTimeshiftReturningToLiveRef.current) return true;
    const now = Date.now();
    if (now - liveTimeshiftCastToLiveAtRef.current < 8000) return true;
    liveTimeshiftCastToLiveAtRef.current = now;
    console.info(`[chromecast-r135] ${reason}: continuing with Live on the TV`);
    void latest.returnToLiveFromProviderTimeshift?.();
    return true;
  };

  // R135 review: how much of the time-shift session has aired, by the wall
  // clock -- not capped by the programme end or the session horizon (those
  // cap the BAR; once everything has aired there is nothing unaired to keep
  // the TV away from, and FINISHED ends it at the real end).
  const liveTimeshiftCastAiredSeconds = () => {
    const startMs = Date.parse(castLatestRef.current?.metadata?.liveTimeshiftStart || '');
    return Number.isFinite(startMs)
      ? Math.max(0, (Date.now() - startMs) / 1000)
      : liveTimeshiftTimelineSeconds();
  };

  // DISPATCHARR_LIVE_TIMESHIFT_CAST_SNAPSHOT_AHEAD_R135: the receiver snapshot
  // reaches a bounded stretch past the aired edge (backend, 10 min). Before
  // the TV runs out of it, hand it a fresh snapshot at the same position: the
  // same cast pipeline keeps producing, only CAF reloads -- one short reload
  // every ~10 min of time-shifted viewing instead of the cast ending.
  const refreshLiveTimeshiftCastSnapshot = async (position) => {
    const state = liveTimeshiftCastRef.current;
    if (state.refreshing) return false;
    const session = castContextRef.current?.getCurrentSession?.();
    const mediaUrl = dispatcharrCastRuntime.mediaUrl;
    if (!session || !mediaUrl || castOpInFlight()) return false;
    const target = Math.max(0, Number(position) || 0);
    let failed = false;
    state.refreshing = true;
    // Spacing counts from this attempt, so a failing refresh cannot storm.
    state.lastLoadAt = Date.now();
    const op = beginCastOp('snapshot-refresh', { position: target });
    castArchiveSeekHealthRef.current = {
      inProgress: true,
      startedAt: Date.now(),
      target,
    };
    try {
      const next = new URL(mediaUrl, window.location.origin);
      next.searchParams.set('cast_start_position', target.toFixed(3));
      next.searchParams.set('cast_seek_nonce', `refresh-${Date.now().toString(36)}`);
      const refreshedUrl = next.toString();
      const latestMeta = castLatestRef.current?.metadata || {};
      const mediaInfo = createCastMediaInfo(
        refreshedUrl,
        window.chrome.cast.media.StreamType.BUFFERED,
        latestMeta.name || latestMeta.title || 'Dispatcharr Archive',
        { fmp4: true }
      );
      const request = new window.chrome.cast.media.LoadRequest(mediaInfo);
      request.autoplay = true;
      request.currentTime = target;
      assertCastOpSession(op, session);
      await castLoadMediaBounded(session, request, op);
      assertCastOp(op);
      const { remotePlayer } = await attachRemotePlayer(session, op);
      assertCastOp(op);
      dispatcharrCastRuntime.mediaUrl = refreshedUrl;
      dispatcharrCastRuntime.archivePosition = target;
      Object.assign(liveTimeshiftCastRef.current, {
        lastLoadAt: Date.now(),
        edgeArmed: false,
        refreshFailedAt: 0,
      });
      castArchiveSeekHealthRef.current = {
        inProgress: false,
        startedAt: 0,
        completedAt: Date.now(),
        target,
      };
      castHealthRef.current = {
        state: String(remotePlayer?.playerState || ''),
        bufferingSince: 0,
        lastPosition: target,
        lastProgressAt: Date.now(),
      };
      console.info(
        `[chromecast-r135] live-rewind snapshot refreshed at ${target.toFixed(1)}s`
      );
      return true;
    } catch (error) {
      castArchiveSeekHealthRef.current = {
        inProgress: false,
        startedAt: 0,
        completedAt: Date.now(),
        target,
      };
      if (!isCastSuperseded(error) && castOpIsCurrent(op)) {
        // A rejected LOAD leaves the old snapshot playing (at its end FINISHED
        // continues Live); one the receiver accepted and then failed leaves it
        // IDLE -- continue with Live instead of the R109 teardown.
        console.warn('[chromecast-r135] live-rewind snapshot refresh failed:', error);
        failed = true;
        state.refreshFailedAt = Date.now();
      }
      return false;
    } finally {
      // Still this refresh's turn? (Stop, a zap, X or Continue Live supersede it.)
      const stillCurrent = castOpIsCurrent(op);
      settleCastOp(op);
      state.refreshing = false;
      let toLive = false;
      if (failed) {
        const mediaSession = castContextRef.current?.getCurrentSession?.()?.getMediaSession?.();
        const idle = window.chrome?.cast?.media?.PlayerState?.IDLE || 'IDLE';
        if (!mediaSession || String(mediaSession.playerState || '') === idle) {
          toLive = true;
          castLiveTimeshiftToLive('the snapshot refresh failed');
        }
      }
      // A Web seek made meanwhile was queued, not dropped.
      const pendingSeek = state.pendingSeek;
      state.pendingSeek = null;
      if (
        pendingSeek != null &&
        !toLive &&
        stillCurrent &&
        (castActiveRef.current || dispatcharrCastRuntime.active)
      ) {
        void commitArchiveCastSeek(pendingSeek);
      }
    }
  };

  // DISPATCHARR_CAST_TRANSITION_HANDBACK_R134: a transition that cannot finish
  // while the TV session is still alive (Continue Live / Rewind on Cast failed
  // on every attempt, or Stop Casting pressed in the middle of one) ends the
  // cast cleanly and continues the SAME content in the Web player --
  // "Loading video..." until it plays. The R109 label ("Cast stopped on the
  // device. Playback was not resumed.") stays reserved for a cast that really
  // ended on the TV.
  const handBackCastTransition = async ({
    mode,
    url = null,
    metadata: handbackMetadata = null,
    reason = '',
    supersededOp = null,
  } = {}) => {
    const op = beginCastOp('handback', { mode, url });
    console.warn(
      `[chromecast-r134] transition handback -> Web ${mode}` + (reason ? ` (${reason})` : '')
    );
    castFailureRecoveryRef.current = true;
    beginDispatcharrCastIntentionalHandback();
    // The loaders raise castHandoffInProgressRef and only a SUCCESSFUL commit
    // lowers it; superseded/failed ones leave it up. Left up, it silently
    // disables the Web player's pause/suspend handling after this handback.
    castHandoffInProgressRef.current = false;
    setCastReturningToLive(false);
    setCastError(null);
    castActiveRef.current = false;
    setCastActive(false);
    setCastDeviceName('');
    setLoadError(null);
    setIsLoading(true);

    const runtimeKind = dispatcharrCastRuntime.kind;
    const runtimeMediaUrl = dispatcharrCastRuntime.mediaUrl;
    const liveStops = [];
    const otherStops = [];
    const classify = (stopUrl) => {
      if (!stopUrl) return;
      if (String(stopUrl).includes('/proxy/ts/cast-hls/')) liveStops.push(stopUrl);
      else otherStops.push(stopUrl);
    };
    if (dispatcharrCastRuntime.leaseTimer != null) {
      window.clearInterval(dispatcharrCastRuntime.leaseTimer);
      dispatcharrCastRuntime.leaseTimer = null;
    }
    classify(dispatcharrCastRuntime.stopUrl || castStopUrlRef.current);
    classify(castDeferredLeaseStopUrlRef.current);
    castDeferredLeaseStopUrlRef.current = null;
    const parked = castParkedLiveLeaseRef.current;
    castParkedLiveLeaseRef.current = null;
    if (parked?.timer != null) window.clearInterval(parked.timer);
    if (parked?.stopUrl) liveStops.push(parked.stopUrl);
    dispatcharrCastRuntime.renewUrl = null;
    dispatcharrCastRuntime.stopUrl = null;
    castRenewUrlRef.current = null;
    castStopUrlRef.current = null;
    otherStops.forEach((stopUrl) => postCastStop(stopUrl, 'transition-handback'));
    if (runtimeKind === 'archive' && runtimeMediaUrl) {
      stopArchiveCastPipelineExact(runtimeMediaUrl);
    }
    if (supersededOp?.archiveCastMediaUrl) {
      stopArchiveCastPipelineExact(supersededOp.archiveCastMediaUrl);
    }

    try {
      (castContextRef.current || window.cast?.framework?.CastContext?.getInstance?.())
        ?.endCurrentSession?.(true);
    } catch (error) {
      console.debug('[chromecast-r134] endCurrentSession skipped:', error);
    }
    castRemoteControllerRef.current = null;
    castRemotePlayerRef.current = null;
    archiveCastControllerRef.current = null;
    clearDispatcharrCastRuntime();
    transportSuspendedKindRef.current = null;
    setTransportSuspendedKind(null);

    const releaseLiveStops = () =>
      liveStops.forEach((stopUrl) => postCastStop(stopUrl, 'transition-handback'));
    try {
      if (mode === 'live') {
        const latest = castLatestRef.current || {};
        setLiveTimeshiftDraftSeconds(0);
        setShowLiveRewindPicker(false);
        setLiveTimeshiftPlaybackSeconds(0);
        if (latest.contentType === 'catchup-hls' && latest.metadata?.liveTimeshift) {
          latest.requestArchiveStop?.(latest.streamUrl);
        }
        livePlaybackStartedRef.current = false;
        if (latest.contentType === 'live' && url && latest.streamUrl === url) {
          safeDestroyPlayer();
          void latest.startLiveWebPlayback?.();
        } else if (url) {
          showVideo(url, 'live', handbackMetadata || { name: 'Live TV' });
        }
        // Keep the warm tee until the Web client is attached (as R15 does for
        // Stop Casting), bounded.
        void (async () => {
          const deadline = Date.now() + 10000;
          while (!livePlaybackStartedRef.current && Date.now() < deadline) {
            await new Promise((resolve) => window.setTimeout(resolve, 200));
          }
          releaseLiveStops();
        })();
      } else {
        releaseLiveStops();
        // The superseded loader may still have a suspend/resume POST in
        // flight; let it land first, or its suspend would beat our resume.
        await awaitCastOpUnwound(supersededOp, 8000);
        if (castOpRef.current === op) {
          const latest = castLatestRef.current || {};
          if (latest.contentType === 'catchup-hls' && (!url || latest.streamUrl === url)) {
            const recorded = Number(supersededOp?.archivePosition);
            const position = Number.isFinite(recorded)
              ? Math.max(0, recorded)
              : Math.max(
                  0,
                  Number(archiveSuspendedPositionRef.current) ||
                    Number(latest.metadata?.liveTimeshiftInitialPositionSeconds) ||
                    0
                );
            archiveSuspendedPositionRef.current = position;
            archiveLocalRuntimeUrlRef.current = null;
            archiveLeaseSuspendedRef.current = true;
            archivePlayerRetainedRef.current = false;
            transportSuspendedKindRef.current = 'archive';
            setTransportSuspendedKind('archive');
            safeDestroyPlayer();
            await latest.resumeSuspendedTransport?.();
          }
          // else: the content switch has not landed yet -- the player effect
          // starts the local rewind on its own once it does.
        }
      }
    } catch (error) {
      console.warn('[chromecast-r134] transition handback failed:', error);
    } finally {
      window.setTimeout(() => {
        if (!castOpInFlight(['start', 'rewind-on-cast', 'continue-live'])) {
          castFailureRecoveryRef.current = false;
          castHandoffInProgressRef.current = false;
        }
        castRecoveryCooldownUntil = Date.now() + 15000;
        settleCastOp(op);
      }, 1500);
    }
  };

  // DISPATCHARR_CAST_LIVE_WARM_DURING_REWIND_R21: Rewind-on-Cast used to
  // release the Live lease, which stopped the Live channel, so Continue
  // Live always cold-started it and the TV joined a 3-4 segment playlist
  // (the ~20s slideshow). Now the Live lease is parked and kept renewed,
  // so Continue Live finds a warm tee with a full window. Measured
  // 2026-09-11: TiVi serves a catch-up connection alongside a held Live one
  // for the same channel at full speed. The parked lease renews only while
  // the TV is still on the rewound (archive) stream -- any other state
  // releases it on the next tick -- and the server lease TTL is the last
  // backstop if the page dies.
  const releaseParkedLiveLease = (reason, { beacon = false } = {}) => {
    const parked = castParkedLiveLeaseRef.current;
    if (!parked) return;
    castParkedLiveLeaseRef.current = null;
    if (parked.timer != null) window.clearInterval(parked.timer);
    console.info(`[chromecast-r21] warm Live lease released (${reason})`);
    if (!parked.stopUrl) return;
    if (beacon) {
      dispatcharrSendBeaconPost(parked.stopUrl);
      return;
    }
    void fetch(parked.stopUrl, {
      method: 'POST',
      keepalive: true,
      cache: 'no-store',
      credentials: 'same-origin',
    }).catch(() => undefined);
  };

  const parkLiveCastLease = () => {
    const renewUrl = dispatcharrCastRuntime.renewUrl || castRenewUrlRef.current;
    const stopUrl = dispatcharrCastRuntime.stopUrl || castStopUrlRef.current;
    if (!renewUrl || !stopUrl) return false;
    // DISPATCHARR_CAST_PARKED_LIVE_LEASE_R134: only a Live cast-hls lease is
    // parked; an existing warm tee is kept as it is.
    if (!String(renewUrl).includes('/proxy/ts/cast-hls/')) {
      return Boolean(castParkedLiveLeaseRef.current);
    }
    releaseParkedLiveLease('replaced');
    if (dispatcharrCastRuntime.leaseTimer != null) {
      window.clearInterval(dispatcharrCastRuntime.leaseTimer);
      dispatcharrCastRuntime.leaseTimer = null;
    }
    // Detach it from the active-lease slot, so the catch-up lease that
    // loadArchiveOnChromecast starts next does not release it.
    dispatcharrCastRuntime.renewUrl = null;
    dispatcharrCastRuntime.stopUrl = null;
    castRenewUrlRef.current = null;
    castStopUrlRef.current = null;
    const parked = { renewUrl, stopUrl, since: Date.now(), timer: null };
    parked.timer = window.setInterval(() => {
      if (castParkedLiveLeaseRef.current !== parked) return;
      // DISPATCHARR_CAST_PARKED_LIVE_LEASE_R134: a Rewind on Cast or Continue
      // Live that is still switching the TV counts as "on the rewind" too.
      const onRewind =
        !dispatcharrPlayerClosingRef.current &&
        (Boolean(castOpInFlight(['rewind-on-cast', 'continue-live'])) ||
          ((castActiveRef.current || dispatcharrCastRuntime.active) &&
            dispatcharrCastRuntime.kind === 'archive'));
      if (!onRewind) {
        releaseParkedLiveLease('TV is no longer on the rewind');
        return;
      }
      if (Date.now() - parked.since > 45 * 60 * 1000) {
        releaseParkedLiveLease('rewind longer than 45 min');
        return;
      }
      // parked=1 (R134): exempt from the receiver-gone watchdog / idle check;
      // every 10 s keeps the server-side parked marker (45 s TTL) fresh.
      void renewParkedLiveLease(parked);
    }, 10000);
    castParkedLiveLeaseRef.current = parked;
    void renewParkedLiveLease(parked);
    console.info('[chromecast-r21] Rewind on Cast: Live tee kept warm for Continue Live');
    return true;
  };

  const waitForCastHlsPrewarm = async (
    mediaUrl,
    minSegments = 2,
    timeoutMs = 14000,
    abortCheck = null // DISPATCHARR_CAST_TRANSITION_OPS_R134: throws once superseded
  ) => {
    const startedAt = Date.now();
    let lastCount = 0;
    while (Date.now() - startedAt < timeoutMs) {
      if (abortCheck) abortCheck();
      try {
        const prewarmProbe = new URL(mediaUrl, window.location.origin);
        prewarmProbe.searchParams.set('prewarm', String(Date.now()));
        const response = await fetch(prewarmProbe.toString(), {
          method: 'GET',
          cache: 'no-store',
          credentials: 'same-origin',
        });
        if (response.ok) {
          const body = await response.text();
          lastCount = (body.match(/^#EXTINF:/gm) || []).length;
          if (lastCount >= minSegments) return lastCount;
        }
      } catch {}
      await new Promise((resolve) => window.setTimeout(resolve, 150));
    }
    throw new Error(`Chromecast HLS prewarm timed out (${lastCount}/${minSegments})`);
  };

  const waitForArchiveCastManifest = async (mediaUrl, timeoutMs = 12000) => {
    const startedAt = Date.now();
    while (Date.now() - startedAt < timeoutMs) {
      try {
        const probe = new URL(mediaUrl, window.location.origin);
        // DISPATCHARR_ARCHIVE_CAST_SENDER_PREWARM_R8_15_0
        // This request comes from the sender and must be allowed to warm the
        // target segment. The receiver-only marker remains on the URL sent to
        // CAF by loadMedia(), where it prevents remote segment traffic from
        // taking producer ownership.
        probe.searchParams.delete('cast_receiver');
        probe.searchParams.set('cast_probe', String(Date.now()));
        const token = useAuthStore.getState().accessToken;
        const response = await fetch(probe.toString(), {
          cache: 'no-store',
          credentials: 'same-origin',
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });
        if (response.ok) return;
      } catch {}
      await new Promise((resolve) => window.setTimeout(resolve, 150));
    }
    throw new Error('Archive HLS did not become ready for Chromecast');
  };

  const waitForChromecastMediaReady = (
    remotePlayer,
    timeoutMs = 20000,
    isCancelled = null // DISPATCHARR_CAST_TRANSITION_OPS_R134
  ) =>
    new Promise((resolve, reject) => {
      const startedAt = Date.now();
      const poll = () => {
        if (isCancelled && isCancelled()) {
          const cancelled = new Error('Chromecast load superseded by a newer Cast action');
          cancelled.castSuperseded = true;
          reject(cancelled);
          return;
        }
        const playerState = remotePlayer?.playerState;
        const mediaLoaded = Boolean(remotePlayer?.isMediaLoaded);
        const readyState =
          playerState === window.chrome?.cast?.media?.PlayerState?.BUFFERING ||
          playerState === window.chrome?.cast?.media?.PlayerState?.PLAYING;
        // DISPATCHARR_ARCHIVE_CAST_PROGRAMME_END_R133: a load that already played
        // out is ready, not stuck; the health tick then closes it as finished.
        const idleFinished =
          playerState ===
            (window.chrome?.cast?.media?.PlayerState?.IDLE || 'IDLE') &&
          String(
            castContextRef.current?.getCurrentSession?.()?.getMediaSession?.()
              ?.idleReason || ''
          ) === (window.chrome?.cast?.media?.IdleReason?.FINISHED || 'FINISHED') &&
          // R133a review: during a seek reload the CURRENT media session is
          // still the finished PRE-seek item until the new LOAD registers --
          // honoring it would complete the wait against the old media.
          !castArchiveSeekHealthRef.current.inProgress;
        if ((mediaLoaded && readyState) || idleFinished) {
          resolve(playerState);
          return;
        }
        if (Date.now() - startedAt >= timeoutMs) {
          // DISPATCHARR_ARCHIVE_FMP4_CAST_RECEIVER_R3
          const mediaSession =
            castContextRef.current?.getCurrentSession?.()?.getMediaSession?.();
          reject(
            new Error(
              'Chromecast media did not enter BUFFERING/PLAYING ' +
                `(remoteState=${String(playerState || 'none')} ` +
                `loaded=${mediaLoaded} mediaState=${String(
                  mediaSession?.playerState || 'none'
                )} idleReason=${String(mediaSession?.idleReason || 'none')})`
            )
          );
          return;
        }
        window.setTimeout(poll, 100);
      };
      poll();
    });

  // DISPATCHARR_ARCHIVE_FMP4_R2
  const createCastMediaInfo = (
    mediaUrl,
    streamType,
    title,
    { fmp4 = false } = {}
  ) => {
    // DISPATCHARR_ARCHIVE_FMP4_CAST_RECEIVER_R3
    // Archive fMP4 gets a Cast-only master playlist with explicit CODECS.
    // Keep dispatcharrCastRuntime.mediaUrl on the underlying media playlist so
    // seek/Stop-Cast handback continues to address the same producer runtime.
    let receiverMediaUrl = mediaUrl;
    if (fmp4) {
      try {
        const parsed = new URL(mediaUrl, window.location.origin);
        if (parsed.pathname.includes('/proxy/catchup-hls/')) {
          parsed.searchParams.set('cast_master', '1');
          receiverMediaUrl = parsed.toString();
        }
      } catch {}
    }
    const mediaInfo = new window.chrome.cast.media.MediaInfo(
      receiverMediaUrl,
      'application/x-mpegURL'
    );
    mediaInfo.streamType = streamType;
    const videoSegmentFormat = fmp4
      ? window.chrome.cast.media.HlsVideoSegmentFormat?.FMP4
      : window.chrome.cast.media.HlsVideoSegmentFormat?.MPEG2_TS;
    if (videoSegmentFormat) mediaInfo.hlsVideoSegmentFormat = videoSegmentFormat;
    const audioSegmentFormat = fmp4
      ? window.chrome.cast.media.HlsSegmentFormat?.FMP4
      : window.chrome.cast.media.HlsSegmentFormat?.TS;
    if (audioSegmentFormat) mediaInfo.hlsSegmentFormat = audioSegmentFormat;
    const mediaMetadata = new window.chrome.cast.media.GenericMediaMetadata();
    mediaMetadata.title = title || 'Dispatcharr';
    mediaInfo.metadata = mediaMetadata;
    return mediaInfo;
  };

  const attachRemotePlayer = async (session, op = null) => {
    const remotePlayer = new window.cast.framework.RemotePlayer();
    const remoteController =
      new window.cast.framework.RemotePlayerController(remotePlayer);
    castRemotePlayerRef.current = remotePlayer;
    castRemoteControllerRef.current = remoteController;
    const receiverState = await waitForChromecastMediaReady(
      remotePlayer,
      20000,
      op ? () => castOpRef.current !== op : null
    );
    return { remotePlayer, remoteController, receiverState };
  };

  // DISPATCHARR_CAST_BOOTSTRAP_STOP_TAIL_R134: a Cast start that lands in
  // the ~2 s stop tail of the same channel (Continue Live right after the warm
  // tee was reaped, a re-Cast right after Stop) got HTTP 503 and the whole
  // handoff failed. Wait for the same live-reopen gate the Web player uses
  // and retry (bounded) instead.
  const waitForCastLiveReopenGate = async (sourceUrl, op = null, timeoutMs = 8000) => {
    const channelId = liveChannelIdFromUrl(sourceUrl);
    if (!channelId) return false;
    const reopenUrl = new URL(
      `/proxy/ts/live-reopen/${encodeURIComponent(channelId)}/start`,
      window.location.origin
    );
    try {
      const profile = new URL(sourceUrl, window.location.origin).searchParams.get(
        'adaptive_output_profile'
      );
      if (profile) reopenUrl.searchParams.set('adaptive_output_profile', profile);
    } catch {}
    const deadline = Date.now() + timeoutMs;
    while (Date.now() < deadline) {
      assertCastOp(op);
      try {
        const response = await fetch(reopenUrl.toString(), {
          method: 'POST',
          cache: 'no-store',
          credentials: 'same-origin',
        });
        if (
          response.status === 204 &&
          response.headers.get('X-Dispatcharr-Live-Reopen-Ready') !== '0'
        ) {
          return true;
        }
        if (response.status !== 202 && response.status !== 503) return false;
      } catch {}
      await new Promise((resolve) => window.setTimeout(resolve, 200));
    }
    return false;
  };

  const startLiveCastBootstrap = async (bootstrapUrl, castSessionId, op = null) => {
    for (let attempt = 0; ; attempt += 1) {
      const controller = new AbortController();
      dispatcharrCastRuntime.bootstrapController = controller;
      const token = useAuthStore.getState().accessToken;
      const headers = {
        'X-Dispatcharr-Cast-Bootstrap': '1',
        'X-Dispatcharr-Cast-Session': String(castSessionId || ''),
      };
      if (token) headers.Authorization = `Bearer ${token}`;
      const response = await fetch(bootstrapUrl, {
        method: 'GET',
        cache: 'no-store',
        credentials: 'same-origin',
        headers,
        signal: controller.signal,
      });
      if (response.ok) {
        const reader = response.body?.getReader?.();
        if (reader) {
          void (async () => {
            try {
              while (true) {
                const { done } = await reader.read();
                if (done) break;
              }
            } catch {}
          })();
        }
        return controller;
      }
      try {
        controller.abort();
      } catch {}
      if (dispatcharrCastRuntime.bootstrapController === controller) {
        dispatcharrCastRuntime.bootstrapController = null;
      }
      if (response.status !== 503 || attempt >= 2) {
        throw new Error(`Chromecast profile bootstrap failed: HTTP ${response.status}`);
      }
      const retryAfter = Math.min(
        3,
        Math.max(1, Number(response.headers.get('Retry-After')) || 1)
      );
      console.info(
        '[chromecast-r134] bootstrap HTTP 503 (channel stop tail); ' +
          `waiting for live reopen, retry ${attempt + 1}/2`
      );
      await new Promise((resolve) => window.setTimeout(resolve, retryAfter * 1000));
      assertCastOp(op);
      await waitForCastLiveReopenGate(bootstrapUrl, op);
      assertCastOp(op);
    }
  };

  const pulseCastProfileCleanup = async (bootstrapUrl) => {
    if (!bootstrapUrl) return false;
    const controller = new AbortController();
    try {
      const token = useAuthStore.getState().accessToken;
      const response = await fetch(bootstrapUrl, {
        method: 'GET',
        cache: 'no-store',
        credentials: 'same-origin',
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        signal: controller.signal,
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      // Headers mean the transient client is registered. Keep it alive for a
      // moment, then abort: handle_client_disconnect now sees lease=absent.
      await new Promise((resolve) => window.setTimeout(resolve, 120));
      return true;
    } catch (error) {
      if (error?.name !== 'AbortError') {
        console.warn('[chromecast-r8.8.44] Cast cleanup pulse failed:', error);
      }
      return false;
    } finally {
      try { controller.abort(); } catch {}
      console.info('[chromecast-r8.8.44] Cast profile cleanup pulse complete');
    }
  };

  // DISPATCHARR_SAME_PROFILE_LIVE_TEE_REMOTE_SCRUB_R8_8_50
  const loadLiveOnChromecast = async (
    session,
    sourceUrlOverride,
    { keepPreviousLease = false, op = null } = {}
  ) => {
    if (!session || !window.chrome?.cast?.media) {
      throw new Error('No Chromecast session is available');
    }

    const normalProfile = currentLiveAdaptiveProfile();
    const target = buildChromecastLiveTarget(normalProfile, sourceUrlOverride);
    if (!target) {
      throw new Error(
        'No active Web adaptive profile is available for same-profile Chromecast handoff'
      );
    }

    castHandoffInProgressRef.current = true;
    castAdaptiveProfileRef.current = target.adaptiveProfile;
    castResumeUrlRef.current = target.resumeUrl;
    let handoffDone = false;
    try {
      // runLiveCastHandoff enforces lease -> optional direct-Web bootstrap ->
      // HLS prewarm -> CAF ready. It aborts the transient bootstrap only after
      // CAF is BUFFERING/PLAYING and releases the lease on every failure path.
      const { prewarmedSegments, receiver } = await runLiveCastHandoff({
        requiresBootstrap: target.requiresBootstrap,
        startLease: () =>
          startCastSessionLease(target.renewUrl, target.stopUrl, {
            keepPrevious: keepPreviousLease,
          }),
        // DISPATCHARR_CAST_TRANSITION_OPS_R134: release exactly THIS
        // handoff's lease (a newer transition may own the active slot by now)
        // and stop at the next step once a newer Cast action superseded it.
        stopLease: () => releaseCastLeaseExact(target.stopUrl, 'live-handoff-unwound'),
        checkpoint: () => assertCastOpSession(op, session),
        startBootstrap: () =>
          startLiveCastBootstrap(target.bootstrapUrl, target.castSession, op),
        // DISPATCHARR_LIVE_CAST_COLD_TEE_PREWARM_R13 (revised in R14)
        // Continue Live creates the Live FFmpeg tee at that instant, so the
        // playlist starts nearly empty. RFC 8216 6.3.3: a client should not
        // start closer than three target durations from the end of a live
        // playlist, so below 3 segments (hls_time=1) CAF cannot start
        // cleanly anyway. R14 keeps the old catch-up playing on the TV
        // during this wait, so it costs latency, not a dead screen. Warm
        // casts find a full window and return at once. The hard guarantee
        // (>=1 segment or throw) is unchanged, and the extra wait can never
        // fail the handoff on its own.
        // DISPATCHARR_CAST_RECEIVER_HOLD_BACK_R20: 3 segments was the
        // RFC minimum, not what the receiver needs to play without
        // stalling. It now joins 9s behind the edge, so wait for ~10
        // (budget 12s). Warm tees return at once; a cold one (Continue
        // Live) fills while the TV keeps playing the rewind (R14).
        prewarm: async () => {
          const abortIfSuperseded = () => assertCastOpSession(op, session);
          const essential = await waitForCastHlsPrewarm(
            target.prewarmUrl,
            1,
            14000,
            abortIfSuperseded
          );
          if (essential >= DISPATCHARR_CAST_RECEIVER_READY_SEGMENTS) return essential;
          try {
            return await waitForCastHlsPrewarm(
              target.prewarmUrl,
              DISPATCHARR_CAST_RECEIVER_READY_SEGMENTS,
              12000,
              abortIfSuperseded
            );
          } catch (error) {
            if (isCastSuperseded(error)) throw error;
            return essential;
          }
        },
        loadReceiver: async () => {
          const mediaInfo = createCastMediaInfo(
            target.mediaUrl,
            window.chrome.cast.media.StreamType.LIVE,
            metadata?.name || metadata?.title || 'Dispatcharr Live',
          { fmp4: true }
          );
          mediaInfo.duration = null;
          // DISPATCHARR_LIVE_FMP4_R3
          const provenFmp4VideoSegmentFormat =
            window.chrome.cast.media.HlsVideoSegmentFormat?.FMP4;
          if (provenFmp4VideoSegmentFormat) {
            mediaInfo.hlsVideoSegmentFormat = provenFmp4VideoSegmentFormat;
          }
          const provenFmp4SegmentFormat =
            window.chrome.cast.media.HlsSegmentFormat?.FMP4;
          if (provenFmp4SegmentFormat) {
            mediaInfo.hlsSegmentFormat = provenFmp4SegmentFormat;
          }
          const request = new window.chrome.cast.media.LoadRequest(mediaInfo);
          request.autoplay = true;
          assertCastOp(op);
          await castLoadMediaBounded(session, request, op);
          assertCastOp(op);
          return attachRemotePlayer(session, op);
        },
      });
      handoffDone = true;
      dispatcharrCastRuntime.bootstrapController = null;
      const { receiverState } = receiver;
      // DISPATCHARR_CAST_TRANSITION_OPS_R134: last check before this handoff
      // claims the cast; a superseded one must never commit over a newer op.
      assertCastOp(op);

      const device = session.getCastDevice?.();
      Object.assign(dispatcharrCastRuntime, {
        active: true,
        kind: 'live',
        // DISPATCHARR_LIVE_CONTINUE_LIVE_CAST_INPLACE_SWITCH
        sourceUrl: sourceUrlOverride || streamUrl,
        resumeUrl: target.resumeUrl,
        mediaUrl: target.mediaUrl,
        bootstrapUrl: target.requiresBootstrap ? target.bootstrapUrl : null,
        adaptiveProfile: target.adaptiveProfile,
        castSessionId: target.castSession,
        ownLiveCastSession: target.castSession, // DISPATCHARR_CAST_REMOTE_REWIND_R135
        deviceName: device?.friendlyName || 'Chromecast',
      });
      transportSuspendedKindRef.current = null;
      setTransportSuspendedKind(null);
      livePlaybackStartedRef.current = false;
      castActiveRef.current = true;
      castHandoffInProgressRef.current = false;
      setCastActive(true);
      setCastDeviceName(dispatcharrCastRuntime.deviceName);
      setCastError(null);

      // Release local MPEG-TS only AFTER the receiver is ready. For direct Web
      // this closes the raw browser consumer; for transcode it closes the
      // existing profile consumer. The Cast lease retains the HLS producer.
      safeDestroyPlayer();
      const video = videoRef.current;
      if (video) {
        try {
          video.pause();
          video.removeAttribute('src');
          video.load();
        } catch {}
      }

      console.info(
        `[chromecast-r8.8.50] Live Cast active same-profile=${target.adaptiveProfile} ` +
          `webDelivery=${target.adaptiveDelivery} bootstrap=${target.requiresBootstrap} ` +
          `prewarm=${prewarmedSegments} receiverState=${receiverState}; ` +
          'single Cast FFmpeg tee retained'
      );
      // DISPATCHARR_CAST_LIVE_STATE_TRACE_R16: diagnostic only. The handoff
      // is declared done the moment CAF reports BUFFERING (see
      // waitForChromecastMediaReady), so how long the TV then sits before
      // PLAYING was invisible in every log. Record each receiver state
      // change for 45 s after the load; the poll stops by itself.
      {
        const traceStart = Date.now();
        // DISPATCHARR_CAST_TRANSITION_OPS_R134: this trace belongs to ITS
        // handoff. Once a newer op runs (Rewind, then Continue Live) it stops
        // without touching that op's "Returning to Live" indicator -- it used
        // to clear it while the TV was still PLAYING the rewind.
        const traceOp = op;
        let lastState = String(receiverState || 'none');
        console.info(
          `[chromecast-r16] live load prewarm=${prewarmedSegments} state +0ms ${lastState}`
        );
        const traceTimer = window.setInterval(() => {
          const rp = castRemotePlayerRef.current;
          const state = String(rp?.playerState || 'none');
          const traceSuperseded = Boolean(traceOp) && castOpRef.current !== traceOp;
          if (
            !traceSuperseded &&
            state ===
            (window.chrome?.cast?.media?.PlayerState?.PLAYING || 'PLAYING')
          ) {
            // DISPATCHARR_CAST_CONTINUE_LIVE_INDICATOR: keep "Returning to
            // Live" up until the receiver actually PLAYS live, not merely
            // when the load resolved (CAF reports BUFFERING at that point).
            setCastReturningToLive(false);
          }
          if (state !== lastState) {
            console.info(
              `[chromecast-r16] live state +${Date.now() - traceStart}ms ` +
                `${lastState} -> ${state} t=${Number(rp?.currentTime || 0).toFixed(1)}`
            );
            lastState = state;
          }
          if (Date.now() - traceStart > 45000 || !rp || traceSuperseded) {
            window.clearInterval(traceTimer);
            // DISPATCHARR_CAST_CONTINUE_LIVE_INDICATOR backstop
            if (!traceSuperseded) setCastReturningToLive(false);
            console.info(
              `[chromecast-r16] live trace end +${Date.now() - traceStart}ms final=${state}`
            );
          }
        }, 250);
      }
      // DISPATCHARR_CAST_LIVE_EDGE_CATCHUP_R104: the Live FFmpeg tee starts
      // the moment Cast is pressed, and the receiver joins near the FRONT of
      // that brand-new playlist. Everything the handoff then costs (receiver
      // launch, prewarm segments, CAF buffering -- ~15s measured on the TV)
      // becomes a PERMANENT distance behind the broadcast, because nothing
      // ever moves the receiver forward again. Once CAF reports 2s of stable
      // PLAYING, issue ONE sender seek to liveSeekableRange.end minus
      // DISPATCHARR_CAST_LIVE_EDGE_HOLDBACK_SECONDS. One shot only -- a
      // user's own rewind must never be fought -- and only forward.
      {
        const PLAYING =
          window.chrome?.cast?.media?.PlayerState?.PLAYING || 'PLAYING';
        const catchUpStart = Date.now();
        let playingSince = 0;
        const catchUpTimer = window.setInterval(() => {
          const rp = castRemotePlayerRef.current;
          const rc = castRemoteControllerRef.current;
          const budgetLeft = Date.now() - catchUpStart <= 45000;
          if (
            !rp ||
            !rc ||
            !budgetLeft ||
            !castActiveRef.current ||
            dispatcharrCastRuntime.kind !== 'live' ||
            (op && castOpRef.current !== op) // DISPATCHARR_CAST_TRANSITION_OPS_R134
          ) {
            window.clearInterval(catchUpTimer);
            if (!budgetLeft) {
              console.info(
                '[chromecast-r104] live edge catch-up expired without a ' +
                  `seekable range (state=${String(rp?.playerState || 'none')})`
              );
            }
            return;
          }
          if (String(rp.playerState || '') !== PLAYING) {
            playingSince = 0;
            return;
          }
          if (!playingSince) playingSince = Date.now();
          if (Date.now() - playingSince < 2000) return;
          // DISPATCHARR_CAST_REMOTE_REWIND_R135: never undo a rewind the user
          // made on the TV remote during this load.
          if (castLiveRemoteRewindRef.current.backwardSeenAt > catchUpStart) {
            window.clearInterval(catchUpTimer);
            console.info('[chromecast-r104] user rewound on the TV; edge catch-up skipped');
            return;
          }
          const range = rp.liveSeekableRange;
          const end = Number(range?.end);
          if (!Number.isFinite(end) || end <= 0) return;
          window.clearInterval(catchUpTimer);
          try {
            const now = Number(rp.currentTime || 0);
            const behind = end - now;
            const target = Math.max(
              Number(range?.start) || 0,
              end - DISPATCHARR_CAST_LIVE_EDGE_HOLDBACK_SECONDS
            );
            if (
              behind > DISPATCHARR_CAST_LIVE_EDGE_HOLDBACK_SECONDS + 2 &&
              target > now
            ) {
              rp.currentTime = target;
              rc.seek();
              console.info(
                `[chromecast-r104] live edge catch-up: ${behind.toFixed(1)}s ` +
                  `behind, seek ${now.toFixed(1)} -> ${target.toFixed(1)}`
              );
            } else {
              console.info(
                `[chromecast-r104] live edge ok: ${behind.toFixed(1)}s behind`
              );
            }
          } catch (error) {
            console.info('[chromecast-r104] live edge catch-up failed', error);
          }
        }, 250);
      }
      // DISPATCHARR_CAST_CONTINUE_LIVE_KEEP_OLD_LEASE_R14: the TV is on Live
      // now, so the catch-up it kept playing meanwhile can be released.
      if (keepPreviousLease) void releaseDeferredCastLease();
    } catch (error) {
      const superseded = isCastSuperseded(error) || !castOpIsCurrent(op);
      if (!superseded) {
        dispatcharrCastRuntime.bootstrapController = null;
        castHandoffInProgressRef.current = false;
      }
      // DISPATCHARR_CAST_TRANSITION_OPS_R134: a handoff that got all the way
      // through runLiveCastHandoff holds a lease nobody else will release.
      if (handoffDone) {
        void releaseCastLeaseExact(target.stopUrl, 'live-handoff-superseded');
      }
      // Continue Live retries or hands back to the Web player; keep the TV's
      // rewind fed meanwhile. A superseded attempt leaves the deferred lease
      // to the op that superseded it (Stop/X release it).
      if (keepPreviousLease && !superseded) restoreDeferredArchiveLease();
      throw error;
    }
  };

  // DISPATCHARR_ARCHIVE_CAST_SEEK_GENERATION_R8_8_48
  const buildArchiveCastSeekMediaUrl = (url, targetSeconds) => {
    const target = Math.max(0, Number(targetSeconds) || 0);
    const next = new URL(url, window.location.origin);
    next.searchParams.set('cast_start_position', target.toFixed(3));
    next.searchParams.set('cast_receiver', '1');
    next.searchParams.set(
      'cast_generation',
      `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
    );
    next.searchParams.set('cast_seek_nonce', Date.now().toString());
    return next.toString();
  };

  // DISPATCHARR_ARCHIVE_INITIAL_DJANGO_NO_GENERATION_R8_14_3
  // Initial Archive Cast deliberately uses the proven pre-r8.8.48 Django
  // steady-playback semantics: target hint + CAF currentTime, but NO active
  // seek-generation. Explicit hard seeks still use generation fencing.
  // DISPATCHARR_ARCHIVE_CAST_ABSOLUTE_TIMELINE_R131: ON by default since the
  // 2026-10-06 TV test (initial LOAD, web-seek reloads at 2296/2723 s and a
  // remote rewind to 1784 s before the web target all ran on the absolute
  // timeline; first receiver GET = the start segment). Escape hatch:
  // localStorage.setItem('dispatcharrCastAbsoluteTimeline', '0').
  const archiveCastAbsoluteTimelineEnabled = () => {
    try {
      return window.localStorage?.getItem('dispatcharrCastAbsoluteTimeline') !== '0';
    } catch {
      return true;
    }
  };

  const buildArchiveCastInitialMediaUrl = (
    url,
    targetSeconds,
    { absoluteTimeline = false } = {}
  ) => {
    const target = Math.max(0, Number(targetSeconds) || 0);
    const next = new URL(url, window.location.origin);
    // DISPATCHARR_ARCHIVE_CAST_ABSOLUTE_TIMELINE_R131: full programme playlist on the receiver.
    if (absoluteTimeline) next.searchParams.set('cast_timeline', 'absolute');
    else next.searchParams.delete('cast_timeline');
    // DISPATCHARR_ARCHIVE_CAST_ISOLATED_PIPELINE_R8_16_0
    // Receiver segment traffic gets a dedicated backend runtime producer. The
    // token persists when buildArchiveCastSeekMediaUrl clones this media URL.
    next.searchParams.set(
      'cast_pipeline',
      `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 14)}`
    );
    next.searchParams.set('cast_start_position', target.toFixed(3));
    next.searchParams.set('cast_receiver', '1');
    next.searchParams.delete('cast_generation');
    next.searchParams.set('cast_seek_nonce', `initial-${Date.now().toString(36)}`);
    // DISPATCHARR_LIVE_TIMESHIFT_CAST_SNAPSHOT_AHEAD_R135: tuning/test override
    // of how far past the aired edge the live-rewind receiver snapshot reaches
    // (backend default 600 s).
    if (absoluteTimeline && next.searchParams.get('live_timeshift') === '1') {
      try {
        const override = window.localStorage?.getItem?.(
          'dispatcharrCastSnapshotAheadSeconds'
        );
        if (override != null && override !== '' && Number.isFinite(Number(override))) {
          next.searchParams.set(
            'cast_snapshot_ahead',
            String(Math.max(0, Math.min(3600, Number(override))))
          );
        }
      } catch {
        // storage unavailable: backend default
      }
    }
    return next.toString();
  };

  // DISPATCHARR_ARCHIVE_NATIVE_SEEK_PREWARM_PROBE_R8_14_15
  // DISPATCHARR_ARCHIVE_NATIVE_SEEK_PROBE_URL_FIX_R8_14_17
  const prewarmArchiveCastPosition = async (
    mediaUrl,
    targetSeconds,
    timeoutMs = 12000,
    nativeSeek = false,
    abortCheck = null // DISPATCHARR_CAST_TRANSITION_OPS_R134: throws once superseded
  ) => {
    const startedAt = Date.now();
    const target = Math.max(0, Number(targetSeconds) || 0);
    let lastReason = 'playlist not ready';
    while (Date.now() - startedAt < timeoutMs) {
      if (abortCheck) abortCheck();
      try {
        const probe = new URL(mediaUrl, window.location.origin);
        // DISPATCHARR_ARCHIVE_CAST_SENDER_PREWARM_R8_15_1
        // The receiver marker belongs only to CAF playback traffic. A sender
        // prewarm must be allowed to wait for and warm the producer target.
        probe.searchParams.delete('cast_receiver');
        probe.searchParams.set('cast_probe', String(Date.now()));
        const token = useAuthStore.getState().accessToken;
        const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
        const response = await fetch(probe.toString(), {
          method: 'GET',
          cache: 'no-store',
          credentials: 'same-origin',
          headers,
        });
        if (!response.ok) {
          lastReason = `playlist HTTP ${response.status}`;
          await new Promise((resolve) => window.setTimeout(resolve, 120));
          continue;
        }
        const body = await response.text();
        const lines = body.split(/\r?\n/);
        const mapLine = lines.find((line) => line.startsWith('#EXT-X-MAP:'));
        const mapMatch = mapLine?.match(/URI="([^"]+)"/);
        const initRef = mapMatch?.[1] || null;
        let elapsed = 0;
        let pendingDuration = null;
        let selected = null;
        let fallback = null;
        for (const line of lines) {
          if (line.startsWith('#EXTINF:')) {
            pendingDuration = Math.max(0, Number.parseFloat(line.slice(8)) || 0);
            continue;
          }
          if (!line || line.startsWith('#') || pendingDuration == null) continue;
          const duration = pendingDuration;
          pendingDuration = null;
          fallback = line;
          // DISPATCHARR_ARCHIVE_RECOVERY_WEB_CAST_SEEK_BOUNDARY
          // Half-open interval ownership: [segmentStart, segmentEnd). An exact
          // boundary belongs to the NEXT segment, matching backend floor(position /
          // segmentDuration) producer placement. Never bias back by +0.001s.
          if (selected == null && target < elapsed + duration) {
            selected = line;
          }
          elapsed += duration;
        }
        const segmentRef = selected || fallback;
        if (!segmentRef) {
          lastReason = 'playlist has no target segment';
          await new Promise((resolve) => window.setTimeout(resolve, 120));
          continue;
        }
        if (!initRef) {
          lastReason = 'playlist has no EXT-X-MAP init segment';
          await new Promise((resolve) => window.setTimeout(resolve, 120));
          continue;
        }
        const initUrl = new URL(initRef, probe);
        const initReady = await fetch(initUrl.toString(), {
          method: 'HEAD',
          cache: 'no-store',
          credentials: 'same-origin',
          headers,
        });
        if (!initReady.ok) {
          lastReason = `init segment HTTP ${initReady.status}`;
          await new Promise((resolve) => window.setTimeout(resolve, 120));
          continue;
        }
        const segmentUrl = new URL(segmentRef, probe);
        // DISPATCHARR_ARCHIVE_RECOVERY_CAST_PREWARM_READ_ONLY
        segmentUrl.searchParams.set('cast_probe', '1');
        if (nativeSeek) {
          segmentUrl.searchParams.set('cast_native_seek', '1');
        }
        const ready = await fetch(segmentUrl.toString(), {
          method: 'HEAD',
          cache: 'no-store',
          credentials: 'same-origin',
          headers,
        });
        if (ready.ok) {
          console.info(
            `[chromecast-r8.8.48] Archive Cast target prewarm ready -> ${target.toFixed(2)}s`
          );
          return true;
        }
        lastReason = `target segment HTTP ${ready.status} (${segmentRef})`;
      } catch (error) {
        lastReason = error?.message || String(error);
      }
      await new Promise((resolve) => window.setTimeout(resolve, 150));
    }
    throw new Error(`Archive target prewarm timed out: ${lastReason}`);
  };

  // DISPATCHARR_ARCHIVE_FMP4_CAST_WINDOW_R2
  const archiveCastAbsolutePosition = (remotePlayer = castRemotePlayerRef.current) => {
    const base = Math.max(
      0,
      Number(dispatcharrCastRuntime.archiveBasePosition) || 0
    );
    const relative = Math.max(0, Number(remotePlayer?.currentTime) || 0);
    return Math.max(0, base + relative);
  };

  // Legacy Django/FFmpeg Archive Cast is intentionally unreachable from the
  // r8.19 UI; retained temporarily only to keep older saved SPA state readable.
  // eslint-disable-next-line no-unused-vars
  const loadArchiveOnChromecast = async (session, { op = null } = {}) => {
    if (!session || !streamUrl || contentType !== 'catchup-hls') {
      throw new Error('No Archive HLS stream is available for Chromecast');
    }

    // DISPATCHARR_ARCHIVE_CAST_REUSE_WEB_PROFILE_R8_14_3
    // Archive Cast must reuse the profile already selected for the active Web
    // Archive session. Do not run a second speed test on the Cast click.
    let adaptiveProfile = null;
    try {
      adaptiveProfile = new URL(streamUrl, window.location.origin).searchParams.get(
        'adaptive_output_profile'
      );
    } catch {}
    if (!adaptiveProfile) {
      throw new Error('Legacy adaptive Archive Cast is disabled');
    } else {
      console.info(
        `[chromecast-r8.14.3] Archive Cast reusing Web adaptive profile=${adaptiveProfile}`
      );
    }

    const video = videoRef.current;
    // DISPATCHARR_LIVE_PROVIDER_TIMESHIFT_CAST_HANDBACK_R5_3
    const providerTimeshiftCast = Boolean(
      contentType === 'catchup-hls' && metadata?.liveTimeshift
    );
    const rawMediaPosition = Math.max(0, Number(video?.currentTime) || 0);
    const displayedProgrammePosition = Number(liveTimeshiftPlaybackSeconds);
    const programmePosition = Number.isFinite(displayedProgrammePosition)
      ? Math.max(0, displayedProgrammePosition)
      : Math.max(0, liveTimeshiftProgrammePosition(video));
    const position = providerTimeshiftCast
      ? Math.min(liveTimeshiftTimelineSeconds(), programmePosition)
      : rawMediaPosition;
    const archiveDuration = providerTimeshiftCast
      ? liveTimeshiftTimelineSeconds()
      : Math.max(0, Number(video?.duration) || 0);
    if (providerTimeshiftCast) {
      liveTimeshiftExpectedPositionRef.current = position;
      liveTimeshiftInitialSeekSettledUrlRef.current = streamUrl;
      setLiveTimeshiftPlaybackSeconds(position);
      console.info(
        `[live-r5.3] Cast handoff programme=${position.toFixed(2)}s ` +
          `rawMedia=${rawMediaPosition.toFixed(2)}s`
      );
    }
    // DISPATCHARR_CAST_TRANSITION_OPS_R134: what this load acquired, so a
    // superseded load can give exactly that back (unwindArchiveCastOp).
    if (op) op.archivePosition = position;
    const archiveCastSourceUrl =
      archiveLocalRuntimeUrlRef.current || streamUrl;
    const castUrl = new URL(archiveCastSourceUrl, window.location.origin);
    castUrl.searchParams.set('adaptive_output_profile', adaptiveProfile);
    // DISPATCHARR_ARCHIVE_CAST_ABSOLUTE_TIMELINE_R131
    // DISPATCHARR_LIVE_TIMESHIFT_CAST_ABSOLUTE_UNIFY: include provider
    // live-rewind so it uses the same absolute full-playlist receiver path
    // as bounded Archive (remote seeks land on in-manifest segments).
    const absoluteCastTimeline = archiveCastAbsoluteTimelineEnabled();
    const mediaUrl = buildArchiveCastInitialMediaUrl(
      castUrl.toString(),
      position,
      { absoluteTimeline: absoluteCastTimeline }
    );

    // DISPATCHARR_ARCHIVE_INITIAL_DJANGO_NO_GENERATION_R8_14_3:
    // Keep the exact programme-relative target hint, but initial steady-state
    // receiver traffic uses the proven no-generation Django segment path.
    castHandoffInProgressRef.current = true;
    archiveSuspendedPositionRef.current = position;
    archivePlaybackStartedRef.current = false;
    // DISPATCHARR_ARCHIVE_FMP4_CAST_WINDOW_R2
    // Detach Hls.js/native Web media before marking its runtime suspended.
    // Otherwise the old Web consumer can issue one last .m4s GET into the
    // suspended base runtime and race the Cast handoff with HTTP 409.
    assertCastOp(op);
    safeDestroyPlayer();
    await new Promise((resolve) => window.setTimeout(resolve, 120));
    assertCastOp(op);
    const webSuspended = await requestArchiveSuspend(archiveCastSourceUrl);
    if (op) op.webSuspended = Boolean(webSuspended);
    assertCastOp(op);
    if (!webSuspended) {
      throw new Error('Archive Web producer could not suspend for Chromecast');
    }
    archiveLeaseSuspendedRef.current = true;

    const resumed = await requestArchiveResume(mediaUrl, position);
    if (resumed && op) op.archiveCastMediaUrl = mediaUrl;
    assertCastOp(op);
    if (!resumed) throw new Error('Archive producer could not restart for Chromecast');
    archiveLeaseSuspendedRef.current = false;
    // DISPATCHARR_ARCHIVE_CAST_INITIAL_BUFFER_R8_15_2
    // Default Media Receiver needs more than a single ready fragment to leave
    // BUFFERING reliably. Warm the start fragment and its immediate successor
    // before sending the first CAF LOAD.
    const abortIfSuperseded = () => assertCastOpSession(op, session);
    await prewarmArchiveCastPosition(mediaUrl, position, 12000, false, abortIfSuperseded);
    assertCastOp(op);
    // DISPATCHARR_ARCHIVE_RECOVERY_CAST_INITIAL_SUCCESSOR_PREWARM
    const initialCastSuccessorPosition = position + 2;
    console.info(
      `[chromecast-r8.8.48] Archive Cast initial successor prewarm -> ${initialCastSuccessorPosition.toFixed(2)}s`
    );
    await prewarmArchiveCastPosition(
      mediaUrl,
      initialCastSuccessorPosition,
      8000,
      false,
      abortIfSuperseded
    );
    assertCastOp(op);
    const archiveRenewUrl = buildArchiveCastLeaseRenewUrl(mediaUrl);
    const archiveReleaseUrl = buildArchiveCastLeaseReleaseUrl(mediaUrl);
    if (!archiveRenewUrl || !archiveReleaseUrl) {
      throw new Error('Archive Cast lease URLs are unavailable');
    }
    if (op) op.archiveLeaseStopUrl = archiveReleaseUrl;
    await startCastSessionLease(archiveRenewUrl, archiveReleaseUrl);
    assertCastOp(op);

    const mediaInfo = createCastMediaInfo(
      mediaUrl,
      window.chrome.cast.media.StreamType.BUFFERED,
      metadata?.name || metadata?.title || 'Dispatcharr Archive',
      { fmp4: true }
    );
    const request = new window.chrome.cast.media.LoadRequest(mediaInfo);
    request.autoplay = true;
    // DISPATCHARR_ARCHIVE_FMP4_CAST_WINDOW_R2
    // CAF receives a playlist window whose first fragment contains the
    // requested programme position. EXT-X-START owns the intra-fragment
    // offset; do not apply the old full-VOD absolute currentTime again.
    // DISPATCHARR_LIVE_TIMESHIFT_CAST_EXPLICIT_CURRENTTIME
    // live_timeshift is a genuinely growing, no-ENDLIST EVENT playlist;
    // receivers are known to prioritize joining such a stream at its
    // own advancing live edge over honoring EXT-X-START. Restore an
    // explicit start position for this case only -- ordinary
    // (bounded) Archive Cast is untouched.
    // DISPATCHARR_ARCHIVE_CAST_ABSOLUTE_TIMELINE_R131: on the full playlist the programme position IS the
    // receiver time, so CAF starts explicitly at it (Shaka prefers the LOAD
    // start over EXT-X-START).
    if (providerTimeshiftCast || absoluteCastTimeline) {
      request.currentTime = position;
    }
    assertCastOp(op);
    if (op) op.archiveLoadSent = true;
    await castLoadMediaBounded(session, request, op);
    assertCastOp(op);
    const { remotePlayer, receiverState } = await attachRemotePlayer(session, op);
    assertCastOp(op);
    // DISPATCHARR_ARCHIVE_FMP4_CAST_WINDOW_R2
    // The receiver timeline is relative to its window. Derive a stable absolute
    // programme clock from the requested target and the first ready CAF time.
    const receiverRelativePosition = Math.max(
      0,
      Number(remotePlayer?.currentTime) || 0
    );
    // DISPATCHARR_ARCHIVE_CAST_ABSOLUTE_TIMELINE_R131: absolute receiver clock -> base 0 (CAF can briefly
    // report 0 while attaching; deriving the base would then be off by T).
    const receiverBasePosition = absoluteCastTimeline
      ? 0
      : Math.max(0, position - receiverRelativePosition);
    const receiverPosition = position;
    castHealthRef.current = {
      state: String(remotePlayer?.playerState || receiverState || ''),
      bufferingSince: 0,
      lastPosition: receiverPosition,
      lastProgressAt: Date.now(),
    };
    // DISPATCHARR_CAST_IDLE_GRACE_R6: the initial Cast load has the same
    // receiver attach window as a seek. loadDirectArchiveOnChromecast
    // already stamps this; the catchup-hls path never did, so a fresh Cast
    // could be torn down by the IDLE watchdog before it ever played.
    castArchiveSeekHealthRef.current = {
      inProgress: false,
      startedAt: 0,
      completedAt: Date.now(),
      target: receiverPosition,
    };
    // DISPATCHARR_LIVE_TIMESHIFT_CAST_SNAPSHOT_AHEAD_R135
    if (providerTimeshiftCast) {
      Object.assign(liveTimeshiftCastRef.current, {
        lastLoadAt: Date.now(),
        edgeArmed: false,
        refreshFailedAt: 0,
      });
    }

    const device = session.getCastDevice?.();
    Object.assign(dispatcharrCastRuntime, {
      active: true,
      kind: 'archive',
      sourceUrl: streamUrl,
      mediaUrl,
      adaptiveProfile,
      deviceName: device?.friendlyName || 'Chromecast',
      archivePosition: receiverPosition,
      archiveBasePosition: receiverBasePosition,
      // DISPATCHARR_LIVE_TIMESHIFT_CAST_SNAPSHOT_AHEAD_R135: the receiver
      // duration includes the not-yet-aired stretch for live rewind.
      archiveDuration: providerTimeshiftCast
        ? archiveDuration
        : Math.max(
            archiveDuration,
            receiverBasePosition + Math.max(0, Number(remotePlayer?.duration) || 0)
          ),
    });
    transportSuspendedKindRef.current = null;
    setTransportSuspendedKind(null);
    castActiveRef.current = true;
    castHandoffInProgressRef.current = false;
    setCastActive(true);
    setCastDeviceName(dispatcharrCastRuntime.deviceName);
    setCastError(null);
    {
      const observer = castArchiveRemoteSeekObserverRef.current;
      if (observer.debounceTimer != null) window.clearTimeout(observer.debounceTimer);
      observer.lastPosition = receiverPosition;
      observer.lastAt = Date.now();
      observer.suppressUntil = Date.now() + 5000;
      observer.pending = false;
      observer.debounceTimer = null;
      observer.candidatePosition = null;
      observer.armDirection = 0;
      observer.armCount = 0;
      observer.armUntil = 0;
      observer.pausedByRemoteScrub = false;
      observer.resumeAfterRemoteScrub = true;
      if (observer.snapbackTimer != null) window.clearTimeout(observer.snapbackTimer);
      observer.snapbackTimer = null;
      observer.scrubAnchorPosition = null;
      observer.virtualTargetPosition = null;
      observer.lastRawRemotePosition = null;
      observer.ignoreAnchorUntil = 0;
      console.info('[chromecast-r8.8.51] Archive Cast remote detector settling 5000ms');
    }
    console.info(
      `[chromecast-r8.14.3] Archive Django initial Cast stable-path ` +
        `position=${position.toFixed(2)}s profile=${adaptiveProfile} receiverState=${receiverState} ` +
        `generation=none`
    );
    console.info(
      `[chromecast-r8.8.35] Archive Cast active position=${position.toFixed(2)}s ` +
        `profile=${adaptiveProfile} receiverState=${receiverState}`
    );
  };

  // DISPATCHARR_CAST_LIVE_REWIND_R17: second half of Rewind-on-Cast. It runs
  // in the render where the component has become catchup-hls, so
  // loadArchiveOnChromecast sees the new streamUrl and metadata.
  useEffect(() => {
    const pending = pendingCastTimeshiftRef.current;
    if (!pending || contentType !== 'catchup-hls' || streamUrl !== pending.url) {
      return;
    }
    pendingCastTimeshiftRef.current = null;
    // DISPATCHARR_CAST_TRANSITION_OPS_R134: the Rewind-on-Cast op was opened by
    // startLiveProviderTimeshift; a newer Cast action may have superseded it
    // before this render landed.
    const op = pending.op || beginCastOp('rewind-on-cast', { url: pending.url });
    if (!castOpIsCurrent(op)) {
      settleCastOp(op);
      return;
    }
    const fail = (reason) => {
      castFailureRecoveryRef.current = false;
      void recoverFailedChromecast(reason);
    };
    const session = castContextRef.current?.getCurrentSession?.();
    if (!session) {
      fail('Rewind on Cast: the Chromecast session is gone');
      settleCastOp(op);
      return;
    }
    (async () => {
      try {
        await loadArchiveOnChromecast(session, { op });
        assertCastOp(op);
        setIsLoading(false);
        castFailureRecoveryRef.current = false;
        // DISPATCHARR_CAST_LIVE_REWIND_UI_R18: show the rewound position at
        // once, then let the transport poller advance it with playback.
        setCastArchivePositionSeconds(
          Math.max(0, Number(dispatcharrCastRuntime.archivePosition) || 0)
        );
        setCastArchiveDurationSeconds(
          Math.max(0, Number(dispatcharrCastRuntime.archiveDuration) || 0)
        );
        setCastTransportEpoch((value) => value + 1);
        console.info(
          '[live-provider-timeshift] Rewind on Cast: TV now plays the rewound stream'
        );
      } catch (error) {
        if (isCastSuperseded(error) || !castOpIsCurrent(op)) {
          // Continue Live / Stop / X took over; it owns the outcome.
          console.info(`[chromecast-r134] Rewind on Cast #${op.seq} superseded; unwinding`);
          unwindArchiveCastOp(op);
          return;
        }
        console.warn('[live-provider-timeshift] Rewind on Cast failed:', error);
        if (!castContextRef.current?.getCurrentSession?.()) {
          // The TV session really ended: R109 applies.
          fail(`Rewind on Cast failed: ${error?.message || error}`);
          return;
        }
        // DISPATCHARR_CAST_REMOTE_REWIND_R135: the TV remote asked for this
        // rewind and the TV would not take it -- keep casting, back to Live on
        // the TV. If the archive LOAD never went out the TV is still on the
        // parked warm Live tee: take that lease back (instant).
        if (op.target?.origin === 'tv-remote') {
          unwindArchiveCastOp(op);
          const liveUrl = metadata?.liveSourceUrl;
          if (!op.archiveLoadSent && liveUrl && unparkLiveCastLease(liveUrl)) {
            castFailureRecoveryRef.current = false;
            requestArchiveStop(streamUrl);
            setLiveTimeshiftDraftSeconds(0);
            setShowLiveRewindPicker(false);
            setLiveTimeshiftPlaybackSeconds(0);
            setIsLoading(false);
            showVideo(liveUrl, 'live', metadata?.liveSourceMetadata || { name: metadata?.name || 'Live TV' });
          } else {
            // R135 review: the recovery fence stays up until Continue Live takes
            // over, so the health tick cannot R109 a TV that is still alive.
            window.setTimeout(() => {
              castFailureRecoveryRef.current = false;
              void castLatestRef.current?.returnToLiveFromProviderTimeshift?.();
            }, 0);
          }
          console.warn('[chromecast-r135] TV remote rewind not taken by the TV; back to Live');
          return;
        }
        // The TV is alive but would not take the rewind: never the R109 dead
        // end -- continue the rewind in the Web player instead.
        unwindArchiveCastOp(op);
        void handBackCastTransition({
          mode: 'timeshift',
          url: op.target?.url || streamUrl,
          reason: `Rewind on Cast failed: ${error?.message || error}`,
          supersededOp: op,
        });
      } finally {
        settleCastOp(op);
        // DISPATCHARR_CAST_REMOTE_REWIND_R135: the TV plays the rewind (or it
        // was superseded / handed off): the "Rewinding on" notice is done.
        setCastRewindingOnTv(null);
      }
    })();
  }, [contentType, streamUrl]);

  // DISPATCHARR_ARCHIVE_CAST_REMUX_R8_19_0
  const loadArchiveCastGeneration = async (
    session,
    payload,
    mediaUrl,
    markReceiverAccepted = () => {}
  ) => {
    if (!session || !window.chrome?.cast?.media) {
      throw new Error('No Chromecast session is available');
    }

    const target = Math.max(0, Number(payload?.base_position) || 0);
    const observer = castArchiveRemoteSeekObserverRef.current;
    observer.suppressUntil = Date.now() + 5000;
    observer.receiverLoadInProgress = true;
    castArchiveSeekHealthRef.current = {
      inProgress: true,
      startedAt: Date.now(),
      completedAt: 0,
      target,
    };

    const mediaInfo = createCastMediaInfo(
      mediaUrl,
      window.chrome.cast.media.StreamType.BUFFERED,
      metadata?.name || metadata?.title || 'Dispatcharr Archive'
    );
    mediaInfo.duration = Math.max(
      1,
      Number(payload?.archive_duration || 0) - target
    );
    const request = new window.chrome.cast.media.LoadRequest(mediaInfo);
    request.autoplay = true;
    // Every immutable generation starts its own receiver timeline at zero.
    request.currentTime = Math.max(0, Number(payload?.start_time) || 0);
    try {
      await session.loadMedia(request);
    } catch (error) {
      observer.receiverLoadInProgress = false;
      throw error;
    }
    markReceiverAccepted();
    let attached;
    try {
      attached = await attachRemotePlayer(session);
    } finally {
      observer.receiverLoadInProgress = false;
      observer.suppressUntil = Date.now() + 3000;
    }

    const relativePosition = Math.max(
      0,
      Number(attached.remotePlayer?.currentTime) || 0
    );
    const absolutePosition = target + relativePosition;
    Object.assign(dispatcharrCastRuntime, {
      mediaUrl,
      archiveBasePosition: target,
      archiveGeneration: payload?.generation || null,
      archiveDuration: Math.max(0, Number(payload?.archive_duration) || 0),
      archivePosition: absolutePosition,
    });
    observer.lastPosition = relativePosition;
    observer.lastAt = Date.now();
    setCastArchivePositionSeconds(absolutePosition);
    setCastArchiveDurationSeconds(dispatcharrCastRuntime.archiveDuration);
    setCastArchivePaused(Boolean(attached.remotePlayer?.isPaused));
    return attached;
  };

  const createArchiveCastController = () => {
    const existing = archiveCastControllerRef.current;
    if (existing?.active) return existing;

    let archiveOrigin = window.location.origin;
    try {
      archiveOrigin = new URL(streamUrl, window.location.origin).origin;
    } catch {}
    const controller = new ArchiveCastController({
      origin: archiveOrigin,
      onSeekStart: (target) => {
        castArchiveSeekHealthRef.current = {
          inProgress: true,
          startedAt: Date.now(),
          completedAt: 0,
          target,
        };
        dispatcharrCastRuntime.archivePosition = target;
        setCastArchivePositionSeconds(target);
        setCastError(null);
      },
      onSeekComplete: (payload) => {
        const position = Math.max(0, Number(payload?.base_position) || 0);
        castArchiveSeekHealthRef.current = {
          inProgress: false,
          startedAt: 0,
          completedAt: Date.now(),
          target: position,
        };
        dispatcharrCastRuntime.archiveBasePosition = position;
        dispatcharrCastRuntime.archiveGeneration = payload?.generation || null;
        castArchiveRemoteSeekObserverRef.current.suppressUntil = Date.now() + 3000;
      },
      onSeekError: (error, target) => {
        castArchiveSeekHealthRef.current = {
          inProgress: false,
          startedAt: 0,
          completedAt: 0,
          target,
        };
        setCastError(error?.message || 'Archive Cast seek failed');
        console.warn('[archive-cast-v2] seek failed; current generation retained:', error);
      },
    });
    archiveCastControllerRef.current = controller;
    dispatcharrCastRuntime.archiveController = controller;
    return controller;
  };

  const loadDirectArchiveOnChromecast = async (session) => {
    if (!session || !streamUrl || contentType !== 'catchup') {
      throw new Error('No direct Archive stream is available for Chromecast');
    }
    const channelId =
      metadata?.channelUuid || archiveChannelIdFromDirectUrl(streamUrl);
    let sessionId = metadata?.sessionId || null;
    try {
      sessionId ||= new URL(streamUrl, window.location.origin).searchParams.get(
        'session_id'
      );
    } catch {}
    if (!channelId || !sessionId) {
      throw new Error('Archive Cast session metadata is unavailable');
    }

    // Direct Web playback keeps a programme-absolute clock even though every
    // reconnect starts media.currentTime at zero. Never use raw video.currentTime.
    const duration = Math.max(1, Number(archiveDurationSeconds) || 1);
    const position = Math.min(
      Math.max(0, Number(archivePositionSeconds) || 0),
      Math.max(0, duration - 0.05)
    );
    const controller = createArchiveCastController();
    castHandoffInProgressRef.current = true;
    const payload = await controller.start({
      channelId,
      sessionId,
      position,
      loadGeneration: (generation, context) =>
        loadArchiveCastGeneration(
          session,
          generation,
          context.mediaUrl,
          context.markReceiverAccepted
        ),
    });

    const device = session.getCastDevice?.();
    Object.assign(dispatcharrCastRuntime, {
      active: true,
      kind: 'archive',
      sourceUrl: streamUrl,
      mediaUrl: controller.mediaUrl(payload),
      adaptiveProfile: null,
      deviceName: device?.friendlyName || 'Chromecast',
      archivePosition: Math.max(0, Number(payload?.base_position) || position),
      archiveBasePosition: Math.max(0, Number(payload?.base_position) || position),
      archiveDuration: Math.max(1, Number(payload?.archive_duration) || duration),
      archiveGeneration: payload?.generation || null,
      archiveController: controller,
    });
    archiveSuspendedPositionRef.current = dispatcharrCastRuntime.archivePosition;
    archivePlaybackStartedRef.current = false;
    transportSuspendedKindRef.current = null;
    setTransportSuspendedKind(null);
    castActiveRef.current = true;
    castHandoffInProgressRef.current = false;
    setCastActive(true);
    setCastDeviceName(dispatcharrCastRuntime.deviceName);
    setCastArchiveDurationSeconds(dispatcharrCastRuntime.archiveDuration);
    setCastError(null);
    castArchiveSeekHealthRef.current = {
      inProgress: false,
      startedAt: 0,
      completedAt: Date.now(),
      target: dispatcharrCastRuntime.archivePosition,
    };

    // The browser remains attached until CAF has accepted the ready generation
    // and the backend has atomically activated it.
    safeDestroyPlayer();
    console.info(
      `[archive-cast-v2] active generation=${payload?.generation || 'unknown'} ` +
        `position=${dispatcharrCastRuntime.archivePosition.toFixed(2)}s`
    );
  };

  // DISPATCHARR_CAST_SEAMLESS_HANDBACK_ARCHIVE_CONTROLS_R8_8_46
  const buildNormalLiveHandbackUrl = (normalProfile) => {
    if (!streamUrl || !normalProfile) return null;
    try {
      const parsed = new URL(streamUrl, window.location.origin);
      parsed.searchParams.set('output_format', 'mpegts');
      parsed.searchParams.set('adaptive_output_profile', normalProfile);
      parsed.searchParams.delete('output_profile');
      parsed.searchParams.delete('cast');
      return parsed.toString();
    } catch {
      return null;
    }
  };

  const primeNormalLiveHandback = async (normalUrl) => {
    if (!normalUrl) throw new Error('Normal Live handback URL is unavailable');
    let lastError = null;
    for (let attempt = 1; attempt <= 4; attempt += 1) {
      const controller = new AbortController();
      let reader = null;
      try {
        const token = useAuthStore.getState().accessToken;
        const response = await fetch(normalUrl, {
          method: 'GET',
          cache: 'no-store',
          credentials: 'same-origin',
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          signal: controller.signal,
        });
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        reader = response.body?.getReader?.() || null;
        if (reader) {
          const firstChunk = await Promise.race([
            reader.read(),
            new Promise((_, reject) =>
              window.setTimeout(
                () => reject(new Error('first MPEG-TS byte timeout')),
                2200
              )
            ),
          ]);
          if (firstChunk?.done) {
            throw new Error('normal Live bridge ended before first MPEG-TS bytes');
          }
        }
        console.info(
          `[chromecast-r8.8.46] normal Live handback bridge ready attempt=${attempt}`
        );
        return { controller, reader };
      } catch (error) {
        lastError = error;
        try { await reader?.cancel?.(); } catch {}
        try { controller.abort(); } catch {}
        if (attempt < 4) {
          await new Promise((resolve) =>
            window.setTimeout(resolve, 180 + attempt * 180)
          );
        }
      }
    }
    throw new Error(
      `Normal Live handback bootstrap failed: ${lastError?.message || lastError || 'unknown'}`
    );
  };

  const resumeLiveWebAfterChromecast = async () => {
    // DISPATCHARR_LIVE_FMP4_R3
    const current = liveWebFmp4SessionRef.current;
    const preferredProfile =
      current?.profile || castAdaptiveProfileRef.current || null;
    await initializeLiveFmp4HlsPlayer({
      preferredProfile,
      reuseExisting: true,
    });
    console.info('[live-cmaf-r3] Stop Cast handback -> Web fMP4 HLS active');
  };

  const formatCastArchiveClock = (value) => {
    const total = Math.max(0, Math.floor(Number(value) || 0));
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const seconds = total % 60;
    const mm = String(minutes).padStart(2, '0');
    const ss = String(seconds).padStart(2, '0');
    return hours > 0 ? `${hours}:${mm}:${ss}` : `${minutes}:${ss}`;
  };

  // DISPATCHARR_LIVE_REWIND_PROGRAMME_TIME_R135: a Live time-shift cast reads
  // in programme time like the local bar; an archive cast keeps its clock.
  const castClockLabel = (value, kind) =>
    liveTimeshiftClockLabel(value, { kind }) || formatCastArchiveClock(value);

  // DISPATCHARR_NATIVE_RANGE_TIP_R133: the one writer of the Cast seek
  // readout. While a press is held, and while the pointer rests where a
  // press only grabs the thumb, it shows the elapsed label's own text
  // (onChange feeds a dragged value into castArchivePositionSeconds, the
  // receiver clock feeds it otherwise) over the thumb; anywhere else, the
  // time a click there seeks to, at the pointer. R132 computed it on
  // mousemove only, so a resting pointer kept the time of its pixel (~11 s
  // per px) while the thumb crept or jumped underneath (33:26 over 33:28).
  const refreshCastSeekTip = () => {
    const tip = castSeekHoverTipRef.current;
    const input = castSeekInputRef.current;
    const pointer = castSeekPointerRef.current;
    if (!tip || !input) {
      // Unmounted under the pointer: its pointerleave/mouseup never come.
      pointer.x = null;
      pointer.y = null;
      pointer.pressed = false;
      pointer.pressValue = null;
      return;
    }
    if (!pointer.pressed && pointer.x == null) {
      tip.style.opacity = '0';
      return;
    }
    const barRect = input.parentElement.getBoundingClientRect();
    if (barRect.width <= 0) return;
    const pressValue = pointer.pressed
      ? null
      : nativeRangePressValue(input, pointer.x, pointer.y);
    if (pressValue === undefined) return;
    // DISPATCHARR_ARCHIVE_CAST_PROGRAMME_END_R133: a click in the last seconds
    // commits the end guard, so say that.
    const tipDuration =
      Number(dispatcharrCastRuntime.archiveDuration) ||
      Number(castArchiveDurationSeconds) ||
      0;
    tip.textContent = castClockLabel(
      pressValue === null
        ? castArchivePositionSeconds
        : tipDuration > 0
          ? Math.min(
              pressValue,
              Math.max(0, tipDuration - ARCHIVE_CAST_END_GUARD_SECONDS)
            )
          : pressValue,
      'tip'
    );
    const anchorX =
      pressValue === null ? nativeRangeThumbCenterX(input) : pointer.x;
    // Keep the whole readout inside the bar: translateX(-50%) would
    // otherwise push half of it past either end.
    const half = (tip.offsetWidth || 0) / 2;
    const x = Math.min(
      Math.max(anchorX - barRect.left, half),
      Math.max(half, barRect.width - half)
    );
    tip.style.left = `${x}px`;
    tip.style.opacity = '1';
  };

  // Same frame as the label: the thumb also moves under a pointer that does
  // not (playback, TV remote, the post-commit jump).
  // After every render (no deps) -- a superset of the position/duration
  // changes it must follow, and cheap while the tip is hidden.
  useLayoutEffect(() => {
    refreshCastSeekTip();
  });

  // DISPATCHARR_ARCHIVE_DJANGO_SEEK_DRAIN_GUARD_R8_14_1
  const pauseArchiveReceiverBeforeProducerSeek = async () => {
    const remotePlayer = castRemotePlayerRef.current;
    const controller = castRemoteControllerRef.current;
    if (!remotePlayer || !controller) return false;
    const PAUSED = window.chrome?.cast?.media?.PlayerState?.PAUSED || 'PAUSED';
    const initialState = String(remotePlayer.playerState || '');
    if (!remotePlayer.isPaused && initialState !== PAUSED) {
      try { controller.playOrPause(); } catch {}
    }
    const deadline = Date.now() + 1200;
    while (Date.now() < deadline) {
      const current = castRemotePlayerRef.current;
      if (!current) break;
      if (current.isPaused || String(current.playerState || '') === PAUSED) break;
      await new Promise((resolve) => window.setTimeout(resolve, 80));
    }
    // Let already-issued old-generation Django segment GETs drain before the
    // single Archive producer is suspended/repositioned. Best effort only.
    await new Promise((resolve) => window.setTimeout(resolve, 180));
    return true;
  };

  // DISPATCHARR_ARCHIVE_CAST_SEEK_GENERATION_R8_8_48
  const commitArchiveCastSeek = async (requestedSeconds) => {
    // DISPATCHARR_CAST_TRANSITION_OPS_R134: no archive reload on the TV while a
    // Rewind on Cast / Continue Live / Web handback is switching it.
    if (castOpInFlight(['rewind-on-cast', 'continue-live', 'handback'])) {
      console.info('[chromecast-r134] Archive Cast seek ignored during a Cast transition');
      castArchiveScrubbingRef.current = false;
      return;
    }
    // DISPATCHARR_LIVE_TIMESHIFT_CAST_SNAPSHOT_AHEAD_R135 (review): a background
    // snapshot refresh is not the user's seek -- queue this one behind it.
    if (liveTimeshiftCastRef.current.refreshing) {
      liveTimeshiftCastRef.current.pendingSeek = requestedSeconds;
      setCastArchivePositionSeconds(Math.max(0, Number(requestedSeconds) || 0));
      castArchiveScrubbingRef.current = false;
      return;
    }
    // DISPATCHARR_ARCHIVE_CAST_STABILITY_REMOTE_SCRUB_GUARD_R8_8_51: one archive producer/receiver reload at a time. Overlapping
    // slider/remote commits are the exact failure mode that can fan out segment
    // waits and starve the web workers behind nginx.
    if (castArchiveSeekHealthRef.current.inProgress) {
      console.warn('[chromecast-r8.8.51] Archive Cast seek ignored: previous seek still in progress');
      // DISPATCHARR_NATIVE_RANGE_TIP_R133: the slider set scrubbing on press;
      // left true, the passive sync stopped publishing the receiver clock and
      // the label/thumb froze at the ignored value. Show the running seek.
      castArchiveScrubbingRef.current = false;
      {
        // R133a review: || treated a target of 0 (seek to 0:00) as unset.
        const runningTarget = Number(castArchiveSeekHealthRef.current.target);
        setCastArchivePositionSeconds(
          Number.isFinite(runningTarget)
            ? Math.max(0, runningTarget)
            : Math.max(0, Number(dispatcharrCastRuntime.archivePosition) || 0)
        );
      }
      return;
    }
    const remotePlayer = castRemotePlayerRef.current;
    // DISPATCHARR_ARCHIVE_CAST_ABSOLUTE_DURATION_R131: RemotePlayer.duration is
    // the receiver WINDOW's length (programme end - window start, see
    // DISPATCHARR_ARCHIVE_FMP4_CAST_WINDOW_R2), never the programme's. Using it
    // first capped every web seek at (total - windowStart - 0.05): on
    // 2026-10-06 a drag past 72:22 committed exactly 4341.950 s
    // (9000 - 4658 - 0.05). Clamp the absolute target with absolute numbers.
    const castWindowBase = Math.max(
      0,
      Number(dispatcharrCastRuntime.archiveBasePosition) || 0
    );
    // Provider live-timeshift: the edge keeps moving, so the value stored at
    // Cast start goes stale once playback passes it.
    const providerTimeshiftEdge =
      contentType === 'catchup-hls' && metadata?.liveTimeshift
        ? liveTimeshiftTimelineSeconds()
        : 0;
    const knownDuration = Math.max(
      0,
      providerTimeshiftEdge ||
        Number(dispatcharrCastRuntime.archiveDuration) ||
        Number(castArchiveDurationSeconds) ||
        (Number(remotePlayer?.duration) > 0
          ? castWindowBase + Number(remotePlayer.duration)
          : 0)
    );
    let target = Math.max(0, Number(requestedSeconds) || 0);
    // DISPATCHARR_ARCHIVE_CAST_PROGRAMME_END_R133: not into the last
    // ARCHIVE_CAST_END_GUARD_SECONDS. The bar's last ~8 px, End and a drag past
    // the end all commit the maximum; on 2026-10-06 such a click (3599.950 of
    // 3600) froze the TV ~30 s and the cast then ended.
    if (knownDuration > 0) {
      target = Math.min(
        target,
        Math.max(0, knownDuration - ARCHIVE_CAST_END_GUARD_SECONDS)
      );
    }

    // DISPATCHARR_ANDROID_TV_REMOTE_ARCHIVE_SEEK_R8_8_49: this hard seek may have originated from either Dispatcharr's
    // slider or the Android TV hardware remote. In both cases fence observer
    // recursion until the new generation is fully loaded.
    const remoteSeekObserver = castArchiveRemoteSeekObserverRef.current;
    if (remoteSeekObserver.debounceTimer != null) {
      window.clearTimeout(remoteSeekObserver.debounceTimer);
    }
    remoteSeekObserver.debounceTimer = null;
    remoteSeekObserver.candidatePosition = null;
    remoteSeekObserver.armDirection = 0;
    remoteSeekObserver.armCount = 0;
    remoteSeekObserver.armUntil = 0;
    remoteSeekObserver.pausedByRemoteScrub = false;
    remoteSeekObserver.resumeAfterRemoteScrub = true;
    if (remoteSeekObserver.snapbackTimer != null) {
      window.clearTimeout(remoteSeekObserver.snapbackTimer);
    }
    remoteSeekObserver.snapbackTimer = null;
    remoteSeekObserver.scrubAnchorPosition = null;
    remoteSeekObserver.virtualTargetPosition = null;
    remoteSeekObserver.lastRawRemotePosition = null;
    remoteSeekObserver.ignoreAnchorUntil = 0;
    remoteSeekObserver.pending = true;
    remoteSeekObserver.suppressUntil = Date.now() + 35000;
    remoteSeekObserver.lastPosition = target;
    remoteSeekObserver.lastAt = Date.now();

    castArchiveScrubbingRef.current = false;
    castArchiveSeekHealthRef.current = {
      inProgress: true,
      startedAt: Date.now(),
      target,
    };
    dispatcharrCastRuntime.archivePosition = target;
    setCastArchivePositionSeconds(target);
    console.info(
      `[chromecast-r8.8.48] Web Player Archive Cast seek -> ${target.toFixed(2)}s`
    );

    try {
      const session = castContextRef.current?.getCurrentSession?.();
      const oldMediaUrl = dispatcharrCastRuntime.mediaUrl;
      if (!session || !oldMediaUrl) {
        throw new Error('Archive Cast session/media is unavailable');
      }
      const mediaUrl = buildArchiveCastSeekMediaUrl(oldMediaUrl, target);
      // DISPATCHARR_ARCHIVE_CAST_ABSOLUTE_TIMELINE_R131: the seek URL is cloned from the session's media
      // URL, so the timeline mode chosen at the initial LOAD carries over.
      let absoluteCastTimeline = false;
      try {
        absoluteCastTimeline =
          new URL(mediaUrl, window.location.origin).searchParams.get(
            'cast_timeline'
          ) === 'absolute';
      } catch {
        absoluteCastTimeline = false;
      }

      // DISPATCHARR_ARCHIVE_DJANGO_SEEK_DRAIN_GUARD_R8_14_1: first pause the receiver and drain already-issued Django
      // segment GETs. The r8.8.55 sender lease remains authoritative.
      await pauseArchiveReceiverBeforeProducerSeek();

      // Keep the audited r8.8.55 synchronous pre-seek lease fence.
      const leaseRenewed = await awaitCastOperation(
        renewCastSessionLeaseNow(),
        2500,
        'Archive hard-seek lease renew',
        false
      );
      if (!leaseRenewed) throw new Error('Archive Cast lease could not be renewed before seek');

      // Suspend MUST succeed before moving the one Archive FFmpeg. Do not race
      // a producer resume against a receiver that still owns the old frontier.
      const suspended = await awaitCastOperation(
        requestArchiveSuspend(oldMediaUrl),
        3000,
        'Archive hard-seek suspend',
        false
      );
      if (!suspended) throw new Error('Archive producer did not suspend cleanly before seek');

      const resumed = await awaitCastOperation(
        requestArchiveResume(mediaUrl, target),
        5500,
        'Archive hard-seek producer resume',
        false
      );
      if (!resumed) throw new Error('Archive producer did not resume at seek target');

      // Resume already re-establishes ownership in r8.8.55. Renew once more
      // synchronously before target prewarm so receiver media traffic never
      // waits for the 30s browser timer.
      const postResumeRenewUrl = buildArchiveCastLeaseRenewUrl(mediaUrl);
      const postResumeLeaseRenewed = await awaitCastOperation(
        renewCastSessionLeaseNow(postResumeRenewUrl),
        2500,
        'Archive hard-seek post-resume lease renew',
        false
      );
      if (!postResumeLeaseRenewed) {
        throw new Error('Archive Cast lease could not be renewed after seek resume');
      }

      // DISPATCHARR_CAST_SEEK_PREWARM_PATIENCE_R9
      // A seek BACKWARD past the producer's start point has nothing cached:
      // the backend stops FFmpeg and restarts it at the new offset, and the
      // first fMP4 segment there needs well over the old 12.5s budget
      // (init.mp4 alone took ~9s, which is why the server-side wait was
      // raised to 12.0). Forward seeks land in already-produced spool and
      // always beat it -- exactly the asymmetry the logs showed. The poll
      // loop exits as soon as the segment is warm, so a larger budget costs
      // nothing in the common case.
      const prewarmed = await awaitCastOperation(
        prewarmArchiveCastPosition(mediaUrl, target, 30000),
        30500,
        'Archive hard-seek target prewarm',
        false
      );
      // Deliberately NOT fatal. The backend logs producer_ok=True for these
      // seeks, i.e. the producer is running at the requested offset and only
      // the first segment is late. Tearing down a healthy Cast session over
      // that is worse than letting CAF buffer for a moment, and the R6 IDLE
      // grace already covers the receiver attach window.
      if (!prewarmed) {
        console.warn(
          '[chromecast-r9] target segment still cold after the prewarm budget; ' +
            'loading the receiver anyway'
        );
      }

      const mediaInfo = createCastMediaInfo(
        mediaUrl,
        window.chrome.cast.media.StreamType.BUFFERED,
        metadata?.name || metadata?.title || 'Dispatcharr Archive',
        { fmp4: true }
      );
      const request = new window.chrome.cast.media.LoadRequest(mediaInfo);
      request.autoplay = true;
      // DISPATCHARR_ARCHIVE_FMP4_CAST_WINDOW_R2
      // The Cast-only manifest is already windowed at target; its
      // EXT-X-START tag owns the exact intra-fragment offset.
      // DISPATCHARR_ARCHIVE_CAST_ABSOLUTE_TIMELINE_R131: full playlist -> start CAF explicitly at target.
      if (absoluteCastTimeline) request.currentTime = target;
      const loaded = await awaitCastOperation(
        session.loadMedia(request).then(() => true),
        9000,
        'Archive hard-seek receiver reload',
        false
      );
      if (!loaded) throw new Error('Chromecast did not accept Archive seek reload');

      const attached = await awaitCastOperation(
        attachRemotePlayer(session),
        10000,
        'Archive hard-seek receiver ready',
        null
      );
      if (!attached) throw new Error('Chromecast did not become ready after Archive seek');

      // DISPATCHARR_ARCHIVE_FMP4_CAST_WINDOW_R2
      const relativePosition = Math.max(
        0,
        Number(attached.remotePlayer?.currentTime) || 0
      );
      // DISPATCHARR_ARCHIVE_CAST_ABSOLUTE_TIMELINE_R131
      const basePosition = absoluteCastTimeline
        ? 0
        : Math.max(0, target - relativePosition);
      const position = target;
      dispatcharrCastRuntime.mediaUrl = mediaUrl;
      dispatcharrCastRuntime.archiveBasePosition = basePosition;
      dispatcharrCastRuntime.archivePosition = position;
      setCastArchivePositionSeconds(position);
      remoteSeekObserver.pending = false;
      remoteSeekObserver.lastPosition = position;
      remoteSeekObserver.lastAt = Date.now();
      remoteSeekObserver.suppressUntil = Date.now() + 1800;
      // DISPATCHARR_CAST_IDLE_GRACE_R6: stamp completedAt. The IDLE watchdog
      // arms its 15s post-seek grace on `completedAt > 0`, and this success
      // path was the one completion site in the file that dropped the field,
      // so the transient IDLE CAF legitimately reports while attaching the
      // freshly loaded media read as a dead receiver and killed the session.
      castArchiveSeekHealthRef.current = {
        inProgress: false,
        startedAt: 0,
        completedAt: Date.now(),
        target: position,
      };
      castHealthRef.current = {
        state: String(attached.remotePlayer?.playerState || ''),
        bufferingSince: 0,
        lastPosition: position,
        lastProgressAt: Date.now(),
      };
      // DISPATCHARR_LIVE_TIMESHIFT_CAST_SNAPSHOT_AHEAD_R135: a Web seek reload
      // is a fresh receiver LOAD (and a fresh snapshot) as well.
      if (castLatestRef.current?.metadata?.liveTimeshift) {
        Object.assign(liveTimeshiftCastRef.current, {
          lastLoadAt: Date.now(),
          edgeArmed: false,
          refreshFailedAt: 0,
        });
      }
      console.info(
        `[chromecast-r8.8.48] Archive Cast seek ready -> ${position.toFixed(2)}s`
      );
    } catch (error) {
      console.warn('[chromecast-r8.8.48] Archive Cast seek failed:', error);
      castArchiveSeekHealthRef.current.inProgress = false;
      remoteSeekObserver.pending = false;
      remoteSeekObserver.lastAt = Date.now();
      remoteSeekObserver.suppressUntil = Date.now() + 1800;
      void recoverFailedChromecast(`Archive seek failed: ${error?.message || error}`);
    }
  };

  const skipArchiveCast = (deltaSeconds) => {
    // DISPATCHARR_ARCHIVE_FMP4_CAST_WINDOW_R2
    const current =
      Number(dispatcharrCastRuntime.archivePosition) ||
      Number(castArchivePositionSeconds) ||
      archiveCastAbsolutePosition() ||
      0;
    commitCastSeek(current + Number(deltaSeconds || 0));
  };

  // DISPATCHARR_ARCHIVE_CAST_PROGRAMME_END_R133: CAF names the reason; when it
  // does not (empty idleReason), a receiver whose last progress was within
  // 2.5 s of the programme end has finished it. An explicit other reason
  // (INTERRUPTED by our own reload, ERROR, CANCELLED) is never "finished".
  const archiveCastFinishedOnReceiver = (mediaSession, lastPosition) => {
    const finished =
      window.chrome?.cast?.media?.IdleReason?.FINISHED || 'FINISHED';
    const reason = String(mediaSession?.idleReason || '');
    if (reason) return reason === finished;
    // R133a review: the timeshift duration is captured at cast start and goes
    // stale at the moving edge, so a dead receiver parked near it must not
    // read as finished; explicit FINISHED (bounded ENDLIST) still counts.
    if (metadata?.liveTimeshift) return false;
    const duration = Number(dispatcharrCastRuntime.archiveDuration) || 0;
    return duration > 0 && Number(lastPosition) >= duration - 2.5;
  };

  const toggleArchiveCastPlayback = () => {
    try {
      castRemoteControllerRef.current?.playOrPause?.();
    } catch (error) {
      console.warn('[chromecast-r8.8.46] Archive Cast play/pause failed:', error);
    }
  };

  // ===== DISPATCHARR_LIVE_PAUSE_CHOICE_R136 =====
  const CAST_PAUSED = 'PAUSED';

  // This tab's channel on the TV (rules of the R135 detector's readMedia,
  // which stays untouched); null when the TV plays something else.
  const readLiveCastMedia = (forStreamUrl) => {
    const media = castContextRef.current?.getCurrentSession?.()?.getMediaSession?.();
    if (!media || typeof media.getEstimatedTime !== 'function') return null;
    const contentId = String(media.media?.contentId || media.media?.contentUrl || '');
    const castChannel = /\/proxy\/ts\/cast-hls\/([^/?#]+)\//.exec(contentId);
    if (!castChannel) return null;
    let castSession = null;
    try {
      if (
        decodeURIComponent(castChannel[1]) !== String(liveChannelIdFromUrl(forStreamUrl) || '')
      ) {
        return null;
      }
      castSession = new URL(contentId, window.location.origin).searchParams.get('cast_session');
    } catch {
      return null;
    }
    let range = null;
    try {
      range =
        (typeof media.getEstimatedLiveSeekableRange === 'function'
          ? media.getEstimatedLiveSeekableRange()
          : null) ||
        media.liveSeekableRange ||
        castRemotePlayerRef.current?.liveSeekableRange ||
        null;
    } catch {
      range = null;
    }
    return {
      media,
      key: `${media.mediaSessionId ?? ''}|${contentId}`,
      t: Number(media.getEstimatedTime()),
      state: String(media.playerState || ''),
      range,
      owner: Boolean(castSession) && castSession === dispatcharrCastRuntime.ownLiveCastSession,
    };
  };

  // One explicit, idempotent receiver command; resolves on the receiver's ack.
  const castLiveMediaCommand = (media, action, { seekTo = null } = {}) =>
    new Promise((resolve, reject) => {
      const M = window.chrome?.cast?.media || {};
      let done = false;
      let timer = null;
      const finish = (fn, value) => {
        if (done) return;
        done = true;
        window.clearTimeout(timer);
        fn(value);
      };
      timer = window.setTimeout(
        () => finish(reject, new Error(`${action} not acknowledged`)),
        CAST_LIVE_COMMAND_TIMEOUT_MS
      );
      const ok = () => finish(resolve, true);
      const fail = (e) =>
        finish(
          reject,
          e instanceof Error ? e : new Error(String(e?.description || e?.code || e || action))
        );
      try {
        if (action === 'pause' && typeof media?.pause === 'function' && M.PauseRequest) {
          media.pause(new M.PauseRequest(), ok, fail);
        } else if (action === 'play' && typeof media?.play === 'function' && M.PlayRequest) {
          media.play(new M.PlayRequest(), ok, fail);
        } else if (action === 'seek-play' && typeof media?.seek === 'function' && M.SeekRequest) {
          const request = new M.SeekRequest();
          request.currentTime = seekTo;
          request.resumeState = M.ResumeState?.PLAYBACK_START || 'PLAYBACK_START';
          media.seek(request, ok, fail);
        } else {
          // R104/R133 controller path, state-checked (no ack exists here).
          const rp = castRemotePlayerRef.current;
          const rc = castRemoteControllerRef.current;
          if (!rp || !rc) throw new Error('no Cast remote controller');
          if (action === 'seek-play') {
            rp.currentTime = seekTo;
            rc.seek();
          }
          const paused = Boolean(rp.isPaused);
          if ((action === 'pause' && !paused) || (action !== 'pause' && paused)) rc.playOrPause();
          ok();
        }
      } catch (error) {
        fail(error);
      }
    });

  // R134 + the R135 gate fences: a Live-cast command runs only on a settled cast.
  const liveCastGate = () => {
    const latest = castLatestRef.current || {};
    const session = castContextRef.current?.getCurrentSession?.();
    const op = castOpInFlight();
    const why = !castActiveRef.current
      ? 'not casting'
      : dispatcharrCastRuntime.kind !== 'live' || latest.contentType !== 'live'
        ? 'not a Live cast'
        : !session
          ? 'no Cast session'
          : op
            ? `a Cast transition is running (op #${op.seq} ${op.kind})`
            : castFailureRecoveryRef.current ||
                castHandoffInProgressRef.current ||
                castReturningToLiveRef.current ||
                liveTimeshiftStartingRef.current ||
                liveTimeshiftReturningToLiveRef.current
              ? 'a Cast transition is running'
              : isDispatcharrCastIntentionalHandback() || dispatcharrPlayerClosingRef.current
                ? 'the cast is ending'
                : null;
    if (why) return { ok: false, why };
    const sample = readLiveCastMedia(latest.streamUrl);
    if (!sample) return { ok: false, why: 'the TV is not playing this channel' };
    return { ok: true, session, sample };
  };

  const waitForCastLiveState = async (op, session, wanted, maxMs = CAST_LIVE_STATE_WAIT_MS) => {
    const deadline = Date.now() + maxMs;
    for (;;) {
      assertCastOpSession(op, session);
      const state = readLiveCastMedia(castLatestRef.current?.streamUrl)?.state || '';
      if (wanted.includes(state) || Date.now() >= deadline) return state;
      await new Promise((resolve) => window.setTimeout(resolve, 150));
    }
  };

  const reportLiveCastCommandFailure = (error, message, session) => {
    if (isCastSuperseded(error)) return;
    console.warn(`[live-pause-r136] ${message}`, error);
    // A TV that went away is R109's to explain, not ours.
    if (
      castActiveRef.current &&
      !castFailureRecoveryRef.current &&
      !dispatcharrPlayerClosingRef.current &&
      castSessionMatches(session)
    ) {
      setCastError(message);
    }
  };

  const pauseLiveCast = async (origin = 'click') => {
    const gate = liveCastGate();
    if (!gate.ok) {
      console.info(`[live-pause-r136] Pause ignored (${origin}): ${gate.why}`);
      return false;
    }
    const { sample, session } = gate;
    if (sample.state === CAST_PAUSED) return true;
    const tv = castDeviceName || 'the TV';
    const op = beginCastOp('live-pause', { origin });
    castLivePressRef.current = { action: 'pause', at: Date.now() };
    setCastLiveCommand('pause');
    try {
      await castLiveMediaCommand(sample.media, 'pause');
      assertCastOpSession(op, session);
      console.info(`[live-pause-r136] Pause sent to ${tv} (${origin}) t=${sample.t.toFixed(1)}`);
      await waitForCastLiveState(op, session, [CAST_PAUSED]);
      return true;
    } catch (error) {
      reportLiveCastCommandFailure(error, `Could not pause on ${tv}.`, session);
      return false;
    } finally {
      setCastLiveCommand(null);
      settleCastOp(op);
    }
  };

  // Continue Live (default): one SEEK to the R104 holdback with PLAYBACK_START
  // when far behind, else PLAY; verified; no reload.
  const continueLiveCast = async (origin = 'click') => {
    const gate = liveCastGate();
    if (!gate.ok) {
      console.info(`[live-pause-r136] Continue Live ignored (${origin}): ${gate.why}`);
      return false;
    }
    const { sample, session } = gate;
    const tv = castDeviceName || 'the TV';
    const holdback = DISPATCHARR_CAST_LIVE_EDGE_HOLDBACK_SECONDS;
    const end = Number(sample.range?.end);
    const start = Number(sample.range?.start);
    const behind =
      Number.isFinite(end) && end > 0 && Number.isFinite(sample.t) ? end - sample.t : null;
    const seekTo =
      behind != null && behind > holdback + 2
        ? Math.max(Number.isFinite(start) ? start + 1 : 0, end - holdback)
        : null;
    if (sample.state !== CAST_PAUSED && seekTo == null) return true; // already live
    const op = beginCastOp('live-resume', { origin, mode: 'live' });
    setCastLiveCommand('live');
    try {
      await castLiveMediaCommand(sample.media, seekTo == null ? 'play' : 'seek-play', { seekTo });
      assertCastOpSession(op, session);
      console.info(
        `[live-pause-r136] Continue Live on ${tv} (${origin}): ` +
          (seekTo == null
            ? `play (${behind == null ? '?' : behind.toFixed(1)}s behind)`
            : `seek ${sample.t.toFixed(1)} -> ${seekTo.toFixed(1)} ` +
              `(edge ${end.toFixed(1)}, ${behind.toFixed(1)}s behind)`)
      );
      let state = await waitForCastLiveState(op, session, ['PLAYING']);
      if (state === CAST_PAUSED) {
        const again = readLiveCastMedia(castLatestRef.current?.streamUrl);
        if (again) await castLiveMediaCommand(again.media, 'play');
        console.info('[live-pause-r136] Continue Live: the TV stayed paused; PLAY re-sent');
        state = await waitForCastLiveState(op, session, ['PLAYING']);
      }
      if (seekTo != null && state === 'PLAYING') {
        assertCastOpSession(op, session);
        const after = readLiveCastMedia(castLatestRef.current?.streamUrl);
        const afterEnd = Number(after?.range?.end);
        const rp = castRemotePlayerRef.current;
        const rc = castRemoteControllerRef.current;
        if (after && rp && rc && Number.isFinite(afterEnd) && afterEnd - after.t > holdback + 8) {
          const to = Math.max(Number(after.range?.start) || 0, afterEnd - holdback);
          rp.currentTime = to;
          rc.seek();
          console.info(
            `[live-pause-r136] Continue Live: the SEEK was not applied ` +
              `(${(afterEnd - after.t).toFixed(1)}s behind); controller seek -> ${to.toFixed(1)}`
          );
        }
      }
      return true;
    } catch (error) {
      reportLiveCastCommandFailure(error, `Could not continue Live on ${tv}.`, session);
      return false;
    } finally {
      setCastLiveCommand(null);
      settleCastOp(op);
    }
  };

  // "Resume from" while the TV still holds the paused frame: plain PLAY.
  // Returns true | false | 'fallback' (the frame left the window).
  const resumeLiveCastHeld = async (origin = 'click') => {
    const gate = liveCastGate();
    if (!gate.ok) {
      console.info(`[live-pause-r136] Resume ignored (${origin}): ${gate.why}`);
      return false;
    }
    const { sample, session } = gate;
    const pause = livePauseRef.current;
    const start = Number(sample.range?.start);
    const held = Boolean(
      pause?.where === 'tv' &&
        sample.state === CAST_PAUSED &&
        sample.key === pause.key &&
        Number.isFinite(sample.t) &&
        Math.abs(sample.t - pause.tHeld) < 1 &&
        Date.now() < (pause.tvBufferUntilMs || 0) &&
        (!Number.isFinite(start) ||
          sample.t - start >= CAST_LIVE_PAUSE_TV_BUFFER_MARGIN_SECONDS - 2)
    );
    if (!held) {
      console.info(
        '[live-pause-r136] the held frame has left the TV window; resuming from the catch-up archive'
      );
      return 'fallback';
    }
    const tv = castDeviceName || 'the TV';
    const op = beginCastOp('live-resume', { origin, mode: 'held' });
    setCastLiveCommand('resume');
    try {
      await castLiveMediaCommand(sample.media, 'play');
      assertCastOpSession(op, session);
      console.info(
        `[live-pause-r136] Resume on ${tv} from the held frame (${origin}) t=${sample.t.toFixed(1)}`
      );
      await waitForCastLiveState(op, session, ['PLAYING']);
      return true;
    } catch (error) {
      reportLiveCastCommandFailure(error, `Could not resume on ${tv}.`, session);
      return false;
    } finally {
      setCastLiveCommand(null);
      settleCastOp(op);
    }
  };

  // Space on a settled Live cast: act on the TRUE receiver state.
  const toggleLiveCastPlayback = (origin = 'space') => {
    const sample = readLiveCastMedia(castLatestRef.current?.streamUrl);
    if (!sample || !sample.state || sample.state === 'IDLE') {
      console.info(`[live-pause-r136] ${origin} ignored: the TV is not playing this Live`);
      return;
    }
    if (sample.state === CAST_PAUSED) {
      // R136b: Continue Live (Space) also drops a "Resume from" still waiting for the archive
      cancelPendingPauseResume(`Continue Live, ${origin}`);
      void continueLiveCast(origin);
    } else {
      void pauseLiveCast(origin);
    }
  };

  // The single source of the "Resume from" label AND its commit.
  const livePauseResumePlan = (
    pause = livePauseRef.current,
    nowMs = Date.now(),
    { archiveOnly = false } = {}
  ) => {
    if (
      !pause ||
      contentType !== 'live' ||
      !liveRewindAvailable ||
      !Number.isFinite(pause.frameInstantMs)
    ) {
      return null;
    }
    if (pause.where === 'tv' && !pause.owner) return null; // R135 ownership
    const landingMs = Math.floor(pause.frameInstantMs / 1000) * 1000;
    if (!archiveOnly && pause.where === 'tv' && nowMs < (pause.tvBufferUntilMs || 0)) {
      return {
        mode: 'tv-buffer',
        landingMs,
        floorSeconds: liveRewindFloorSeconds({ onCast: true }),
        readyInMs: 0,
        label: liveInstantLabel(landingMs),
      };
    }
    const windowSeconds = liveProviderTimeshiftWindowSeconds(nowMs);
    const rewindSeconds = Math.max(1, Math.ceil((nowMs - landingMs) / 1000));
    if (windowSeconds < 1 || rewindSeconds > windowSeconds + LIVE_PAUSE_WINDOW_SLACK_SECONDS) {
      return null;
    }
    // A local pause whose resume will play on a TV after all (Cast pressed
    // while paused, its start still loading) is planned like a TV pause:
    // the commit will use the TV floor, and the label must not promise less.
    const onCast =
      pause.where === 'tv' || liveRewindCastingLive() || Boolean(castOpInFlight(['start']));
    const floorSeconds = liveRewindFloorSeconds({ onCast });
    // DISPATCHARR_ARCHIVE_PTS_ALIGN_R137: a local resume starts on the
    // estimated instant itself, to the millisecond (the label keeps its whole
    // second); a resume that plays on a TV keeps the whole second it always used.
    const targetMs = onCast ? landingMs : Math.round(pause.frameInstantMs);
    return {
      mode: 'archive',
      landingMs,
      targetMs,
      rewindSeconds,
      // R136b: the label is the paused moment itself, always (it used to be
      // the landing, which crept up to the pause while the pause was young).
      // It can be played once it is as old as the rewind floor (R137: 10 s
      // on a followed archive, 30 s otherwise and on a TV); an earlier click
      // waits this long, then starts exactly there.
      floorSeconds,
      // The whole second at which the target is `floor` old: the commit
      // counts the window in whole seconds, so starting any earlier would
      // have the floor move the landing off the target.
      readyInMs: Math.max(
        0,
        Math.ceil(
          (Math.max(targetMs, liveProviderTimeshiftWindowStartMs(nowMs)) + floorSeconds * 1000) / 1000
        ) *
          1000 -
          nowMs
      ),
      label: liveInstantLabel(landingMs),
    };
  };

  // R136b: drop a "Resume from" that is still waiting for the archive.
  const cancelPendingPauseResume = (why = '') => {
    if (livePauseResumeTimerRef.current != null) {
      window.clearTimeout(livePauseResumeTimerRef.current);
      livePauseResumeTimerRef.current = null;
    }
    if (livePauseRef.current?.busy === 'waiting') {
      setLivePause((current) =>
        current && current.busy === 'waiting' ? { ...current, busy: null, resumeAtMs: 0 } : current
      );
      if (why) console.info(`[live-pause-r136] Resume from cancelled (${why})`);
    }
  };

  // Default action of every paused Live (button, Space, the bar's play).
  const continueLiveFromPause = async (origin = 'click') => {
    livePauseCalibrationRef.current?.cancel?.(); // R137: its archive is being left
    // R136b: Continue Live wins over a "Resume from" still waiting for the archive.
    if (livePauseRef.current?.busy === 'waiting') {
      cancelPendingPauseResume(`Continue Live, ${origin}`);
    }
    if (castActiveRef.current) return continueLiveCast(origin);
    if (transportSuspendedKindRef.current !== 'live') return false;
    const busy = livePauseRef.current?.busy;
    if (busy || liveTimeshiftStartingRef.current) {
      console.info(
        `[live-pause-r136] Continue Live ignored (${origin}): ` +
          (busy === 'stopping' ? 'the cast is still stopping' : 'Resume from is starting')
      );
      return false;
    }
    console.info(`[live-pause-r136] Continue Live (${origin})`);
    await (castLatestRef.current?.resumeSuspendedTransport || resumeSuspendedTransport)();
    return true;
  };

  // DISPATCHARR_ARCHIVE_PTS_ALIGN_R137: after a "Resume from" has started,
  // look for the paused picture in what the archive plays first and note what
  // the estimate's constant should have been (see LIVE_PAUSE_LAG_STORE_KEY).
  // Reads frames only; never touches playback. It belongs to the one archive
  // session that this resume opened and ends with it.
  const beginLivePauseCalibration = (calibration) => {
    livePauseCalibrationRef.current?.cancel?.();
    if (!calibration?.sig || !calibration.key) return;
    if (typeof HTMLVideoElement?.prototype?.requestVideoFrameCallback !== 'function') return;
    // startLiveProviderTimeshift has just put the session into the store
    const opened = useVideoStore.getState();
    const sessionUrl = opened?.streamUrl;
    const openedMeta = opened?.metadata;
    if (
      !sessionUrl ||
      opened.contentType !== 'catchup-hls' ||
      !openedMeta?.liveTimeshift ||
      openedMeta.liveTimeshiftOrigin !== 'pause'
    ) {
      return;
    }
    const sessionStartMs = Math.floor(Date.parse(openedMeta.liveTimeshiftStart || '') / 1000) * 1000;
    const initial = Number(openedMeta.liveTimeshiftInitialPositionSeconds);
    if (!Number.isFinite(sessionStartMs) || !Number.isFinite(initial)) return;
    const startedAt = Date.now();
    const rows = [];
    let done = false;
    let armedOn = null;
    let firstMedia = null;
    let lastMedia = null;
    let timer = null;
    const stop = () => {
      done = true;
      if (timer != null) window.clearInterval(timer);
      timer = null;
      if (livePauseCalibrationRef.current?.cancel === stop) livePauseCalibrationRef.current = null;
    };
    const sameSession = () => {
      const state = useVideoStore.getState();
      return state?.streamUrl === sessionUrl && state?.contentType === 'catchup-hls';
    };
    const alignedAt = (media) => {
      const noted = archiveAlignedFragmentsRef.current;
      return noted.url === sessionUrl && noted.indexes.has(Math.floor(media));
    };
    const conclude = () => {
      stop();
      if (rows.length < 20) return;
      const match = livePauseMatch(rows);
      const { best, cost, runnerUp } = match;
      const span = rows[rows.length - 1][0] - rows[0][0];
      // a player that does not report its fragments (native HLS) teaches nothing, silently
      if (archiveAlignedFragmentsRef.current.url !== sessionUrl) return;
      if (
        !alignedAt(rows[0][0]) ||
        !alignedAt(rows[best][0]) ||
        (match.row >= 0 && !alignedAt(rows[match.row][0]))
      ) {
        console.info('[live-pause-r137] this archive start was not made on the exact instant; nothing learned');
        return;
      }
      if (match.row >= 0) {
        const found = rows[match.row][0];
        const trueInstantMs = sessionStartMs + found * 1000;
        const measured = (calibration.at - trueInstantMs) / 1000 - calibration.ahead;
        if (measured >= LIVE_PAUSE_LAG_MIN_SECONDS - 0.5 && measured <= LIVE_PAUSE_LAG_MAX_SECONDS + 0.5) {
          const entry = noteLivePauseLag(calibration.key, { measured });
          const known = readLivePauseLag(calibration.key);
          console.info(
            `[live-pause-r137] paused frame found ${(found - rows[0][0]).toFixed(2)}s into ` +
              `the resumed archive: lag constant ${measured.toFixed(2)}s ` +
              `(used ${calibration.constant.toFixed(2)}s, next ${entry && known ? known.value.toFixed(2) : '-'}s + margin)` +
              (match.exact ? '' : `; a slow picture, the first of ${match.alike.toFixed(2)}s that look alike`)
          );
        } else {
          console.info(
            `[live-pause-r137] paused frame found, but a lag constant of ${measured.toFixed(2)}s ` +
              'is outside anything plausible; nothing learned'
          );
        }
        return;
      }
      if (cost > LIVE_PAUSE_MATCH_COST && span >= 3) {
        const entry = noteLivePauseLag(calibration.key, {
          absent: true,
          base: calibration.base,
          guess: calibration.guess,
        });
        const known = readLivePauseLag(calibration.key);
        console.info(
          `[live-pause-r137] paused frame not in the first ${span.toFixed(1)}s of the resumed ` +
            `archive (best ${cost.toFixed(2)}): ` +
            (entry && known
              ? `next start earlier, lag constant ${known.value.toFixed(2)}s + margin`
              : 'the first guess is used from now on')
        );
        return;
      }
      console.info(
        `[live-pause-r137] paused frame not told apart (best ${cost.toFixed(2)}, ` +
          `runner-up ${Number.isFinite(runnerUp) ? runnerUp.toFixed(2) : '-'}); nothing learned`
      );
    };
    const onFrame = (_now, frame) => {
      armedOn = null;
      if (done) return;
      if (!sameSession()) {
        stop();
        return;
      }
      const video = videoRef.current;
      const media = Number(frame?.mediaTime);
      if (video && Number.isFinite(media)) {
        // Only the stretch the resume itself plays says anything: a first
        // frame somewhere else, or a jump, means the viewer has moved on.
        if (
          (firstMedia == null && Math.abs(media - initial) > 1.5) ||
          (lastMedia != null && (media < lastMedia - 0.2 || media > lastMedia + 1.5))
        ) {
          stop();
          return;
        }
        lastMedia = media;
        const signature = livePauseFrameSignature(video);
        if (signature) {
          if (firstMedia == null) firstMedia = media;
          rows.push([media, livePauseSignatureDistance(calibration.sig, signature)]);
          if (media - firstMedia >= LIVE_PAUSE_MATCH_WINDOW_SECONDS || rows.length >= 400) {
            conclude();
            return;
          }
        }
      }
      arm();
    };
    const arm = () => {
      const video = videoRef.current;
      if (done || !video || typeof video.requestVideoFrameCallback !== 'function') return;
      if (armedOn === video) return;
      armedOn = video;
      try {
        video.requestVideoFrameCallback(onFrame);
      } catch {
        armedOn = null;
      }
    };
    timer = window.setInterval(() => {
      if (done) return;
      if (!sameSession()) {
        stop();
        return;
      }
      if (Date.now() - startedAt > LIVE_PAUSE_MATCH_TIMEOUT_MS) {
        if (rows.length) conclude();
        else stop();
        return;
      }
      arm();
    }, 200);
    livePauseCalibrationRef.current = { cancel: stop };
    arm();
  };

  // Explicit choice only (click / Enter on the focused button).
  const resumeFromPause = async (origin = 'click', { afterWait = false } = {}) => {
    let pause = livePauseRef.current;
    if (!pause) return false;
    livePauseCalibrationRef.current?.cancel?.(); // R137: a search belongs to one resume
    // busy 'waiting' is this very request coming back from its wait
    if (pause.busy && !(afterWait && pause.busy === 'waiting')) return false;
    const giveUp = (why) => {
      if (afterWait) cancelPendingPauseResume(why);
      return false;
    };
    // DISPATCHARR_ARCHIVE_PTS_ALIGN_R137: a local resume is planned with the
    // floor the gate names NOW -- the one its commit is going to use. Planned
    // with an older note, a floor that had gone up meanwhile had the commit
    // start as far back as the new floor (up to 20 s before the paused
    // moment) instead of waiting for it. The record is busy while the gate
    // is asked (0.9 s at the very most), so a second click finds it taken.
    if (
      pause.where === 'local' &&
      transportSuspendedKindRef.current === 'live' &&
      !liveTimeshiftStartingRef.current &&
      !liveRewindCastingLive() &&
      !castOpInFlight(['start'])
    ) {
      const held = pause.busy || null; // 'waiting' when this is the wait coming back
      setLivePause((current) => (current ? { ...current, busy: 'resume' } : current));
      try {
        await refreshLiveRewindFloor();
      } finally {
        setLivePause((current) =>
          current && current.busy === 'resume' ? { ...current, busy: held } : current
        );
      }
      const after = livePauseRef.current;
      if (
        !after ||
        after.where !== 'local' ||
        after.frameInstantMs !== pause.frameInstantMs ||
        (after.busy || null) !== held
      ) {
        // the pause ended or changed hands while the gate was asked
        return false;
      }
      pause = after;
    }
    let plan = livePauseResumePlan(pause);
    if (!plan) return giveUp('no longer possible');
    if (pause.where === 'tv') {
      if (!castActiveRef.current) return giveUp('not casting');
      if (plan.mode === 'tv-buffer') {
        const held = await resumeLiveCastHeld(origin);
        if (held !== 'fallback') return held === true;
        plan = livePauseResumePlan(livePauseRef.current, Date.now(), { archiveOnly: true });
        if (!plan) return false;
      }
      const gate = liveCastGate();
      if (!gate.ok) {
        console.info(`[live-pause-r136] Resume from ignored (${origin}): ${gate.why}`);
        return giveUp(gate.why);
      }
    } else if (transportSuspendedKindRef.current !== 'live' || liveTimeshiftStartingRef.current) {
      return giveUp('the player moved on');
    }
    // R136b: resume lands on the paused moment, never before it. A pause
    // younger than the rewind floor waits until it is that old (the overlay
    // says so; Continue Live / Space cancels the wait).
    if (plan.readyInMs > 0) {
      const waitingFor = pause.frameInstantMs;
      const waitingWhere = pause.where;
      const readyInMs = plan.readyInMs;
      setLivePause((current) =>
        current ? { ...current, busy: 'waiting', resumeAtMs: Date.now() + readyInMs } : current
      );
      console.info(
        `[live-pause-r136] Resume from ${plan.label} (${waitingWhere}, ${origin}): ` +
          `the archive reaches it in ${(readyInMs / 1000).toFixed(1)}s ` +
          `(floor ${plan.floorSeconds}s); waiting`
      );
      if (livePauseResumeTimerRef.current != null) {
        window.clearTimeout(livePauseResumeTimerRef.current);
      }
      livePauseResumeTimerRef.current = window.setTimeout(() => {
        livePauseResumeTimerRef.current = null;
        const current = livePauseRef.current;
        if (
          !current ||
          current.busy !== 'waiting' ||
          current.where !== waitingWhere ||
          current.frameInstantMs !== waitingFor
        ) {
          return;
        }
        void (castLatestRef.current?.resumeFromPause || resumeFromPause)(origin, {
          afterWait: true,
        });
      }, readyInMs + 80);
      return true;
    }
    setLivePause((current) => (current ? { ...current, busy: 'resume' } : current));
    console.info(
      `[live-pause-r136] Resume from ${plan.label} (${pause.where}, ${origin}): ` +
        `provider time-shift rewind=${plan.rewindSeconds}s`
    );
    let started = false;
    try {
      started = Boolean(
        await (castLatestRef.current?.startLiveProviderTimeshift || startLiveProviderTimeshift)(
          plan.rewindSeconds,
          { origin: 'pause', targetInstantMs: plan.targetMs ?? plan.landingMs }
        )
      );
      // DISPATCHARR_ARCHIVE_PTS_ALIGN_R137 (a resume that went to a TV has
      // no local frames to read)
      if (
        started &&
        pause.where === 'local' &&
        pause.calibration &&
        !castActiveRef.current &&
        !liveRewindCastingLive()
      ) {
        beginLivePauseCalibration(pause.calibration);
      }
    } finally {
      if (!started) {
        // only its own state: a paused handback may own the record meanwhile
        setLivePause((current) =>
          current && current.busy === 'resume' ? { ...current, busy: null } : current
        );
      }
    }
    return started;
  };

  // Stop Casting while the TV is paused: hand back PAUSED (no autoplay).
  const stopLiveCastIntoPausedWeb = async () => {
    const pause = livePauseRef.current;
    if (
      !castActiveRef.current ||
      dispatcharrCastRuntime.kind !== 'live' ||
      contentType !== 'live' ||
      !isVisible ||
      castOpInFlight() ||
      pause?.where !== 'tv' ||
      // R136 review: a "Resume from" whose POST is running owns this transition;
      // R136b: one still waiting for the archive does not (just a timer).
      (pause.busy && pause.busy !== 'waiting') ||
      liveTimeshiftStartingRef.current ||
      !Number.isFinite(pause.frameInstantMs) ||
      readLiveCastMedia(streamUrl)?.state !== CAST_PAUSED
    ) {
      return false;
    }
    console.info(
      '[live-pause-r136] Stop Casting while the TV is paused -> the Web player stays paused'
    );
    // R137: nothing has asked for the web floor while this Live was on the TV
    void refreshLiveRewindFloor();
    setLivePause({
      where: 'local',
      streamUrl,
      pausedAtMs: pause.pausedAtMs,
      frameInstantMs: pause.frameInstantMs,
      approx: pause.approx,
      by: 'cast-stop',
      busy: 'stopping',
    });
    transportSuspendedKindRef.current = 'live';
    setTransportSuspendedKind('live');
    // R136 review (D17): the picker must not pop back after Continue Live.
    setShowLiveRewindPicker(false);
    setLiveTimeshiftDraftSeconds(0);
    livePlaybackStartedRef.current = false;
    try {
      await stopChromecast({ resumeLocal: false });
    } finally {
      setLivePause((current) =>
        current?.where === 'local' && current.busy === 'stopping'
          ? { ...current, busy: null }
          : current
      );
    }
    return true;
  };

  // DISPATCHARR_CAST_BADGE_MEASURED_R8: "Stop Casting" is wider than "Cast",
  // and hand-picked offsets for it were wrong twice (121 overlapped, 127 still
  // met with a 0px seam). Measure the button that is actually rendered and put
  // Continue Live one fixed gap to its left. A plain useEffect; the literal
  // fallbacks below keep the very first paint sane.
  useEffect(() => {
    const node = castButtonRef.current;
    if (!node) {
      setCastButtonWidth(0);
      return;
    }
    const width = Math.round(node.getBoundingClientRect().width);
    if (width > 0) setCastButtonWidth(width);
  }, [
    castActive,
    castHoverRequired,
    castSdkReady,
    castAvailable,
    contentType,
    isVisible,
    castDeviceName,
  ]);

  // DISPATCHARR_ARCHIVE_CAST_REMUX_R8_19_0: all slider, skip and hardware
  // remote targets converge on one debounced latest-wins control path.
  const commitArchiveCastV2Seek = (requestedSeconds) => {
    const controller =
      archiveCastControllerRef.current || dispatcharrCastRuntime.archiveController;
    const session = castContextRef.current?.getCurrentSession?.();
    if (!controller?.active || !session) {
      castArchiveScrubbingRef.current = false; // DISPATCHARR_NATIVE_RANGE_TIP_R133
      setCastError('Archive Cast session is unavailable');
      return;
    }
    const duration = Math.max(
      1,
      Number(dispatcharrCastRuntime.archiveDuration) ||
        Number(castArchiveDurationSeconds) ||
        1
    );
    const target = Math.min(
      Math.max(0, Number(requestedSeconds) || 0),
      Math.max(0, duration - ARCHIVE_CAST_END_GUARD_SECONDS) // DISPATCHARR_ARCHIVE_CAST_PROGRAMME_END_R133
    );
    castArchiveScrubbingRef.current = false;
    castArchiveSeekHealthRef.current = {
      inProgress: true,
      startedAt: Date.now(),
      completedAt: 0,
      target,
    };
    castArchiveRemoteSeekObserverRef.current.suppressUntil = Date.now() + 35000;
    dispatcharrCastRuntime.archivePosition = target;
    setCastArchivePositionSeconds(target);
    controller.scheduleSeek(target, (payload, context) =>
      loadArchiveCastGeneration(
        session,
        payload,
        context.mediaUrl,
        context.markReceiverAccepted
      )
    );
  };

  // DISPATCHARR_CAST_SEEK_ROUTE_R4: the slider used to call the legacy Django
  // path unconditionally. That path starts with requestArchiveSuspend(), and
  // buildArchiveSessionControlUrl() returns null for anything that is not
  // 'catchup-hls' -- so for real Archive it threw on the first step every
  // time and could never seek. Direct Archive Cast owns an
  // ArchiveCastController; when it is live, its transactional seek wins.
  const commitCastSeek = (requestedSeconds) => {
    const controller =
      archiveCastControllerRef.current || dispatcharrCastRuntime.archiveController;
    if (controller?.active) {
      commitArchiveCastV2Seek(requestedSeconds);
      return;
    }
    void commitArchiveCastSeek(requestedSeconds);
  };

  const skipArchiveCastV2 = (deltaSeconds) => {
    const current = Math.max(
      0,
      Number(dispatcharrCastRuntime.archivePosition) ||
        Number(castArchivePositionSeconds) ||
        0
    );
    commitArchiveCastV2Seek(current + Number(deltaSeconds || 0));
  };

  // DISPATCHARR_CAST_FAILSAFE_ARCHIVE_RESEEK_R8_8_47
  const awaitCastOperation = async (promise, timeoutMs, label, fallback = null) => {
    let timer = null;
    try {
      return await Promise.race([
        Promise.resolve(promise),
        new Promise((_, reject) => {
          timer = window.setTimeout(
            () => reject(new Error(`${label} timed out after ${timeoutMs}ms`)),
            timeoutMs
          );
        }),
      ]);
    } catch (error) {
      console.warn(`[chromecast-r8.8.47] ${label} failed:`, error);
      return fallback;
    } finally {
      if (timer != null) window.clearTimeout(timer);
    }
  };

  // DISPATCHARR_ARCHIVE_REMOTE_SEAMLESS_PRODUCER_SEEK_R8_14_10
  const commitArchiveRemoteSeamlessSeek = async (
    requestedSeconds,
    resumeAfterSeek = true
  ) => {
    const observer = castArchiveRemoteSeekObserverRef.current;
    if (castArchiveSeekHealthRef.current.inProgress) {
      console.warn(
        '[chromecast-r8.14.10] hardware remote seek ignored: seek already in progress'
      );
      return false;
    }

    const remotePlayer = castRemotePlayerRef.current;
    const remoteController = castRemoteControllerRef.current;
    const knownDuration = Math.max(
      0,
      Number(remotePlayer?.duration) ||
        Number(castArchiveDurationSeconds) ||
        Number(dispatcharrCastRuntime.archiveDuration) ||
        0
    );
    let target = Math.max(0, Number(requestedSeconds) || 0);
    if (knownDuration > 0) {
      target = Math.min(target, Math.max(0, knownDuration - 0.05));
    }

    const mediaUrl = dispatcharrCastRuntime.mediaUrl;
    if (!mediaUrl || !remotePlayer || !remoteController) {
      console.warn(
        '[chromecast-r8.14.10] seamless remote seek unavailable: current Cast media/controller missing'
      );
      return false;
    }

    observer.pending = true;
    observer.suppressUntil = Date.now() + 20000;
    observer.lastPosition = target;
    observer.lastAt = Date.now();
    castArchiveSeekHealthRef.current = {
      inProgress: true,
      startedAt: Date.now(),
      target,
    };
    dispatcharrCastRuntime.archivePosition = target;
    setCastArchivePositionSeconds(target);

    try {
      const renewed = await awaitCastOperation(
        renewCastSessionLeaseNow(),
        2500,
        'Archive remote seamless lease renew',
        false
      );
      if (!renewed) {
        throw new Error(
          'Archive Cast lease could not be renewed before seamless remote seek'
        );
      }

      // DISPATCHARR_ARCHIVE_REMOTE_REPOSITION_TIMEOUT_R8_14_18
      const repositioned = await awaitCastOperation(
        requestArchiveNativeRemoteSeek(mediaUrl, target),
        15000,
        'Archive remote seamless producer reposition',
        false
      );
      if (!repositioned) {
        throw new Error(
          'Archive producer did not reposition for seamless remote seek'
        );
      }

      const postRenewUrl = buildArchiveCastLeaseRenewUrl(mediaUrl);
      const renewedAfter = await awaitCastOperation(
        renewCastSessionLeaseNow(postRenewUrl),
        2500,
        'Archive remote seamless post-reposition lease renew',
        false
      );
      if (!renewedAfter) {
        throw new Error(
          'Archive Cast lease could not be renewed after seamless reposition'
        );
      }

      const prewarmed = await awaitCastOperation(
        prewarmArchiveCastPosition(mediaUrl, target, 12000, true),
        12500,
        'Archive remote seamless target prewarm',
        false
      );
      if (!prewarmed) {
        throw new Error('Archive seamless target segment did not prewarm');
      }

      // Keep the current Cast MediaSession. This is an in-place seek nudge,
      // not a new media LOAD.
      try {
        remotePlayer.currentTime = target;
        remoteController.seek();
      } catch (error) {
        console.warn(
          '[chromecast-r8.14.10] in-place receiver seek nudge failed:',
          error
        );
      }

      const PLAYING =
        window.chrome?.cast?.media?.PlayerState?.PLAYING || 'PLAYING';
      const PAUSED =
        window.chrome?.cast?.media?.PlayerState?.PAUSED || 'PAUSED';

      // DISPATCHARR_ARCHIVE_REMOTE_SCRUB_PAUSE_HOLD_R8_14_11
      // During a long scrub the sender paused this SAME MediaSession. First wait
      // for the in-place seek to settle while PAUSED/PLAYING near target; then
      // restore playback only if it was playing before the scrub.
      const settleDeadline = Date.now() + 12000;
      let settled = false;

      while (Date.now() < settleDeadline) {
        const current = castRemotePlayerRef.current;
        if (!current) break;
        const state = String(current.playerState || '');
        const position = Math.max(0, Number(current.currentTime) || 0);
        const closeEnough = Math.abs(position - target) <= 8.0;
        if (closeEnough && (state === PLAYING || state === PAUSED)) {
          settled = true;
          break;
        }

        // A PAUSED media item should remain seekable. Retry only the in-place
        // seek nudge; never LOAD a new MediaInfo here.
        if (
          Date.now() + 2500 < settleDeadline &&
          Math.abs(position - target) > 8.0
        ) {
          try {
            current.currentTime = target;
            castRemoteControllerRef.current?.seek?.();
          } catch {}
        }
        await new Promise((resolve) => window.setTimeout(resolve, 250));
      }

      if (!settled) {
        throw new Error(
          'Chromecast did not settle on the existing MediaSession after seamless remote seek'
        );
      }

      if (resumeAfterSeek) {
        const current = castRemotePlayerRef.current;
        const state = String(current?.playerState || '');
        if (current?.isPaused || state === PAUSED) {
          try {
            castRemoteControllerRef.current?.playOrPause?.();
            console.info(
              '[chromecast-r8.14.11] hardware remote scrub hold -> resume same MediaSession'
            );
          } catch (error) {
            console.warn(
              '[chromecast-r8.14.11] hardware remote resume failed:',
              error
            );
          }

          const playDeadline = Date.now() + 6000;
          while (Date.now() < playDeadline) {
            const latest = castRemotePlayerRef.current;
            if (!latest) break;
            const latestState = String(latest.playerState || '');
            if (latestState === PLAYING) break;
            await new Promise((resolve) => window.setTimeout(resolve, 150));
          }
        }
      }

      const finalPlayer = castRemotePlayerRef.current;
      const finalPosition = Math.max(
        0,
        Number(finalPlayer?.currentTime) || target
      );
      dispatcharrCastRuntime.archivePosition = finalPosition;
      setCastArchivePositionSeconds(finalPosition);
      observer.pending = false;
      observer.lastPosition = finalPosition;
      observer.lastAt = Date.now();
      observer.suppressUntil = Date.now() + 15000;
      observer.armDirection = 0;
      castArchiveSeekHealthRef.current = {
        inProgress: false,
        startedAt: 0,
        completedAt: Date.now(),
        target: finalPosition,
      };
      castHealthRef.current = {
        state: String(finalPlayer?.playerState || ''),
        bufferingSince: 0,
        lastPosition: finalPosition,
        lastProgressAt: Date.now(),
      };

      console.info(
        `[chromecast-r8.14.10] hardware remote seamless seek ready -> ${finalPosition.toFixed(2)}s; ` +
          'same Cast MediaSession retained'
      );
      return true;
    } catch (error) {
      console.warn(
        '[chromecast-r8.14.11] seamless remote seek failed; preserving current Cast MediaSession:',
        error
      );

      if (resumeAfterSeek) {
        const current = castRemotePlayerRef.current;
        const PAUSED =
          window.chrome?.cast?.media?.PlayerState?.PAUSED || 'PAUSED';
        const state = String(current?.playerState || '');
        if (current?.isPaused || state === PAUSED) {
          try {
            castRemoteControllerRef.current?.playOrPause?.();
          } catch {}
        }
      }

      castArchiveSeekHealthRef.current = {
        inProgress: false,
        startedAt: 0,
        completedAt: 0,
        target,
      };
      observer.pending = false;
      observer.lastAt = Date.now();
      observer.suppressUntil = Date.now() + 1000;
      return false;
    }
  };

  // DISPATCHARR_ARCHIVE_R2_SEEK_HANDBACK_FIXES
  const waitForArchiveCastSeekBeforeHandback = async (timeoutMs = 35000) => {
    const startedAt = Date.now();
    while (Date.now() - startedAt < timeoutMs) {
      const health = castArchiveSeekHealthRef.current;
      const observer = castArchiveRemoteSeekObserverRef.current;
      const busy = Boolean(
        health?.inProgress ||
          observer?.pending ||
          observer?.receiverLoadInProgress
      );
      if (!busy) {
        // Let the final mediaUrl/archivePosition assignments and CAF event
        // callbacks from the completed seek settle before snapshotting handback.
        await new Promise((resolve) => window.setTimeout(resolve, 180));
        const latestHealth = castArchiveSeekHealthRef.current;
        const latestObserver = castArchiveRemoteSeekObserverRef.current;
        if (
          !latestHealth?.inProgress &&
          !latestObserver?.pending &&
          !latestObserver?.receiverLoadInProgress
        ) {
          return true;
        }
      }
      await new Promise((resolve) => window.setTimeout(resolve, 100));
    }
    return false;
  };

  // DISPATCHARR_ARCHIVE_FMP4_R2
  const waitForArchiveWebHandbackReady = async (
    url,
    targetSeconds = 0,
    timeoutMs = 12000
  ) => {
    if (!url) return false;
    const target = Math.max(0, Number(targetSeconds) || 0);
    const startedAt = Date.now();
    while (Date.now() - startedAt < timeoutMs) {
      try {
        const probe = new URL(url, window.location.origin);
        probe.searchParams.set('web_handback_probe', String(Date.now()));
        const token = useAuthStore.getState().accessToken;
        const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
        const response = await fetch(probe.toString(), {
          method: 'GET',
          cache: 'no-store',
          credentials: 'same-origin',
          headers,
        });
        if (!response.ok) {
          await new Promise((resolve) => window.setTimeout(resolve, 150));
          continue;
        }
        const manifest = await response.text();
        const lines = manifest.split(/\r?\n/).map((line) => line.trim());
        const mapLine = lines.find((line) => line.startsWith('#EXT-X-MAP:'));
        const mapMatch = mapLine?.match(/URI="([^"]+)"/);
        if (!manifest.includes('#EXTM3U') || !mapMatch?.[1]) {
          await new Promise((resolve) => window.setTimeout(resolve, 150));
          continue;
        }

        let elapsed = 0;
        let pendingDuration = null;
        let selected = null;
        let successor = null;
        for (const line of lines) {
          if (line.startsWith('#EXTINF:')) {
            pendingDuration = Math.max(0, Number.parseFloat(line.slice(8)) || 0);
            continue;
          }
          if (!line || line.startsWith('#') || pendingDuration == null) continue;
          const duration = pendingDuration;
          pendingDuration = null;
          if (selected == null && target < elapsed + duration) {
            selected = line;
          } else if (selected != null && successor == null) {
            successor = line;
            break;
          }
          elapsed += duration;
        }
        if (!selected) {
          await new Promise((resolve) => window.setTimeout(resolve, 150));
          continue;
        }

        const refs = [mapMatch[1], selected, successor].filter(Boolean);
        const checks = await Promise.all(
          refs.map((ref) =>
            fetch(new URL(ref, probe).toString(), {
              method: 'HEAD',
              cache: 'no-store',
              credentials: 'same-origin',
              headers,
            })
          )
        );
        if (checks.every((item) => item.ok)) {
          console.info(
            `[archive-fmp4-r2] Web handback ready init+target at ${target.toFixed(2)}s`
          );
          return true;
        }
      } catch {}
      await new Promise((resolve) => window.setTimeout(resolve, 150));
    }
    return false;
  };

  // DISPATCHARR_SAME_PROFILE_LIVE_TEE_REMOTE_SCRUB_R8_8_50
  // DISPATCHARR_CAST_HANDBACK_GUARD_R19: self-heal the Web Live handback.
  // Whatever tears the fresh local player down in the first seconds after
  // Stop Cast (the [chromecast-r19] warnings above name it), bring Web Live
  // back once through the normal fMP4 path instead of leaving a dead
  // player -- unless the user paused, closed, switched source or cast again.
  const checkLiveHandbackHealth = (handbackAt, handbackStreamUrl) => {
    if (castLiveHandbackAtRef.current !== handbackAt) return;
    if (castLiveHandbackHealedRef.current) return;
    const store = useVideoStore.getState();
    const video = videoRef.current;
    if (
      !video ||
      !store.isVisible ||
      store.contentType !== 'live' ||
      store.streamUrl !== handbackStreamUrl ||
      dispatcharrPlayerClosingRef.current ||
      castActiveRef.current ||
      castHandoffInProgressRef.current ||
      dispatcharrCastRuntime.active ||
      liveWebFmp4TransitionRef.current ||
      reconnectTimeoutRef.current ||
      liveUserPauseAtRef.current >= handbackAt
    ) {
      return;
    }
    const suspended = transportSuspendedKindRef.current === 'live';
    const detached = !playerRef.current || !video.currentSrc;
    if (!suspended && !detached) return;
    castLiveHandbackHealedRef.current = true;
    console.warn(
      `[chromecast-r19] Web Live died ${Date.now() - handbackAt}ms after Stop Cast handback ` +
        `(player=${playerRef.current ? 'present' : 'gone'} ` +
        `src=${video.currentSrc ? 'set' : 'empty'} suspended=${suspended}); restarting Web Live`
    );
    transportSuspendedKindRef.current = 'live';
    void resumeSuspendedTransport();
  };

  const armLiveHandbackGuard = (handbackStreamUrl) => {
    const handbackAt = Date.now();
    castLiveHandbackAtRef.current = handbackAt;
    castLiveHandbackPauseResumedRef.current = false;
    castLiveHandbackHealedRef.current = false;
    [1500, 3500, 6000, 9000, 13000, 18000].forEach((delay) => {
      window.setTimeout(
        () => checkLiveHandbackHealth(handbackAt, handbackStreamUrl),
        delay
      );
    });
  };

  // DISPATCHARR_CAST_TRANSITION_OPS_R134: Stop Casting is an op as well. In
  // the middle of Continue Live / Rewind on Cast there is no settled cast to
  // hand back yet: end the cast and continue that very content in the Web
  // player (it used to hand back a half-switched archive -> "Archive session
  // could not be resumed"). Otherwise the regular teardown runs and
  // supersedes any loader that is still in flight.
  const stopChromecast = async (options = {}) => {
    const { resumeLocal = true, preserveLocal = false } = options || {};
    setCastReturningToLive(false);
    const transition = castOpInFlight(['rewind-on-cast', 'continue-live']);
    if (
      transition &&
      resumeLocal &&
      !preserveLocal &&
      castContextRef.current?.getCurrentSession?.()
    ) {
      await handBackCastTransition({
        mode: transition.kind === 'continue-live' ? 'live' : 'timeshift',
        url: transition.target?.url || null,
        metadata: transition.target?.metadata || null,
        reason: `Stop Casting during ${transition.kind}`,
        supersededOp: transition,
      });
      return;
    }
    const stopOp = beginCastOp('stop');
    try {
      return await stopChromecastNow({ ...(options || {}), stopOp });
    } finally {
      settleCastOp(stopOp);
    }
  };

  const stopChromecastNow = async ({
    resumeLocal = true,
    preserveLocal = false,
    reason = 'stop-casting', // DISPATCHARR_CAST_RELEASE_REASON_R133
    stopOp = null, // DISPATCHARR_CAST_TRANSITION_OPS_R134
  } = {}) => {
    // A newer action (Continue Live / Rewind locally, a new Cast) owns the Web
    // player once it supersedes this Stop: the handback below then stands down.
    const stopSuperseded = () => Boolean(stopOp) && castOpRef.current !== stopOp;
    const context = castContextRef.current;
    const kind =
      dispatcharrCastRuntime.kind ||
      (contentType === 'catchup' || contentType === 'catchup-hls' ? 'archive' : 'live');

    // DISPATCHARR_ARCHIVE_R2_SEEK_HANDBACK_FIXES
    // A deliberate Stop immediately after a Cast seek waits for that exact seek
    // transaction to finish. Never release generation ownership while the
    // producer/receiver is between suspend, resume, prewarm and CAF reload.
    if (kind === 'archive' && resumeLocal && !preserveLocal) {
      const seekSettled = await waitForArchiveCastSeekBeforeHandback();
      if (!seekSettled) {
        throw new Error(
          'Archive Cast seek is still settling; Stop Casting handback was deferred'
        );
      }
    }

    const archivePosition = Math.max(
      0,
      Number(dispatcharrCastRuntime.archivePosition) ||
        (Number(dispatcharrCastRuntime.archiveBasePosition) +
          Number(castRemotePlayerRef.current?.currentTime || 0)) ||
        0
    );
    const archiveHandbackMediaUrl =
      kind === 'archive' ? dispatcharrCastRuntime.mediaUrl : null;
    const archiveController =
      archiveCastControllerRef.current || dispatcharrCastRuntime.archiveController;
    const rawProfile =
      dispatcharrCastRuntime.adaptiveProfile || castAdaptiveProfileRef.current || '';
    const normalProfile = String(rawProfile).replace(/^cast-/, '');
    const sameProfileResumeUrl =
      castResumeUrlRef.current ||
      dispatcharrCastRuntime.resumeUrl ||
      buildNormalLiveHandbackUrl(normalProfile);

    beginDispatcharrCastIntentionalHandback();
    castHandoffInProgressRef.current = true;
    castLiveHandbackAtRef.current = 0; // DISPATCHARR_CAST_HANDBACK_GUARD_R19
    releaseParkedLiveLease('Stop Cast'); // DISPATCHARR_CAST_LIVE_WARM_DURING_REWIND_R21
    castActiveRef.current = false;
    setCastActive(false);
    setCastDeviceName('');

    if (kind === 'live') {
      try { dispatcharrCastRuntime.bootstrapController?.abort?.(); } catch {}
      dispatcharrCastRuntime.bootstrapController = null;
      // Stop periodic lease renewals, but intentionally KEEP the lease/URLs until
      // the normal Web client has reattached to this same OutputProfileManager.
      await awaitCastOperation(
        stopCastSessionLease(false),
        1000,
        'Cast lease renewal pause',
        true
      );
    } else if (kind === 'archive' && archiveController) {
      await awaitCastOperation(
        archiveController.stop(),
        3500,
        'Archive Cast v2 stop',
        false
      );
    } else if (kind === 'archive') {
      await awaitCastOperation(
        stopCastSessionLease(true, reason),
        3500,
        'Archive HLS Cast lease release',
        false
      );
    }

    try { context?.endCurrentSession(true); } catch (error) {
      console.debug('[chromecast-r8.8.50] endCurrentSession skipped:', error);
    }

    castRemoteControllerRef.current = null;
    castRemotePlayerRef.current = null;

    let liveResumed = false;
    let liveResumeError = null;
    if (
      kind === 'live' &&
      resumeLocal &&
      !preserveLocal &&
      isVisible &&
      sameProfileResumeUrl &&
      !stopSuperseded() // DISPATCHARR_CAST_TRANSITION_OPS_R134
    ) {
      try {
        transportSuspendedKindRef.current = null;
        setTransportSuspendedKind(null);
        livePlaybackStartedRef.current = false;
        setLoadError(null);
        setIsLoading(true);
        safeDestroyPlayer();
        await initializeLivePlayer(false, sameProfileResumeUrl);
        // DISPATCHARR_CAST_STOP_HANDBACK_WAIT_R15: do not release the Cast
        // lease until the local player is really running. initializeLivePlayer
        // resolves before its MPEG-TS request has reached the server, and the
        // release below used to beat it -- the server then saw no viewer. The
        // wait is bounded, so a player that never starts cannot hold the
        // lease; castHandoffInProgressRef stays true throughout, which keeps
        // syncCastState from treating the closing session as a failure.
        const handbackDeadline = Date.now() + 8000;
        while (!livePlaybackStartedRef.current && Date.now() < handbackDeadline) {
          await new Promise((resolve) => window.setTimeout(resolve, 150));
        }
        liveResumed = true;
        console.info('[chromecast-r8.8.45] Stop Cast handback -> local web player started');
        console.info(
          `[chromecast-r8.8.50] Stop Cast handback -> same Web profile=${normalProfile} ` +
            '(same FFmpeg retained)'
        );
      } catch (error) {
        liveResumeError = error;
        console.warn('[chromecast-r8.8.50] same-profile Web handback failed:', error);
      }
    }

    if (kind === 'live') {
      // Local HTTP is attached (normal case) before this removes the Redis Cast
      // ownership. If no local handback was requested, release immediately and
      // normal zero-client cleanup stops FFmpeg + removes its /tmp spool.
      await awaitCastOperation(
        stopCastSessionLease(true),
        3500,
        'Cast lease release',
        false
      );
    }

    archiveCastControllerRef.current = null;
    clearDispatcharrCastRuntime();

    if (resumeLocal && isVisible) {
      if (kind === 'archive' && contentType === 'catchup-hls') {
        // DISPATCHARR_ARCHIVE_RECOVERY_CAST_HANDBACK_REUSE
        // Stop Casting is an ownership transfer, not a new playback request.
        // CAF is gone and the sender lease is released, but the isolated HLS
        // producer remains alive. Reattach Web to that exact cast_pipeline.
        const handbackUrl = buildArchiveCastWebHandbackUrl(
          archiveHandbackMediaUrl,
          archivePosition
        );
        archiveSuspendedPositionRef.current = archivePosition;
        archivePlaybackStartedRef.current = false;
        transportSuspendedKindRef.current = 'archive';
        setTransportSuspendedKind('archive');

        if (handbackUrl) {
          archiveLocalRuntimeUrlRef.current = handbackUrl;
          archiveLeaseSuspendedRef.current = false;
          console.info(
            `[chromecast-r8.8.48] Stop Cast -> Web reusing Archive producer ` +
              `position=${archivePosition.toFixed(2)}s pipeline=${new URL(handbackUrl).searchParams.get('cast_pipeline')}`
          );
          // DISPATCHARR_ARCHIVE_R2_SEEK_HANDBACK_FIXES
          // Lease release has already transferred generation ownership to Web.
          // Before HLS.js attaches, prove that the inherited pipeline serves a
          // manifest and at least the first referenced fragment. This removes
          // the narrow post-seek handback window that produced fragLoadTimeOut.
          let handbackReady = await waitForArchiveWebHandbackReady(
            handbackUrl,
            archivePosition
          );
          if (!handbackReady) {
            console.warn(
              '[archive-r2] inherited Cast pipeline not ready; refreshing same-pipeline producer before Web attach'
            );
            const restarted = await requestArchiveResume(handbackUrl, archivePosition);
            if (restarted) {
              handbackReady = await waitForArchiveWebHandbackReady(
                handbackUrl,
                archivePosition
              );
            }
          }
          if (stopSuperseded()) {
            // DISPATCHARR_CAST_TRANSITION_OPS_R134: a newer action owns the
            // Web player now (e.g. Continue Live right after Stop).
            console.info('[chromecast-r134] Stop Cast archive handback superseded; standing down');
          } else if (!handbackReady) {
            // DISPATCHARR_CAST_HANDBACK_FRESH_FALLBACK_R134: the inherited cast
            // pipeline never served the target fragment (its producer stalled;
            // seen once in testing). This used to end in the error "Archive Web
            // handback pipeline did not become fragment-ready" and a stuck
            // player. Drop that pipeline and resume the same position with a
            // fresh Web producer instead -- slower, but no dead end.
            console.warn(
              '[chromecast-r134] inherited Cast pipeline not fragment-ready; ' +
                'resuming with a fresh Web producer'
            );
            stopArchiveCastPipelineExact(archiveHandbackMediaUrl);
            archiveLocalRuntimeUrlRef.current = null;
            archiveLeaseSuspendedRef.current = true;
            await resumeSuspendedTransport();
          } else {
            await resumeSuspendedTransport({ reuseRunningArchiveProducer: true });
          }
        } else {
          archiveLocalRuntimeUrlRef.current = null;
          archiveLeaseSuspendedRef.current = true;
          console.warn(
            '[chromecast-r8.8.48] Stop Cast handback has no reusable pipeline; falling back to Web resume'
          );
          if (!stopSuperseded()) await resumeSuspendedTransport();
        }
      } else if (kind === 'archive' && contentType === 'catchup') {
        archiveSuspendedPositionRef.current = archivePosition;
        archiveLeaseSuspendedRef.current = false;
        archivePlaybackStartedRef.current = false;
        archiveSegmentStartMediaTimeRef.current = null;
        transportSuspendedKindRef.current = null;
        setTransportSuspendedKind(null);
        setArchiveOffsetSeconds(Math.floor(archivePosition));
        setArchivePositionSeconds(archivePosition);
        setArchivePaused(false);
        setLoadError(null);
        setIsLoading(true);
        requestArchivePing(streamUrl);
        safeDestroyPlayer();
        await initializeLivePlayer(true, null, Math.floor(archivePosition));
      } else if (
        kind === 'live' &&
        contentType === 'live' &&
        !preserveLocal &&
        !liveResumed
      ) {
        // Fail-open fallback only. Normal r8.8.50 never measures again; this is
        // used solely when the persisted same-profile URL is unavailable/broken.
        try {
          await resumeLiveWebAfterChromecast();
          liveResumed = true;
          console.warn(
            '[chromecast-r8.8.50] used emergency fresh-profile Web fallback after same-profile handback failure'
          );
        } catch (fallbackError) {
          setCastError(
            fallbackError?.message || liveResumeError?.message || String(fallbackError)
          );
        }
      }
    } else if (!preserveLocal) {
      safeDestroyPlayer();
    }

    if (!stopSuperseded()) castHandoffInProgressRef.current = false;
    if (!context?.getCurrentSession?.()) finishDispatcharrCastIntentionalHandback();

    // DISPATCHARR_CAST_HANDBACK_GUARD_R19
    if (
      kind === 'live' &&
      liveResumed &&
      resumeLocal &&
      !preserveLocal &&
      isVisible &&
      !stopSuperseded()
    ) {
      armLiveHandbackGuard(streamUrl);
    }
  };

  const recoverFailedChromecast = async (
    reason,
    { programmeFinished = false } = {} // DISPATCHARR_ARCHIVE_CAST_PROGRAMME_END_R133
  ) => {
    // DISPATCHARR_CAST_X_CLOSE_NO_HANDBACK_R8_8_53
    if (dispatcharrPlayerClosingRef.current) {
      castActiveRef.current = false;
      castHandoffInProgressRef.current = false;
      setCastActive(false);
      setCastDeviceName('');
      clearDispatcharrCastRuntime();
      try { castContextRef.current?.endCurrentSession?.(true); } catch {}
      console.info('[chromecast-r8.8.53] stale Cast recovery ignored during X close');
      return;
    }
    // DISPATCHARR_CAST_TRANSITION_OPS_R134: while a transition is switching a
    // LIVE session, a receiver state of the old/new item is not a TV-side
    // stop -- that op owns the outcome (it hands back to the Web player if it
    // cannot finish). Only a session that is really gone gets R109.
    {
      const transition = castOpInFlight([
        'start',
        'rewind-on-cast',
        'continue-live',
        'handback',
        'snapshot-refresh', // R135
      ]);
      if (
        transition &&
        (transition.kind === 'handback' ||
          (!programmeFinished && castContextRef.current?.getCurrentSession?.()))
      ) {
        console.info(
          `[chromecast-r134] recovery deferred during ${transition.kind} #${transition.seq}: ${reason}`
        );
        return;
      }
    }
    if (castFailureRecoveryRef.current) return;
    setCastReturningToLive(false);
    // R109: nothing resumes, so a loading overlay raised by an interrupted
    // transition would read as a hang next to the label.
    setIsLoading(false);
    // DISPATCHARR_LIVE_CAST_RECOVERY_COOLDOWN_MODULE_LEVEL
    if (Date.now() < castRecoveryCooldownUntil) {
      console.debug(
        `[chromecast-r8.8.47] recovery cooldown active, ignoring stale duplicate (${reason})`
      );
      return;
    }
    castFailureRecoveryRef.current = true;
    const kind =
      dispatcharrCastRuntime.kind ||
      (contentType === 'catchup' || contentType === 'catchup-hls' ? 'archive' : 'live');
    const lastArchivePosition = Math.max(
      0,
      Number(dispatcharrCastRuntime.archivePosition) ||
        Number(castArchiveSeekHealthRef.current.target) ||
        (Number(dispatcharrCastRuntime.archiveBasePosition) +
          Number(castRemotePlayerRef.current?.currentTime || 0)) ||
        0
    );

    // DISPATCHARR_CAST_TV_STOP_KILLS_EVERYTHING_R109/R109b: when the cast
    // ends because the receiver itself went away (killed from the TV/STB,
    // TV powered off, another app launched, receiver IDLE, CAF session gone)
    // the user's rule is "do not continue anywhere" -- for BOTH live and
    // archive: stop the server side (live lease release -> zero-client
    // cleanup stops the tee; archive controller stop ends its generation) and
    // resume NO local player. A deliberate Stop Casting pressed in the Web
    // Player never lands here -- it calls stopChromecast({resumeLocal:true})
    // directly and still hands back to local playback.
    if (programmeFinished) {
      console.info(`[chromecast-r133] ${reason}; ending the Cast cleanly`);
    } else {
      console.warn(
        `[chromecast-r8.8.47] receiver unhealthy: ${reason}; ` +
          'stopping everything (receiver gone)'
      );
    }
    // DISPATCHARR_ARCHIVE_CAST_PROGRAMME_END_R133: nothing resumes the cast
    // runtime after this, so stop its producer and free its spool now instead
    // of leaving both to the 30 s activity reaper and the session cleanup.
    const castRuntimeStopUrl =
      kind === 'archive' && contentType === 'catchup-hls'
        ? buildArchiveStopUrl(dispatcharrCastRuntime.mediaUrl)
        : null;

    // Drop the UI lock synchronously. Even if CAF/backend cleanup is slow,
    // Watch Live and the local player are immediately usable again.
    beginDispatcharrCastIntentionalHandback();
    castActiveRef.current = false;
    castHandoffInProgressRef.current = true;
    setCastActive(false);
    setCastDeviceName('');
    setCastError(null);
    if (kind === 'archive') {
      archiveSuspendedPositionRef.current = lastArchivePosition;
    }

    try {
      await awaitCastOperation(
        stopChromecast({
          resumeLocal: false,
          reason: programmeFinished ? 'programme-finished' : reason,
        }),
        11000,
        'automatic Cast failure teardown',
        false
      );
    } catch (error) {
      console.warn('[chromecast-r8.8.47] automatic handback exception:', error);
    } finally {
      // Absolute fail-safe. stopChromecast normally performs all of this, but
      // a broken receiver must never retain global Dispatcharr ownership.
      try { castContextRef.current?.endCurrentSession?.(true); } catch {}
      castRemoteControllerRef.current = null;
      castRemotePlayerRef.current = null;
      clearDispatcharrCastRuntime();
      castActiveRef.current = false;
      castHandoffInProgressRef.current = false;
      setCastActive(false);
      setCastDeviceName('');
      castArchiveSeekHealthRef.current = { inProgress: false, startedAt: 0, target: 0 };
      castHealthRef.current = {
        state: '',
        bufferingSince: 0,
        lastPosition: 0,
        lastProgressAt: Date.now(),
      };
      finishDispatcharrCastIntentionalHandback();
      // DISPATCHARR_LIVE_CAST_REARM_HANDBACK_FENCE_ON_COMPLETION: the fence just
      // closed above protects nothing during the settling period that
      // follows -- exactly where a late/stale CAF session event can
      // re-arm castActive from this same torn-down session. Re-open it
      // fresh from this point (not just from recovery's start).
      beginDispatcharrCastIntentionalHandback();
      castFailureRecoveryRef.current = false;
      // DISPATCHARR_LIVE_CAST_RECOVERY_COOLDOWN_MODULE_LEVEL
      castRecoveryCooldownUntil = Date.now() + 15000;
      // DISPATCHARR_CAST_TV_STOP_KILLS_EVERYTHING_R109/R109b: tell the user
      // the cast was stopped and nothing was resumed, so the empty player
      // does not read as a hang.
      if (castRuntimeStopUrl) {
        fetch(castRuntimeStopUrl, {
          method: 'POST',
          cache: 'no-store',
          credentials: 'same-origin',
          keepalive: true,
        }).catch(() => {});
      }
      setCastError(
        programmeFinished
          ? ARCHIVE_CAST_FINISHED_NOTICE
          : 'Cast stopped on the device. Playback was not resumed.'
      );
      console.info('[chromecast-r8.8.47] Cast failure ownership fully cleared');
    }
  };

  // DISPATCHARR_SENDER_LIVENESS_ORPHAN_FFMPEG_GUARD_R8_8_52
  const dispatcharrSendBeaconPost = (url) => {
    if (!url) return false;
    try {
      if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
        return navigator.sendBeacon(
          url,
          new Blob([], { type: 'text/plain' })
        );
      }
      void fetch(url, {
        method: 'POST',
        keepalive: true,
        cache: 'no-store',
        credentials: 'same-origin',
      });
      return true;
    } catch {
      return false;
    }
  };

  // DISPATCHARR_UNIFIED_CAST_LEASE_NO_WEB_HEARTBEATS_R8_8_54
  const buildDispatcharrLiveReopenUrl = (preferredProfile = null) => {
    const channelId = liveChannelIdFromUrl(streamUrl);
    if (!channelId) return null;
    const reopenUrl = new URL(
      `/proxy/ts/live-reopen/${encodeURIComponent(channelId)}/start`,
      window.location.origin
    );
    // DISPATCHARR_WEB_LIVE_TUNING_PER_PROFILE_R34
    // The adaptive profile is chosen from this client's measured speed, and a
    // 4 Mbps link needs a far wider live-sync band than a 20 Mbps one. Pass it
    // to the gate so the server answers with the band for this tier.
    try {
      // streamUrl is the bare channel URL; the profile is only attached later
      // by appendAdaptiveOutputProfile(), so prefer the one it last used.
      const sourceUrl = new URL(streamUrl, window.location.origin);
      const adaptiveProfile =
        preferredProfile ||
        sourceUrl.searchParams.get('adaptive_output_profile') ||
        getLastAdaptiveOutputProfile();
      if (adaptiveProfile) {
        reopenUrl.searchParams.set('adaptive_output_profile', adaptiveProfile);
      }
    } catch {
      // Without it the server simply falls back to the flat web_live values.
    }
    return reopenUrl.toString();
  };

  // DISPATCHARR_UNIFIED_CAST_LEASE_NO_WEB_HEARTBEATS_R8_8_54: page exit is an explicit stop; visibility changes remain harmless.
  useEffect(() => {
    dispatcharrPageExitHandledRef.current = false;
    const onDispatcharrPageExit = () => {
      if (dispatcharrPageExitHandledRef.current) return;
      dispatcharrPageExitHandledRef.current = true;

      if (castActiveRef.current || dispatcharrCastRuntime.active) {
        const kind = dispatcharrCastRuntime.kind;
        if (dispatcharrCastRuntime.leaseTimer != null) {
          window.clearInterval(dispatcharrCastRuntime.leaseTimer);
          dispatcharrCastRuntime.leaseTimer = null;
        }
        if (kind === 'live') {
          const stopUrl = dispatcharrCastRuntime.stopUrl || castStopUrlRef.current;
          if (stopUrl) dispatcharrSendBeaconPost(stopUrl);
        } else if (kind === 'archive') {
          const controller =
            archiveCastControllerRef.current ||
            dispatcharrCastRuntime.archiveController;
          void controller?.stop?.({ keepalive: true }).catch(() => undefined);
          const stopUrl = dispatcharrCastRuntime.stopUrl || castStopUrlRef.current;
          if (stopUrl) dispatcharrSendBeaconPost(stopUrl);
          // DISPATCHARR_ARCHIVE_RECOVERY_CAST_X_IMMEDIATE_STOP
          const activeArchiveCastUrl =
            dispatcharrCastRuntime.mediaUrl ||
            archiveLocalRuntimeUrlRef.current ||
            streamUrl;
          console.info(
            '[chromecast-r8.8.48] page exit -> stopping active Archive Cast producer'
          );
          requestArchiveStop(activeArchiveCastUrl, { beacon: true });
        }
        try { castContextRef.current?.endCurrentSession?.(true); } catch {}
        console.info('[chromecast-r8.8.54] page exit -> stopping active Cast session');
        return;
      }

      if (contentType === 'catchup' || contentType === 'catchup-hls') {
        requestArchiveStop(
          archiveLocalRuntimeUrlRef.current || streamUrl,
          { beacon: true }
        );
      }
    };

    window.addEventListener('pagehide', onDispatcharrPageExit);
    window.addEventListener('beforeunload', onDispatcharrPageExit);
    return () => {
      window.removeEventListener('pagehide', onDispatcharrPageExit);
      window.removeEventListener('beforeunload', onDispatcharrPageExit);
    };
  }, [streamUrl, contentType]);

  const handleChromecastToggle = async (event) => {
    dispatcharrPlayerClosingRef.current = false;
    event?.stopPropagation?.();
    event?.preventDefault?.();
    setCastError(null);
    let mediaLoadStarted = false;
    try {
      const sdkReady = await loadDispatcharrCastSdk();
      if (!sdkReady) {
        throw new Error('Google Cast Web Sender is not available in this browser');
      }
      const context =
        castContextRef.current || window.cast.framework.CastContext.getInstance();
      castContextRef.current = context;

      // DISPATCHARR_CAST_TRANSITION_OPS_R134: a press while a Cast start is
      // still loading must not start a second, parallel load.
      if (castOpInFlight(['start']) && context.getCurrentSession()) {
        console.info('[chromecast-r134] Cast press ignored: the Cast start is still loading');
        return;
      }
      const transitionInFlight = castOpInFlight(['rewind-on-cast', 'continue-live']);
      if (
        castActiveRef.current ||
        dispatcharrCastRuntime.active ||
        (transitionInFlight && context.getCurrentSession())
      ) {
        // DISPATCHARR_LIVE_PAUSE_CHOICE_R136: a paused Live cast hands back paused.
        if (!transitionInFlight && (await stopLiveCastIntoPausedWeb())) return;
        await stopChromecast({ resumeLocal: true });
        return;
      }
      // A press right after Stop waits until that teardown has ended the OLD
      // session (its endCurrentSession() would otherwise end the NEW one) --
      // only that long, and at most 3 s: requestSession() below still needs
      // this click's user activation (~5 s in Chrome).
      {
        const teardownDeadline = Date.now() + 3000;
        while (
          castOpInFlight(['stop', 'handback', 'close']) &&
          context.getCurrentSession() &&
          Date.now() < teardownDeadline
        ) {
          await new Promise((resolve) => window.setTimeout(resolve, 100));
        }
      }

      // DISPATCHARR_CAST_INTENTIONAL_HANDBACK_R8_8_45: a deliberate NEW Cast press supersedes an old handback fence.
      finishDispatcharrCastIntentionalHandback();
      let session = context.getCurrentSession();
      if (!session) {
        // DISPATCHARR_CAST_PICKER_REENTRY_R124: while the device picker is
        // already open, a second icon press stops here. In windowed mode the
        // press's own mousedown is what dismisses the bubble (Chrome closes a
        // toolbar bubble on an outside click); the pending requestSession then
        // rejects with CANCEL and the catch below returns silently, so the
        // press reads as "hide the picker". The page cannot close it any other
        // way (see castPickerPendingRef).
        // DISPATCHARR_CAST_PICKER_TRAP_R126: that same press can also arrive
        // just after the CANCEL settled -- never let it reopen the picker.
        if (castPickerGuardActive()) return;
        castPickerPendingRef.current = true;
        castPickerOpenedAtRef.current = Date.now();
        // DISPATCHARR_CAST_PICKER_TRAP_R126: leave stage fullscreen BEFORE the
        // picker opens. Chrome 155+ forces this exit itself and refuses
        // requestFullscreen while the picker is up; on Chrome <= 154 (macOS)
        // the bubble drawn over fullscreen content could not be dismissed by a
        // click. Anchored to the visible toolbar it closes on an outside click
        // like any toolbar bubble. exitFullscreen() needs no activation and
        // does not consume the click's activation, which requestSession()
        // (PresentationRequest.start()) still requires.
        // DISPATCHARR_CAST_PICKER_NO_FS_RESTORE_R130: the player stays in the
        // floating window afterwards -- a dismissed picker no longer re-enters
        // fullscreen (user's choice: one click anywhere just hides the menu;
        // fullscreen comes back only from the player's own ⛶ button).
        if (typeof document !== 'undefined') {
          const fullscreenEl =
            document.fullscreenElement || document.webkitFullscreenElement;
          const video = videoRef.current;
          if (fullscreenEl && video && fullscreenEl.contains(video)) {
            try {
              if (document.exitFullscreen) {
                await document.exitFullscreen();
              } else if (document.webkitExitFullscreen) {
                document.webkitExitFullscreen();
              }
            } catch {
              // Still fullscreen: the picker opens over the video as before.
            }
          }
        }
        try {
          await context.requestSession();
        } finally {
          castPickerPendingRef.current = false;
          castPickerSettledAtRef.current = Date.now();
        }
        session = context.getCurrentSession();
      }
      if (!session) throw new Error('No Chromecast session was selected');

      // DISPATCHARR_CAST_TRANSITION_OPS_R134: dispatch on the CURRENT content.
      // The picker can stay open while the user rewinds or returns to Live;
      // the click-time closure then cast the wrong thing (or a stopped
      // session). A local switch that is mid-flight finishes first.
      {
        const switchDeadline = Date.now() + 5000;
        while (
          (liveTimeshiftReturningToLiveRef.current || liveTimeshiftStartingRef.current) &&
          Date.now() < switchDeadline
        ) {
          await new Promise((resolve) => window.setTimeout(resolve, 100));
        }
      }
      const latest = castLatestRef.current || {};
      const latestContentType = latest.contentType || contentType;
      const startOp = beginCastOp('start', { sourceUrl: latest.streamUrl || streamUrl });
      // A superseded Rewind on Cast / Continue Live (X, Stop with the TV gone,
      // a channel switch) can leave the recovery guard up; a new cast starts
      // clean, or its TV-side stop would never be recovered.
      castFailureRecoveryRef.current = false;
      mediaLoadStarted = true;
      try {
        if (latestContentType === 'catchup-hls') {
          await (latest.loadArchive || loadArchiveOnChromecast)(session, { op: startOp });
        } else if (latestContentType === 'catchup') {
          await (latest.loadDirectArchive || loadDirectArchiveOnChromecast)(session);
        } else {
          await (latest.loadLive || loadLiveOnChromecast)(session, undefined, {
            op: startOp,
          });
        }
      } catch (startError) {
        if (isCastSuperseded(startError) || !castOpIsCurrent(startOp)) {
          unwindArchiveCastOp(startOp);
          const superseded = new Error('Cast start superseded');
          superseded.castSuperseded = true;
          throw superseded;
        }
        throw startError;
      } finally {
        settleCastOp(startOp);
      }
    } catch (error) {
      // DISPATCHARR_CAST_TRANSITION_OPS_R134: a superseded start unwound
      // itself; the action that superseded it (X, a source change) owns the
      // cleanup -- never end its session or release its leases from here.
      if (isCastSuperseded(error)) {
        console.info('[chromecast-r134] Cast start superseded; cleanup belongs to the newer action');
        return;
      }
      castHandoffInProgressRef.current = false;
      // DISPATCHARR_CAST_PICKER_CANCEL_R124: CAF rejects a dismissed device
      // picker with the bare chrome.cast.ErrorCode.CANCEL code ('cancel'), a
      // string, not an Error. Closing the picker is the user changing their
      // mind, not a failure -- never park that raw code in the red error box.
      // Only the pre-load stage can mean a picker dismissal (mediaLoadStarted
      // flips true right after a session exists), so a mid-load CANCEL still
      // takes the full cleanup path below.
      const castCancelCode = window.chrome?.cast?.ErrorCode?.CANCEL || 'cancel';
      if (
        !mediaLoadStarted &&
        (error === castCancelCode ||
          error?.code === castCancelCode ||
          error?.message === castCancelCode)
      ) {
        console.debug('[chromecast-r124] Cast device picker dismissed by user');
        // DISPATCHARR_CAST_PICKER_NO_FS_RESTORE_R130: R126 re-entered stage
        // fullscreen here on a quick cancel; by request the dismissal now just
        // hides the menu and the player stays windowed.
        return;
      }
      const message = error?.message || String(error);
      console.warn('[chromecast-r8.8.35] Cast failed:', error);
      setCastError(message);
      if (mediaLoadStarted) {
        try { dispatcharrCastRuntime.bootstrapController?.abort?.(); } catch {}
        if (contentType === 'catchup') {
          const controller =
            archiveCastControllerRef.current ||
            dispatcharrCastRuntime.archiveController;
          void controller?.stop?.().catch(() => undefined);
          archiveCastControllerRef.current = null;
        } else {
          await stopCastSessionLease(true);
        }
        clearDispatcharrCastRuntime();
        // A failed Live handoff never crossed the CAF-ready ownership
        // boundary, so the original local Web player is still the fallback.
        // Do not destroy it while cleaning the failed Cast session.
        try { castContextRef.current?.endCurrentSession(true); } catch {}
      }
    }
  };

  useEffect(() => {
    const castCapableContent =
      contentType === 'live' ||
      contentType === 'catchup' ||
      contentType === 'catchup-hls';
    if (!isVisible || !castCapableContent) {
      setCastAvailable(false);
      setCastSdkReady(false);
      return undefined;
    }

    let cancelled = false;
    let context = null;
    let castStateListener = null;
    let sessionStateListener = null;

    loadDispatcharrCastSdk().then((sdkReady) => {
      if (!sdkReady || cancelled) {
        if (!cancelled) setCastSdkReady(false);
        return;
      }
      setCastSdkReady(true);
      context = window.cast.framework.CastContext.getInstance();
      castContextRef.current = context;
      context.setOptions({
        receiverApplicationId:
          window.chrome.cast.media.DEFAULT_MEDIA_RECEIVER_APP_ID,
        autoJoinPolicy: window.chrome.cast.AutoJoinPolicy.ORIGIN_SCOPED,
        // DISPATCHARR_CAST_NO_SESSION_RESUME_R113: without this the framework
        // resumes a "saved" session after the receiver was unreachable -- turn
        // the TV back on and the dead cast reappears ("Casting to...") and
        // re-enables the tee. We drive the whole cast lifecycle explicitly, so
        // never let CAF resurrect a session on its own.
        resumeSavedSession: false,
      });

      const syncCastState = () => {
        const state = context.getCastState();
        setCastAvailable(
          state !== window.cast.framework.CastState.NO_DEVICES_AVAILABLE
        );
        const session = context.getCurrentSession();

        // DISPATCHARR_CAST_INTENTIONAL_HANDBACK_R8_8_45: intentional Stop-Cast handback is NOT a receiver failure.
        // While CAF is draining the old session, never rehydrate Cast ownership
        // from its stale contentId and never route SESSION_ENDED through the
        // unexpected-disconnect path that destroys the local web player.
        if (isDispatcharrCastIntentionalHandback()) {
          castActiveRef.current = false;
          setCastActive(false);
          setCastDeviceName('');
          if (!session) {
            finishDispatcharrCastIntentionalHandback();
            console.info('[chromecast-r8.8.45] intentional handback session closed');
          }
          return;
        }

        // DISPATCHARR_CAST_TRANSITION_OPS_R134: while a Cast transition is
        // switching the TV, its media is in flux (old item, LOAD, INTERRUPTED).
        // The op owns the outcome: never rehydrate, adopt a lease or recover
        // from that intermediate state here.
        if (
          session &&
          castOpInFlight([
            'start',
            'rewind-on-cast',
            'continue-live',
            'handback',
            'snapshot-refresh', // R135
          ])
        ) {
          return;
        }

        if (session) {
          const mediaSession = session.getMediaSession?.();
          const contentId =
            mediaSession?.media?.contentId || mediaSession?.media?.contentUrl || '';
          if (isDispatcharrCastContent(contentId)) {
            const kind =
              contentId.includes('/archive-cast-media-v2/') ||
              contentId.includes('/proxy/catchup-hls/')
                ? 'archive'
                : 'live';
            const device = session.getCastDevice?.();
            dispatcharrCastRuntime.active = true;
            dispatcharrCastRuntime.kind = kind;
            dispatcharrCastRuntime.sourceUrl ||= streamUrl;
            // createCastMediaInfo adds cast_master=1 for CAF only; the runtime
            // media URL must stay the media playlist that prewarm and the
            // Stop-Casting handback probe parse for EXTINF/EXT-X-MAP.
            try {
              const runtimeMediaUrl = new URL(String(contentId), window.location.origin);
              runtimeMediaUrl.searchParams.delete('cast_master');
              dispatcharrCastRuntime.mediaUrl = runtimeMediaUrl.toString();
            } catch {
              dispatcharrCastRuntime.mediaUrl = String(contentId);
            }
            dispatcharrCastRuntime.deviceName =
              device?.friendlyName || dispatcharrCastRuntime.deviceName || 'Chromecast';
            castActiveRef.current = true;
            castHandoffInProgressRef.current = false;
            setCastActive(true);
            setCastDeviceName(dispatcharrCastRuntime.deviceName);
            transportSuspendedKindRef.current = null;
            setTransportSuspendedKind(null);
            safeDestroyPlayer();

            // Rebuild RemotePlayer refs after a route/tab remount as well. This
            // keeps Stop Archive handback anchored to the receiver's CURRENT
            // position instead of the position from the original Cast press.
            if (!castRemotePlayerRef.current) {
              try {
                const remotePlayer = new window.cast.framework.RemotePlayer();
                const remoteController =
                  new window.cast.framework.RemotePlayerController(remotePlayer);
                castRemotePlayerRef.current = remotePlayer;
                castRemoteControllerRef.current = remoteController;
              } catch {}
            }
            if (kind === 'archive') {
              const relativePosition = Number(
                castRemotePlayerRef.current?.currentTime
              );
              dispatcharrCastRuntime.archivePosition = Math.max(
                0,
                Number.isFinite(relativePosition)
                  ? Number(dispatcharrCastRuntime.archiveBasePosition || 0) +
                      relativePosition
                  : Number(dispatcharrCastRuntime.archivePosition) || 0
              );
            }

            // DISPATCHARR_CAST_PARKED_LEASE_NOT_ORPHAN_R31: a null leaseTimer
            // used to mean "nothing is renewing this channel, adopt it". Since
            // R21 it can also mean "the Live lease is parked while the TV plays
            // the rewind" — and adopting then resurrected that very lease into
            // the active slot, giving it a second renew timer (two renews in
            // the same second in the access log) until the archive flow
            // released "the current lease" and stopped the Live channel
            // mid-rewind. Continue Live then had to cold-start the tee, which
            // is what made the TV flap on a 10-segment playlist. A parked lease
            // is not an orphan.
            if (
              dispatcharrCastRuntime.leaseTimer == null &&
              !castParkedLiveLeaseRef.current
            ) {
              try {
                if (kind === 'live') {
                  const media = new URL(String(contentId), window.location.origin);
                  const base = media.pathname.replace(/\/(?:master|index)\.m3u8$/, '');
                  const castSession =
                    media.searchParams.get('cast_session') || dispatcharrCastRuntime.castSessionId;
                  const renew = new URL(`${base}/renew`, window.location.origin);
                  const stop = new URL(`${base}/stop`, window.location.origin);
                  if (castSession) {
                    renew.searchParams.set('cast_session', castSession);
                    stop.searchParams.set('cast_session', castSession);
                    dispatcharrCastRuntime.castSessionId = castSession;
                  }
                  const routeProfile = base.split('/').filter(Boolean).pop() || null;
                  if (routeProfile) {
                    const normalProfile = String(routeProfile).replace(/^cast-/, '');
                    const restoredTransport = buildLiveCastTransportUrls(
                      dispatcharrCastRuntime.resumeUrl || streamUrl,
                      normalProfile,
                      window.location.origin
                    );
                    if (restoredTransport) {
                      castResumeUrlRef.current = restoredTransport.resumeUrl;
                      dispatcharrCastRuntime.resumeUrl = restoredTransport.resumeUrl;
                      dispatcharrCastRuntime.bootstrapUrl =
                        restoredTransport.requiresBootstrap
                          ? restoredTransport.bootstrapUrl
                          : null;
                    }
                    dispatcharrCastRuntime.adaptiveProfile = normalProfile;
                  }
                  void startCastSessionLease(renew.toString(), stop.toString()).catch(() => undefined);
                } else if (kind === 'archive') {
                  archiveCastControllerRef.current =
                    dispatcharrCastRuntime.archiveController || null;
                }
              } catch {}
            }
            return;
          }
        }

        // DISPATCHARR_CAST_FAILSAFE_ARCHIVE_RESEEK_R8_8_47: a surviving CAF session shell is not enough to own
        // Dispatcharr. IDLE or missing/non-Dispatcharr media is unhealthy once
        // runtime ownership had already been established.
        if (session && dispatcharrCastRuntime.active) {
          const mediaSession = session.getMediaSession?.();
          const contentId =
            mediaSession?.media?.contentId || mediaSession?.media?.contentUrl || '';
          const playerState = String(
            castRemotePlayerRef.current?.playerState || mediaSession?.playerState || ''
          );
          const idleState = window.chrome?.cast?.media?.PlayerState?.IDLE || 'IDLE';
          const runtimeKind = dispatcharrCastRuntime.kind;
          const seekGrace =
            castArchiveSeekHealthRef.current.inProgress &&
            Date.now() - castArchiveSeekHealthRef.current.startedAt < 60000;
          const postSeekGrace =
            runtimeKind === 'archive' &&
            castArchiveSeekHealthRef.current.completedAt > 0 &&
            Date.now() - castArchiveSeekHealthRef.current.completedAt < 15000;
          // DISPATCHARR_ARCHIVE_REMOTE_SCRUB_STATE_GRACE_R8_14_13
          // CAF can report IDLE while the virtual remote scrub is holding the
          // existing MediaSession at its safe anchor. The health watchdog has
          // the same grace; keep this state listener from recovering first.
          const remoteSeekObserver = castArchiveRemoteSeekObserverRef.current;
          const remoteScrubGrace =
            runtimeKind === 'archive' &&
            contentType === 'catchup' &&
            (remoteSeekObserver.debounceTimer != null ||
              remoteSeekObserver.candidatePosition !== null ||
              remoteSeekObserver.pending ||
              Date.now() < (Number(remoteSeekObserver.suppressUntil) || 0));
          // DISPATCHARR_ARCHIVE_REMOTE_SCRUB_EMPTY_MEDIA_GRACE_R8_14_14
          const idleDuringRemoteScrubGrace =
            playerState === idleState && remoteScrubGrace;
          // DISPATCHARR_ARCHIVE_CAST_PROGRAMME_END_R133: same rule as the health
          // tick -- the programme ending is not a receiver failure.
          if (
            playerState === idleState &&
            runtimeKind === 'archive' &&
            !seekGrace &&
            // R133a review: 3 s stale-media guard, same as the health tick.
            (!castArchiveSeekHealthRef.current.completedAt ||
              Date.now() - castArchiveSeekHealthRef.current.completedAt > 3000) &&
            !remoteScrubGrace &&
            archiveCastFinishedOnReceiver(
              mediaSession,
              castHealthRef.current.lastPosition
            )
          ) {
            // DISPATCHARR_LIVE_TIMESHIFT_CAST_CATCHUP_TO_LIVE_R135
            if (castLiveTimeshiftToLive('the TV reached the end of the time-shift')) {
              return;
            }
            void recoverFailedChromecast('archive programme finished on the TV', {
              programmeFinished: true,
            });
            return;
          }
          if (
            ((!contentId && !idleDuringRemoteScrubGrace) ||
              (Boolean(contentId) && !isDispatcharrCastContent(contentId)) ||
              (playerState === idleState && !remoteScrubGrace)) &&
            !seekGrace &&
            !postSeekGrace
          ) {
            void recoverFailedChromecast(
              !contentId
                ? 'CAF session has no media'
                : playerState === idleState
                  ? 'CAF receiver became IDLE'
                  : 'CAF media is no longer Dispatcharr'
            );
            return;
          }
        }

        // DISPATCHARR_CAST_STOP_HANDBACK_WAIT_R15: the intentional-handback
        // fence above is cleared by the FIRST session-less callback, and CAF
        // delivers several while the old session closes. The next one used to
        // fall through to this unexpected-disconnect path and destroy the
        // local player the handback had just started. A handback in progress
        // owns teardown itself.
        if (
          !session &&
          !castHandoffInProgressRef.current &&
          (castActiveRef.current || dispatcharrCastRuntime.active)
        ) {
          if (dispatcharrCastRuntime.kind === 'archive') {
            void recoverFailedChromecast('Archive Cast session disconnected');
            return;
          }
          const cleanupBootstrapUrl = dispatcharrCastRuntime.bootstrapUrl;
          const cleanupWasDedicated =
            String(dispatcharrCastRuntime.adaptiveProfile || '').startsWith('cast-');
          void (async () => {
            if (dispatcharrCastRuntime.kind === 'archive') {
              const controller =
                archiveCastControllerRef.current ||
                dispatcharrCastRuntime.archiveController;
              await awaitCastOperation(
                controller?.stop?.() || Promise.resolve(true),
                3500,
                'Disconnected Archive Cast stop',
                false
              );
              archiveCastControllerRef.current = null;
            } else {
              await awaitCastOperation(
                stopCastSessionLease(true),
                3500,
                'Disconnected Cast lease release',
                false
              );
            }
            // DISPATCHARR_SAME_PROFILE_LIVE_TEE_REMOTE_SCRUB_R8_8_50: cleanup pulse exists only for legacy dedicated sessions.
            if (cleanupWasDedicated && cleanupBootstrapUrl) {
              await awaitCastOperation(
                pulseCastProfileCleanup(cleanupBootstrapUrl),
                2500,
                'Disconnected Cast profile cleanup pulse',
                false
              );
            }
          })();
          // DISPATCHARR_CAST_STOP_DETERMINISTIC_TEARDOWN_R8_8_44
          clearDispatcharrCastRuntime();
          castActiveRef.current = false;
          castHandoffInProgressRef.current = false;
          setCastActive(false);
          setCastDeviceName('');
          safeDestroyPlayer();
          setCastError('Chromecast disconnected. Start playback again locally if needed.');
        }
      };

      castStateListener = syncCastState;
      sessionStateListener = syncCastState;
      context.addEventListener(
        window.cast.framework.CastContextEventType.CAST_STATE_CHANGED,
        castStateListener
      );
      context.addEventListener(
        window.cast.framework.CastContextEventType.SESSION_STATE_CHANGED,
        sessionStateListener
      );
      syncCastState();
    });

    return () => {
      cancelled = true;
      if (context && castStateListener) {
        context.removeEventListener(
          window.cast.framework.CastContextEventType.CAST_STATE_CHANGED,
          castStateListener
        );
      }
      if (context && sessionStateListener) {
        context.removeEventListener(
          window.cast.framework.CastContextEventType.SESSION_STATE_CHANGED,
          sessionStateListener
        );
      }
      // Do NOT stop the global lease/timer on route component unmount.
    };
  }, [isVisible, contentType, streamUrl]);

  // DISPATCHARR_CAST_FAILSAFE_ARCHIVE_RESEEK_R8_8_47
  useEffect(() => {
    if (!castActive || !isVisible) return undefined;
    let cancelled = false;

    const tick = () => {
      if (cancelled || castFailureRecoveryRef.current) return;
      // DISPATCHARR_LIVE_CAST_TICK_RESPECTS_INTENTIONAL_HANDBACK: an intentional
      // Stop-Cast handback (recoverFailedChromecast itself, or the user's
      // own Stop Casting) legitimately ends the CAF session on purpose.
      // This poll must not treat that expected, still-settling absence
      // as a brand new failure -- mirror the same fence syncCastState()
      // (the CAST_STATE_CHANGED listener above) already respects.
      if (isDispatcharrCastIntentionalHandback()) return;
      const context = castContextRef.current;
      const session = context?.getCurrentSession?.();
      if (!session) {
        void recoverFailedChromecast('Cast session disappeared');
        return;
      }

      const mediaSession = session.getMediaSession?.();
      const remotePlayer = castRemotePlayerRef.current;
      const state = String(
        remotePlayer?.playerState || mediaSession?.playerState || ''
      );
      const now = Date.now();
      const health = castHealthRef.current;
      const seekHealth = castArchiveSeekHealthRef.current;
      // DISPATCHARR_ARCHIVE_FMP4_CAST_WINDOW_R2
      const position =
        dispatcharrCastRuntime.kind === 'archive'
          ? archiveCastAbsolutePosition(remotePlayer)
          : Math.max(
              0,
              Number(remotePlayer?.currentTime) ||
                Number(dispatcharrCastRuntime.archivePosition) ||
                0
            );

      if (position > health.lastPosition + 0.25) {
        health.lastPosition = position;
        health.lastProgressAt = now;
      }

      const PLAYING = window.chrome?.cast?.media?.PlayerState?.PLAYING || 'PLAYING';
      const PAUSED = window.chrome?.cast?.media?.PlayerState?.PAUSED || 'PAUSED';
      const BUFFERING = window.chrome?.cast?.media?.PlayerState?.BUFFERING || 'BUFFERING';
      const IDLE = window.chrome?.cast?.media?.PlayerState?.IDLE || 'IDLE';

      // DISPATCHARR_ARCHIVE_REMOTE_SCRUB_HEALTH_GRACE_R8_14_9
      // While the hardware remote is still scrubbing, r8.14.8 intentionally
      // fences receiver target segment GETs with retryable 503 until the sender
      // performs the one trailing transactional seek. CAF may transiently
      // report BUFFERING or IDLE during that collection window. Treating IDLE
      // as fatal here races the 1500ms trailing commit and hands playback back
      // to Web before commitArchiveCastSeek() can run.
      const remoteSeekObserver = castArchiveRemoteSeekObserverRef.current;
      const remoteScrubGrace =
        contentType === 'catchup' &&
        dispatcharrCastRuntime.kind === 'archive' &&
        (
          remoteSeekObserver.debounceTimer != null ||
          remoteSeekObserver.candidatePosition !== null ||
          remoteSeekObserver.pending ||
          now < (Number(remoteSeekObserver.suppressUntil) || 0)
        );

      if (remoteScrubGrace) {
        console.debug('[chromecast-r8.14.9] remote IDLE during hardware scrub is grace');
        health.bufferingSince = 0;
        health.lastProgressAt = now;
        health.lastPosition = position;
      } else if (state === PLAYING || state === PAUSED) {
        health.bufferingSince = 0;
        health.lastProgressAt = now;
        if (state === PLAYING) health.lastPosition = position;
      } else if (state === BUFFERING) {
        if (!health.bufferingSince) health.bufferingSince = now;
        const limit = seekHealth.inProgress ? 60000 : 25000;
        if (
          now - health.bufferingSince > limit &&
          now - health.lastProgressAt > limit
        ) {
          void recoverFailedChromecast(
            seekHealth.inProgress
              ? 'Archive seek remained BUFFERING'
              : 'Chromecast remained BUFFERING'
          );
          return;
        }
      } else {
        health.bufferingSince = 0;
      }

      // DISPATCHARR_ARCHIVE_CAST_PROGRAMME_END_R133: IDLE/FINISHED is the TV
      // reaching the programme's end, not a dead receiver: every archive cast
      // that played to its end used to close with the receiver-gone label.
      // Not held back by the post-seek grace -- a seek near the end finishes
      // inside it.
      if (
        state === IDLE &&
        dispatcharrCastRuntime.kind === 'archive' &&
        !seekHealth.inProgress &&
        // R133a review: for 1-3 s after a seek reload the session can still
        // expose the OLD item's FINISHED; the 5 s end guard means a genuine
        // finish lands >=5 s after completion, so 3 s costs nothing.
        (!seekHealth.completedAt || now - seekHealth.completedAt > 3000) &&
        !remoteScrubGrace &&
        archiveCastFinishedOnReceiver(mediaSession, health.lastPosition)
      ) {
        // DISPATCHARR_LIVE_TIMESHIFT_CAST_CATCHUP_TO_LIVE_R135
        if (castLiveTimeshiftToLive('the TV reached the end of the time-shift')) {
          return;
        }
        void recoverFailedChromecast('archive programme finished on the TV', {
          programmeFinished: true,
        });
        return;
      }

      // IDLE is expected while collecting a hardware-remote scrub because the
      // read-only receiver fence temporarily withholds the far-away media.
      // It is also expected during the controlled hard-reload itself.
      if (state === IDLE) {
        const seekGrace =
          (seekHealth.inProgress && now - seekHealth.startedAt < 60000) ||
          (seekHealth.completedAt > 0 && now - seekHealth.completedAt < 15000);
        if (!seekGrace && !remoteScrubGrace) {
          const idleReason = mediaSession?.idleReason || 'unknown';
          // DISPATCHARR_LIVE_TIMESHIFT_CAST_CATCHUP_TO_LIVE_R135: a time-shift
          // receiver that errors out next to the aired edge ran into segments
          // that have not aired (remote fast-forward): continue with Live.
          if (
            String(idleReason) ===
              (window.chrome?.cast?.media?.IdleReason?.ERROR || 'ERROR') &&
            castLatestRef.current?.metadata?.liveTimeshift &&
            liveTimeshiftCastAiredSeconds() - health.lastPosition < 30 &&
            castLiveTimeshiftToLive('the TV hit the live edge')
          ) {
            return;
          }
          // R135 review: a snapshot refresh that failed on the receiver.
          if (
            castLatestRef.current?.metadata?.liveTimeshift &&
            now - (Number(liveTimeshiftCastRef.current.refreshFailedAt) || 0) < 60000 &&
            (String(idleReason) === 'unknown' ||
              String(idleReason) ===
                (window.chrome?.cast?.media?.IdleReason?.ERROR || 'ERROR')) &&
            castLiveTimeshiftToLive('the snapshot refresh failed')
          ) {
            return;
          }
          void recoverFailedChromecast(`receiver IDLE (${idleReason})`);
          return;
        }
      }

      if (
        dispatcharrCastRuntime.active &&
        !seekHealth.inProgress &&
        !remoteScrubGrace &&
        mediaSession &&
        !isDispatcharrCastContent(
          mediaSession?.media?.contentId || mediaSession?.media?.contentUrl || ''
        )
      ) {
        void recoverFailedChromecast('receiver media ownership changed');
        return;
      }

      health.state = state;
    };

    tick();
    const timer = window.setInterval(tick, 500);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [castActive, isVisible, contentType, streamUrl]);

  // Only a real source URL change ends an existing Cast. A component remount on
  // another Dispatcharr tab has the same sourceUrl and must remain "Casting".
  useEffect(() => {
    // DISPATCHARR_CAST_TRANSITION_OPS_R134: Watch Live / another Archive while
    // a Cast start is still loading for the previous source -- that start must
    // not commit over the new local stream (it destroyed the new player and
    // left the TV on the old channel). Supersede it and end its session, as
    // the established-cast branch below does for a committed cast.
    {
      const pendingStart = castOpInFlight(['start']);
      if (
        isVisible &&
        pendingStart &&
        pendingStart.target?.sourceUrl &&
        pendingStart.target.sourceUrl !== streamUrl
      ) {
        settleCastOp(beginCastOp('stop', { reason: 'source-changed' }));
        beginDispatcharrCastIntentionalHandback();
        castHandoffInProgressRef.current = false;
        castFailureRecoveryRef.current = false;
        {
          const abandonedStop = dispatcharrCastRuntime.stopUrl || castStopUrlRef.current;
          if (dispatcharrCastRuntime.leaseTimer != null) {
            window.clearInterval(dispatcharrCastRuntime.leaseTimer);
            dispatcharrCastRuntime.leaseTimer = null;
          }
          dispatcharrCastRuntime.renewUrl = null;
          dispatcharrCastRuntime.stopUrl = null;
          castRenewUrlRef.current = null;
          castStopUrlRef.current = null;
          if (abandonedStop) postCastStop(abandonedStop, 'source-changed');
          if (pendingStart.archiveCastMediaUrl) {
            stopArchiveCastPipelineExact(pendingStart.archiveCastMediaUrl);
          }
        }
        castActiveRef.current = false;
        setCastActive(false);
        setCastDeviceName('');
        try {
          castContextRef.current?.endCurrentSession?.(true);
        } catch {}
        console.info('[chromecast-r134] Cast start abandoned: the source changed');
        return;
      }
    }
    if (!dispatcharrCastRuntime.active || !isVisible) return;
    if (
      dispatcharrCastRuntime.sourceUrl &&
      dispatcharrCastRuntime.sourceUrl !== streamUrl
    ) {
      // DISPATCHARR_CAST_FAILSAFE_ARCHIVE_RESEEK_R8_8_47: Watch Live/new Archive is authoritative user intent. Do not
      // let stale Cast ownership block or later destroy the new local stream.
      beginDispatcharrCastIntentionalHandback();
      castActiveRef.current = false;
      setCastActive(false);
      setCastDeviceName('');
      setCastError(null);
      void stopChromecast({
        resumeLocal: false,
        preserveLocal: true,
        reason: 'source-changed', // R133a review: not a user Stop Casting
      }).catch(
        (error) =>
          console.warn('[chromecast-r8.8.47] stale Cast source cleanup failed:', error)
      );
    }
  }, [streamUrl]);

  // DISPATCHARR_CAST_REMOTE_REWIND_R135: TV-remote rewind during a Live cast.
  // CAF turns the remote's rewind key (and Left on TVs without the D-pad UI;
  // on D-pad TVs the preview commits on OK) into an immediate SEEK -10 s inside
  // the ~21 s live window and broadcasts the new status to every sender. The
  // sender never sees a key -- only the receiver's position going DOWN within
  // the same live media, which nothing else does: playback raises it, pause and
  // buffering hold it, R104 and fast-forward only move it up. One press, or a
  // burst, enters Rewind on Cast exactly like the web Rewind button. Reads come
  // from the media session's own estimate (computed at call time); the
  // RemotePlayer copy is refreshed by a timer a hidden tab throttles.
  useEffect(() => {
    if (!castActive || !isVisible || contentType !== 'live') return undefined;
    const effectStreamUrl = streamUrl;
    const PlayerState = window.chrome?.cast?.media?.PlayerState || {};
    const PLAYING = PlayerState.PLAYING || 'PLAYING';
    const IDLE = PlayerState.IDLE || 'IDLE';
    const RemoteEvents = window.cast?.framework?.RemotePlayerEventType || {};
    const watchedEvents = [
      RemoteEvents.CURRENT_TIME_CHANGED || 'currentTimeChanged',
      RemoteEvents.PLAYER_STATE_CHANGED || 'playerStateChanged',
    ];
    const clearOwnNotice = () =>
      setCastRewindingOnTv((value) => (value === 'tv-remote' ? null : value));
    let base = null;
    let cand = null;
    let key = '';
    let keyPlayingAt = 0;
    let fired = false;
    let boundController = null;
    let boundMedia = null;
    let disposed = false;

    const gateOpen = () => {
      const latest = castLatestRef.current || {};
      return Boolean(
        latest.contentType === 'live' &&
          latest.streamUrl === effectStreamUrl &&
          latest.liveRewindAvailable &&
          (castActiveRef.current || dispatcharrCastRuntime.active) &&
          dispatcharrCastRuntime.kind === 'live' &&
          !castOpInFlight() &&
          !castFailureRecoveryRef.current &&
          !castHandoffInProgressRef.current &&
          !liveTimeshiftStartingRef.current &&
          !liveTimeshiftReturningToLiveRef.current &&
          !castReturningToLiveRef.current &&
          !isDispatcharrCastIntentionalHandback() &&
          !dispatcharrPlayerClosingRef.current &&
          castContextRef.current?.getCurrentSession?.()
      );
    };

    const readMedia = () => {
      const media = castContextRef.current?.getCurrentSession?.()?.getMediaSession?.();
      if (!media || typeof media.getEstimatedTime !== 'function') return null;
      const contentId = String(media.media?.contentId || media.media?.contentUrl || '');
      // Only this tab's own channel: another tab may have joined the cast
      // while showing a different one.
      const castChannel = /\/proxy\/ts\/cast-hls\/([^/?#]+)\//.exec(contentId);
      if (!castChannel) return null;
      let castSession = null;
      try {
        if (
          decodeURIComponent(castChannel[1]) !==
          String(liveChannelIdFromUrl(effectStreamUrl) || '')
        ) {
          return null;
        }
        castSession = new URL(contentId, window.location.origin).searchParams.get(
          'cast_session'
        );
      } catch {
        return null;
      }
      let range = null;
      try {
        range =
          (typeof media.getEstimatedLiveSeekableRange === 'function'
            ? media.getEstimatedLiveSeekableRange()
            : null) ||
          media.liveSeekableRange ||
          castRemotePlayerRef.current?.liveSeekableRange ||
          null;
      } catch {
        range = null;
      }
      return {
        media,
        key: `${media.mediaSessionId ?? ''}|${contentId}`,
        t: Number(media.getEstimatedTime()),
        state: String(media.playerState || ''),
        range,
        // this tab loaded the Live the TV plays (only the loader acts on a press)
        owner: Boolean(castSession) && castSession === dispatcharrCastRuntime.ownLiveCastSession,
      };
    };

    const clearCandidate = () => {
      if (cand?.timer != null) window.clearTimeout(cand.timer);
      // R135 review: a candidate dropped on a media change / cleanup must not
      // leave "Rewinding on <TV>..." up (confirm() nulls cand before a start).
      if (cand) clearOwnNotice();
      cand = null;
    };

    const confirm = () => {
      if (!cand || disposed) return;
      const pressed = cand;
      cand = null;
      const now = Date.now();
      const sample = readMedia();
      const reject = (reason) => {
        console.info(`[chromecast-r135] TV remote rewind not taken: ${reason}`);
        clearOwnNotice();
      };
      if (!sample || sample.key !== pressed.key) {
        reject('the TV media changed');
        return;
      }
      if (!gateOpen()) {
        reject('a Cast transition is running');
        return;
      }
      const wouldBe = pressed.wouldBeAtFirst + (now - pressed.firstAt) / 1000;
      if (wouldBe - sample.t < 4) {
        reject('the playhead came back (a glitch, not a press)');
        return;
      }
      const end = Number(sample.range?.end);
      const behind = Number.isFinite(end) && end > 0 ? end - sample.t : null;
      if (behind != null && behind < CAST_REMOTE_REWIND_MIN_BEHIND_EDGE_SECONDS) {
        reject('the TV is not behind the live edge');
        return;
      }
      fired = true;
      const rewind = Math.max(
        30,
        Math.ceil((behind ?? 25) + CAST_REMOTE_REWIND_EXTRA_SECONDS)
      );
      console.info(
        `[chromecast-r135] TV remote rewind -> Rewind on Cast ${rewind}s ` +
          `(${behind == null ? '?' : behind.toFixed(1)}s behind the receiver edge)`
      );
      const run = async () => {
        let started = false;
        try {
          started = Boolean(
            await castLatestRef.current?.startLiveProviderTimeshift?.(rewind, {
              origin: 'tv-remote',
            })
          );
        } catch {
          started = false;
        }
        if (!started) {
          fired = false;
          clearOwnNotice();
        }
        return started;
      };
      // Only the loader of this Live gets here (observe) and its cast_session
      // is unique: one press, one rewind -- no cross-tab lock needed.
      void run();
    };

    const observe = () => {
      if (disposed) return;
      const rc = castRemoteControllerRef.current;
      if (rc && rc !== boundController) {
        for (const type of watchedEvents) {
          try {
            boundController?.removeEventListener?.(type, observe);
          } catch {
            // the old controller went away with its media
          }
        }
        boundController = null;
        try {
          for (const type of watchedEvents) rc.addEventListener(type, observe);
          boundController = rc;
        } catch {
          boundController = null;
        }
      }
      const sample = readMedia();
      if (sample?.media && sample.media !== boundMedia) {
        try {
          boundMedia?.removeUpdateListener?.(observe);
        } catch {
          // ignore
        }
        boundMedia = null;
        try {
          sample.media.addUpdateListener?.(observe);
          boundMedia = sample.media;
        } catch {
          boundMedia = null;
        }
      }
      if (!sample) {
        base = null;
        return;
      }
      const now = Date.now();
      if (sample.key !== key) {
        key = sample.key;
        base = null;
        clearCandidate();
        keyPlayingAt = 0;
        fired = false;
      }
      if (sample.state === PLAYING && !keyPlayingAt) keyPlayingAt = now;
      if (!Number.isFinite(sample.t) || !(sample.t > 0) || !sample.state || sample.state === IDLE) {
        base = null;
        return;
      }
      if (!base) {
        if (sample.state === PLAYING) base = { t: sample.t, at: now, playing: true };
        return;
      }
      // Where playback would be now: it only advances while PLAYING.
      const expected = base.t + (base.playing ? (now - base.at) / 1000 : 0);
      const drop = expected - sample.t;
      base = { t: sample.t, at: now, playing: sample.state === PLAYING };
      if (drop < CAST_REMOTE_REWIND_MIN_DROP_SECONDS) return;
      const rangeStart = Number(sample.range?.start);
      if (Number.isFinite(rangeStart) && sample.t < rangeStart - 5) return;
      castLiveRemoteRewindRef.current.backwardSeenAt = now;
      console.info(
        `[chromecast-r135] TV remote rewind seen: ${expected.toFixed(1)} -> ` +
          `${sample.t.toFixed(1)} (drop ${drop.toFixed(1)}s)`
      );
      if (fired) return;
      // Only the tab that loaded this Live on the TV: a tab that merely joined
      // the cast (another tab, profile or device started it) leaves the press
      // to that sender -- two senders acting made two rewinds.
      if (!sample.owner) {
        console.info('[chromecast-r135] TV remote rewind ignored: another sender owns this cast');
        return;
      }
      if (!keyPlayingAt || now - keyPlayingAt < CAST_REMOTE_REWIND_ARM_PLAYING_MS) {
        console.info('[chromecast-r135] TV remote rewind ignored: the TV has only just started playing');
        return;
      }
      if (!gateOpen()) {
        console.info('[chromecast-r135] TV remote rewind ignored: a Cast transition is running');
        return;
      }
      if (!cand) {
        cand = { key: sample.key, firstAt: now, lastAt: now, wouldBeAtFirst: expected, timer: null };
        setCastRewindingOnTv('tv-remote');
      }
      cand.lastAt = now;
      if (cand.timer != null) window.clearTimeout(cand.timer);
      cand.timer = null;
      if (typeof document !== 'undefined' && document.hidden) {
        confirm(); // timers are throttled in a hidden tab
        return;
      }
      const due = Math.min(
        cand.lastAt + CAST_REMOTE_REWIND_CONFIRM_MS,
        cand.firstAt + CAST_REMOTE_REWIND_SETTLE_MAX_MS
      );
      cand.timer = window.setTimeout(confirm, Math.max(0, due - now));
    };

    observe();
    const poll = window.setInterval(observe, 500);
    return () => {
      disposed = true;
      window.clearInterval(poll);
      clearCandidate();
      for (const type of watchedEvents) {
        try {
          boundController?.removeEventListener?.(type, observe);
        } catch {
          // ignore
        }
      }
      try {
        boundMedia?.removeUpdateListener?.(observe);
      } catch {
        // ignore
      }
    };
  }, [castActive, isVisible, contentType, streamUrl]);

  // DISPATCHARR_LIVE_PAUSE_CHOICE_R136: mirror a paused Live cast (web pause or
  // TV remote) into livePause {where:'tv'} -- transitions only (R122). Display
  // only: a TV-remote pause or play is never acted upon (R135 ownership).
  useEffect(() => {
    const dropTvRecord = (why) => {
      if (livePauseRef.current?.where !== 'tv') return;
      console.info(`[live-pause-r136] TV pause cleared (${why})`);
      setLivePause(null);
    };
    if (!castActive || !isVisible || contentType !== 'live') {
      dropTvRecord('not a Live cast');
      setCastLiveReadable(false);
      return undefined;
    }
    const effectStreamUrl = streamUrl;
    // R136 review: the state this run last saw, so a pause is only dated and
    // attributed when its transition was actually observed.
    let lastSeenState = null;
    const RemoteEvents = window.cast?.framework?.RemotePlayerEventType || {};
    const watchedEvents = [
      RemoteEvents.PLAYER_STATE_CHANGED || 'playerStateChanged',
      RemoteEvents.IS_PAUSED_CHANGED || 'isPausedChanged',
    ];
    let boundController = null;
    let disposed = false;
    const unbind = () => {
      for (const type of watchedEvents) {
        try {
          boundController?.removeEventListener?.(type, observe);
        } catch {
          // gone with its media
        }
      }
      boundController = null;
    };
    function observe() {
      if (disposed) return;
      const rc = castRemoteControllerRef.current;
      if (rc && rc !== boundController) {
        unbind();
        try {
          for (const type of watchedEvents) rc.addEventListener(type, observe);
          boundController = rc;
        } catch {
          boundController = null;
        }
      }
      if (dispatcharrCastRuntime.kind !== 'live') {
        setCastLiveReadable(false);
        return; // a switch owns the screen
      }
      const current = livePauseRef.current;
      if (current?.where === 'local' && current.busy === 'stopping') return; // paused handback
      const sample = readLiveCastMedia(effectStreamUrl);
      setCastLiveReadable(Boolean(sample));
      if (!sample) return;
      const seenBefore = lastSeenState;
      lastSeenState = sample.state;
      if (sample.state !== CAST_PAUSED) {
        if (current?.where === 'tv') {
          console.info(`[live-pause-r136] TV left PAUSED (${sample.state || 'none'})`);
          setLivePause(null);
        }
        return;
      }
      if ((current?.where === 'tv' && current.key === sample.key) || !Number.isFinite(sample.t)) {
        return;
      }
      const now = Date.now();
      const end = Number(sample.range?.end);
      const start = Number(sample.range?.start);
      const hasRange = Number.isFinite(end) && end > 0;
      const behind = hasRange
        ? Math.max(0, end - sample.t)
        : DISPATCHARR_CAST_LIVE_EDGE_HOLDBACK_SECONDS;
      const frameInstantMs = now - (behind + CAST_REMOTE_REWIND_EXTRA_SECONDS) * 1000;
      const press = castLivePressRef.current;
      const byWeb = Boolean(press && press.action === 'pause' && now - press.at < 6000);
      const sawPause = seenBefore === 'PLAYING' || seenBefore === 'BUFFERING';
      const tvBufferUntilMs =
        hasRange && Number.isFinite(start)
          ? now + (sample.t - start - CAST_LIVE_PAUSE_TV_BUFFER_MARGIN_SECONDS) * 1000
          : 0;
      setLivePause({
        where: 'tv',
        streamUrl: effectStreamUrl,
        key: sample.key,
        owner: sample.owner,
        pausedAtMs: byWeb ? press.at : sawPause ? now : NaN,
        frameInstantMs,
        approx: !hasRange,
        by: byWeb ? 'web' : sawPause ? 'tv-remote' : 'unknown',
        busy: null,
        tHeld: sample.t,
        tvBufferUntilMs,
      });
      console.info(
        `[live-pause-r136] TV paused (${byWeb ? 'web' : sawPause ? 'tv-remote' : 'unknown'}) held=${sample.t.toFixed(1)} ` +
          `behind=${behind.toFixed(1)}s frame_at=${new Date(frameInstantMs).toISOString()} ` +
          `owner=${sample.owner ? 1 : 0} ` +
          `tv_buffer=${Math.max(0, Math.round((tvBufferUntilMs - now) / 1000))}s`
      );
    }
    observe();
    const poll = window.setInterval(observe, 500);
    return () => {
      disposed = true;
      window.clearInterval(poll);
      unbind();
      dropTvRecord('cast view changed');
      setCastLiveReadable(false);
    };
  }, [castActive, isVisible, contentType, streamUrl]);

  // DISPATCHARR_ARCHIVE_CAST_REMUX_R8_19_0: permanently disable the old
  // Django-producer hardware-remote observer. It contained a second block after
  // an unconditional return and could race multiple producer reposition calls.
  useEffect(() => {
    if (
      !castActive ||
      contentType !== 'catchup-hls' ||
      dispatcharrCastRuntime.kind !== 'archive'
    ) {
      castArchiveScrubbingRef.current = false;
      // DISPATCHARR_ANDROID_TV_REMOTE_ARCHIVE_SEEK_R8_8_49
      if (castArchiveRemoteSeekObserverRef.current.debounceTimer != null) {
        window.clearTimeout(castArchiveRemoteSeekObserverRef.current.debounceTimer);
      }
      if (castArchiveRemoteSeekObserverRef.current.snapbackTimer != null) {
        window.clearTimeout(castArchiveRemoteSeekObserverRef.current.snapbackTimer);
      }
      castArchiveRemoteSeekObserverRef.current = {
        lastPosition: null,
        lastAt: 0,
        suppressUntil: 0,
        receiverLoadInProgress: false,
        pending: false,
        debounceTimer: null,
        candidatePosition: null,
        armDirection: 0,
        armCount: 0,
        armUntil: 0,
        pausedByRemoteScrub: false,
        resumeAfterRemoteScrub: true,
        scrubAnchorPosition: null,
        virtualTargetPosition: null,
        lastRawRemotePosition: null,
        snapbackTimer: null,
        ignoreAnchorUntil: 0,
      };
      setCastArchivePaused(false);
      return undefined;
    }

    // DISPATCHARR_ARCHIVE_CAST_PASSIVE_REMOTE_POSITION_R8_15_3
    // Default Media Receiver does not expose a hardware-key release event.
    // Never infer one from a timer: long key holds have multi-second gaps and
    // an inferred release would reload Cast at an intermediate position. Keep
    // remote seeking passive; explicit Web controls own transactional seeks.
    const syncPassiveArchiveCastTransport = () => {
      const remotePlayer = castRemotePlayerRef.current;
      if (!remotePlayer) return;
      const observer = castArchiveRemoteSeekObserverRef.current;
      const now = Date.now();
      // DISPATCHARR_ARCHIVE_FMP4_CAST_WINDOW_R2
      const position = archiveCastAbsolutePosition(remotePlayer);
      const relativeDuration = Math.max(0, Number(remotePlayer.duration) || 0);
      const basePosition = Math.max(
        0,
        Number(dispatcharrCastRuntime.archiveBasePosition) || 0
      );
      // DISPATCHARR_LIVE_TIMESHIFT_CAST_SNAPSHOT_AHEAD_R135: the receiver's
      // duration includes the not-yet-aired stretch; the Web bar (and its seek
      // clamp) only offers what has aired.
      const liveTimeshiftCast = Boolean(castLatestRef.current?.metadata?.liveTimeshift);
      const duration = liveTimeshiftCast
        ? Math.max(1, Math.floor(liveTimeshiftTimelineSeconds())) // R135 review: whole seconds
        : Math.max(
            Number(dispatcharrCastRuntime.archiveDuration) || 0,
            basePosition + relativeDuration
          );
      // DISPATCHARR_ARCHIVE_CAST_ABSOLUTE_DURATION_R131: while a Web seek reload
      // is in flight archiveBasePosition is still the OLD window's base but
      // RemotePlayer.duration can already be the NEW window's length; after a
      // backward seek their sum overstates the programme, and this max() would
      // keep that forever (it is the seek clamp now).
      const castSeekReloadInFlight = Boolean(
        castArchiveSeekHealthRef.current.inProgress ||
          observer.pending ||
          observer.receiverLoadInProgress
      );
      if (duration > 0 && !castSeekReloadInFlight) {
        dispatcharrCastRuntime.archiveDuration = duration;
        setCastArchiveDurationSeconds(duration);
      }

      // DISPATCHARR_ARCHIVE_RECOVERY_WEB_CAST_SEEK_CLOCK_FENCE
      // Explicit Web-player Cast seek is transactional: pause -> producer
      // reposition/prewarm -> CAF loadMedia(target) -> receiver ready. During
      // loadMedia() RemotePlayer.currentTime legitimately flashes to 0 while
      // the new media item is attaching. The 125 ms passive observer used to
      // publish that transient zero into dispatcharrCastRuntime/UI, racing the
      // explicit target and making a successful seek appear to snap to 0.
      //
      // Keep the committed target authoritative until commitArchiveCastSeek()
      // finishes. Hardware-remote collection is unchanged; this fence is only
      // active while the explicit Web seek/receiver load says it owns the clock.
      // DISPATCHARR_ARCHIVE_CAST_ABSOLUTE_TIMELINE_R131: with base 0 a receiver
      // still attaching (BUFFERING/IDLE at currentTime 0) has no programme
      // position yet -- keep the committed one instead of publishing 0:00.
      const passiveReceiverState = String(remotePlayer.playerState || '');
      const absoluteClockNotReady = Boolean(
        basePosition === 0 &&
          !(Number(remotePlayer.currentTime) > 0) &&
          Number(dispatcharrCastRuntime.archivePosition) > 1 &&
          passiveReceiverState !==
            (window.chrome?.cast?.media?.PlayerState?.PLAYING || 'PLAYING') &&
          passiveReceiverState !==
            (window.chrome?.cast?.media?.PlayerState?.PAUSED || 'PAUSED')
      );
      const explicitWebSeekOwnsClock = Boolean(
        castArchiveSeekHealthRef.current.inProgress ||
          observer.pending ||
          observer.receiverLoadInProgress ||
          absoluteClockNotReady
      );
      if (!explicitWebSeekOwnsClock) {
        observer.lastPosition = position;
        observer.lastAt = now;
        dispatcharrCastRuntime.archivePosition = position;
        if (!castArchiveScrubbingRef.current) {
          setCastArchivePositionSeconds(position);
        }
      }
      setCastArchivePaused(Boolean(remotePlayer.isPaused));
      // DISPATCHARR_LIVE_TIMESHIFT_CAST_CATCHUP_TO_LIVE_R135 /
      // DISPATCHARR_LIVE_TIMESHIFT_CAST_SNAPSHOT_AHEAD_R135
      if (
        liveTimeshiftCast &&
        !castSeekReloadInFlight &&
        !absoluteClockNotReady &&
        !castOpInFlight() &&
        !castFailureRecoveryRef.current
      ) {
        const receiverState = String(remotePlayer.playerState || '');
        const playingState =
          window.chrome?.cast?.media?.PlayerState?.PLAYING || 'PLAYING';
        const bufferingState =
          window.chrome?.cast?.media?.PlayerState?.BUFFERING || 'BUFFERING';
        // R135 review: only the absolute VOD snapshot has an ahead stretch to
        // guard and to refresh (the opt-out window timeline is an EVENT list
        // of aired segments with a window-relative clock).
        let absoluteReceiver = false;
        try {
          absoluteReceiver =
            new URL(dispatcharrCastRuntime.mediaUrl || '', window.location.origin).searchParams.get(
              'cast_timeline'
            ) === 'absolute';
        } catch {
          absoluteReceiver = false;
        }
        // The receiver's own estimate: a hidden tab's RemotePlayer clock lags.
        let tvPosition = position;
        if (absoluteReceiver) {
          try {
            const estimated = Number(
              castContextRef.current?.getCurrentSession?.()?.getMediaSession?.()?.getEstimatedTime?.()
            );
            if (Number.isFinite(estimated) && estimated > 0) tvPosition = basePosition + estimated;
          } catch {
            // keep the RemotePlayer position
          }
        }
        if (absoluteReceiver && tvPosition > 1) {
          const latestMeta = castLatestRef.current?.metadata || {};
          const sessionEnd = Math.max(
            Number(latestMeta.liveTimeshiftSessionDurationSeconds) || 0,
            (Number(latestMeta.durationMinutes) || 0) * 60
          );
          const airedNow = liveTimeshiftCastAiredSeconds();
          const lag = airedNow - tvPosition;
          const edgeMoving = !sessionEnd || airedNow < sessionEnd;
          const ltc = liveTimeshiftCastRef.current;
          if (lag >= LIVE_TIMESHIFT_CAST_EDGE_ARM_SECONDS) ltc.edgeArmed = true;
          if (
            edgeMoving &&
            (receiverState === playingState || receiverState === bufferingState) &&
            ltc.edgeArmed &&
            lag < LIVE_TIMESHIFT_CAST_LIVE_EDGE_SECONDS
          ) {
            castLiveTimeshiftToLive('the TV caught up with Live');
          } else if (receiverState === playingState) {
            const listedEnd = basePosition + relativeDuration;
            if (
              listedEnd > 0 &&
              listedEnd - tvPosition <= LIVE_TIMESHIFT_CAST_SNAPSHOT_REFRESH_LEAD_SECONDS &&
              (!sessionEnd || listedEnd < sessionEnd - 2) &&
              !ltc.refreshing &&
              now - (Number(ltc.lastLoadAt) || 0) >= LIVE_TIMESHIFT_CAST_REFRESH_MIN_INTERVAL_MS
            ) {
              void refreshLiveTimeshiftCastSnapshot(tvPosition);
            }
          }
        }
      }
    };

    // DISPATCHARR_LIVE_TIMESHIFT_CAST_SNAPSHOT_AHEAD_R135 (review): a tab hidden
    // for a while runs this interval about once a minute, but the receiver's
    // MEDIA_STATUS still arrives at once: re-evaluate on it, and continue with
    // Live the moment a time-shift snapshot plays out.
    let boundMediaSession = null;
    const onMediaStatus = () => {
      try {
        const mediaSession = castContextRef.current?.getCurrentSession?.()?.getMediaSession?.();
        const idle = window.chrome?.cast?.media?.PlayerState?.IDLE || 'IDLE';
        const finished = window.chrome?.cast?.media?.IdleReason?.FINISHED || 'FINISHED';
        const seekHealth = castArchiveSeekHealthRef.current;
        if (
          mediaSession &&
          String(mediaSession.playerState || '') === idle &&
          String(mediaSession.idleReason || '') === finished &&
          castLatestRef.current?.metadata?.liveTimeshift &&
          !seekHealth.inProgress &&
          (!seekHealth.completedAt || Date.now() - seekHealth.completedAt > 3000) &&
          !castOpInFlight() &&
          !castFailureRecoveryRef.current
        ) {
          castLiveTimeshiftToLive('the TV reached the end of the time-shift');
          return;
        }
      } catch {
        // fall through to the plain transport sync
      }
      syncPassiveArchiveCastTransport();
    };
    const bindMediaStatus = () => {
      if (!castLatestRef.current?.metadata?.liveTimeshift) return;
      const mediaSession = castContextRef.current?.getCurrentSession?.()?.getMediaSession?.();
      if (!mediaSession || mediaSession === boundMediaSession) return;
      try {
        boundMediaSession?.removeUpdateListener?.(onMediaStatus);
      } catch {
        // the old item went away
      }
      boundMediaSession = null;
      try {
        mediaSession.addUpdateListener?.(onMediaStatus);
        boundMediaSession = mediaSession;
      } catch {
        boundMediaSession = null;
      }
    };
    bindMediaStatus();
    syncPassiveArchiveCastTransport();
    const passiveTimer = window.setInterval(() => {
      bindMediaStatus();
      syncPassiveArchiveCastTransport();
    }, 125);
    return () => {
      window.clearInterval(passiveTimer);
      try {
        boundMediaSession?.removeUpdateListener?.(onMediaStatus);
      } catch {
        // ignore
      }
      boundMediaSession = null;
    };

    const syncArchiveCastTransport = () => {
      const remotePlayer = castRemotePlayerRef.current;
      if (!remotePlayer) return;
      const duration = Math.max(
        0,
        Number(remotePlayer.duration) ||
          Number(dispatcharrCastRuntime.archiveDuration) ||
          0
      );
      const position = Math.max(
        0,
        Number(remotePlayer.currentTime) ||
          Number(dispatcharrCastRuntime.archivePosition) ||
          0
      );
      if (duration > 0) {
        dispatcharrCastRuntime.archiveDuration = duration;
        setCastArchiveDurationSeconds(duration);
      }

      // DISPATCHARR_ARCHIVE_REMOTE_SEEK_SINGLE_OWNER_R8_14_6
      // Hardware remote changes CAF currentTime first. The receiver may issue
      // target segment GETs immediately, but Django fences those no-generation
      // requests from restarting the ONE producer. After one stable >4s jump,
      // the sender performs exactly ONE existing transactional hard seek.
      const remoteSeekObserver = castArchiveRemoteSeekObserverRef.current;
      const now = Date.now();
      const seekHealth = castArchiveSeekHealthRef.current;
      const lastAt = Number(remoteSeekObserver.lastAt) || 0;
      const elapsedMs = lastAt > 0 ? now - lastAt : 0;
      const hasBaseline =
        remoteSeekObserver.lastPosition !== null &&
        Number.isFinite(Number(remoteSeekObserver.lastPosition)) &&
        lastAt > 0;
      // DISPATCHARR_ARCHIVE_REMOTE_VIRTUAL_SCRUB_ANCHOR_R8_14_12
      // A long Android-TV hardware scrub changes the receiver currentTime before
      // the sender can reposition the on-demand Archive producer. Leaving the
      // receiver parked on those far-away positions eventually drives the same
      // MediaSession to IDLE even though Django correctly fences the requests.
      //
      // Treat hardware seek positions as INPUT only while scrubbing:
      //   1. pause the same Cast MediaSession once;
      //   2. accumulate the remote deltas into a virtual target;
      //   3. after each physical remote jump, seek the paused receiver back to a
      //      known-good anchor inside the current producer window;
      //   4. after 1500 ms idle, move the producer once to the virtual target,
      //      prewarm it, then seek/resume the SAME MediaSession there.
      const collectingRemoteScrub =
        remoteSeekObserver.debounceTimer != null &&
        remoteSeekObserver.virtualTargetPosition !== null &&
        Number.isFinite(Number(remoteSeekObserver.virtualTargetPosition));
      const remoteSeekSuppressed =
        seekHealth.inProgress ||
        remoteSeekObserver.pending ||
        now < (Number(remoteSeekObserver.suppressUntil) || 0);

      const PLAYING =
        window.chrome?.cast?.media?.PlayerState?.PLAYING || 'PLAYING';
      const PAUSED =
        window.chrome?.cast?.media?.PlayerState?.PAUSED || 'PAUSED';
      const BUFFERING =
        window.chrome?.cast?.media?.PlayerState?.BUFFERING || 'BUFFERING';
      const remoteState = String(remotePlayer.playerState || '');

      const anchorPosition = Math.max(
        0,
        Number(remoteSeekObserver.scrubAnchorPosition) ||
          Number(remoteSeekObserver.lastPosition) ||
          0
      );
      const nearAnchor =
        remoteSeekObserver.scrubAnchorPosition !== null &&
        Math.abs(position - anchorPosition) <= 2.0;
      const ownAnchorReturn =
        collectingRemoteScrub &&
        nearAnchor &&
        now < (Number(remoteSeekObserver.ignoreAnchorUntil) || 0);

      if (
        !remoteSeekSuppressed &&
        hasBaseline &&
        elapsedMs >= 80 &&
        elapsedMs <= 1500 &&
        !ownAnchorReturn
      ) {
        const stableState =
          remoteState === PLAYING ||
          remoteState === PAUSED ||
          (collectingRemoteScrub && remoteState === BUFFERING);

        const rawBaseline = collectingRemoteScrub
          ? (
              Number.isFinite(Number(remoteSeekObserver.lastRawRemotePosition))
                ? Number(remoteSeekObserver.lastRawRemotePosition)
                : anchorPosition
            )
          : (
              Number(remoteSeekObserver.lastPosition) +
              (remoteState === PLAYING ? elapsedMs / 1000 : 0)
            );
        const rawDelta = position - rawBaseline;
        const direction = Math.sign(rawDelta);

        if (
          stableState &&
              Math.abs(rawDelta) >= 4.0
        ) {
          let virtualTarget;
          if (!collectingRemoteScrub) {
            const firstAnchor = Math.max(
              0,
              Number(remoteSeekObserver.lastPosition) ||
                Math.max(0, position - rawDelta)
            );
            remoteSeekObserver.scrubAnchorPosition = firstAnchor;
            remoteSeekObserver.virtualTargetPosition = Math.max(
              0,
              duration > 0
                ? Math.min(position, Math.max(0, duration - 0.05))
                : position
            );
            remoteSeekObserver.lastRawRemotePosition = position;
            remoteSeekObserver.armDirection = direction || 0;
            remoteSeekObserver.resumeAfterRemoteScrub =
              remoteState === PLAYING && !remotePlayer.isPaused;
            remoteSeekObserver.pausedByRemoteScrub = false;

            // DISPATCHARR_ARCHIVE_REMOTE_SCRUB_PAUSE_HOLD_R8_14_11
            if (remoteSeekObserver.resumeAfterRemoteScrub) {
              try {
                castRemoteControllerRef.current?.playOrPause?.();
                remoteSeekObserver.pausedByRemoteScrub = true;
                console.info(
                  `[chromecast-r8.14.12] hardware remote virtual scrub -> pause same MediaSession; ` +
                    `anchor=${firstAnchor.toFixed(2)}s firstTarget=${position.toFixed(2)}s`
                );
              } catch (error) {
                console.warn(
                  '[chromecast-r8.14.12] virtual scrub pause failed:',
                  error
                );
              }
            }
            virtualTarget = Number(remoteSeekObserver.virtualTargetPosition);
          } else {
            const currentVirtual = Math.max(
              0,
              Number(remoteSeekObserver.virtualTargetPosition) ||
                anchorPosition
            );
            virtualTarget = currentVirtual + rawDelta;
            if (duration > 0) {
              virtualTarget = Math.min(
                virtualTarget,
                Math.max(0, duration - 0.05)
              );
            }
            virtualTarget = Math.max(0, virtualTarget);
            remoteSeekObserver.virtualTargetPosition = virtualTarget;
            remoteSeekObserver.lastRawRemotePosition = position;
            // DISPATCHARR_ARCHIVE_REMOTE_SCRUB_DIRECTION_REVERSAL_R8_14_19
            // The receiver is returned to its anchor between key presses. A
            // subsequent negative delta is real hardware-remote intent, not
            // an anchor return (filtered above), so retain its sign.
            remoteSeekObserver.armDirection = direction || 0;
          }

          remoteSeekObserver.candidatePosition = virtualTarget;

          if (remoteSeekObserver.debounceTimer != null) {
            window.clearTimeout(remoteSeekObserver.debounceTimer);
          }

          // Snap only the receiver transport back to the safe anchor. The
          // virtual target continues advancing independently.
          if (remoteSeekObserver.snapbackTimer != null) {
            window.clearTimeout(remoteSeekObserver.snapbackTimer);
          }
          remoteSeekObserver.snapbackTimer = window.setTimeout(() => {
            const observer = castArchiveRemoteSeekObserverRef.current;
            if (
              observer.pending ||
              observer.debounceTimer == null ||
              observer.scrubAnchorPosition === null
            ) {
              observer.snapbackTimer = null;
              return;
            }

            const currentPlayer = castRemotePlayerRef.current;
            const currentController = castRemoteControllerRef.current;
            if (!currentPlayer || !currentController) {
              observer.snapbackTimer = null;
              return;
            }

            const safeAnchor = Math.max(
              0,
              Number(observer.scrubAnchorPosition) || 0
            );
            try {
              currentPlayer.currentTime = safeAnchor;
              currentController.seek();
              observer.ignoreAnchorUntil = Date.now() + 900;
              observer.lastRawRemotePosition = safeAnchor;
              console.debug(
                `[chromecast-r8.14.12] virtual scrub snapback -> ${safeAnchor.toFixed(2)}s; ` +
                  `virtualTarget=${Number(observer.virtualTargetPosition || safeAnchor).toFixed(2)}s`
              );
            } catch (error) {
              console.warn(
                '[chromecast-r8.14.12] virtual scrub snapback failed:',
                error
              );
            } finally {
              observer.snapbackTimer = null;
            }
          }, 120);

          castHealthRef.current.lastPosition = anchorPosition;
          castHealthRef.current.lastProgressAt = now;
          console.info(
            `[chromecast-r8.14.12] hardware remote virtual target -> ${virtualTarget.toFixed(2)}s; ` +
              `receiver held at anchor=${Number(remoteSeekObserver.scrubAnchorPosition || anchorPosition).toFixed(2)}s`
          );

          remoteSeekObserver.debounceTimer = window.setTimeout(() => {
            const observer = castArchiveRemoteSeekObserverRef.current;
            const virtual = Number(observer.virtualTargetPosition);
            const candidate = Number(observer.candidatePosition);
            const commitTarget = Math.max(
              0,
              Number.isFinite(virtual)
                ? virtual
                : Number.isFinite(candidate)
                  ? candidate
                  : Number(castRemotePlayerRef.current?.currentTime) || 0
            );

            if (observer.snapbackTimer != null) {
              window.clearTimeout(observer.snapbackTimer);
              observer.snapbackTimer = null;
            }
            observer.debounceTimer = null;
            observer.candidatePosition = null;
            observer.pending = true;
            observer.suppressUntil = Date.now() + 35000;
            observer.lastPosition = commitTarget;
            observer.lastAt = Date.now();

            console.info(
              `[chromecast-r8.14.12] hardware remote virtual trailing commit -> ${commitTarget.toFixed(2)}s`
            );

            const resumeAfterSeek =
              observer.resumeAfterRemoteScrub !== false;

            void commitArchiveRemoteSeamlessSeek(
              commitTarget,
              resumeAfterSeek
            )
              .then((seamlessReady) => {
                if (seamlessReady) return true;

                console.warn(
                  `[chromecast-r8.14.12] virtual remote seek did not settle -> ${commitTarget.toFixed(2)}s; ` +
                    'keeping current Cast MediaSession, no automatic loadMedia fallback'
                );
                return false;
              })
              .finally(() => {
                const currentObserver =
                  castArchiveRemoteSeekObserverRef.current;
                currentObserver.pending = false;
                currentObserver.armDirection = 0;
                currentObserver.pausedByRemoteScrub = false;
                currentObserver.resumeAfterRemoteScrub = true;
                currentObserver.scrubAnchorPosition = null;
                currentObserver.virtualTargetPosition = null;
                currentObserver.lastRawRemotePosition = null;
                currentObserver.ignoreAnchorUntil = 0;
                currentObserver.lastPosition =
                  Number(castRemotePlayerRef.current?.currentTime) || commitTarget;
                currentObserver.lastAt = Date.now();
                currentObserver.suppressUntil = Date.now() + 3000;
              });
          }, 1500);
        }
      }

      if (ownAnchorReturn) {
        remoteSeekObserver.lastRawRemotePosition = anchorPosition;
      }

      if (!remoteSeekObserver.pending) {
        if (
          remoteSeekObserver.debounceTimer != null &&
          remoteSeekObserver.scrubAnchorPosition !== null
        ) {
          remoteSeekObserver.lastPosition = Math.max(
            0,
            Number(remoteSeekObserver.scrubAnchorPosition) || position
          );
        } else {
          remoteSeekObserver.lastPosition = position;
        }
        remoteSeekObserver.lastAt = now;
      }

      const displayedPosition =
        remoteSeekObserver.debounceTimer != null &&
        Number.isFinite(Number(remoteSeekObserver.virtualTargetPosition))
          ? Math.max(0, Number(remoteSeekObserver.virtualTargetPosition))
          : position;
      dispatcharrCastRuntime.archivePosition = displayedPosition;
      if (!castArchiveScrubbingRef.current) {
        setCastArchivePositionSeconds(displayedPosition);
      }
      setCastArchivePaused(Boolean(remotePlayer.isPaused));
    };

    syncArchiveCastTransport();
    const timer = window.setInterval(syncArchiveCastTransport, 250);
    return () => window.clearInterval(timer);
    // DISPATCHARR_CAST_LIVE_REWIND_UI_R18: castTransportEpoch -- see its
    // declaration. Without it this effect ran while kind was still 'live',
    // bailed, and never restarted: the bar sat at 0:00.
  }, [castActive, contentType, streamUrl, castTransportEpoch]);

  // DISPATCHARR_ARCHIVE_CAST_REMUX_R8_19_0: observe only receiver transport.
  // A discontinuity from a TV remote becomes one programme-absolute target and
  // enters the same debounced latest-wins controller used by the Web slider.
  useEffect(() => {
    if (
      !castActive ||
      contentType !== 'catchup' ||
      dispatcharrCastRuntime.kind !== 'archive'
    ) {
      castArchiveRemoteSeekObserverRef.current = {
        lastPosition: null,
        lastAt: 0,
        suppressUntil: 0,
      };
      setCastArchivePaused(false);
      return undefined;
    }

    const syncArchiveCastV2Transport = () => {
      const remotePlayer = castRemotePlayerRef.current;
      if (!remotePlayer) return;
      const observer = castArchiveRemoteSeekObserverRef.current;
      const now = Date.now();
      const relativePosition = Math.max(
        0,
        Number(remotePlayer.currentTime) || 0
      );
      const basePosition = Math.max(
        0,
        Number(dispatcharrCastRuntime.archiveBasePosition) || 0
      );
      const duration = Math.max(
        1,
        Number(dispatcharrCastRuntime.archiveDuration) ||
          Number(castArchiveDurationSeconds) ||
          1
      );
      const absolutePosition = Math.min(
        basePosition + relativePosition,
        Math.max(0, duration - 0.05)
      );
      const state = String(remotePlayer.playerState || '');
      const playing =
        state ===
        (window.chrome?.cast?.media?.PlayerState?.PLAYING || 'PLAYING');

      const controller =
        archiveCastControllerRef.current ||
        dispatcharrCastRuntime.archiveController;
      const collectingLatestIntent = Boolean(
        controller?.latestSeek || controller?.seekRunning
      );
      if (
        observer.lastPosition != null &&
        observer.lastAt > 0 &&
        !observer.receiverLoadInProgress &&
        (now >= (Number(observer.suppressUntil) || 0) || collectingLatestIntent)
      ) {
        const elapsed = Math.max(0, (now - observer.lastAt) / 1000);
        const expected = Number(observer.lastPosition) + (playing ? elapsed : 0);
        if (Math.abs(relativePosition - expected) >= 4) {
          console.info(
            `[archive-cast-v2] hardware remote target -> ${absolutePosition.toFixed(2)}s`
          );
          commitArchiveCastV2Seek(absolutePosition);
        }
      }

      observer.lastPosition = relativePosition;
      observer.lastAt = now;
      if (!castArchiveSeekHealthRef.current.inProgress) {
        dispatcharrCastRuntime.archivePosition = absolutePosition;
        if (!castArchiveScrubbingRef.current) {
          setCastArchivePositionSeconds(absolutePosition);
        }
      }
      setCastArchiveDurationSeconds(duration);
      setCastArchivePaused(Boolean(remotePlayer.isPaused));
    };

    syncArchiveCastV2Transport();
    const timer = window.setInterval(syncArchiveCastV2Transport, 250);
    return () => window.clearInterval(timer);
  }, [castActive, contentType, streamUrl]);

  // Modified hideVideo handler to clean up player first
  // DISPATCHARR_CAST_X_CLOSE_NO_HANDBACK_R8_8_53
  // DISPATCHARR_CAST_TRANSITION_OPS_R134: async Cast flows outlive the render
  // that started them; they read the CURRENT content and functions here.
  castLatestRef.current = {
    contentType,
    streamUrl,
    metadata,
    loadLive: loadLiveOnChromecast,
    loadArchive: loadArchiveOnChromecast,
    loadDirectArchive: loadDirectArchiveOnChromecast,
    requestArchiveStop,
    resumeSuspendedTransport,
    startLiveWebPlayback,
    startLiveProviderTimeshift,
    returnToLiveFromProviderTimeshift,
    liveRewindAvailable, // DISPATCHARR_CAST_REMOTE_REWIND_R135
    // DISPATCHARR_LIVE_PAUSE_CHOICE_R136
    continueLiveFromPause,
    resumeFromPause,
    toggleLiveCastPlayback,
    stopLiveCastIntoPausedWeb,
  };

  // DISPATCHARR_LIVE_PAUSE_CHOICE_R136: the paused-Live choice (local overlay and
  // Cast overlay). Plain helper, no hooks; never wraps or keys the stage (R123).
  const renderLivePauseChoices = (where) => {
    const pause = livePause && livePause.where === where ? livePause : null;
    const nowMs = Date.now();
    const tvName = castDeviceName || 'the TV';
    const plan = livePauseResumePlan(pause, nowMs);
    const busy = pause?.busy || null;
    // R136b: while "Resume from" waits for the archive, Continue Live stays usable.
    const waiting = busy === 'waiting';
    const locked = Boolean((busy && !waiting) || (where === 'tv' && castLiveCommand));
    const at =
      pause && Number.isFinite(pause.frameInstantMs) ? liveInstantLabel(pause.frameInstantMs) : '';
    const ago =
      pause && Number.isFinite(pause.pausedAtMs)
        ? formatArchiveClock((nowMs - pause.pausedAtMs) / 1000)
        : '';
    const tooLong = Boolean(
      pause && liveRewindAvailable && (where === 'local' || pause.owner) && !plan
    );
    const sub = [
      at && `at ${at}`,
      ago && `${ago} ago`,
      where === 'tv' && pause?.by === 'tv-remote' && 'from the TV remote',
      tooLong && 'too long ago to resume',
    ]
      .filter(Boolean)
      .join(' · ');
    // R136 review: keep both choices on one row in the small floating player.
    const narrow = Number(videoSize?.width) < 360;
    const buttonBase = {
      WebkitAppearance: 'none',
      appearance: 'none',
      borderRadius: 6,
      padding: coarsePointer
        ? narrow
          ? '12px 12px'
          : '12px 18px'
        : narrow
          ? '8px 8px'
          : '8px 14px',
      minHeight: coarsePointer ? 44 : undefined,
      fontSize: 14,
      cursor: locked ? 'default' : 'pointer',
    };
    return (
      <>
        {sub && (
          <Text
            className="dispatcharr-fs-suspend-sub"
            size="xs"
            style={{
              color: 'rgba(255,255,255,0.72)',
              fontVariantNumeric: 'tabular-nums',
              textAlign: 'center',
            }}
          >
            {sub}
          </Text>
        )}
        <Box
          className="dispatcharr-fs-suspend-row"
          style={{
            display: 'flex',
            flexWrap: narrow ? 'nowrap' : 'wrap',
            justifyContent: 'center',
            gap: narrow ? 6 : 10,
          }}
        >
          <button
            type="button"
            className="dispatcharr-fs-resume dispatcharr-fs-resume-primary"
            data-dispatcharr-pause-choice="live"
            aria-keyshortcuts="Space"
            title={where === 'tv' ? `Continue Live on ${tvName} (Space)` : 'Continue Live (Space)'}
            disabled={locked}
            onClick={() => {
              void continueLiveFromPause('click');
            }}
            style={{
              ...buttonBase,
              border: '1px solid #fff',
              background: '#fff',
              color: '#111',
              fontWeight: 700,
              opacity: locked ? 0.55 : 1,
            }}
          >
            <span style={{ color: '#e5484d', marginRight: 6 }}>●</span>
            Continue Live
          </button>
          {plan && (
            <button
              type="button"
              className="dispatcharr-fs-resume dispatcharr-fs-resume-alt"
              data-dispatcharr-pause-choice="resume"
              data-dispatcharr-resume-mode={plan.mode}
              title={
                waiting || plan.readyInMs > 0
                  ? `Archive playback stays ${plan.floorSeconds ?? LIVE_REWIND_FLOOR_DEFAULT_SECONDS} s behind Live: this starts as soon as the paused moment is that old`
                  : plan.mode === 'tv-buffer'
                    ? `Continue on ${tvName} from where it paused`
                    : 'Continue the programme from where it was paused'
              }
              disabled={locked || waiting}
              onClick={() => {
                void resumeFromPause('click');
              }}
              style={{
                ...buttonBase,
                border: '1px solid rgba(255,255,255,0.7)',
                background: 'rgba(20,20,20,0.9)',
                color: '#fff',
                fontVariantNumeric: 'tabular-nums',
                opacity: locked && busy !== 'resume' ? 0.55 : 1,
              }}
            >
              {busy === 'resume' || waiting
                ? `Starting from ${plan.label}…`
                : `Resume from ${plan.label}`}
            </button>
          )}
        </Box>
        {waiting && (
          <Text
            className="dispatcharr-fs-suspend-wait"
            data-dispatcharr-pause-wait="1"
            size="xs"
            style={{
              color: 'rgba(255,255,255,0.72)',
              fontVariantNumeric: 'tabular-nums',
              textAlign: 'center',
            }}
          >
            {`Starts in ${Math.max(
              1,
              Math.ceil(((Number(pause.resumeAtMs) || nowMs) - nowMs) / 1000)
            )} s · archive playback stays ${plan?.floorSeconds ?? LIVE_REWIND_FLOOR_DEFAULT_SECONDS} s behind Live`}
          </Text>
        )}
        {busy === 'stopping' && (
          <Text size="xs" style={{ color: 'rgba(255,255,255,0.6)' }}>
            Stopping the cast…
          </Text>
        )}
        {!coarsePointer && (
          <Text
            className="dispatcharr-fs-suspend-hint"
            style={{
              fontSize: 11,
              color: 'rgba(255,255,255,0.5)',
              display: videoSize.height < 220 ? 'none' : undefined,
            }}
          >
            Space: Continue Live
          </Text>
        )}
        {where === 'local' && !isLoading && loadError && (
          <Text size="xs" c="red" style={{ textAlign: 'center', maxWidth: '90%' }}>
            {loadError}
          </Text>
        )}
      </>
    );
  };

  const handleClose = (e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    dispatcharrPlayerClosingRef.current = true;
    const activeUrl = archiveLocalRuntimeUrlRef.current || streamUrl;
    // DISPATCHARR_CAST_TRANSITION_OPS_R134: X in the middle of a Cast
    // transition closes that cast too and supersedes its loader.
    const closingTransition = castOpInFlight();
    const wasCasting =
      castActiveRef.current ||
      dispatcharrCastRuntime.active ||
      Boolean(closingTransition && castContextRef.current?.getCurrentSession?.());
    const castKind = dispatcharrCastRuntime.kind;
    if (closingTransition || wasCasting) settleCastOp(beginCastOp('close'));
    // DISPATCHARR_ARCHIVE_RECOVERY_CAST_X_IMMEDIATE_STOP
    const activeArchiveCastUrl =
      castKind === 'archive'
        ? dispatcharrCastRuntime.mediaUrl ||
          archiveLocalRuntimeUrlRef.current ||
          streamUrl
        : null;

    // Fence stale SESSION_ENDED/health callbacks synchronously. X means close,
    // never "Cast -> Web" handback. This removes the Normal Live handback 503
    // race observed while the old channel is in its teardown tail.
    if (wasCasting) {
      beginDispatcharrCastIntentionalHandback();
      castHandoffInProgressRef.current = true;
      castActiveRef.current = false;
      setCastActive(false);
      setCastDeviceName('');

      if (castKind === 'live') {
        if (dispatcharrCastRuntime.leaseTimer != null) {
          window.clearInterval(dispatcharrCastRuntime.leaseTimer);
          dispatcharrCastRuntime.leaseTimer = null;
        }
        const stopUrl = dispatcharrCastRuntime.stopUrl || castStopUrlRef.current;
        if (stopUrl) dispatcharrSendBeaconPost(stopUrl);
      } else if (castKind === 'archive') {
        const controller =
          archiveCastControllerRef.current ||
          dispatcharrCastRuntime.archiveController;
        void controller?.stop?.({ keepalive: true }).catch(() => undefined);
        console.info(
          '[chromecast-r8.8.48] X close -> stopping active Archive Cast producer'
        );
        requestArchiveStop(activeArchiveCastUrl || activeUrl, { beacon: true });
      }
      // DISPATCHARR_CAST_TRANSITION_OPS_R134: mid-transition the active slot
      // can hold a lease of either kind, plus Continue Live's deferred one.
      if (castKind !== 'live') {
        const activeStopUrl = dispatcharrCastRuntime.stopUrl || castStopUrlRef.current;
        if (activeStopUrl) dispatcharrSendBeaconPost(activeStopUrl);
      }
      if (castDeferredLeaseStopUrlRef.current) {
        dispatcharrSendBeaconPost(castDeferredLeaseStopUrlRef.current);
        castDeferredLeaseStopUrlRef.current = null;
      }
      if (closingTransition?.archiveCastMediaUrl) {
        stopArchiveCastPipelineExact(closingTransition.archiveCastMediaUrl, { beacon: true });
      }
      setCastReturningToLive(false);
      castFailureRecoveryRef.current = false;
      try { castContextRef.current?.endCurrentSession?.(true); } catch {}
      castRemoteControllerRef.current = null;
      castRemotePlayerRef.current = null;
      archiveCastControllerRef.current = null;
      clearDispatcharrCastRuntime();
      castHandoffInProgressRef.current = false;
      releaseParkedLiveLease('X close', { beacon: true }); // DISPATCHARR_CAST_LIVE_WARM_DURING_REWIND_R21
      console.info('[chromecast-r8.8.53] X close -> Cast stopped without Web handback');
    }

    safeDestroyPlayer();
    if (!wasCasting && (contentType === 'catchup' || contentType === 'catchup-hls')) {
      requestArchiveStop(activeUrl);
    }
    window.setTimeout(() => hideVideo(), 20);
  };
  const clampToVisible = useCallback(
    (x, y) => {
      if (typeof window === 'undefined') return { x, y };

      const totalHeight = videoSize.height + HEADER_HEIGHT + ERROR_HEIGHT;
      const minX = 0;
      // minY = 0 ensures the header row is always within the viewport so the
      // user can always grab and reposition the window.
      const minY = 0;
      const maxX = Math.max(0, window.innerWidth - videoSize.width);
      const maxY = Math.max(0, window.innerHeight - totalHeight);

      return {
        x: Math.min(Math.max(x, minX), maxX),
        y: Math.min(Math.max(y, minY), maxY),
      };
    },
    [HEADER_HEIGHT, ERROR_HEIGHT, videoSize.height, videoSize.width]
  );

  const clampToVisibleWithSize = useCallback(
    (x, y, width, height) => {
      if (typeof window === 'undefined') return { x, y };

      const totalHeight = height + HEADER_HEIGHT + ERROR_HEIGHT;
      const minX = 0; // left edge must stay in viewport
      const minY = 0; // header must always be reachable
      const maxX = Math.max(0, window.innerWidth - width);
      const maxY = Math.max(0, window.innerHeight - totalHeight);

      return {
        x: Math.min(Math.max(x, minX), maxX),
        y: Math.min(Math.max(y, minY), maxY),
      };
    },
    [HEADER_HEIGHT, ERROR_HEIGHT]
  );

  const handleResizeMove = useCallback(
    (event) => {
      if (!resizeStateRef.current) return;

      // If the mouse button was released outside the window, stop resizing.
      // Remove the move listeners immediately; the mouseup/touchend listener
      // (endResize) will clean itself up the next time the user clicks.
      if (event.type === 'mousemove' && event.buttons === 0) {
        resizeStateRef.current = null;
        setIsResizing(false);
        window.removeEventListener('mousemove', handleResizeMove);
        window.removeEventListener('touchmove', handleResizeMove);
        return;
      }

      const { clientX, clientY } = getClientCoordinates(event);
      const {
        startX,
        startY,
        startWidth,
        startHeight,
        startPos,
        handle,
        aspectRatio,
      } = resizeStateRef.current;

      const ratio = aspectRatio || aspectRatioRef.current;
      const { width: nextWidth, height: nextHeight } = calculateNewDimensions(
        clientX - startX,
        clientY - startY,
        startWidth,
        startHeight,
        handle,
        ratio
      );

      const constrainedSize = applyConstraints(
        nextWidth,
        nextHeight,
        ratio,
        startPos,
        handle,
        MIN_WIDTH,
        MIN_HEIGHT,
        VISIBLE_MARGIN
      );

      setVideoSize({
        width: Math.round(constrainedSize.width),
        height: Math.round(constrainedSize.height),
      });

      updatePositionIfNeeded(
        handle,
        startPos,
        startWidth,
        startHeight,
        constrainedSize
      );
    },
    [MIN_HEIGHT, MIN_WIDTH, VISIBLE_MARGIN, clampToVisibleWithSize]
  );

  const updatePositionIfNeeded = (
    handle,
    startPos,
    startWidth,
    startHeight,
    newSize
  ) => {
    if (!handle.isLeft && !handle.isTop) return;

    const posX = startPos?.x ?? 0;
    const posY = startPos?.y ?? 0;
    let nextX = handle.isLeft ? posX + (startWidth - newSize.width) : posX;
    let nextY = handle.isTop ? posY + (startHeight - newSize.height) : posY;

    const clamped = clampToVisibleWithSize(
      nextX,
      nextY,
      newSize.width,
      newSize.height
    );
    const nextPos = {
      x: handle.isLeft ? clamped.x : nextX,
      y: handle.isTop ? clamped.y : nextY,
    };

    setDragPosition(nextPos);
    dragPositionRef.current = nextPos;
  };

  const endResize = useCallback(() => {
    setIsResizing(false);
    resizeStateRef.current = null;
    window.removeEventListener('mousemove', handleResizeMove);
    window.removeEventListener('mouseup', endResize);
    window.removeEventListener('touchmove', handleResizeMove);
    window.removeEventListener('touchend', endResize);
  }, [handleResizeMove]);

  const startResize = (event, handle) => {
    event.stopPropagation();
    event.preventDefault();

    const { clientX, clientY } = getClientCoordinates(event);

    const aspectRatio =
      videoSize.height > 0
        ? videoSize.width / videoSize.height
        : aspectRatioRef.current;
    aspectRatioRef.current = aspectRatio;
    const startPos = dragPositionRef.current ||
      initialPositionRef.current || { x: 0, y: 0 };

    resizeStateRef.current = {
      startX: clientX,
      startY: clientY,
      startWidth: videoSize.width,
      startHeight: videoSize.height,
      aspectRatio,
      startPos,
      handle,
    };

    setIsResizing(true);

    window.addEventListener('mousemove', handleResizeMove);
    window.addEventListener('mouseup', endResize);
    window.addEventListener('touchmove', handleResizeMove);
    window.addEventListener('touchend', endResize);
  };

  useEffect(() => {
    return () => {
      endResize();
    };
  }, [endResize]);

  useEffect(() => {
    dragPositionRef.current = dragPosition;
  }, [dragPosition]);

  // Keep videoSizeRef current so the window-resize handler never closes over
  // a stale videoSize value.
  useEffect(() => {
    videoSizeRef.current = videoSize;
  }, [videoSize]);

  // Re-clamp size and position when the browser viewport changes
  useEffect(() => {
    const handleWindowResize = () => {
      const maxW = window.innerWidth - VISIBLE_MARGIN;
      const maxH = window.innerHeight - VISIBLE_MARGIN;
      const { width: curW, height: curH } = videoSizeRef.current ?? {
        width: 320,
        height: 180,
      };

      let newW = curW;
      let newH = curH;
      if (curW > maxW || curH > maxH) {
        const scale = Math.min(maxW / curW, maxH / curH);
        newW = Math.max(MIN_WIDTH, Math.round(curW * scale));
        newH = Math.max(MIN_HEIGHT, Math.round(curH * scale));
        setVideoSize({ width: newW, height: newH });
      }

      if (dragPositionRef.current) {
        const totalH = newH + HEADER_HEIGHT + ERROR_HEIGHT;
        const clamped = {
          x: Math.min(
            Math.max(dragPositionRef.current.x, 0),
            Math.max(0, window.innerWidth - newW)
          ),
          y: Math.min(
            Math.max(dragPositionRef.current.y, 0),
            Math.max(0, window.innerHeight - totalH)
          ),
        };
        if (
          clamped.x !== dragPositionRef.current.x ||
          clamped.y !== dragPositionRef.current.y
        ) {
          setDragPosition(clamped);
          dragPositionRef.current = clamped;
        }
      }
    };
    window.addEventListener('resize', handleWindowResize);
    return () => window.removeEventListener('resize', handleWindowResize);
  }, [MIN_WIDTH, MIN_HEIGHT, VISIBLE_MARGIN, HEADER_HEIGHT, ERROR_HEIGHT]);

  // Persist size whenever it changes, but skip high-frequency writes during
  // an active resize drag — the final value is captured when isResizing clears.
  useEffect(() => {
    if (!isResizing) {
      savePlayerPrefs({ size: videoSize });
    }
  }, [videoSize, isResizing]);

  // Persist position when a resize ends
  useEffect(() => {
    if (!isResizing && dragPositionRef.current) {
      savePlayerPrefs({ position: dragPositionRef.current });
    }
  }, [isResizing]);

  // Initialize the floating window near bottom-right once
  useEffect(() => {
    if (initialPositionRef.current || typeof window === 'undefined') return;

    // Try to restore the last saved position
    const savedPos = getPlayerPrefs().position;
    if (savedPos) {
      const clamped = clampToVisible(savedPos.x, savedPos.y);
      initialPositionRef.current = clamped;
      setDragPosition(clamped);
      dragPositionRef.current = clamped;
      return;
    }

    const totalHeight = videoSize.height + HEADER_HEIGHT + ERROR_HEIGHT;
    const initialX = Math.max(10, window.innerWidth - videoSize.width - 20);
    const initialY = Math.max(10, window.innerHeight - totalHeight - 20);
    const pos = clampToVisible(initialX, initialY);

    initialPositionRef.current = pos;
    setDragPosition(pos);
    dragPositionRef.current = pos;
  }, [
    clampToVisible,
    videoSize.height,
    videoSize.width,
    HEADER_HEIGHT,
    ERROR_HEIGHT,
  ]);

  const handleDragStart = useCallback(
    (event, data) => {
      const clientX = event.touches?.[0]?.clientX ?? event.clientX;
      const clientY = event.touches?.[0]?.clientY ?? event.clientY;
      const rect = videoContainerRef.current?.getBoundingClientRect();

      if (clientX != null && clientY != null && rect) {
        dragOffsetRef.current = {
          x: clientX - rect.left,
          y: clientY - rect.top,
        };
      } else {
        dragOffsetRef.current = { x: 0, y: 0 };
      }

      const clamped = clampToVisible(data?.x ?? 0, data?.y ?? 0);
      setDragPosition(clamped);
      dragPositionRef.current = clamped;
    },
    [clampToVisible]
  );

  const handleDrag = useCallback(
    (event) => {
      // If the mouse button was released outside the browser window the
      // mouseup event is never delivered to react-draggable. Detect this on
      // the next mousemove and fire a synthetic mouseup so it stops dragging.
      if (event.type === 'mousemove' && event.buttons === 0) {
        window.dispatchEvent(new MouseEvent('mouseup', { bubbles: true }));
        return;
      }
      const clientX = event.touches?.[0]?.clientX ?? event.clientX;
      const clientY = event.touches?.[0]?.clientY ?? event.clientY;
      if (clientX == null || clientY == null) return;

      const nextX = clientX - (dragOffsetRef.current?.x ?? 0);
      const nextY = clientY - (dragOffsetRef.current?.y ?? 0);
      const clamped = clampToVisible(nextX, nextY);
      setDragPosition(clamped);
      dragPositionRef.current = clamped;
    },
    [clampToVisible]
  );

  const handleDragStop = useCallback(
    (_, data) => {
      const clamped = clampToVisible(data?.x ?? 0, data?.y ?? 0);
      setDragPosition(clamped);
      dragPositionRef.current = clamped;
      savePlayerPrefs({ position: clamped });
    },
    [clampToVisible]
  );

  // If the floating video is hidden or no URL is selected, do not render
  if (!isVisible || !streamUrl) {
    return null;
  }

  return (
    <Draggable
      nodeRef={videoContainerRef}
      cancel=".floating-video-no-drag"
      disabled={isResizing}
      position={dragPosition || undefined}
      defaultPosition={initialPositionRef.current || { x: 0, y: 0 }}
      onStart={handleDragStart}
      onDrag={handleDrag}
      onStop={handleDragStop}
    >
      <div
        ref={videoContainerRef}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: `${videoSize.width}px`,
          zIndex: 9999,
          backgroundColor: '#333',
          borderRadius: '8px',
          overflow: 'visible',
          boxShadow: '0 2px 10px rgba(0,0,0,0.7)',
        }}
      >
        {/* Header row with optional title and close button */}
        <Flex
          justify="space-between"
          align="center"
          style={{
            padding: '3px 3px 3px 8px',
            minHeight: '38px',
            cursor: 'grab',
            userSelect: 'none',
          }}
        >
          {metadata?.name ? (
            <Text
              size="xs"
              fw={600}
              style={{
                color: 'rgba(255,255,255,0.9)',
                flex: 1,
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                paddingRight: 4,
                textShadow: '0px 1px 3px rgba(0,0,0,0.8)',
                userSelect: 'none',
              }}
            >
              {metadata.name}
            </Text>
          ) : (
            <Box style={{ flex: 1 }} />
          )}
          <CloseButton
            onClick={handleClose}
            onTouchEnd={handleClose}
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            style={{
              minHeight: '32px',
              minWidth: '32px',
              cursor: 'pointer',
              touchAction: 'manipulation',
              flexShrink: 0,
            }}
          />
        </Flex>

        {/* Video container with relative positioning for the overlay */}
        <Box
          style={{ position: 'relative' }}
          // DISPATCHARR_LIVE_REWIND_NATIVE_MATCH_AND_IDLE_HOVER
          onMouseMove={() => {
            setCastPlayerHover(true);
            if (castPlayerIdleTimerRef.current != null) {
              window.clearTimeout(castPlayerIdleTimerRef.current);
            }
            castPlayerIdleTimerRef.current = window.setTimeout(() => {
              castPlayerIdleTimerRef.current = null;
              setCastPlayerHover(false);
            }, 4000);
          }}
          onMouseEnter={() => {
            // DISPATCHARR_ARCHIVE_RECOVERY_CAST_PLAYER_HOVER
            setCastPlayerHover(true);
            setShowControls(true);
            if (castPlayerIdleTimerRef.current != null) {
              window.clearTimeout(castPlayerIdleTimerRef.current);
            }
            castPlayerIdleTimerRef.current = window.setTimeout(() => {
              castPlayerIdleTimerRef.current = null;
              setCastPlayerHover(false);
            }, 4000);
            if (contentType === 'vod' && !isLoading) {
              setShowOverlay(true);
              if (overlayTimeoutRef.current) {
                clearTimeout(overlayTimeoutRef.current);
              }
            }
          }}
          onMouseLeave={() => {
            // DISPATCHARR_ARCHIVE_RECOVERY_CAST_PLAYER_HOVER
            if (castPlayerIdleTimerRef.current != null) {
              window.clearTimeout(castPlayerIdleTimerRef.current);
              castPlayerIdleTimerRef.current = null;
            }
            setCastPlayerHover(false);
            if (contentType === 'vod' && !isLoading) {
              startOverlayTimer();
            }
          }}
          // DISPATCHARR_LIVE_REWIND_TOUCH_IDLE_HIDE: touch
          // devices never fire onMouseMove/onMouseEnter/onMouseLeave above
          // at all, so castPlayerHover's idle-fade never engaged and
          // castHoverRequired's own (hover: hover)-is-false fallback left
          // these badges permanently shown on iOS. A tap now drives the
          // exact same show-then-idle-fade behavior onMouseEnter does --
          // and, per DISPATCHARR_LIVE_REWIND_MOUNT_REVERT_TOGGLE_TIMING_POSITION, a second tap on
          // the video/background itself (not on an actual control) while
          // already showing dismisses immediately instead of only
          // re-arming the timer, matching native tap-to-hide behavior.
          onTouchStart={(event) => {
            // DISPATCHARR_IOS_BADGE_FOLLOWS_NATIVE_CONTROLS_R81
            const isControlTap = Boolean(event.target?.closest?.('button'));
            if (castPlayerHover && !isControlTap) {
              hideBadgesWithNativeControls();
              return;
            }
            // Show now; the fade is armed on touchend, as WebKit arms its own
            // auto-hide on pointerup.
            showBadgesWithNativeControls({ fade: false });
          }}
          onTouchEnd={() => {
            if (!castPlayerHover) return;
            showBadgesWithNativeControls();
          }}
          onTouchCancel={() => {
            if (!castPlayerHover) return;
            showBadgesWithNativeControls();
          }}
          // DISPATCHARR_LIVE_REWIND_POSITION_TIMING_CLICK_TOGGLE: once
          // native <video controls> are active, the browser's own
          // media-controls layer often consumes touch gestures on the
          // video surface before they ever reach onTouchStart above --
          // click is a separate, higher-level event the browser still
          // dispatches afterward and is delivered far more reliably.
          // Touch-only (desktop's hover already owns show/hide).
          onClick={(event) => {
            if (castHoverRequired) return;
            const isControlTap = Boolean(event.target?.closest?.('button'));
            if (isControlTap) return;
            // DISPATCHARR_IOS_BADGE_FOLLOWS_NATIVE_CONTROLS_R81: a tap outside
            // the controls toggles them in WebKit, so it toggles these too.
            if (castPlayerHover) {
              hideBadgesWithNativeControls();
              return;
            }
            showBadgesWithNativeControls();
          }}
        >
          {/* Enhanced video element with better controls for VOD */}
          <video
            ref={videoRef}
            // DISPATCHARR_LIVE_DESKTOP_CLICK_DBLCLICK_R121: with native controls
            // off, desktop Live lost Chrome's click-to-pause. Restore it on OUR
            // transport path (pause -> suspend -> Resume Live) and map
            // double-click to OUR stage fullscreen. The 250ms delay keeps a
            // double-click from pausing first (verified in headless Chromium).
            onClick={(event) => {
              if (contentType !== 'live' || coarsePointer || castActive || isLoading) return;
              // DISPATCHARR_CAST_PICKER_TRAP_R126: with Chrome's Cast picker
              // open, a click on the video means "close that menu", never
              // pause -- and the picker's CANCEL trails the click, so the
              // guard outlives the pending flag by a beat.
              if (castPickerGuardActive()) return;
              // Native controls re-enabled from the context menu toggle on click
              // themselves; the open Rewind picker owns this click.
              if (event.currentTarget.controls || showLiveRewindPicker) return;
              if (event.detail > 1) return;
              window.clearTimeout(liveClickTimerRef.current);
              // 350ms, not 250: a relaxed double-click (OS window ~500ms)
              // otherwise pauses + suspends first and its 2nd click lands on
              // the Resume overlay, so fullscreen never happens.
              liveClickTimerRef.current = window.setTimeout(() => {
                liveClickTimerRef.current = null;
                toggleLivePlayback();
              }, 350);
            }}
            onDoubleClick={() => {
              if (contentType !== 'live' || coarsePointer || castActive) return;
              if (castPickerGuardActive()) return; // DISPATCHARR_CAST_PICKER_TRAP_R126
              window.clearTimeout(liveClickTimerRef.current);
              liveClickTimerRef.current = null;
              toggleArchiveFullscreen();
            }}
            // DISPATCHARR_CAST_SEAMLESS_HANDBACK_ARCHIVE_CONTROLS_R8_8_46
            onMouseEnter={() => setCastHover(true)}
            onMouseLeave={(event) => {
              const next = event.relatedTarget;
              if (
                next?.closest?.('.dispatcharr-cast-hover-button') ||
                next?.closest?.('.dispatcharr-cast-archive-controls') ||
                // DISPATCHARR_LIVE_PAUSE_CHOICE_R136
                next?.closest?.('.dispatcharr-cast-live-controls, .dispatcharr-cast-live-paused')
              ) return;
              setCastHover(false);
            }}
            onTouchStart={() => setCastHover(true)}
            // DISPATCHARR_WEB_CATCHUP_MOBILE_R8_7_7
            // DISPATCHARR_LIVE_MOBILE_STABILITY_R8_8_22
            playsInline
            autoPlay={contentType === 'catchup-hls'}
            // DISPATCHARR_LIVE_NATIVE_CONTROLS_DEFER_UNTIL_LOADED: native
            // controls otherwise painted their own centered play button
            // over Live while it was still loading, ahead of/instead of
            // the Loading-stream overlay below.
            controls={
              contentType === 'catchup-hls'
                ? coarsePointer
                : contentType === 'catchup'
                  ? false
                  : contentType === 'live'
                    ? // DISPATCHARR_LIVE_DESKTOP_CUSTOM_CONTROLS_R120: desktop
                      // Live uses our own bar (below); only touch/mobile keeps
                      // the native controls. This removes the shadow-DOM native
                      // fullscreen button that stole Space / forced native
                      // <video> fullscreen on Chrome.
                      coarsePointer &&
                      showControls &&
                      !isLoading &&
                      !showLiveRewindPicker &&
                      // DISPATCHARR_LIVE_PAUSE_CHOICE_R136: no native controls on the
                      // empty <video> under the Cast overlay (touch).
                      !castActive
                    : showControls
            }
            // DISPATCHARR_LIVE_REMOVE_FS_BADGE_STYLE_REWIND_GREEN
            controlsList={
              // DISPATCHARR_LIVE_DESKTOP_NO_NATIVE_CONTROLS_R121: backstop -- if
              // native controls ever reappear on desktop Live (Chrome's context
              // menu "Show controls"), 'nofullscreen' disables their fullscreen
              // button and native double-click fullscreen (verified, Chromium
              // 153), so the <video> itself can't go fullscreen. Touch devices
              // keep native fullscreen (their only path).
              contentType === 'live' && !coarsePointer
                ? 'nodownload nofullscreen'
                : contentType === 'live' ||
                    (contentType === 'catchup-hls' && metadata?.liveTimeshift)
                  ? 'nodownload'
                  : undefined
            }
            className="floating-video-no-drag"
            // DISPATCHARR_LIVE_VIDEO_HIDE_DURING_LOAD: WebKit/
            // Chromium paint a default paused-video play button for any
            // unplayed <video>, independent of the controls attribute --
            // Live's autoPlay is false and playback only starts once
            // loading finishes, so that affordance showed the whole
            // loading window. Hiding the element's own rendering (not
            // just its controls) removes it, leaving only the Loading
            // overlay below visible.
            style={{
              width: '100%',
              height: `${videoSize.height}px`,
              backgroundColor: '#000',
              borderRadius: '0 0 8px 8px',
              opacity: contentType === 'live' && isLoading ? 0 : 1,
              // Better controls styling for VOD
              ...(contentType === 'vod' && {
                controlsList: 'nodownload',
                playsInline: true,
              }),
            }}
            // Add poster for VOD if available
            {...(contentType === 'vod' && {
              poster: metadata?.logo?.url, // Use VOD poster if available
            })}
          />

          {(contentType === 'live' || contentType === 'catchup' || contentType === 'catchup-hls') &&
            (castSdkReady || castAvailable || castActive) &&
            !transportSuspendedKind && (
            <button
              type="button"
              ref={castButtonRef}
              data-dispatcharr-cast-ui="DISPATCHARR_ARCHIVE_RECOVERY_CAST_HOVER_UI"
              className="floating-video-no-drag dispatcharr-cast-hover-button dispatcharr-fs-badge"
              title={castActive ? 'Stop Chromecast' : ((contentType === 'catchup' || contentType === 'catchup-hls') ? 'Cast Archive to Chromecast' : 'Cast Live to Chromecast')}
              onClick={handleChromecastToggle}
              onMouseEnter={() => setCastHover(true)}
              onMouseLeave={(event) => {
                if (event.relatedTarget?.tagName === 'VIDEO') return;
                setCastHover(false);
              }}
              style={{
                position: 'absolute',
                // DISPATCHARR_LIVE_DESKTOP_BADGE_STRIP_NATIVE_APPEARANCE: Safari's
                // UA stylesheet gives <button> a native bezel/background by
                // default that can still show through explicit inline
                // background/border on WebKit -- Chrome normalizes this away
                // on its own, which is why the dark plate looked different
                // between the two browsers. Explicitly killing native button
                // chrome makes our own background/border the only thing
                // rendered, identically, everywhere.
                WebkitAppearance: 'none',
                appearance: 'none',
                // DISPATCHARR_LIVE_BADGES_TOP_PLUS_PLAIN_LIVE_FULLSCREEN: back to the top-right
                // corner (confirmed preferred over the bottom placement).
                // DISPATCHARR_LIVE_DESKTOP_BADGE_NUDGE_UP_SAFARI_LINE: nudged
                // up 2px on desktop to line up with Safari's own native
                // controls -- mobile keeps its existing position.
                top: castHoverRequired ? 6 : 8,
                // DISPATCHARR_LIVE_DESKTOP_BADGE_SHIFT_RIGHT: whole
                // cluster nudged 4px closer to the edge on desktop.
                right: castHoverRequired ? 4 : 8,
                zIndex: 35,
                // DISPATCHARR_ARCHIVE_RECOVERY_CAST_HOVER_UI
                // The idle Cast action belongs to the whole Web Player hover,
                // not the nested video/control hover state. Native HLS controls
                // and overlays can move pointer ownership away from <video>
                // without the pointer leaving the player at all.
                // DISPATCHARR_ARCHIVE_RECOVERY_CAST_ACTION_UI
                // Keep the idle button transparent, but give the action a subtle
                // white outline on hover. Active Stop Casting is slightly stronger.
                // DISPATCHARR_LIVE_DESKTOP_BADGE_STYLE_AND_POSITION: idle
                // Cast (transparent bg, white text) was invisible against a
                // light program background on desktop -- gives desktop's
                // idle state the same bordered/light-gray treatment as
                // Rewind/Continue Live below. Active/Stop Casting's green
                // fill is already visible on any background, untouched.
                // DISPATCHARR_LIVE_DESKTOP_BADGE_EPG_GREEN_TEXT: desktop
                // now always uses the same light-gray-transparent pill
                // Rewind/Continue Live use, active or not -- Cast's old
                // opaque-green-fill + bright-white-border special case for
                // castActive is scoped to mobile only now (already
                // confirmed working there, untouched).
                // DISPATCHARR_LIVE_DESKTOP_BADGE_70PCT_TRANSPARENT: 0.10
                // alpha gave the pill almost no contrast of its own on a
                // bright/busy video frame, sometimes making the EPG-green
                // text unreadable -- 0.30 (~70% transparent, was asked for
                // literally) keeps it a visible backing plate more often.
                // DISPATCHARR_LIVE_DESKTOP_BADGE_DARK_BACKING_PLATE: light-gray
                // pill (however transparent) never made sense once text
                // moved to bright green/white -- a dark plate is what
                // captions/subtitles use for reliable contrast on any
                // video content, so the desktop pill switches to that
                // instead of chasing alpha values on the wrong base color.
                border: castHoverRequired
                  ? '1px solid rgba(255,255,255,0.15)'
                  : castActive
                    ? '1px solid rgba(255,255,255,0.82)'
                    : '1px solid rgba(255,255,255,0.58)',
                borderRadius: 6,
                background: castHoverRequired
                  ? 'rgba(0,0,0,0.45)'
                  : castActive
                    ? 'rgba(20,110,65,0.94)'
                    : 'transparent',
                // DISPATCHARR_BADGE_BLUR_ONLY_WHEN_VISIBLE_R122: Chromium keeps
                // drawing a backdrop-filter layer at opacity 0 (an extra render
                // pass every frame, and no macOS video overlay), so attach the
                // blur only while the badge is shown.
                backdropFilter:
                  castHoverRequired && (castActive || castPlayerHover)
                    ? 'blur(12px)'
                    : 'none',
                WebkitBackdropFilter:
                  castHoverRequired && (castActive || castPlayerHover)
                    ? 'blur(12px)'
                    : 'none',
                // white text only survives where it still sits on an
                // opaque green fill (mobile's active state) -- green text
                // there would be nearly invisible against green.
                // DISPATCHARR_LIVE_DESKTOP_BADGES_ADJACENT_WHITE: desktop is
                // white now, matching Rewind/Continue Live beside it and
                // the native controls below. Mobile is untouched: its
                // active state stays white on the green fill, its idle
                // state stays EPG green.
                color: castHoverRequired
                  ? '#fff'
                  : castActive
                    ? 'white'
                    : '#3ba882',
                // DISPATCHARR_LIVE_DESKTOP_BADGE_TRANSPARENT_CAST_SIZE: match
                // Rewind/Continue Live's explicit box instead of Cast's own
                // fixed vertical padding, so all three read as the same size.
                // DISPATCHARR_LIVE_DESKTOP_BADGE_NATIVE_SCALE_SIZE: desktop only,
                // scaled down to match the native player's own compact
                // control icons -- mobile keeps its existing size.
                height: castHoverRequired ? 32 : 24,
                display: 'flex',
                alignItems: 'center',
                padding: castHoverRequired ? '0 14px' : '0 10px',
                cursor: 'pointer',
                fontSize: castHoverRequired ? 16 : 12,
                fontWeight: 600,
                opacity: castActive || castPlayerHover ? 1 : 0,
                pointerEvents:
                  castActive || castPlayerHover ? 'auto' : 'none',
                transition: 'opacity 140ms ease',
                textShadow: castActive && !castHoverRequired
                  ? 'none'
                  : '0 1px 3px rgba(0,0,0,0.95)',
              }}
            >
              {castActive
                ? 'Stop Casting'
                : castHoverRequired
                  ? <CastGlyph size={16} strokeWidth={2.25} />
                  : 'Cast'}
            </button>
          )}

          {(contentType === 'live' || contentType === 'catchup' || contentType === 'catchup-hls') && castActive && (
            <Box
              className="floating-video-no-drag"
              style={{
                position: 'absolute',
                inset: 0,
                zIndex: 30,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'rgba(0,0,0,0.72)',
                pointerEvents: 'none',
              }}
            >
              <Box style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                <Text size="sm" c="white" fw={600} aria-live="polite">
                  {castReturningToLive
                    ? `Returning to Live${castDeviceName ? ` on ${castDeviceName}` : ''}...`
                    : castRewindingOnTv // DISPATCHARR_CAST_REMOTE_REWIND_R135
                      ? castRewindingOnTv === 'pause' // DISPATCHARR_LIVE_PAUSE_CHOICE_R136
                        ? `Resuming on ${castDeviceName || 'the TV'}...`
                        : `Rewinding on ${castDeviceName || 'the TV'}...`
                      : contentType === 'live' && castLiveCommand // R136
                        ? `${
                            castLiveCommand === 'pause'
                              ? 'Pausing'
                              : castLiveCommand === 'live'
                                ? 'Continuing Live'
                                : 'Resuming'
                          } on ${castDeviceName || 'the TV'}...`
                        : contentType === 'live' && livePause?.where === 'tv' // R136
                          ? `Paused on ${castDeviceName || 'the TV'}`
                          : `Casting${castDeviceName ? ` to ${castDeviceName}` : ''}`}
                </Text>
                {castRewindingOnTv === 'tv-remote' && !castReturningToLive && (
                  <Text size="xs" c="dimmed" fw={500}>
                    from the TV remote
                  </Text>
                )}
                {castReturningToLive && (
                  <Text size="xs" c="yellow" fw={600}>
                    Please wait, do not press Stop Casting
                  </Text>
                )}
                {/* DISPATCHARR_LIVE_PAUSE_CHOICE_R136: the paused Live cast's choice */}
                {contentType === 'live' &&
                  livePause?.where === 'tv' &&
                  !castReturningToLive &&
                  !castRewindingOnTv && (
                  <Box
                    className="floating-video-no-drag dispatcharr-cast-live-paused"
                    data-dispatcharr-paused="tv-live"
                    onMouseDown={(event) => event.stopPropagation()}
                    onTouchStart={(event) => event.stopPropagation()}
                    onClick={(event) => event.stopPropagation()}
                    style={{
                      pointerEvents: 'auto',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 8,
                      marginTop: 4,
                    }}
                  >
                    {renderLivePauseChoices('tv')}
                  </Box>
                )}
              </Box>

              {(contentType === 'catchup' || contentType === 'catchup-hls') &&
                (!castHoverRequired || castHover) && (
                <Box
                  className="floating-video-no-drag dispatcharr-cast-archive-controls"
                  onMouseEnter={() => setCastHover(true)}
                  onMouseLeave={(event) => {
                    if (event.relatedTarget?.tagName === 'VIDEO') return;
                    setCastHover(false);
                  }}
                  style={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 34,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '7px 8px',
                    background:
                      'linear-gradient(transparent, rgba(0,0,0,0.88) 35%, rgba(0,0,0,0.96))',
                    pointerEvents: 'auto',
                  }}
                >
                  {/* DISPATCHARR_CAST_SEEK_HOVER_CLOCK_R11: the same mm:ss
                      readout the Rewind bar has. This bar is itself
                      position:absolute and has no overflow clipping, so the
                      tip can simply sit above it -- no head-room trick
                      needed here (unlike TimeshiftSeekBar). */}
                  <div
                    ref={castSeekHoverTipRef}
                    style={{
                      position: 'absolute',
                      bottom: 'calc(100% + 4px)',
                      left: 0,
                      transform: 'translateX(-50%)',
                      padding: '2px 6px',
                      borderRadius: 4,
                      background: 'rgba(0,0,0,0.9)',
                      color: 'white',
                      fontSize: 11,
                      fontVariantNumeric: 'tabular-nums',
                      whiteSpace: 'nowrap',
                      pointerEvents: 'none',
                      opacity: 0,
                      transition: 'opacity 0.12s ease',
                      zIndex: 38,
                    }}
                  >
                    0:00
                  </div>
                  <button
                    type="button"
                    title={castArchivePaused ? 'Play Archive on Chromecast' : 'Pause Archive on Chromecast'}
                    onClick={toggleArchiveCastPlayback}
                    style={{
                      border: 0,
                      background: 'transparent',
                      color: 'white',
                      cursor: 'pointer',
                      fontSize: 16,
                      minWidth: 28,
                    }}
                  >
                    {castArchivePaused ? '▶' : '❚❚'}
                  </button>
                  <button
                    type="button"
                    title="Rewind 10 seconds on Chromecast"
                    onClick={() => skipArchiveCast(-10)}
                    style={{
                      border: 0, background: 'transparent', color: 'white',
                      cursor: 'pointer', fontSize: 12, minWidth: 32,
                    }}
                  >
                    <span
                      style={{
                        position: 'relative',
                        width: 24,
                        height: 24,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <RotateCcw size={21} strokeWidth={2.1} />
                      <span
                        style={{
                          position: 'absolute',
                          fontSize: 7,
                          fontWeight: 800,
                          lineHeight: 1,
                        }}
                      >
                        10
                      </span>
                    </span>
                  </button>
                  <Text size="xs" c="white" style={{ minWidth: 42 }}>
                    {castClockLabel(castArchivePositionSeconds, 'position')}
                  </Text>
                  <input
                    ref={castSeekInputRef}
                    className="floating-video-no-drag dispatcharr-scrub-range"
                    type="range"
                    min="0"
                    max={Math.max(1, castArchiveDurationSeconds || dispatcharrCastRuntime.archiveDuration || 1)}
                    step="1"
                    // DISPATCHARR_NATIVE_RANGE_TIP_R133: floored like the elapsed
                    // label. The raw clock was ROUNDED by the step="1" value
                    // sanitizer, so the thumb sat up to 1 s ahead of the label.
                    value={Math.min(
                      Math.max(0, Math.floor(castArchivePositionSeconds)),
                      Math.max(1, castArchiveDurationSeconds || dispatcharrCastRuntime.archiveDuration || 1)
                    )}
                    onMouseDown={(event) => {
                      // R133a review: a right-click's mouseup goes to the
                      // context menu, wedging pressed/scrubbing.
                      if (event.button !== 0) return;
                      castArchiveScrubbingRef.current = true;
                      // DISPATCHARR_NATIVE_RANGE_TIP_R133: listeners run before the
                      // browser moves the thumb, so this is the pre-press value.
                      const pointer = castSeekPointerRef.current;
                      pointer.pressed = true;
                      pointer.pressValue = event.currentTarget.value;
                      refreshCastSeekTip();
                    }}
                    onTouchStart={(event) => {
                      castArchiveScrubbingRef.current = true;
                      castSeekPointerRef.current.pressValue = event.currentTarget.value;
                    }}
                    // DISPATCHARR_CAST_SEEK_HOVER_CLOCK_R11
                    // DISPATCHARR_NATIVE_RANGE_TIP_R133: PointerEvent, not
                    // MouseEvent -- its clientX keeps the fractional pixel.
                    onPointerMove={(event) => {
                      if (event.pointerType === 'touch') return;
                      const pointer = castSeekPointerRef.current;
                      pointer.x = event.clientX;
                      pointer.y = event.clientY;
                      // While pressed, the thumb moves only AFTER this listener
                      // (browser default action); onChange refreshes the tip then.
                      if (!pointer.pressed) refreshCastSeekTip();
                    }}
                    onPointerLeave={() => {
                      const pointer = castSeekPointerRef.current;
                      pointer.x = null;
                      pointer.y = null;
                      refreshCastSeekTip();
                    }}
                    onChange={(event) => {
                      castArchiveScrubbingRef.current = true;
                      setCastArchivePositionSeconds(Number(event.currentTarget.value));
                    }}
                    onMouseUp={(event) => {
                      if (event.button !== 0) return; // R133a review
                      const pointer = castSeekPointerRef.current;
                      const grabOnly = pointer.pressValue === event.currentTarget.value;
                      pointer.pressed = false;
                      pointer.pressValue = null;
                      if (grabOnly) {
                        // DISPATCHARR_NATIVE_RANGE_TIP_R133: a click ON the thumb
                        // never moves it; committing it reloaded the receiver at
                        // the second it was already playing.
                        castArchiveScrubbingRef.current = false;
                        refreshCastSeekTip();
                        return;
                      }
                      commitCastSeek(event.currentTarget.value);
                    }}
                    onTouchEnd={(event) => {
                      const pointer = castSeekPointerRef.current;
                      const grabOnly = pointer.pressValue === event.currentTarget.value;
                      pointer.pressValue = null;
                      if (grabOnly) {
                        castArchiveScrubbingRef.current = false;
                        return;
                      }
                      commitCastSeek(event.currentTarget.value);
                    }}
                    onKeyUp={(event) => {
                      if (
                        event.key === 'ArrowLeft' ||
                        event.key === 'ArrowRight' ||
                        event.key === 'Home' ||
                        event.key === 'End'
                      ) {
                        commitCastSeek(event.currentTarget.value);
                      }
                    }}
                    style={{
                      flex: 1,
                      minWidth: 60,
                      cursor: 'pointer',
                      '--dispatcharr-scrub-fill': scrubFillPercent(
                        castArchivePositionSeconds,
                        0,
                        Math.max(1, castArchiveDurationSeconds || dispatcharrCastRuntime.archiveDuration || 1)
                      ),
                    }}
                  />
                  <Text size="xs" c="white" style={{ minWidth: 42 }}>
                    {castClockLabel(
                      castArchiveDurationSeconds || dispatcharrCastRuntime.archiveDuration || 0,
                      'edge'
                    )}
                  </Text>
                  <button
                    type="button"
                    title="Forward 10 seconds on Chromecast"
                    onClick={() => skipArchiveCast(10)}
                    style={{
                      border: 0, background: 'transparent', color: 'white',
                      cursor: 'pointer', fontSize: 12, minWidth: 32,
                    }}
                  >
                    <span
                      style={{
                        position: 'relative',
                        width: 24,
                        height: 24,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <RotateCw size={21} strokeWidth={2.1} />
                      <span
                        style={{
                          position: 'absolute',
                          fontSize: 7,
                          fontWeight: 800,
                          lineHeight: 1,
                        }}
                      >
                        10
                      </span>
                    </span>
                  </button>
                </Box>
              )}
              {/* DISPATCHARR_LIVE_PAUSE_CHOICE_R136: pause / Continue Live of a Live cast */}
              {contentType === 'live' &&
                castLiveReadable && // R136 review: only for the channel the TV plays
                !showLiveRewindPicker &&
                !castReturningToLive &&
                !castRewindingOnTv &&
                // R136 review: the paused panel carries Continue Live itself; the
                // bar would cover its buttons in the default 320x180 player.
                (!castHoverRequired || castHover) &&
                livePause?.where !== 'tv' && (
                <Box
                  className="floating-video-no-drag dispatcharr-cast-live-controls"
                  onMouseEnter={() => setCastHover(true)}
                  onMouseLeave={(event) => {
                    if (event.relatedTarget?.tagName === 'VIDEO') return;
                    setCastHover(false);
                  }}
                  onMouseDown={(event) => event.stopPropagation()}
                  onTouchStart={(event) => event.stopPropagation()}
                  style={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 34,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '7px 8px',
                    background:
                      'linear-gradient(transparent, rgba(0,0,0,0.88) 35%, rgba(0,0,0,0.96))',
                    pointerEvents: 'auto',
                  }}
                >
                  <button
                    type="button"
                    className="dispatcharr-cast-live-play"
                    data-dispatcharr-live-cast-transport={livePause?.where === 'tv' ? 'play' : 'pause'}
                    title={
                      livePause?.where === 'tv'
                        ? `Continue Live on ${castDeviceName || 'the TV'} (Space)`
                        : `Pause on ${castDeviceName || 'the TV'} (Space)`
                    }
                    disabled={Boolean(castLiveCommand)}
                    onClick={() => {
                      void (livePause?.where === 'tv'
                        ? continueLiveFromPause('bar')
                        : pauseLiveCast('bar'));
                    }}
                    style={{
                      border: 0,
                      background: 'transparent',
                      color: 'white',
                      cursor: castLiveCommand ? 'default' : 'pointer',
                      fontSize: 16,
                      minWidth: coarsePointer ? 44 : 28,
                      minHeight: coarsePointer ? 44 : undefined,
                      opacity: castLiveCommand ? 0.55 : 1,
                    }}
                  >
                    {livePause?.where === 'tv' ? '▶' : '❚❚'}
                  </button>
                  <Text size="xs" fw={700} c={livePause?.where === 'tv' ? 'yellow' : 'white'}>
                    {livePause?.where === 'tv' ? 'Paused' : 'LIVE'}
                  </Text>
                </Box>
              )}
            </Box>
          )}

          {(contentType === 'live' || contentType === 'catchup' || contentType === 'catchup-hls') && castError && (
            <Box
              className="floating-video-no-drag dispatcharr-fs-badge"
              title="Dismiss"
              // DISPATCHARR_CAST_ERROR_AUTOHIDE_R124: click dismisses.
              // stopPropagation so the click can never fall through to the
              // stage's badge toggle (touch) or any click-to-pause surface.
              onClick={(event) => {
                event.stopPropagation();
                setCastError(null);
              }}
              style={{
                position: 'absolute',
                top: 44,
                right: 8,
                zIndex: 35,
                maxWidth: '75%',
                borderRadius: 5,
                // DISPATCHARR_ARCHIVE_CAST_PROGRAMME_END_R133: the programme ending
                // is news, not a failure -- neutral, not red.
                background:
                  castError === ARCHIVE_CAST_FINISHED_NOTICE
                    ? 'rgba(25,25,25,0.9)'
                    : 'rgba(120,20,20,0.92)',
                padding: '5px 8px',
                cursor: 'pointer',
              }}
            >
              <Text size="xs" c="white">{castError}</Text>
            </Box>
          )}

          {/* DISPATCHARR_LIVE_NATIVE_SEEK_STARTS_TIMESHIFT: the 30s/slider/5m pre-rewind picker
             was removed at the user's request -- native <video
             controls> is now the only rewind UI for plain Live.
             Dragging its scrubber back to the live buffer's
             earliest native-seekable edge transparently opens a
             real provider time-shift session there (see the
             'seeking' listener wired further up in this
             component); that session's own native seek bar is
             already calibrated to the full provider DVR window
             (DISPATCHARR_LIVE_PROVIDER_TIMESHIFT_*), so continued
             backward dragging keeps working natively once it
             takes over. */}

          {/* DISPATCHARR_LIVE_TIMESHIFT_NATIVE_CONTROLS: dropped the custom scrubber/fullscreen
             overlay for live_timeshift. video.controls = contentType
             !== 'catchup' already renders the browser's own native
             controls (scrubber, fullscreen, hover time tooltip) here;
             this bar only duplicated/covered them and round-tripped
             every seek through React state, which is what caused the
             visible 0:00 -> Loading Video -> jump stutter. The LIVE
             return button just below is unaffected. */}

          {/* DISPATCHARR_LIVE_EXPLICIT_REWIND_BUTTON */}
          {/* DISPATCHARR_CAST_LIVE_REWIND_R17: also while casting Live. */}
          {/* DISPATCHARR_LIVE_REWIND_ONLY_WITH_CATCHUP_R99 */}
          {contentType === 'live' && !transportSuspendedKind && liveRewindAvailable && (
            <button
              type="button"
              className="floating-video-no-drag dispatcharr-fs-badge"
              title="Rewind"
              // DISPATCHARR_LIVE_BADGE_HOVER_CONTAINER_ONLY
              onClick={() =>
                setShowLiveRewindPicker((value) => {
                  // R133a review: a stale draft (> the new window) left the
                  // thumb clamped away from the label on reopen.
                  if (!value) setLiveTimeshiftDraftSeconds(0);
                  return !value;
                })
              }
              style={{
                position: 'absolute',
                // DISPATCHARR_LIVE_DESKTOP_BADGE_STRIP_NATIVE_APPEARANCE: Safari's
                // UA stylesheet gives <button> a native bezel/background by
                // default that can still show through explicit inline
                // background/border on WebKit -- Chrome normalizes this away
                // on its own, which is why the dark plate looked different
                // between the two browsers. Explicitly killing native button
                // chrome makes our own background/border the only thing
                // rendered, identically, everywhere.
                WebkitAppearance: 'none',
                appearance: 'none',
                // DISPATCHARR_LIVE_DESKTOP_BADGE_STYLE_AND_POSITION
                top: castHoverRequired ? 6 : 4,
                // DISPATCHARR_LIVE_REWIND_CONTINUELIVE_CLEAR_LARGER_CAST: 64px
                // was sized for Cast's old compact footprint -- widened now
                // that Cast itself is visibly bigger, so there's an actual
                // gap between the two instead of them touching.
                // DISPATCHARR_LIVE_DESKTOP_BADGES_ADJACENT_WHITE: 56 puts
                // Rewind's right edge 6px from Cast's left edge (Cast is
                // at right 4 and 46 wide: 14+14 padding, 16px glyph, 2px
                // border), instead of the old 96 that deliberately held
                // them apart.
                // DISPATCHARR_CAST_LIVE_REWIND_R17: while casting the Cast badge
                // reads "Stop Casting" and is much wider; clear its measured
                // width the same way Continue Live does (R8).
                right: castHoverRequired
                  ? castActive
                    ? (castButtonWidth ? 4 + castButtonWidth + 6 : 127)
                    : (castSdkReady || castAvailable ? 56 : 4)
                  : castActive
                    ? (castButtonWidth ? 8 + castButtonWidth + 6 : 99)
                    : (castSdkReady || castAvailable ? 99 : 34),
                zIndex: 36,
                height: castHoverRequired ? 32 : 24,
                display: 'flex',
                alignItems: 'center',
                border: castHoverRequired
                  ? '1px solid rgba(255,255,255,0.15)'
                  : 0,
                // DISPATCHARR_LIVE_DESKTOP_BADGE_RADIUS_GAP_ALPHA: match
                // Cast's own rounded corners (Rewind/Continue Live were still
                // sharp rectangles).
                borderRadius: castHoverRequired ? 6 : 0,
                background: castHoverRequired
                  ? 'rgba(0,0,0,0.45)'
                  : 'transparent',
                // DISPATCHARR_BADGE_BLUR_ONLY_WHEN_VISIBLE_R122
                backdropFilter:
                  castHoverRequired && (castActive || castPlayerHover)
                    ? 'blur(12px)'
                    : 'none',
                WebkitBackdropFilter:
                  castHoverRequired && (castActive || castPlayerHover)
                    ? 'blur(12px)'
                    : 'none',
                // DISPATCHARR_LIVE_DESKTOP_BADGE_ENLARGE_NATIVE_MATCH: the
                // previous shrink went the wrong direction -- native
                // controls read as bigger, not smaller.
                padding: castHoverRequired ? '0 14px' : '0 8px',
                fontSize: castHoverRequired ? 17 : 13,
                // DISPATCHARR_LIVE_DESKTOP_BADGES_ADJACENT_WHITE: white on
                // desktop, to read as one row with the native controls
                // below. Mobile keeps the EPG green.
                color: castHoverRequired ? '#fff' : '#3ba882',
                cursor: 'pointer',
                fontWeight: 600,
                textShadow: '0 1px 3px rgba(0,0,0,0.95)',
                // DISPATCHARR_CAST_LIVE_REWIND_UI_R18: always visible while
                // casting (there is no picture to hover then), hover-only for
                // local playback -- the same rule Continue Live uses.
                opacity: castActive || castPlayerHover ? 1 : 0,
                pointerEvents: castActive || castPlayerHover ? 'auto' : 'none',
                transition: 'opacity 140ms ease',
              }}
            >
              {castHoverRequired ? (
                <RewindGlyph size={18} strokeWidth={2.25} />
              ) : (
                '⏪ Rewind'
              )}
            </button>
          )}

          {/* DISPATCHARR_CAST_LIVE_REWIND_R17: also while casting Live. */}
          {contentType === 'live' &&
            !transportSuspendedKind &&
            showLiveRewindPicker && (
            <Box
              className="floating-video-no-drag dispatcharr-fs-bar"
              onMouseDown={(event) => event.stopPropagation()}
              onTouchStart={(event) => event.stopPropagation()}
              style={{
                position: 'absolute',
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 34,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '7px 8px',
                background:
                  'linear-gradient(transparent, rgba(0,0,0,0.82) 30%, rgba(0,0,0,0.95))',
              }}
            >
              <div
                ref={rewindHoverTipRef}
                className="dispatcharr-fs-tip"
                style={{
                  position: 'absolute',
                  transform: 'translate(-50%, -100%)',
                  padding: '2px 6px',
                  borderRadius: 4,
                  background: 'rgba(0,0,0,0.9)',
                  color: 'white',
                  fontSize: 11,
                  fontVariantNumeric: 'tabular-nums',
                  whiteSpace: 'nowrap',
                  pointerEvents: 'none',
                  opacity: 0,
                  zIndex: 60,
                }}
              />
              <Text
                className="dispatcharr-fs-time"
                size="xs"
                c="white"
                style={{ minWidth: 48, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}
              >
                {/* DISPATCHARR_LIVE_REWIND_PROGRAMME_TIME_R135 */}
                {liveRewindPointLabel(liveRewindPickerDraft)}
              </Text>
              <input
                ref={rewindPickerInputRef}
                className="floating-video-no-drag dispatcharr-scrub-range dispatcharr-fs-range"
                type="range"
                min={liveRewindPickerMin}
                max="0"
                step="10"
                value={liveRewindPickerDraft}
                title="Drag to choose how far back to rewind"
                onMouseDown={(event) => {
                  if (event.button !== 0) return; // R133a review
                  freezeRewindPickerScale(event.currentTarget); // R135
                  rewindPickerPointerRef.current.pressed = true;
                  refreshRewindPickerTip();
                }}
                onTouchStart={(event) => freezeRewindPickerScale(event.currentTarget)}
                onTouchCancel={() => {
                  rewindPickerFrozenMinRef.current = null; // R135
                }}
                // DISPATCHARR_NATIVE_RANGE_TIP_R133: PointerEvent coordinates;
                // the tip itself is written by refreshRewindPickerTip.
                onPointerMove={(event) => {
                  if (event.pointerType === 'touch') return;
                  // DISPATCHARR_LIVE_REWIND_PROGRAMME_TIME_R135: hold the scale.
                  freezeRewindPickerScale(event.currentTarget);
                  const pointer = rewindPickerPointerRef.current;
                  pointer.x = event.clientX;
                  pointer.y = event.clientY;
                  if (!pointer.pressed) refreshRewindPickerTip();
                }}
                onPointerLeave={() => {
                  const pointer = rewindPickerPointerRef.current;
                  pointer.x = null;
                  pointer.y = null;
                  // DISPATCHARR_LIVE_REWIND_PROGRAMME_TIME_R135: release the scale.
                  if (!pointer.pressed) rewindPickerFrozenMinRef.current = null;
                  refreshRewindPickerTip();
                }}
                onChange={(event) =>
                  setLiveTimeshiftDraftSeconds(Number(event.currentTarget.value) || 0)
                }
                onMouseUp={(event) => {
                  if (event.button !== 0) return; // R133a review
                  rewindPickerPointerRef.current.pressed = false;
                  rewindPickerFrozenMinRef.current = null; // R135
                  const rewind = Math.abs(Number(event.currentTarget.value) || 0);
                  setShowLiveRewindPicker(false);
                  if (rewind >= 1) void startLiveProviderTimeshift(rewind);
                }}
                onTouchEnd={(event) => {
                  rewindPickerFrozenMinRef.current = null; // R135
                  const rewind = Math.abs(Number(event.currentTarget.value) || 0);
                  setShowLiveRewindPicker(false);
                  if (rewind >= 1) void startLiveProviderTimeshift(rewind);
                }}
                onKeyUp={(event) => {
                  if (event.key !== 'Enter') return;
                  rewindPickerFrozenMinRef.current = null; // R135
                  const rewind = Math.abs(Number(event.currentTarget.value) || 0);
                  setShowLiveRewindPicker(false);
                  if (rewind >= 1) void startLiveProviderTimeshift(rewind);
                }}
                style={{
                  flex: 1,
                  minWidth: 70,
                  cursor: 'pointer',
                  '--dispatcharr-scrub-fill': scrubFillPercent(
                    liveRewindPickerDraft,
                    liveRewindPickerMin,
                    0
                  ),
                }}
              />
              {/* DISPATCHARR_LIVE_REWIND_PROGRAMME_TIME_R135: the live programme's
                  running time -- the bar follows the broadcast. */}
              <Text
                className="dispatcharr-fs-time"
                size="xs"
                c="white"
                style={{
                  minWidth: 48,
                  textAlign: 'right',
                  whiteSpace: 'nowrap',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {liveRewindEdgeLabel()}
              </Text>
              <button
                type="button"
                className="dispatcharr-fs-btn dispatcharr-fs-close"
                title="Cancel"
                onClick={() => setShowLiveRewindPicker(false)}
                style={{
                  border: 0,
                  background: 'transparent',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: 15,
                  minWidth: 24,
                }}
              >
                ✕
              </button>
            </Box>
          )}

          {/* DISPATCHARR_LIVE_CONTINUE_LIVE_LABEL_AND_CAST_AWARE: renamed from
             "Go Live", and no longer hidden while castActive -- shown
             alongside Stop Casting so Cast can jump the TV back to the
             live edge without a separate Stop-then-Go-Live round trip. */}
          {contentType === 'catchup-hls' && metadata?.liveTimeshift && (
            <button
              type="button"
              className="floating-video-no-drag dispatcharr-fs-badge"
              title="Continue Live"
              onClick={returnToLiveFromProviderTimeshift}
              style={{
                position: 'absolute',
                // DISPATCHARR_LIVE_DESKTOP_BADGE_STRIP_NATIVE_APPEARANCE: Safari's
                // UA stylesheet gives <button> a native bezel/background by
                // default that can still show through explicit inline
                // background/border on WebKit -- Chrome normalizes this away
                // on its own, which is why the dark plate looked different
                // between the two browsers. Explicitly killing native button
                // chrome makes our own background/border the only thing
                // rendered, identically, everywhere.
                WebkitAppearance: 'none',
                appearance: 'none',
                // DISPATCHARR_LIVE_REMOVE_FS_BADGE_STYLE_REWIND_GREEN
                // DISPATCHARR_LIVE_DESKTOP_BADGE_STYLE_AND_POSITION
                // DISPATCHARR_LIVE_CONTINUELIVE_CLEAR_STOP_CASTING: "Stop
                // Casting" is visibly wider than idle "Cast" -- the same
                // 64px offset that clears the idle label doesn't clear the
                // active one, so castActive gets its own, further-left tier.
                top: castHoverRequired ? 6 : 4,
                // DISPATCHARR_LIVE_DESKTOP_BADGES_ADJACENT_WHITE: same 56 as
                // Rewind -- 6px clear of Cast's left edge. The castActive
                // tier keeps its own wider offset because Cast renders
                // the much wider "Stop Casting" label in that state.
                // DISPATCHARR_CAST_BADGE_GAP_R7: 121 was exactly the width
                // of the "Stop Casting" pill plus its own 4px offset, so the
                // two badges met with a 0px seam. The idle tier below clears
                // "Cast" by 6px; give the active tier the same 6px.
                // DISPATCHARR_CAST_BADGE_MEASURED_R8: Cast's own offset plus its
                // measured width plus a 6px gap -- the same clearance the idle
                // tier has always had, but derived instead of guessed.
                right: castHoverRequired
                  ? castActive
                    ? (castButtonWidth ? 4 + castButtonWidth + 6 : 127)
                    : (castSdkReady || castAvailable ? 56 : 4)
                  : castActive && castButtonWidth
                    ? 8 + castButtonWidth + 6
                    : (castSdkReady || castAvailable || castActive ? 99 : 34),
                zIndex: 36,
                height: castHoverRequired ? 32 : 24,
                display: 'flex',
                alignItems: 'center',
                border: castHoverRequired
                  ? '1px solid rgba(255,255,255,0.15)'
                  : 0,
                // DISPATCHARR_LIVE_DESKTOP_BADGE_RADIUS_GAP_ALPHA: match
                // Cast's own rounded corners (Rewind/Continue Live were still
                // sharp rectangles).
                borderRadius: castHoverRequired ? 6 : 0,
                background: castHoverRequired
                  ? 'rgba(0,0,0,0.45)'
                  : 'transparent',
                // DISPATCHARR_BADGE_BLUR_ONLY_WHEN_VISIBLE_R122
                backdropFilter:
                  castHoverRequired && (castActive || castPlayerHover)
                    ? 'blur(12px)'
                    : 'none',
                WebkitBackdropFilter:
                  castHoverRequired && (castActive || castPlayerHover)
                    ? 'blur(12px)'
                    : 'none',
                // DISPATCHARR_LIVE_DESKTOP_BADGES_ADJACENT_WHITE: the label
                // goes white with the rest of the cluster on desktop;
                // the live dot keeps its red on its own span, since that
                // colour is the status signal, not decoration.
                color: castHoverRequired ? '#fff' : '#ff3b30',
                padding: castHoverRequired ? '0 14px' : '0 8px',
                cursor: 'pointer',
                fontSize: castHoverRequired ? 16 : 12,
                fontWeight: 700,
                textShadow: '0 1px 3px rgba(0,0,0,0.95)',
                opacity: castActive || castPlayerHover ? 1 : 0,
                pointerEvents:
                  castActive || castPlayerHover ? 'auto' : 'none',
                transition: 'opacity 140ms ease',
              }}
            >
              <span style={{ color: '#ff3b30', marginRight: 6 }}>●</span>
              Continue Live
            </button>
          )}

          {/* DISPATCHARR_LIVE_REMOVE_FS_BADGE_STYLE_REWIND_GREEN: no custom
             fullscreen trigger -- native controls' own button
             owns fullscreen again, at the explicitly accepted
             cost of Go Live/Rewind being unreachable while
             genuinely fullscreened. */}

          {transportSuspendedKind && !castActive && (
            <Box
              className="floating-video-no-drag dispatcharr-fs-suspend"
              data-dispatcharr-paused={transportSuspendedKind === 'live' ? 'local-live' : 'archive'}
              style={{
                position: 'absolute',
                inset: 0,
                zIndex: 30,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 10,
                background: 'rgba(0, 0, 0, 0.72)',
              }}
              onMouseDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
            >
              <Text
                className="dispatcharr-fs-suspend-title"
                size="sm"
                c="white"
                fw={600}
                aria-live="polite"
              >
                {/* DISPATCHARR_LIVE_PAUSE_CHOICE_R136 */}
                {transportSuspendedKind === 'live' || archiveSoftPaused
                  ? 'Paused'
                  : 'Stream suspended'}
              </Text>
              {transportSuspendedKind === 'live' ? (
                renderLivePauseChoices('local')
              ) : (
                <button
                  type="button"
                  className="dispatcharr-fs-resume"
                  onClick={resumeSuspendedTransport}
                  style={{
                    border: '1px solid rgba(255,255,255,0.7)',
                    borderRadius: 6,
                    background: 'rgba(20,20,20,0.9)',
                    color: 'white',
                    padding: '8px 14px',
                    cursor: 'pointer',
                    fontSize: 14,
                  }}
                >
                  Resume Archive
                </button>
              )}
            </Box>
          )}

          {/* DISPATCHARR_CAST_LOCAL_BARS_HIDDEN_R4: same reason as the
              time-shift bar above -- it drives the destroyed local player. */}
          {contentType === 'catchup' && !isLoading && !castActive && (
            <Box
              className="floating-video-no-drag dispatcharr-fs-bar"
              onMouseDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
              style={{
                position: 'absolute',
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 12,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '7px 8px',
                background:
                  'linear-gradient(transparent, rgba(0,0,0,0.88) 35%, rgba(0,0,0,0.94))',
              }}
            >
              <button
                type="button"
                className="dispatcharr-fs-btn dispatcharr-fs-play"
                title={archivePaused ? 'Play' : 'Pause'}
                onClick={toggleArchivePlayback}
                style={{
                  border: 0,
                  background: 'transparent',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: 16,
                  minWidth: 26,
                }}
              >
                {archivePaused ? '▶' : '❚❚'}
              </button>
              <Text
                className="dispatcharr-fs-time"
                size="xs"
                c="white"
                style={{ minWidth: 38 }}
              >
                {formatArchiveClock(archivePositionSeconds)}
              </Text>
              <input
                className="floating-video-no-drag dispatcharr-scrub-range dispatcharr-fs-range"
                type="range"
                min="0"
                max={Math.max(1, archiveDurationSeconds - 1)}
                step="1"
                value={Math.min(
                  Math.max(0, archivePositionSeconds),
                  Math.max(1, archiveDurationSeconds - 1)
                )}
                onMouseDown={() => {
                  archiveScrubbingRef.current = true;
                }}
                onTouchStart={() => {
                  archiveScrubbingRef.current = true;
                }}
                onChange={(e) => {
                  archiveScrubbingRef.current = true;
                  setArchivePositionSeconds(Number(e.currentTarget.value));
                }}
                onMouseUp={(e) => commitArchiveSeek(e.currentTarget.value)}
                onTouchEnd={(e) => commitArchiveSeek(e.currentTarget.value)}
                onKeyUp={(e) => {
                  if (
                    e.key === 'ArrowLeft' ||
                    e.key === 'ArrowRight' ||
                    e.key === 'Home' ||
                    e.key === 'End'
                  ) {
                    commitArchiveSeek(e.currentTarget.value);
                  }
                }}
                style={{
                  flex: 1,
                  minWidth: 60,
                  cursor: 'pointer',
                  '--dispatcharr-scrub-fill': scrubFillPercent(
                    archivePositionSeconds,
                    0,
                    Math.max(1, archiveDurationSeconds - 1)
                  ),
                }}
              />
              <Text
                className="dispatcharr-fs-time"
                size="xs"
                c="white"
                style={{ minWidth: 38 }}
              >
                {formatArchiveClock(archiveDurationSeconds)}
              </Text>
              <button
                type="button"
                className="dispatcharr-fs-btn dispatcharr-fs-mute"
                title={archiveMuted ? 'Unmute' : 'Mute'}
                onClick={toggleArchiveMute}
                style={{
                  border: 0,
                  background: 'transparent',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: 15,
                  minWidth: 28,
                }}
              >
                {archiveMuted ? '🔇' : '🔊'}
              </button>
              <button
                type="button"
                className="dispatcharr-fs-btn dispatcharr-fs-full"
                title="Fullscreen"
                onClick={toggleArchiveFullscreen}
                style={{
                  border: 0,
                  background: 'transparent',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: 18,
                  minWidth: 28,
                }}
              >
                ⛶
              </button>
            </Box>
          )}
          {/* DISPATCHARR_LIVE_DESKTOP_CUSTOM_CONTROLS_R120: desktop Live control
              bar, replacing the native <video> controls. Lives inside the stage
              Box (same parent as <video> and the Resume overlay), so it renders
              in our stage fullscreen. Shown on hover while playing; while
              suspended the "Resume Live" overlay takes over. Fullscreen uses the
              stage path (toggleArchiveFullscreen) so the overlay can render and
              Space (our document handler) owns play/pause in every browser. */}
          {contentType === 'live' &&
            !coarsePointer &&
            !castActive &&
            !transportSuspendedKind &&
            !isLoading && (
            <Box
              className="floating-video-no-drag dispatcharr-fs-bar"
              onMouseDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
              style={{
                position: 'absolute',
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 12,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '7px 8px',
                background:
                  'linear-gradient(transparent, rgba(0,0,0,0.88) 35%, rgba(0,0,0,0.94))',
                opacity: castPlayerHover ? 1 : 0,
                pointerEvents: castPlayerHover ? 'auto' : 'none',
                transition: 'opacity 140ms ease',
              }}
            >
              <button
                type="button"
                className="dispatcharr-fs-btn dispatcharr-fs-play"
                title="Pause"
                onClick={toggleLivePlayback}
                style={{
                  border: 0,
                  background: 'transparent',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: 16,
                  minWidth: 26,
                }}
              >
                ❚❚
              </button>
              <Box style={{ flex: 1 }} />
              <button
                type="button"
                className="dispatcharr-fs-btn dispatcharr-fs-mute"
                title={liveMuted || liveVolume === 0 ? 'Unmute' : 'Mute'}
                onClick={toggleLiveMute}
                style={{
                  border: 0,
                  background: 'transparent',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: 15,
                  minWidth: 28,
                }}
              >
                {liveMuted || liveVolume === 0 ? '🔇' : '🔊'}
              </button>
              {/* DISPATCHARR_LIVE_DESKTOP_VOLUME_R121: replaces the native volume
                  slider. A range input does not keep Space (R121 handler), so
                  Space still toggles playback after a drag. */}
              <input
                className="floating-video-no-drag dispatcharr-scrub-range dispatcharr-fs-range dispatcharr-fs-volume"
                type="range"
                min="0"
                max="1"
                step="0.05"
                aria-label="Volume"
                title="Volume"
                value={liveMuted ? 0 : liveVolume}
                onChange={(event) => {
                  const video = videoRef.current;
                  if (!video) return;
                  const next = Math.min(
                    1,
                    Math.max(0, Number(event.currentTarget.value) || 0)
                  );
                  video.volume = next;
                  if (next > 0 && video.muted) video.muted = false;
                  setLiveVolume(next);
                  setLiveMuted(video.muted);
                }}
                style={{
                  width: 72,
                  cursor: 'pointer',
                  '--dispatcharr-scrub-fill': scrubFillPercent(
                    liveMuted ? 0 : liveVolume,
                    0,
                    1
                  ),
                }}
              />
              <button
                type="button"
                className="dispatcharr-fs-btn dispatcharr-fs-full"
                title="Fullscreen"
                // DISPATCHARR_LIVE_FS_BUTTON_BLUR_R121: drop the focus the click
                // gave this button (Chrome, Edge, Opera and Firefox focus a
                // clicked <button>; Safari does not), so it never sits focused
                // inside the fullscreen stage where Space could re-activate it.
                onClick={(event) => {
                  event.currentTarget.blur();
                  toggleArchiveFullscreen();
                }}
                style={{
                  border: 0,
                  background: 'transparent',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: 18,
                  minWidth: 28,
                }}
              >
                ⛶
              </button>
            </Box>
          )}
          {/* Never while Casting. This bar drives the local <video>, which the
              Cast handoff destroys, so it stayed on top of the Cast overlay
              (zIndex 33 > 30) looking live while doing nothing -- and it sits
              on the path from the video to the real Cast controls, so passing
              over it dropped castHover and the working bar never appeared.
              DISPATCHARR_CAST_LOCAL_BARS_HIDDEN_R4 */}
          {contentType === 'catchup-hls' && !isLoading && !coarsePointer && !castActive && (
              <TimeshiftSeekBar
                videoRef={videoRef}
                shakaPlayerRef={shakaTimeshiftPlayerRef}
                visible={castPlayerHover}
                onFullscreen={toggleArchiveFullscreen}
                // DISPATCHARR_LIVE_REWIND_PROGRAMME_TIME_R135
                clockLabel={metadata?.liveTimeshift ? liveTimeshiftClockLabel : null}
                // DISPATCHARR_ARCHIVE_PTS_ALIGN_R137
                latestSeekTime={metadata?.liveTimeshift ? liveTimeshiftLatestSeekSeconds : null}
              />
            )}

          {/* VOD title overlay when not loading - auto-hides after 4 seconds */}
          {!isLoading && metadata && contentType === 'vod' && showOverlay && (
            <Box
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                background: 'linear-gradient(rgba(0,0,0,0.8), transparent)',
                padding: '10px 10px 20px',
                color: 'white',
                pointerEvents: 'none', // Allow clicks to pass through to video controls
                transition: 'opacity 0.3s ease-in-out',
                opacity: showOverlay ? 1 : 0,
              }}
            >
              <Text
                size="sm"
                weight={500}
                style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.8)' }}
              >
                {metadata.name}
              </Text>
              {metadata.year && (
                <Text
                  size="xs"
                  color="dimmed"
                  style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.8)' }}
                >
                  {metadata.year}
                </Text>
              )}
            </Box>
          )}

          {/* DISPATCHARR_ARCHIVE_IOS_TAP_TO_PLAY_R71: replaces the loading
              overlay when the browser refused to start without a tap. */}
          {archiveTapToPlay && contentType === 'catchup-hls' && (
            <Box
              role="button"
              tabIndex={0}
              aria-label="Play archive"
              onClick={handleArchiveTapToPlay}
              onTouchEnd={handleArchiveTapToPlay}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  handleArchiveTapToPlay(event);
                }
              }}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backgroundColor: 'rgba(0, 0, 0, 0.55)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 6,
                cursor: 'pointer',
              }}
            >
              <Box
                style={{
                  width: 64,
                  height: 64,
                  borderRadius: '50%',
                  backgroundColor: 'rgba(255, 255, 255, 0.92)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <PlayGlyph size={30} color="#111" fill="#111" style={{ marginLeft: 4 }} />
              </Box>
              <Text color="white" size="sm" mt={10}>
                Tap to play
              </Text>
            </Box>
          )}

          {/* Loading overlay - only show when loading */}
          {isLoading && !archiveTapToPlay && (
            <Box
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 5,
              }}
            >
              <Loader color="cyan" size="md" />
              <Text color="white" size="sm" mt={10}>
                Loading {contentType === 'live' ? 'stream' : 'video'}...
              </Text>
            </Box>
          )}

          {/* Error message below video - doesn't block controls.
              DISPATCHARR_FULLSCREEN_LOAD_ERROR_IN_STAGE_R124: lives INSIDE the
              stage Box, because in stage fullscreen only the fullscreen element
              is rendered -- as a sibling this strip was invisible there and a
              failed (re)start of Live read as a dead black screen. In the
              floating player it is the same flow strip right below the
              <video>; in fullscreen .dispatcharr-fs-error pins it to the
              bottom of the stage (the video is forced to 100% height there and
              the stage clips overflow). */}
          {!isLoading && loadError && (
            <Box
              className="dispatcharr-fs-error"
              style={{
                padding: '10px',
                backgroundColor: '#2d1b2e',
                borderTop: '1px solid #444',
              }}
            >
              <Text color="red" size="xs" style={{ textAlign: 'center' }}>
                {loadError}
              </Text>
            </Box>
          )}
        </Box>

        <ResizeHandles startResize={startResize} />
      </div>
    </Draggable>
  );
}
